import React, { Component, useState, useEffect, useMemo, useRef, useCallback } from 'react';

// Defined outside LotSenseApp so React always sees a stable component reference.
// Defining it inside the parent render (the old IIFE pattern) caused React to
// treat it as a new component type on every parent re-render, resetting all
// form state whenever the user typed in any field.
function QuickAddVehicleModal({ onClose, vehicles, soldVehicles = [], onRefresh }) {
  const [qf, setQf] = useState({ vin: '', year: '', make: '', model: '', trim: '', color: '', mileage: '', purchasePrice: '', estimatedSale: '', auctionId: '', buyer: '', purchaseDate: new Date().toISOString().split('T')[0], notes: '' });
  const [qAuctions, setQAuctions] = useState([]);
  const [qDecoding, setQDecoding] = useState(false);
  const [qSaving, setQSaving] = useState(false);
  const [qError, setQError] = useState('');
  const [qDecoded, setQDecoded] = useState(false);
  const [qVinManual, setQVinManual] = useState(false);
  const [qDuplicateVin, setQDuplicateVin] = useState(false);
  const qVinReady = qDecoded || qVinManual;

  useEffect(() => { api.getAuctions().then(setQAuctions).catch(() => {}); }, []);

  const decodeVin = useCallback(async (vin) => {
    if (!vin || vin.length !== 17) { setQError('VIN must be 17 characters'); return; }
    setQDecoding(true); setQError('');
    try {
      const resp = await fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVin/${vin}?format=json`);
      const data = await resp.json();
      const gv = (v) => { const r = data.Results?.find(x => x.Variable === v); return r?.Value && r.Value !== 'Not Applicable' ? r.Value : ''; };
      const errCode = gv('Error Code');
      if (errCode && errCode.includes('1')) { setQError('Invalid VIN'); return; }
      const make = gv('Make'), model = gv('Model');
      if (!make || !model) { setQError('Could not decode make/model'); return; }
      setQf(f => ({ ...f, year: gv('Model Year') || f.year, make, model, trim: gv('Trim') || f.trim }));
      setQDecoded(true);
    } catch (e) { setQError('Decode failed: ' + e.message); }
    finally { setQDecoding(false); }
  }, []);

  useEffect(() => {
    if (qVinManual) return;
    if (qf.vin.length === 17 && !qDecoded && !qDecoding) decodeVin(qf.vin);
  }, [qf.vin, qDecoded, qDecoding, decodeVin, qVinManual]);

  const handleSubmit = async (e, forceOverride = false) => {
    if (e) e.preventDefault();
    if (!qVinReady) { setQError('Please decode VIN first or check Pre-1981 / does not decode'); return; }
    if (qVinManual) {
      if (!qf.vin || qf.vin.length < 4) { setQError('VIN required (minimum 4 characters)'); return; }
      if (!qf.year || !qf.make || !qf.model) { setQError('Year, make, and model are required'); return; }
    }
    if (!qf.auctionId) { setQError('Select an auction source'); return; }
    if (!qf.mileage) { setQError('Odometer is required'); return; }
    if (!qf.color) { setQError('Color is required'); return; }
    if (!qf.purchasePrice) { setQError('Purchase price is required'); return; }

    const auction = qAuctions.find(a => a.id === parseInt(qf.auctionId));
    if (!auction) { setQError('Invalid auction'); return; }
    const last4 = qf.vin.slice(-4).toUpperCase();
    const abbrev = auction.abbreviation || auction.name.slice(0, 3).toUpperCase();
    let stockNumber = `${last4}-${abbrev}`;
    const existing = [...vehicles, ...soldVehicles].map(v => v.stockNumber).filter(Boolean);
    let suffix = 2;
    while (existing.includes(stockNumber)) { stockNumber = `${last4}-${abbrev}-${suffix}`; suffix++; }

    setQSaving(true); setQError(''); setQDuplicateVin(false);
    try {
      await api.createVehicle({
        vin: qf.vin, stockNumber, year: qf.year, make: qf.make, model: qf.model, trim: qf.trim,
        color: qf.color, mileage: qf.mileage, purchasePrice: parseFloat(qf.purchasePrice) || 0,
        estimatedSale: parseFloat(qf.estimatedSale) || 0, auctionId: parseInt(qf.auctionId),
        auction: auction.name, buyer: qf.buyer || null, purchaseDate: qf.purchaseDate || null,
        whatItNeeds: qf.notes || '', titleStatus: 'absent', endUse: 'retail',
        paymentMethod: 'Floorplan', inspectionType: 'dealer_direct',
        auctionAnnouncements: qVinManual ? 'Pre-1981 / VIN does not decode' : undefined,
        forceOverride: forceOverride || undefined,
      });
      onRefresh();
      onClose();
    } catch (err) {
      if (err.message && err.message.includes('VIN already exists')) {
        setQDuplicateVin(true);
        setQError('');
      } else {
        setQError(err.message);
      }
    }
    finally { setQSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-lg rounded-xl shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-neutral-200 flex justify-between items-center">
          <h2 className="font-bold text-lg">⚡ Quick Add Vehicle</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-2xl">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          {qError && <div className="text-red-600 text-sm p-2 bg-red-50 rounded">{qError}</div>}
          {qDuplicateVin && (
            <div className="p-3 bg-amber-50 border border-amber-300 rounded text-sm">
              <p className="font-semibold text-amber-800 mb-1">This VIN already exists in inventory.</p>
              <p className="text-amber-700 mb-2">The vehicle will be saved with a suffix (e.g. VIN-B) so both records coexist.</p>
              <button type="button" onClick={() => handleSubmit(null, true)} disabled={qSaving}
                className="px-4 py-1.5 bg-amber-600 text-white text-sm rounded hover:bg-amber-700 disabled:opacity-50 font-medium">
                {qSaving ? 'Adding...' : 'Override — Add Anyway'}
              </button>
            </div>
          )}
          <div className="flex gap-2">
            <input type="text" placeholder={qVinManual ? 'VIN (manual entry)' : 'VIN (17 chars)'} value={qf.vin} onChange={e => { setQf(f => ({...f, vin: e.target.value.toUpperCase()})); if (!qVinManual && e.target.value.length !== 17) setQDecoded(false); }} className="flex-1 px-3 py-2 text-sm border rounded font-mono" maxLength={17} autoFocus />
            <button type="button" onClick={() => decodeVin(qf.vin)} disabled={qDecoding || qf.vin.length !== 17 || qVinManual} className="px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
              {qDecoding ? '...' : 'Decode'}
            </button>
          </div>
          <label className="flex items-center gap-2 text-xs text-neutral-600 cursor-pointer">
            <input type="checkbox" checked={qVinManual} onChange={e => { setQVinManual(e.target.checked); if (e.target.checked) { setQDecoded(false); setQError(''); } }} className="rounded" />
            Pre-1981 / VIN does not decode — enter year, make, model manually
          </label>
          {qDecoded && !qVinManual && <div className="text-xs text-green-600 font-medium">✓ {qf.year} {qf.make} {qf.model} {qf.trim}</div>}
          {qVinManual && <div className="text-xs text-amber-600 font-medium">Manual VIN entry — decode skipped</div>}
          <div className="grid grid-cols-4 gap-2">
            <input type="text" placeholder="Year" value={qf.year} onChange={e => setQf(f => ({...f, year: e.target.value}))} className="px-2 py-1.5 text-sm border rounded" />
            <input type="text" placeholder="Make" value={qf.make} onChange={e => setQf(f => ({...f, make: e.target.value}))} className="px-2 py-1.5 text-sm border rounded" />
            <input type="text" placeholder="Model" value={qf.model} onChange={e => setQf(f => ({...f, model: e.target.value}))} className="px-2 py-1.5 text-sm border rounded" />
            <input type="text" placeholder="Color *" value={qf.color} onChange={e => setQf(f => ({...f, color: e.target.value}))} className="px-2 py-1.5 text-sm border rounded" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <input type="number" placeholder="Odometer *" value={qf.mileage} onChange={e => setQf(f => ({...f, mileage: e.target.value}))} className="px-2 py-1.5 text-sm border rounded" />
            <input type="number" placeholder="Purchase $" value={qf.purchasePrice} onChange={e => setQf(f => ({...f, purchasePrice: e.target.value}))} className="px-2 py-1.5 text-sm border rounded" step="0.01" />
            <input type="number" placeholder="Est. Sale $" value={qf.estimatedSale} onChange={e => setQf(f => ({...f, estimatedSale: e.target.value}))} className="px-2 py-1.5 text-sm border rounded" step="0.01" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <select value={qf.auctionId} onChange={e => setQf(f => ({...f, auctionId: e.target.value}))} className="px-2 py-1.5 text-sm border rounded">
              <option value="">Auction Source *</option>
              {qAuctions.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <select value={qf.buyer} onChange={e => setQf(f => ({...f, buyer: e.target.value}))} className="px-2 py-1.5 text-sm border rounded">
              <option value="">Our Buyer</option>
              <option value="Chris">Chris</option>
              <option value="Alex">Alex</option>
            </select>
            <input type="date" value={qf.purchaseDate} onChange={e => setQf(f => ({...f, purchaseDate: e.target.value}))} className="px-2 py-1.5 text-sm border rounded" />
          </div>
          <textarea placeholder="Notes (optional)" value={qf.notes} onChange={e => setQf(f => ({...f, notes: e.target.value}))} className="w-full px-2 py-1.5 text-sm border rounded" rows={2} />
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-100 rounded-lg">Cancel</button>
            <button type="submit" disabled={qSaving || !qVinReady} className="px-6 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 font-medium">
              {qSaving ? 'Adding...' : 'Add Vehicle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
import { formatDate } from './utils/dates';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from './context/AuthContext';
import { useTheme } from './context/ThemeContext';
import { useLanguage } from './context/LanguageContext';
import api from './services/api';

// ============================================================================
// VERSION TRACKING - Update with every deploy
// ============================================================================
// v2 - deploy confirmation banner
// Prefer setting these at build time (Netlify/Railway):
// - REACT_APP_APP_VERSION
// - REACT_APP_BUILD_TIMESTAMP
const APP_VERSION = process.env.REACT_APP_APP_VERSION || "v2";
const APP_BUILD_DATE = process.env.REACT_APP_BUILD_TIMESTAMP || "Feb 1 2026 9:47 PM";
const APP_BUILD_KEY = `${APP_VERSION}__${APP_BUILD_DATE}`;
// ============================================================================
import CategoriesManager from './components/CategoriesManager';
import BankAccountManager from './components/BankAccountManager';
import SettingsPage from './components/admin/SettingsPage';
import { ReconDashboard } from './components/ReconModule';
import ArbDashboard from './components/ArbDashboard';
import PartsRoom from './components/recon/PartsRoom';
import DetailDashboard from './components/detail/DetailDashboard';
import BuyerDashboard from './components/buyer/BuyerDashboard';
import TimeClock from './components/TimeClock';
import TitleNotesModal from './components/TitleNotesModal';
import BankAccountModal from './components/BankAccountModal';
import NotificationBell from './components/notifications/NotificationBell';
import MessagingPanel from './components/messaging/MessagingPanel';
import QuickAssign from './components/shared/QuickAssign';
import AnalyticsDashboard from './components/analytics/AnalyticsDashboard';
import FormTemplateManager from './components/FormTemplateManager';
import PrintPacketModal from './components/PrintPacketModal';
import VindexPanel from './components/VindexPanel';
import BillPaymentChecklist from './components/BillPaymentChecklist';
import BackupManager from './components/BackupManager';
import UtilitiesReport from './components/UtilitiesReport';
import { VehicleFilesButton } from './components/VehicleFilesModal';
import VehicleQRCode, { QRButton, QRCodePopup } from './components/VehicleQRCode';
import QuickEditModal from './components/QuickEditModal';
import AIAdvisor from './components/AIAdvisor';
import CustomersTab from './components/accounting/CustomersTab';
import InvoicesTab from './components/accounting/InvoicesTab';
import BuyerBalancesTab from './components/accounting/BuyerBalancesTab';
import DepositsTab from './components/accounting/DepositsTab';
import AlertSubscriptions from './components/AlertSubscriptions';
import SalesTaxReportTab from './components/SalesTaxReportTab';
import WorkflowModule from './components/WorkflowModule';
import ShopWorkflow from './components/ShopWorkflow';
import PreSaleCalculator from './components/PreSaleCalculator';
import DealDeskLite from './components/DealDeskLite';
import BhphPortfolio from './components/bhph/BhphPortfolio';
import { stashBhphPrefill } from './components/bhph/mapDealToBhphPrefill';

// VIN Display Component - Shows full VIN with copy button and last 8/6 highlighting
// Consistent format across all modules: full VIN on one line, last 8 bold, last 6 super bold
const VinDisplay = ({ vin, className = '' }) => {
  const [copied, setCopied] = useState(false);
  
  if (!vin) return <span className={`text-neutral-400 ${className}`}>-</span>;
  
  const prefix = vin.slice(0, -8);
  const middle = vin.slice(-8, -6);
  const last6 = vin.slice(-6);
  
  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(vin);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };
  
  return (
    <span className={`inline-flex items-center gap-1 font-mono text-xs ${className}`}>
      <span className="text-neutral-400">{prefix}</span>
      <span className="font-semibold text-neutral-600">{middle}</span>
      <span className="font-bold text-neutral-900 text-sm">{last6}</span>
      <button
        onClick={handleCopy}
        className="ml-1 p-0.5 text-neutral-400 hover:text-blue-600 hover:bg-blue-50 rounded"
        title={copied ? 'Copied!' : 'Copy VIN'}
      >
        {copied ? (
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
        )}
      </button>
    </span>
  );
};

// Customer Proposal Modal - quote a vehicle for a customer with Deal Desk pricing math.
// Defined at module level (like QuickAddVehicleModal) for a stable component reference.
const CustomerProposalModal = ({ vehicle, showProfit, onClose }) => {
  const [taxRates, setTaxRates] = useState({});
  const [dealerInfo, setDealerInfo] = useState({});
  const [calculation, setCalculation] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [pricing, setPricing] = useState({
    purchasePrice: parseFloat(vehicle?.estimatedSale) || '',
    tradeInValue: 0,
    county: 'Lucas',
    isOutOfState: false,
    outOfStateTaxRate: 0, // buyer's home-state rate, typed manually (e.g. Michigan 6)
    docFee: 300
  });
  const [fees, setFees] = useState([
    { id: 1, name: 'Lien Fee', amount: 0 }
  ]);

  useEffect(() => {
    Promise.all([api.getTaxRates(), api.getDealerInfo().catch(() => ({}))])
      .then(([rates, dealer]) => {
        setTaxRates(rates);
        setDealerInfo(dealer);
      })
      .catch(e => console.error('Failed to load proposal data:', e));
  }, []);

  useEffect(() => {
    if (!pricing.purchasePrice) {
      setCalculation(null);
      return;
    }
    let cancelled = false;
    setCalcLoading(true);
    const timer = setTimeout(() => {
      api.calculateSale({ vehicleId: vehicle.id, saleType: 'retail', ...pricing })
        .then(result => { if (!cancelled) setCalculation(result); })
        .catch(e => console.error('Proposal calculation error:', e))
        .finally(() => { if (!cancelled) setCalcLoading(false); });
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [vehicle.id, pricing]);

  const customFeesTotal = fees.reduce((sum, f) => sum + (parseFloat(f.amount) || 0), 0);
  const grandTotal = calculation ? calculation.sale.totalDue + customFeesTotal : 0;
  const money = (n) => `$${(parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handlePrint = () => {
    if (!calculation) return;
    const s = calculation.sale;
    const feeRows = fees.filter(f => f.name && parseFloat(f.amount) > 0)
      .map(f => `<tr><td>${f.name}</td><td class="r">${money(f.amount)}</td></tr>`).join('');
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>Customer Proposal - ${vehicle.stockNumber || ''}</title>
      <style>
        body{font-family:-apple-system,'Segoe UI',sans-serif;padding:32px;max-width:640px;margin:0 auto;color:#1c1917}
        h1{font-size:20px;margin:0} h2{font-size:15px;margin:24px 0 8px;border-bottom:2px solid #1c1917;padding-bottom:4px}
        .dealer{color:#57534e;font-size:13px;margin-top:4px}
        table{width:100%;border-collapse:collapse;font-size:14px}
        td{padding:6px 4px;border-bottom:1px solid #e7e5e4} .r{text-align:right;font-family:ui-monospace,monospace}
        .total td{font-weight:bold;font-size:17px;border-top:2px solid #1c1917;border-bottom:none}
        .meta{font-size:13px;color:#57534e;margin-top:8px}
        .disclaimer{font-size:11px;color:#a8a29e;margin-top:28px}
      </style></head><body>
      <h1>${dealerInfo.name || 'Vehicle Proposal'}</h1>
      <div class="dealer">${[dealerInfo.address, dealerInfo.city && `${dealerInfo.city}, ${dealerInfo.state || ''} ${dealerInfo.zip || ''}`, dealerInfo.phone].filter(Boolean).join(' · ')}</div>
      <div class="meta">Date: ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}${customerName ? ` &nbsp;·&nbsp; Prepared for: ${customerName}` : ''}</div>
      <h2>Vehicle</h2>
      <table>
        <tr><td>Vehicle</td><td class="r">${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''} ${vehicle.trim || ''}</td></tr>
        <tr><td>VIN</td><td class="r">${vehicle.vin || '-'}</td></tr>
        <tr><td>Odometer</td><td class="r">${vehicle.mileage ? Number(vehicle.mileage).toLocaleString() + ' mi' : '-'}</td></tr>
        <tr><td>Color</td><td class="r">${vehicle.color || '-'}</td></tr>
        <tr><td>Stock #</td><td class="r">${vehicle.stockNumber || '-'}</td></tr>
      </table>
      <h2>Pricing</h2>
      <table>
        <tr><td>Sale Price</td><td class="r">${money(s.purchasePrice)}</td></tr>
        ${s.tradeInValue > 0 ? `<tr><td>Trade-In</td><td class="r">-${money(s.tradeInValue)}</td></tr>` : ''}
        <tr><td>Sales Tax (${s.taxRate}% — ${s.taxRate > 0 ? s.county + ' County' : 'Out of State'})</td><td class="r">${money(s.salesTax)}</td></tr>
        <tr><td>Documentation Fee</td><td class="r">${money(s.docFee)}</td></tr>
        <tr><td>Title Fee</td><td class="r">${money(s.titleFee)}</td></tr>
        <tr><td>Temp Tag</td><td class="r">${money(s.tempTagFee)}</td></tr>
        ${feeRows}
        <tr class="total"><td>Total</td><td class="r">${money(grandTotal)}</td></tr>
      </table>
      <div class="disclaimer">This proposal is an estimate for discussion purposes and is not a binding contract. Pricing, taxes, and fees are subject to change until a purchase agreement is signed.</div>
      </body></html>
    `);
    win.document.close();
    win.print();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full p-6 my-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-xl font-bold">Customer Proposal</h3>
          <div className="flex gap-2">
            <button
              onClick={handlePrint}
              disabled={!calculation}
              className="px-4 py-2 bg-neutral-800 text-white rounded-lg text-sm font-medium hover:bg-neutral-700 disabled:opacity-50"
            >
              Print Proposal
            </button>
            <button onClick={onClose} className="px-3 py-2 text-neutral-500 hover:bg-neutral-100 rounded-lg text-sm">Close</button>
          </div>
        </div>

        {/* Vehicle info */}
        <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 mb-4 mt-3">
          <div className="font-semibold mb-2">{vehicle.year} {vehicle.make} {vehicle.model} {vehicle.trim || ''}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-sm">
            <div><span className="text-neutral-400">Stock #:</span> <span className="font-mono">{vehicle.stockNumber || '-'}</span></div>
            <div><span className="text-neutral-400">Odometer:</span> {vehicle.mileage ? `${Number(vehicle.mileage).toLocaleString()} mi` : '-'}</div>
            <div><span className="text-neutral-400">Color:</span> {vehicle.color || '-'}</div>
            <div className="col-span-2 md:col-span-1"><span className="text-neutral-400">VIN:</span> <span className="font-mono text-xs">{vehicle.vin || '-'}</span></div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Inputs */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Customer Name (optional, shown on printout)</label>
              <input type="text" value={customerName} onChange={e => setCustomerName(e.target.value)} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Sale Price</label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-neutral-500">$</span>
                <input type="number" value={pricing.purchasePrice} onChange={e => setPricing(p => ({ ...p, purchasePrice: parseFloat(e.target.value) || 0 }))} className="w-full pl-7 pr-3 py-2 border border-neutral-200 rounded-lg" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Trade-In Value</label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-neutral-500">$</span>
                <input type="number" value={pricing.tradeInValue} onChange={e => setPricing(p => ({ ...p, tradeInValue: parseFloat(e.target.value) || 0 }))} className="w-full pl-7 pr-3 py-2 border border-neutral-200 rounded-lg" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Doc Fee</label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-neutral-500">$</span>
                <input type="number" value={pricing.docFee} onChange={e => setPricing(p => ({ ...p, docFee: parseFloat(e.target.value) || 0 }))} className="w-full pl-7 pr-3 py-2 border border-neutral-200 rounded-lg" />
              </div>
            </div>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={pricing.isOutOfState} onChange={e => setPricing(p => ({ ...p, isOutOfState: e.target.checked }))} />
              <span className="text-sm">Out of State Buyer</span>
            </label>
            {pricing.isOutOfState ? (
              <div>
                <label className="block text-sm font-medium mb-1">Tax Rate % <span className="text-neutral-400 font-normal">(Michigan = 6, tax-exempt = 0)</span></label>
                <input
                  type="number" step="0.25" min="0" max="15"
                  value={pricing.outOfStateTaxRate}
                  onChange={e => setPricing(p => ({ ...p, outOfStateTaxRate: e.target.value === '' ? '' : parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg font-mono"
                  placeholder="0"
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium mb-1">Tax County</label>
                <select value={pricing.county} onChange={e => setPricing(p => ({ ...p, county: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg">
                  {taxRates.counties?.map(c => (
                    <option key={c} value={c}>{c} ({taxRates.rates?.[c]}%)</option>
                  ))}
                </select>
              </div>
            )}
            {/* Additional fees */}
            <div className="border-t border-neutral-200 pt-3">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium">Additional Fees</label>
                <button type="button" onClick={() => setFees(f => [...f, { id: Date.now(), name: '', amount: 0 }])} className="text-xs text-blue-600 hover:text-blue-800">+ Add Fee</button>
              </div>
              <div className="space-y-2">
                {fees.map(fee => (
                  <div key={fee.id} className="flex gap-2 items-center">
                    <input type="text" value={fee.name} onChange={e => setFees(f => f.map(x => x.id === fee.id ? { ...x, name: e.target.value } : x))} placeholder="Fee name" className="flex-1 px-2 py-1 text-sm border border-neutral-200 rounded" />
                    <div className="relative w-24">
                      <span className="absolute left-2 top-1 text-neutral-500 text-sm">$</span>
                      <input type="number" value={fee.amount} onChange={e => setFees(f => f.map(x => x.id === fee.id ? { ...x, amount: parseFloat(e.target.value) || 0 } : x))} className="w-full pl-5 pr-2 py-1 text-sm border border-neutral-200 rounded" />
                    </div>
                    <button type="button" onClick={() => setFees(f => f.filter(x => x.id !== fee.id))} className="text-red-500 hover:text-red-700 text-sm px-1">×</button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-neutral-50 rounded-lg p-4">
            <h4 className="font-medium text-lg mb-4">Proposal Summary</h4>
            {calcLoading ? (
              <div className="text-center py-8 text-neutral-500">Calculating...</div>
            ) : calculation ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span>Sale Price:</span><span className="font-mono">{money(calculation.sale.purchasePrice)}</span></div>
                {calculation.sale.tradeInValue > 0 && <div className="flex justify-between text-green-600"><span>Trade-In:</span><span className="font-mono">-{money(calculation.sale.tradeInValue)}</span></div>}
                <div className="flex justify-between"><span>Sales Tax ({calculation.sale.taxRate}%):</span><span className="font-mono">{money(calculation.sale.salesTax)}</span></div>
                <div className="flex justify-between"><span>Doc Fee:</span><span className="font-mono">{money(calculation.sale.docFee)}</span></div>
                <div className="flex justify-between"><span>Title Fee:</span><span className="font-mono">{money(calculation.sale.titleFee)}</span></div>
                <div className="flex justify-between"><span>Temp Tag:</span><span className="font-mono">{money(calculation.sale.tempTagFee)}</span></div>
                {fees.filter(f => f.name && parseFloat(f.amount) > 0).map(fee => (
                  <div key={fee.id} className="flex justify-between text-neutral-600"><span>{fee.name}:</span><span className="font-mono">{money(fee.amount)}</span></div>
                ))}
                <div className="border-t border-neutral-300 my-2"></div>
                <div className="flex justify-between font-bold text-lg">
                  <span>Total:</span>
                  <span className="font-mono">{money(grandTotal)}</span>
                </div>
                {showProfit && (
                  <>
                    <div className="border-t border-neutral-300 my-2"></div>
                    <div className="flex justify-between text-neutral-500"><span>Vehicle Cost (internal):</span><span className="font-mono">{money(calculation.costs.totalVehicleCost)}</span></div>
                    <div className={`flex justify-between font-medium ${calculation.profit.grossProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      <span>Gross Profit (internal):</span><span className="font-mono">{money(calculation.profit.grossProfit)}</span>
                    </div>
                    <div className="text-xs text-neutral-400">Internal lines are not included on the printed proposal.</div>
                  </>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-neutral-400">Enter a sale price to calculate</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const LotSenseApp = () => {
  const { currentUser, logout, isAdmin, canAccess, hasPermission, canSeeFinancials, getDefaultModule, getUserModules } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const { language, setLanguage, t, isRTL } = useLanguage();
  
  // Read initial module from URL hash, fallback to 'dashboard'
  const getModuleFromHash = () => {
    const hash = window.location.hash.replace('#', '');
    return hash || 'home';
  };
  
  const [currentModule, setCurrentModuleState] = useState(getModuleFromHash);
  
  // Wrapper to sync module changes with URL hash
  const setCurrentModule = (module) => {
    setCurrentModuleState(module);
    window.location.hash = module;
  };
  
  // Listen for browser back/forward navigation
  useEffect(() => {
    const handleHashChange = () => {
      const newModule = getModuleFromHash();
      setCurrentModuleState(newModule);
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);
  const [vehicles, setVehicles] = useState([]);
  const [soldTitleVehicles, setSoldTitleVehicles] = useState([]);
  const [expenses, setExpenses] = useState([]);

  // Sanitize expenses coming from the API so they never contain raw objects that React might try to render directly
  const sanitizeExpense = (exp) => {
    if (!exp) return exp;
    const safe = { ...exp };
    // Normalize date to plain string if a Date instance sneaks in
    if (safe.date instanceof Date) {
      safe.date = safe.date.toISOString();
    }
    // Guard against accidental object values on common string fields
    ['description', 'category', 'memo', 'payment_method', 'check_number'].forEach((key) => {
      if (safe[key] && typeof safe[key] === 'object') {
        safe[key] = JSON.stringify(safe[key]);
      }
    });
    return safe;
  };
  const [vendors, setVendors] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [myRepairOrders, setMyRepairOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [bankAccounts, setBankAccounts] = useState([]); // Add bankAccounts state
  const [accountingTab, setAccountingTab] = useState(null); // For navigating to specific accounting tab
  // Home screen tile order (secondary tiles) — persisted in localStorage
  const DEFAULT_SECONDARY_ORDER = ['titles', 'transport', 'recon', 'admintasks'];
  const [secondaryTileOrder, setSecondaryTileOrder] = useState(() => {
    try {
      const saved = localStorage.getItem('lotsense_tile_order_secondary');
      return saved ? JSON.parse(saved) : DEFAULT_SECONDARY_ORDER;
    } catch { return DEFAULT_SECONDARY_ORDER; }
  });
  const [adminExpanded, setAdminExpanded] = useState(false);
  const [homeDragOver, setHomeDragOver] = useState(null);
  const [dealDeskNewSaleTrigger, setDealDeskNewSaleTrigger] = useState(0);
  const [accountingExpenseTrigger, setAccountingExpenseTrigger] = useState(0);
  
  // Modal states
  const [titleNotesModal, setTitleNotesModal] = useState({ isOpen: false, vehicle: null });
  const [bankAccountModal, setBankAccountModal] = useState({ isOpen: false, account: null });
  const [messagingOpen, setMessagingOpen] = useState(false);
  const [quickAssignOpen, setQuickAssignOpen] = useState(false);
  const [passwordModal, setPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [quickEditModal, setQuickEditModal] = useState({ isOpen: false, vehicle: null });
  const [qrModalVehicle, setQrModalVehicle] = useState(null);
  const [passwordError, setPasswordError] = useState('');
  // Show deploy banner whenever build timestamp changes (on every deploy)
  // - Always check on mount if build timestamp is different from last seen
  // - Login also sets a sessionStorage flag (see AuthContext)
  // - Banner shows regardless of what module user is in
  const [showVersionPopup, setShowVersionPopup] = useState(() => {
    try {
      const loginFlag = sessionStorage.getItem('lotsense_show_version_banner') === '1';
      const lastSeenKey = localStorage.getItem('lotsense_last_seen_build_key');
      // Show banner if login flag is set OR if build key has changed
      return loginFlag || lastSeenKey !== APP_BUILD_KEY;
    } catch {
      return true; // safest: show banner
    }
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Bank account modal state (debug removed)

  const dismissVersionBanner = useCallback(() => {
    setShowVersionPopup(false);
    try {
      localStorage.setItem('lotsense_last_seen_build_key', APP_BUILD_KEY);
      sessionStorage.removeItem('lotsense_show_version_banner');
    } catch {
      // ignore
    }
  }, []);

  // Check on every mount if build timestamp has changed (for new deploys)
  useEffect(() => {
    try {
      const lastSeenKey = localStorage.getItem('lotsense_last_seen_build_key');
      if (lastSeenKey !== APP_BUILD_KEY) {
        setShowVersionPopup(true);
      }
    } catch {
      // ignore
    }
  }, []); // Run once on mount

  // Auto-dismiss version banner after 5 seconds (or click to dismiss)
  useEffect(() => {
    if (!showVersionPopup) return;

    try {
      sessionStorage.removeItem('lotsense_show_version_banner');
    } catch {
      // ignore
    }

    const timer = setTimeout(() => {
      dismissVersionBanner();
    }, 5000);
    return () => clearTimeout(timer);
  }, [showVersionPopup, dismissVersionBanner]);

  // Global function to open QR modal from VehicleForm
  useEffect(() => {
    window.openQRModal = (vehicle) => setQrModalVehicle(vehicle);
    return () => { delete window.openQRModal; };
  }, []);

  // Clock-in prompt at login for shop users (skippable, once per day)
  const [showClockInPrompt, setShowClockInPrompt] = useState(false);
  const [clockInPromptBusy, setClockInPromptBusy] = useState(false);

  useEffect(() => {
    if (currentUser?.role !== 'shop') return;
    const todayKey = new Date().toDateString();
    try {
      if (localStorage.getItem('lotsense_clockin_skip_date') === todayKey) return;
    } catch { /* ignore */ }
    let cancelled = false;
    api.getClockStatus()
      .then(status => {
        if (!cancelled && !status.clockedIn) setShowClockInPrompt(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentUser]);

  const handlePromptClockIn = async () => {
    setClockInPromptBusy(true);
    try {
      await api.clockIn();
      setShowClockInPrompt(false);
    } catch (err) {
      alert('Failed to clock in: ' + err.message);
    } finally {
      setClockInPromptBusy(false);
    }
  };

  const handlePromptSkip = () => {
    try {
      localStorage.setItem('lotsense_clockin_skip_date', new Date().toDateString());
    } catch { /* ignore */ }
    setShowClockInPrompt(false);
  };

  useEffect(() => {
    if (currentUser && currentModule !== 'home' && !canAccess(currentModule)) {
      setCurrentModule(getDefaultModule());
    }
  }, [currentUser, currentModule, canAccess, getDefaultModule]);

  // Core refresh logic - showLoadingState controls whether to show loading spinner
  const doRefresh = useCallback(async (showLoadingState = true) => {
    if (showLoadingState) setLoading(true);
    try {
      const promises = [
        hasPermission('vehicles', 'read') ? api.getVehicles({ includeSold: false }) : Promise.resolve([]),
        hasPermission('expenses', 'read') ? api.getExpenses() : Promise.resolve([]),
        hasPermission('vendors', 'read') ? api.getVendors() : Promise.resolve([]),
        api.getMyTasks().catch(() => []),
        hasPermission('expenses', 'read') ? api.getBankAccounts().catch(() => []) : Promise.resolve([]),
        api.getMyRepairOrders().catch(() => []),
        isAdmin() ? api.getUsers().catch(() => []) : Promise.resolve([]),
        hasPermission('vehicles', 'read') ? api.getTitlesPendingVehicles().catch(() => []) : Promise.resolve([]),
      ];
      const [v, e, vn, t, ba, ros, u, stv] = await Promise.all(promises);
      setVehicles(v);
      setSoldTitleVehicles(stv || []);
      setExpenses((e || []).map(sanitizeExpense));
      setVendors(vn);
      setTasks(t);
      setBankAccounts(ba);
      setMyRepairOrders(ros);
      setUsers(u);
      setSelectedVehicle(prev => {
        if (!prev) return prev;
        const updated = v?.find(x => x.id === prev.id);
        if (updated) return updated;
        // Vehicle may be sold and excluded from default getVehicles(); refetch by ID
        api.getVehicle(prev.id).then(fresh => setSelectedVehicle(fresh)).catch(() => {});
        return prev;
      });
      setRefreshTrigger(t => t + 1);
    } catch (e) {
      console.error('Fetch failed:', e);
    } finally {
      if (showLoadingState) setLoading(false);
    }
  }, [hasPermission]);

  // Full refresh with loading indicator (initial load, manual refresh button)
  const refreshData = useCallback(() => doRefresh(true), [doRefresh]);
  
  // Silent refresh without loading indicator (inline edits) - preserves scroll
  const silentRefresh = useCallback(() => doRefresh(false), [doRefresh]);

  const handleCreateROFromInventory = useCallback(async (vehicleId) => {
    try {
      const existing = await api.getVehicleRepairOrders(vehicleId);
      const openRO = (existing || []).find(ro => ro.status !== 'complete' && ro.status !== 'closed');
      const roId = openRO?.id;
      if (roId) {
        setCurrentModule('recon');
        localStorage.setItem('pendingROId', roId.toString());
        return;
      }
      const newRO = await api.createRepairOrder({ vehicleId });
      refreshData();
      setCurrentModule('recon');
      localStorage.setItem('pendingROId', (newRO?.id || newRO).toString());
    } catch (err) {
      alert('Failed to create RO: ' + err.message);
    }
  }, [refreshData]);

  useEffect(() => { refreshData(); }, [refreshData]);

  const handleSearch = useCallback(async () => {
    if (!hasPermission('vehicles', 'read')) return;
    try {
      const data = await api.getVehicles(searchQuery ? { search: searchQuery } : {});
      setVehicles(data);
    } catch (e) {
      console.error('Search failed:', e);
    }
  }, [searchQuery, hasPermission]);

  useEffect(() => {
    const t = setTimeout(handleSearch, 300);
    return () => clearTimeout(t);
  }, [searchQuery, handleSearch]);

  // All available modules with labels (translated)
  const allModules = {
    dashboard: t('nav.dashboard'),
    admintasks: t('nav.admintasks'),
    analytics: t('nav.analytics'),
    accounting: t('nav.accounting'),
    dealdesk: t('nav.dealdesk'),
    reports: t('nav.reports'),
    titles: t('nav.titles'),
    inventory: t('nav.inventory'),
    inventory_limited: t('nav.inventory'),
    recon: t('nav.recon'),
    arb: t('nav.arb'),
    partsroom: t('nav.partsroom'),
    detail: t('nav.detail'),
    transport: t('nav.transport'),
    buyerdash: t('nav.buyerdash'),
    timeclock: t('nav.timeclock'),
    alerts: t('nav.alerts'),
    admin: t('nav.admin'),
    workflow: t('nav.workflow'),
    reconworkflow: t('nav.reconworkflow'),
    dealdecklite: t('nav.dealdecklite'),
    bhph: t('nav.bhph'),
  };

  // Get nav items in the order defined by user's modules
  const userModules = getUserModules();
  const navItems = userModules
    .filter(moduleId => allModules[moduleId])
    .map(moduleId => ({ id: moduleId, label: allModules[moduleId] }));

  // Exclude sold, copart, wholesale (disposed) - salvage vehicles sent to auction should not show in inventory
  const disposedStatuses = ['sold', 'copart', 'wholesale', 'wholesale_out'];
  const activeVehicles = vehicles.filter(v => 
    !disposedStatuses.includes((v.status || '').toLowerCase()) && 
    v.vehicleType !== 'shop' && 
    v.vehicleType !== 'customer'
  );
  
  // titlesVehicles: Merges active vehicles with sold+pending-title vehicles.
  // A sold vehicle disappears from Titles ONLY when the Deal Desk "Title" checkbox is checked
  // (which sets titleStatus='complete'). soldTitleVehicles is fetched separately because the
  // main vehicles state excludes sold vehicles (includeSold: false).
  const titlesVehicleIds = new Set(vehicles.map(v => v.id));
  const titlesVehicles = [
    ...vehicles,
    ...soldTitleVehicles.filter(v => !titlesVehicleIds.has(v.id)),
  ].filter(v => {
    if (v.vehicleType === 'shop' || v.vehicleType === 'customer') return false;
    if (v.titleStatus === 'complete') return false;
    return true;
  });
  
  const showFinancials = canSeeFinancials();

  return (
    <div className="h-screen flex bg-neutral-50 text-neutral-800" dir={isRTL ? 'rtl' : 'ltr'} style={{ fontFamily: 'system-ui, sans-serif', fontSize: '14px' }}>
      {/* Clock-in prompt for shop users (skippable, once per day) */}
      {showClockInPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[110] p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 text-center">
            <h3 className="text-xl font-bold mb-1">
              Good {new Date().getHours() < 12 ? 'morning' : 'afternoon'}, {currentUser?.name?.split(' ')[0]}!
            </h3>
            <p className="text-sm text-neutral-500 mb-5">You're not clocked in yet. Want to clock in now?</p>
            <div className="flex justify-center gap-3">
              <button
                onClick={handlePromptSkip}
                className="px-4 py-2 text-neutral-500 hover:bg-neutral-100 rounded-lg text-sm"
              >
                Skip for today
              </button>
              <button
                onClick={handlePromptClockIn}
                disabled={clockInPromptBusy}
                className="px-6 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 disabled:opacity-50"
              >
                {clockInPromptBusy ? 'Clocking In...' : 'Clock In Now'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Version banner - auto-dismisses after 5 seconds */}
      {showVersionPopup && (
        <div 
          className="fixed top-0 left-0 right-0 bg-neutral-900 text-white py-2 px-4 z-[100] flex items-center justify-between shadow-lg cursor-pointer"
          onClick={dismissVersionBanner}
        >
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium">Version: {APP_VERSION}</span>
            <span className="text-neutral-500">|</span>
            <span className="text-sm text-neutral-200">Pushed: {APP_BUILD_DATE}</span>
          </div>
          <button 
            onClick={(e) => { e.stopPropagation(); dismissVersionBanner(); }} 
            className="text-white/70 hover:text-white text-xl px-2"
          >
            ×
          </button>
        </div>
      )}
      {/* Mobile sidebar overlay backdrop */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}
      <aside className={`${sidebarOpen ? 'w-64 md:w-44' : 'w-0 overflow-hidden'} bg-neutral-900 flex flex-col transition-all fixed md:relative z-50 h-full`}>
        <div className="p-3 border-b border-neutral-800 flex items-center justify-between">
          <span className="text-neutral-300 text-sm font-medium">LotSense</span>
          <button onClick={() => setSidebarOpen(false)} className="text-neutral-500 hover:text-neutral-300 md:hidden text-lg">✕</button>
        </div>
        <nav className="flex-1 py-2 overflow-y-auto">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => { setCurrentModule(item.id); setSidebarOpen(false); }}
              className={`w-full text-left px-4 py-2.5 md:px-3 md:py-1.5 text-sm ${currentModule === item.id ? 'text-white bg-neutral-800' : 'text-neutral-400 hover:text-neutral-200'}`}
            >
              {item.label}
            </button>
          ))}
        </nav>
        {isAdmin() && (
          <Link to="/admin" className="px-4 py-2.5 md:px-3 md:py-1.5 text-sm text-neutral-500 hover:text-neutral-300 border-t border-neutral-800">Admin</Link>
        )}
        <div className="p-3 border-t border-neutral-800">
          <div className="text-sm text-neutral-400">{currentUser?.name}</div>
          <div className="flex gap-2 mt-1">
            <button onClick={() => setPasswordModal(true)} className="text-xs text-neutral-500 hover:text-neutral-300">{t('actions.changePassword')}</button>
            <span className="text-neutral-700">|</span>
            <button onClick={logout} className="text-xs text-neutral-500 hover:text-neutral-300">{t('actions.signOut')}</button>
          </div>
          <div className="flex gap-1 mt-2">
            <button onClick={() => setLanguage('en')} className={`text-xs px-2 py-0.5 rounded ${language === 'en' ? 'bg-teal-600 text-white' : 'text-neutral-500 hover:text-neutral-300'}`}>EN</button>
            <button onClick={() => setLanguage('ar')} className={`text-xs px-2 py-0.5 rounded ${language === 'ar' ? 'bg-teal-600 text-white' : 'text-neutral-500 hover:text-neutral-300'}`}>عربي</button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-12 bg-white border-b border-neutral-200 flex items-center px-2 md:px-4 gap-2 md:gap-4">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 text-neutral-400 hover:text-neutral-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          <button onClick={() => setCurrentModule('home')} className="p-1.5 text-neutral-400 hover:text-teal-600 hover:bg-neutral-100 rounded transition" title="Home - Module Grid">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1h-2z" /></svg>
          </button>
          <span className="text-neutral-600 font-medium text-sm md:text-base truncate">{currentModule === 'home' ? t('common.home') : (navItems.find(n => n.id === currentModule)?.label || t('nav.dashboard'))}</span>
          
          {/* Refresh Button */}
          <button
            onClick={refreshData}
            className="p-1.5 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded transition"
            title="Refresh data"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          
          {hasPermission('vehicles', 'read') && currentModule === 'inventory' && (
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-32 md:w-56 h-7 px-2 text-sm bg-neutral-100 border border-neutral-200 rounded"
            />
          )}
          
          {/* Spacer */}
          <div className="flex-1" />
          
          {/* Quick Assign Button - hidden on mobile */}
          <button
            onClick={() => setQuickAssignOpen(true)}
            className="hidden md:block p-2 text-neutral-600 hover:bg-neutral-100 rounded-lg transition relative"
            title="Assign Task"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </button>
          
          {/* Messaging Button - hidden on mobile */}
          <button
            onClick={() => setMessagingOpen(true)}
            className="hidden md:block p-2 text-neutral-600 hover:bg-neutral-100 rounded-lg transition relative"
            title="Messages"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </button>
          
          {/* Dark/Light Mode Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition"
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? (
              <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>
          
          {/* Notification Bell */}
          <NotificationBell />
        </header>

        <main className="flex-1 overflow-auto p-2 md:p-4">
          {loading ? (
            <div className="text-neutral-400 text-sm">Loading...</div>
          ) : (
            <>
              {currentModule === 'home' && (() => {
                const moduleIcons = {
                  dashboard:        { icon: 'DB',  color: 'from-blue-500 to-blue-600',    desc: 'Overview & stats' },
                  admintasks:       { icon: '✓',   color: 'from-purple-500 to-purple-600', desc: 'Weekly task list' },
                  analytics:        { icon: 'AN',  color: 'from-indigo-500 to-indigo-600', desc: 'Data & insights' },
                  accounting:       { icon: '$',   color: 'from-emerald-500 to-emerald-600', desc: 'Books & payments' },
                  dealdesk:         { icon: 'DD',  color: 'from-teal-500 to-teal-600',    desc: 'Full deal management' },
                  dealdecklite:     { icon: 'DL',  color: 'from-teal-400 to-teal-500',    desc: 'Quick sale entry' },
                  reports:          { icon: 'RP',  color: 'from-slate-500 to-slate-600',   desc: 'Generate reports' },
                  titles:           { icon: 'TT',  color: 'from-amber-500 to-amber-600',   desc: 'Title tracking' },
                  inventory:        { icon: 'INV', color: 'from-sky-500 to-sky-600',      desc: 'Vehicle inventory' },
                  inventory_limited:{ icon: 'INV', color: 'from-sky-400 to-sky-500',      desc: 'Vehicle inventory' },
                  recon:            { icon: 'RC',  color: 'from-orange-500 to-orange-600', desc: 'Recon dashboard' },
                  workorders:       { icon: 'WO',  color: 'from-red-500 to-red-600',      desc: 'Repair orders' },
                  arb:              { icon: 'ARB', color: 'from-rose-500 to-rose-600',     desc: 'Arbitration cases' },
                  partsroom:        { icon: 'PR',  color: 'from-zinc-500 to-zinc-600',     desc: 'Parts management' },
                  detail:           { icon: 'DT',  color: 'from-pink-500 to-pink-600',    desc: 'Detail queue' },
                  transport:        { icon: 'TR',  color: 'from-cyan-500 to-cyan-600',    desc: 'Transport & pickups' },
                  buyerdash:        { icon: 'BD',  color: 'from-violet-500 to-violet-600', desc: 'Buyer tools' },
                  timeclock:        { icon: 'TC',  color: 'from-yellow-500 to-yellow-600', desc: 'Clock in/out' },
                  alerts:           { icon: 'AL',  color: 'from-red-400 to-red-500',      desc: 'Alert settings' },
                  admin:            { icon: 'AD',  color: 'from-gray-500 to-gray-600',    desc: 'System settings' },
                  workflow:         { icon: 'WF',  color: 'from-blue-600 to-blue-700',    desc: 'Vehicle workflow' },
                  reconworkflow:    { icon: 'SW',  color: 'from-orange-400 to-orange-500', desc: 'Shop workflow' },
                  bhph:             { icon: 'BH',  color: 'from-stone-600 to-stone-700',  desc: 'BHPH contracts & portfolio' },
                };

                const userModuleIds = new Set(navItems.map(n => n.id));
                const navLabelMap = Object.fromEntries(navItems.map(n => [n.id, n.label]));
                const tileFor = (id) => ({
                  id,
                  label: navLabelMap[id] || id.charAt(0).toUpperCase() + id.slice(1),
                  ...(moduleIcons[id] || { icon: '?', color: 'from-neutral-400 to-neutral-500', desc: '' }),
                });
                const goTo = (id) => setCurrentModule(id);

                // PRIMARY — always show if user has access
                const primaryIds = ['inventory', 'accounting', 'dealdesk', 'reconworkflow'];
                const primaryTiles = primaryIds.filter(id => userModuleIds.has(id) || id === 'inventory').map(tileFor);

                // SECONDARY — user-reorderable, filtered to what they have access to
                const secondaryIds = secondaryTileOrder.filter(id => userModuleIds.has(id));
                // Also catch any user modules not in the predefined lists
                const knownIds = new Set([...primaryIds, ...DEFAULT_SECONDARY_ORDER, 'analytics', 'reports']);
                const extraIds = [...userModuleIds].filter(id => !knownIds.has(id));
                const secondaryTiles = [...secondaryIds, ...extraIds].map(tileFor);

                // ADMIN group
                const adminIds = ['analytics', 'reports'].filter(id => userModuleIds.has(id));
                const adminTiles = adminIds.map(tileFor);

                // Drag handlers for secondary tiles
                let dragSrcId = null;
                const onDragStart = (id) => { dragSrcId = id; };
                const onDragOver = (e, id) => { e.preventDefault(); setHomeDragOver(id); };
                const onDrop = (e, targetId) => {
                  e.preventDefault();
                  setHomeDragOver(null);
                  if (!dragSrcId || dragSrcId === targetId) return;
                  setSecondaryTileOrder(prev => {
                    const full = prev.filter(id => userModuleIds.has(id));
                    const srcIdx = full.indexOf(dragSrcId);
                    const tgtIdx = full.indexOf(targetId);
                    if (srcIdx === -1 || tgtIdx === -1) return prev;
                    const next = [...full];
                    next.splice(srcIdx, 1);
                    next.splice(tgtIdx, 0, dragSrcId);
                    localStorage.setItem('lotsense_tile_order_secondary', JSON.stringify(next));
                    return next;
                  });
                };

                return (
                  <div className="max-w-5xl mx-auto">
                    {/* Welcome */}
                    <div className="mb-5">
                      <h1 className="text-xl font-bold text-neutral-800">LotSense</h1>
                      <p className="text-sm text-neutral-500">Welcome back, {currentUser?.name?.split(' ')[0] || 'User'}</p>
                    </div>

                    <div className="flex gap-5 items-start">
                      {/* ── Left Quick-Actions Sidebar ── */}
                      <div className="w-40 flex-shrink-0">
                        <p className="text-[10px] font-semibold text-neutral-400 uppercase tracking-widest mb-3">Quick Actions</p>
                        <div className="space-y-2">
                          {userModuleIds.has('dealdesk') && (
                            <button
                              onClick={() => { goTo('dealdesk'); setDealDeskNewSaleTrigger(t => t + 1); }}
                              className="w-full flex items-center gap-2 px-3 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-xs font-semibold shadow-sm transition-colors"
                            >
                              Enter Sale
                            </button>
                          )}
                          {userModuleIds.has('accounting') && (
                            <button
                              onClick={() => { goTo('accounting'); setAccountingTab('expenses'); setAccountingExpenseTrigger(t => t + 1); }}
                              className="w-full flex items-center gap-2 px-3 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold shadow-sm transition-colors"
                            >
                              Enter Expense
                            </button>
                          )}
                          {(userModuleIds.has('inventory') || userModuleIds.has('inventory_limited')) && hasPermission('vehicles', 'write') && (
                            <button
                              onClick={() => { goTo('inventory'); setEditingVehicle({}); }}
                              className="w-full flex items-center gap-2 px-3 py-2.5 bg-sky-600 hover:bg-sky-700 text-white rounded-xl text-xs font-semibold shadow-sm transition-colors"
                            >
                              Add Vehicle
                            </button>
                          )}
                          {userModuleIds.has('reports') && (
                            <button
                              onClick={() => goTo('reports')}
                              className="w-full flex items-center gap-2 px-3 py-2.5 bg-slate-600 hover:bg-slate-700 text-white rounded-xl text-xs font-semibold shadow-sm transition-colors"
                            >
                              View Reports
                            </button>
                          )}
                        </div>
                      </div>

                      {/* ── Main Tile Grid ── */}
                      <div className="flex-1 space-y-5">

                        {/* PRIMARY — large tiles */}
                        {primaryTiles.length > 0 && (
                          <div>
                            <p className="text-[10px] font-semibold text-neutral-400 uppercase tracking-widest mb-2">Main Modules</p>
                            <div className="grid grid-cols-2 gap-4">
                              {primaryTiles.map(tile => (
                                <button
                                  key={tile.id}
                                  onClick={() => goTo(tile.id)}
                                  className="bg-white border border-neutral-200 rounded-2xl p-5 hover:shadow-xl hover:border-neutral-300 transition-all text-center group"
                                >
                                  <div className={`w-16 h-16 mx-auto mb-3 rounded-2xl bg-gradient-to-br ${tile.color} flex items-center justify-center text-lg font-bold text-white shadow group-hover:scale-110 transition-transform`}>
                                    {tile.icon}
                                  </div>
                                  <div className="font-bold text-base text-neutral-800">{tile.label}</div>
                                  {tile.desc && <div className="text-xs text-neutral-400 mt-1">{tile.desc}</div>}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* SECONDARY — smaller draggable tiles */}
                        {secondaryTiles.length > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-[10px] font-semibold text-neutral-400 uppercase tracking-widest">More Modules <span className="normal-case font-normal text-neutral-300">— drag to reorder</span></p>
                              <button
                                onClick={() => {
                                  localStorage.removeItem('lotsense_tile_order_secondary');
                                  setSecondaryTileOrder(DEFAULT_SECONDARY_ORDER);
                                }}
                                className="text-[10px] text-neutral-300 hover:text-neutral-500 transition-colors"
                              >
                                Reset order
                              </button>
                            </div>
                            <div className="grid grid-cols-4 gap-3">
                              {secondaryTiles.map(tile => (
                                <button
                                  key={tile.id}
                                  draggable
                                  onDragStart={() => onDragStart(tile.id)}
                                  onDragOver={(e) => onDragOver(e, tile.id)}
                                  onDrop={(e) => onDrop(e, tile.id)}
                                  onDragEnd={() => setHomeDragOver(null)}
                                  onClick={() => goTo(tile.id)}
                                  className={`bg-white border rounded-xl p-3 hover:shadow-md transition-all text-center group cursor-grab active:cursor-grabbing ${
                                    homeDragOver === tile.id ? 'border-blue-400 bg-blue-50 scale-105' : 'border-neutral-200 hover:border-neutral-300'
                                  }`}
                                >
                                  <div className={`w-10 h-10 mx-auto mb-1.5 rounded-xl bg-gradient-to-br ${tile.color} flex items-center justify-center text-xs font-bold text-white shadow-sm group-hover:scale-110 transition-transform`}>
                                    {tile.icon}
                                  </div>
                                  <div className="font-semibold text-xs text-neutral-800">{tile.label}</div>
                                  {tile.desc && <div className="text-[10px] text-neutral-400 mt-0.5 leading-tight">{tile.desc}</div>}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* ADMIN — collapsible group */}
                        {adminTiles.length > 0 && (
                          <div>
                            <button
                              onClick={() => setAdminExpanded(x => !x)}
                              className="flex items-center gap-1.5 text-[10px] font-semibold text-neutral-400 uppercase tracking-widest mb-2 hover:text-neutral-600 transition-colors w-full text-left"
                            >
                              <span className={`transition-transform ${adminExpanded ? 'rotate-90' : ''}`}>▶</span>
                              Admin
                            </button>
                            {adminExpanded && (
                              <div className="grid grid-cols-4 gap-3">
                                {adminTiles.map(tile => (
                                  <button
                                    key={tile.id}
                                    onClick={() => goTo(tile.id)}
                                    className="bg-white border border-neutral-200 rounded-xl p-3 hover:shadow-md hover:border-neutral-300 transition-all text-center group"
                                  >
                                    <div className={`w-10 h-10 mx-auto mb-1.5 rounded-xl bg-gradient-to-br ${tile.color} flex items-center justify-center text-xs font-bold text-white shadow-sm group-hover:scale-110 transition-transform`}>
                                      {tile.icon}
                                    </div>
                                    <div className="font-semibold text-xs text-neutral-800">{tile.label}</div>
                                    {tile.desc && <div className="text-[10px] text-neutral-400 mt-0.5 leading-tight">{tile.desc}</div>}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                      </div>
                    </div>
                  </div>
                );
              })()}
              {currentModule === 'dashboard' && <Dashboard vehicles={activeVehicles} tasks={tasks} myRepairOrders={myRepairOrders} currentUser={currentUser} showFinancials={showFinancials} onSelect={setSelectedVehicle} onRefresh={refreshData} />}
              {currentModule === 'admintasks' && <AdminWeeklyTasks currentUser={currentUser} vehicles={vehicles} onNavigate={(module, tab) => {
                setCurrentModule(module);
                if (tab) setAccountingTab(tab);
              }} />}
              {currentModule === 'analytics' && isAdmin() && <AnalyticsDashboard />}
              {currentModule === 'inventory' && <Inventory vehicles={activeVehicles} refreshTrigger={refreshTrigger} showFinancials={showFinancials} onSelect={setSelectedVehicle} onEdit={setEditingVehicle} onRefresh={refreshData} onSilentRefresh={silentRefresh} canEdit={hasPermission('vehicles', 'write')} onNavigateToRecon={(vehicleId) => { setCurrentModule('recon'); /* Store vehicle ID for recon to pick up */ localStorage.setItem('pendingReconVehicle', vehicleId); }} onStartInspection={(vehicleId) => { setCurrentModule('recon'); localStorage.setItem('pendingInspectionVehicle', vehicleId.toString()); }} onCreateRO={handleCreateROFromInventory} onQuickEdit={(vehicle) => setQuickEditModal({ isOpen: true, vehicle })} />}
              {currentModule === 'inventory_limited' && <Inventory vehicles={activeVehicles} showFinancials={false} onSelect={setSelectedVehicle} onEdit={null} onRefresh={refreshData} canEdit={false} onNavigateToRecon={(vehicleId) => { setCurrentModule('recon'); localStorage.setItem('pendingReconVehicle', vehicleId); }} onStartInspection={(vehicleId) => { setCurrentModule('recon'); localStorage.setItem('pendingInspectionVehicle', vehicleId.toString()); }} onCreateRO={handleCreateROFromInventory} limitedView={true} />}
              {currentModule === 'transport' && <Transport onRefresh={refreshData} vendors={vendors} onOpenTransportNotes={(vehicle) => setTitleNotesModal({ isOpen: true, vehicle, module: 'transport' })} onStartInspection={(vehicleId) => { setCurrentModule('recon'); localStorage.setItem('pendingInspectionVehicle', vehicleId.toString()); }} />}
              {currentModule === 'recon' && <ReconDashboard key="recon-dashboard" vehicles={activeVehicles} onRefresh={refreshData} />}
              {currentModule === 'workflow' && <WorkflowModule onRefresh={refreshData} onSilentRefresh={silentRefresh} onSelectVehicle={setSelectedVehicle} currentUser={currentUser} onNavigateToRO={(roId) => { setCurrentModule('recon'); localStorage.setItem('pendingROId', roId.toString()); }} onNavigateToPartsRoom={() => setCurrentModule('partsroom')} />}
              {currentModule === 'reconworkflow' && <ShopWorkflow onRefresh={refreshData} onSilentRefresh={silentRefresh} onSelectVehicle={setSelectedVehicle} currentUser={currentUser} onNavigateToRO={(roId) => { setCurrentModule('recon'); localStorage.setItem('pendingROId', roId.toString()); }} />}
              {currentModule === 'dealdecklite' && <DealDeskLite vehicles={activeVehicles} onRefresh={refreshData} />}
              {currentModule === 'bhph' && <BhphPortfolio />}
              {currentModule === 'arb' && <ArbDashboard onBack={() => setCurrentModule('dashboard')} />}
              {currentModule === 'partsroom' && (
                <div className="space-y-4">
                  <h1 className="text-2xl font-bold">Parts Room</h1>
                  <PartsRoom onRefresh={refreshData} />
                </div>
              )}
              {currentModule === 'detail' && <DetailDashboard currentUser={currentUser} onRefresh={refreshData} />}
              {currentModule === 'titles' && <Titles vehicles={titlesVehicles} onRefresh={refreshData} onOpenTitleNotes={(vehicle) => setTitleNotesModal({ isOpen: true, vehicle })} />}
              {currentModule === 'accounting' && <AccountingErrorBoundary onRefresh={refreshData}><AccountingModule currentUser={currentUser} expenses={expenses} vehicles={vehicles} vendors={vendors} onRefresh={refreshData} onSilentRefresh={silentRefresh} showFinancials={showFinancials} initialTab={accountingTab} refreshTrigger={refreshTrigger} onOpenBankAccount={(account) => {
                setBankAccountModal({ isOpen: true, account });
              }} autoOpenExpenseTrigger={accountingExpenseTrigger} onQuickEdit={(vehicle) => setQuickEditModal({ isOpen: true, vehicle })} /></AccountingErrorBoundary>}
              {currentModule === 'vendors' && <VendorList vendors={vendors} onRefresh={refreshData} canEdit={hasPermission('vendors', 'write')} />}
              {currentModule === 'timeclock' && <TimeClock />}
              {currentModule === 'expenses' && <ShopExpenses currentUser={currentUser} vehicles={vehicles} vendors={vendors} onRefresh={refreshData} />}
              {currentModule === 'dealdesk' && <DealDesk vehicles={vehicles} onRefresh={refreshData} refreshTrigger={refreshTrigger} openNewSaleTrigger={dealDeskNewSaleTrigger} onSendToBhph={async (deal) => {
                let payload = { ...deal };
                if (deal.vehicle_id) {
                  try {
                    const v = await api.getVehicle(deal.vehicle_id);
                    if (v) {
                      payload = {
                        ...payload,
                        purchase_price: v.purchasePrice ?? v.purchase_price,
                        recon_cost: v.reconCost ?? v.recon_cost,
                        transport_cost: v.transportCost ?? v.transport_cost,
                        total_invested_value: v.totalInvestedValue ?? v.total_invested_value,
                        total_cost: v.totalCost ?? v.total_cost,
                      };
                    }
                  } catch (_) { /* keep deal-only prefill */ }
                }
                stashBhphPrefill(payload);
                setCurrentModule('bhph');
              }} canAccessBhph={canAccess('bhph')} onEditVehicleFromDeal={async (vehicleId) => { let v = vehicles.find(x => x.id === vehicleId); if (!v) { try { v = await api.getVehicle(vehicleId); } catch(e) { alert('Could not load vehicle'); return; } } setSelectedVehicle(v); }} canEditVehicle={hasPermission('vehicles', 'write')} />}
              {currentModule === 'reports' && <ReportsModule />}
              {currentModule === 'buyerdash' && <BuyerDashboard />}
              {currentModule === 'alerts' && isAdmin() && <AlertSubscriptions users={users} />}
              {currentModule === 'admin' && <SettingsPage />}
            </>
          )}
        </main>
      </div>

      {selectedVehicle && <VehicleDetail vehicle={selectedVehicle} onClose={() => setSelectedVehicle(null)} showFinancials={showFinancials} onEdit={() => { setEditingVehicle(selectedVehicle); setSelectedVehicle(null); }} canEdit={hasPermission('vehicles', 'write')} limitedView={currentModule === 'inventory_limited'} onNavigateToRO={(roId) => { setCurrentModule('recon'); localStorage.setItem('pendingROId', roId.toString()); }} onNavigateToInspection={(vehicleId) => { setCurrentModule('recon'); localStorage.setItem('pendingInspectionVehicle', vehicleId.toString()); }} />}
      {editingVehicle && <VehicleForm vehicle={editingVehicle} onClose={() => setEditingVehicle(null)} onSave={refreshData} isNew={editingVehicle.id === undefined} existingStockNumbers={vehicles.map(v => v.stockNumber)} />}
      
      {/* New Modals */}
      <TitleNotesModal
        vehicle={titleNotesModal.vehicle}
        isOpen={titleNotesModal.isOpen}
        module={titleNotesModal.module || 'titles'}
        onClose={() => setTitleNotesModal({ isOpen: false, vehicle: null })}
        onUpdate={(updatedVehicle) => {
          setVehicles(vehicles.map(v => v.id === updatedVehicle.id ? updatedVehicle : v));
        }}
      />
      
      {bankAccountModal.isOpen && (
        <BankAccountModal
          account={bankAccountModal.account}
          isOpen={bankAccountModal.isOpen}
          onClose={() => setBankAccountModal({ isOpen: false, account: null })}
          bankAccounts={bankAccounts} // Pass bankAccounts
          onRefresh={refreshData} // Pass refresh function
        />
      )}
      
      {/* Messaging Panel */}
      <MessagingPanel 
        isOpen={messagingOpen} 
        onClose={() => setMessagingOpen(false)} 
      />
      
      {/* Quick Assign Modal */}
      <QuickAssign
        isOpen={quickAssignOpen}
        onClose={() => setQuickAssignOpen(false)}
      />

      {/* Quick Edit Modal */}
      <QuickEditModal
        vehicle={quickEditModal.vehicle}
        isOpen={quickEditModal.isOpen}
        onClose={() => setQuickEditModal({ isOpen: false, vehicle: null })}
        onUpdate={refreshData}
      />

      {/* QR Code Modal - triggered after adding vehicle or from inventory */}
      {qrModalVehicle && (
        <VehicleQRCode vehicle={qrModalVehicle} onClose={() => setQrModalVehicle(null)} />
      )}

      {/* Password Change Modal */}
      {passwordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Change Password</h2>
              <button onClick={() => { setPasswordModal(false); setPasswordError(''); setPasswordForm({ current: '', new: '', confirm: '' }); }} className="text-neutral-400 hover:text-neutral-600">✕</button>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              setPasswordError('');
              
              if (passwordForm.new !== passwordForm.confirm) {
                setPasswordError('New passwords do not match');
                return;
              }
              if (passwordForm.new.length < 8) {
                setPasswordError('Password must be at least 8 characters');
                return;
              }
              if (!/\d/.test(passwordForm.new)) {
                setPasswordError('Password must contain at least 1 number');
                return;
              }
              
              setPasswordLoading(true);
              try {
                await api.request('/auth/password', {
                  method: 'PUT',
                  body: JSON.stringify({ currentPassword: passwordForm.current, newPassword: passwordForm.new })
                });
                alert('Password changed successfully!');
                setPasswordModal(false);
                setPasswordForm({ current: '', new: '', confirm: '' });
              } catch (err) {
                setPasswordError(err.message || 'Failed to change password');
              }
              setPasswordLoading(false);
            }} className="p-6 space-y-4">
              {passwordError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded">{passwordError}</div>
              )}
              <div>
                <label className="block text-sm text-neutral-600 mb-1">Current Password</label>
                <input
                  type="password"
                  value={passwordForm.current}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                  className="w-full h-9 px-3 text-sm border border-neutral-300 rounded"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-neutral-600 mb-1">New Password</label>
                <input
                  type="password"
                  value={passwordForm.new}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new: e.target.value })}
                  className="w-full h-9 px-3 text-sm border border-neutral-300 rounded"
                  placeholder="Min 8 chars, 1 number"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-neutral-600 mb-1">Confirm New Password</label>
                <input
                  type="password"
                  value={passwordForm.confirm}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                  className="w-full h-9 px-3 text-sm border border-neutral-300 rounded"
                  required
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setPasswordModal(false); setPasswordError(''); setPasswordForm({ current: '', new: '', confirm: '' }); }}
                  className="flex-1 h-10 border border-neutral-300 rounded-lg font-medium hover:bg-neutral-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="flex-1 h-10 bg-neutral-800 text-white rounded-lg font-medium hover:bg-neutral-700 disabled:opacity-50"
                >
                  {passwordLoading ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <AIAdvisor isAdmin={isAdmin()} />
    </div>
  );
};

// Dashboard with Notifications
const Dashboard = ({ vehicles, tasks, myRepairOrders, currentUser, showFinancials, onSelect, onRefresh }) => {
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [localTasks, setLocalTasks] = useState(tasks || []);
  const [localROs, setLocalROs] = useState(myRepairOrders || []);
  const [localVehicles, setLocalVehicles] = useState(vehicles || []);
  const [snoozeModal, setSnoozeModal] = useState({ show: false, ro: null });
  const [snoozeDays, setSnoozeDays] = useState(3);
  const [snoozeReason, setSnoozeReason] = useState('');
  const [arbAlerts, setArbAlerts] = useState([]);
  const [arbActionModal, setArbActionModal] = useState({ show: false, vehicle: null });
  const [arbFileModal, setArbFileModal] = useState({ show: false, vehicle: null });
  const [arbResolveModal, setArbResolveModal] = useState({ show: false, vehicle: null });
  
  // Sync localVehicles when vehicles prop changes
  useEffect(() => {
    setLocalVehicles(vehicles || []);
  }, [vehicles]);
  
  // Check if user should see shop tasks (based on showShopTasks field)
  const showShopTasks = currentUser?.showShopTasks === true;
  const role = currentUser?.role;
  
  // Check if daily prompts have been completed today
  const today = new Date().toDateString();
  const lastPartsCheck = localStorage.getItem('lastPartsCheck');
  const lastClockIn = localStorage.getItem('lastClockIn');
  const needsPartsCheck = showShopTasks && lastPartsCheck !== today;
  const needsClockIn = showShopTasks && lastClockIn !== today;
  
  const handlePartsCheckComplete = () => {
    localStorage.setItem('lastPartsCheck', today);
    alert(' Parts delivery check completed for today!');
    // Force re-render by updating state
    setLocalTasks([...localTasks]);
  };

  const handleClockIn = () => {
    localStorage.setItem('lastClockIn', today);
    alert(' Clocked in for today!');
    // Force re-render by updating state
    setLocalTasks([...localTasks]);
  };

  // Keep localTasks in sync when parent tasks prop changes (e.g. after full refresh)
  useEffect(() => {
    setLocalTasks(tasks || []);
  }, [tasks]);

  // Keep localROs in sync when parent myRepairOrders prop changes
  useEffect(() => {
    setLocalROs(myRepairOrders || []);
  }, [myRepairOrders]);

  // Load ARB alerts and check reminders for admin
  useEffect(() => {
    const loadArbAlerts = async () => {
      try {
        const alerts = await api.getArbAlerts();
        setArbAlerts(alerts || []);
        
        // Check for ARB reminders if admin
        if (currentUser?.role === 'admin') {
          await api.checkArbReminders();
        }
      } catch (e) {
        console.error('Failed to load ARB alerts:', e);
      }
    };
    loadArbAlerts();
  }, [currentUser?.role]);

  // Handle ARB action
  const handleArbAction = async (vehicleId, action, notes, arbData = {}) => {
    try {
      await api.updateArbAction(vehicleId, action, notes, arbData);
      setArbAlerts(prev => prev.filter(v => v.id !== vehicleId));
      setArbActionModal({ show: false, vehicle: null });
      if (onRefresh) onRefresh();
      alert(`ARB action "${action}" completed successfully`);
    } catch (error) {
      console.error('Failed to update ARB status:', error);
      alert('Failed to update ARB status: ' + (error.message || 'Unknown error'));
    }
  };

  // Handle RO mark complete
  const handleROComplete = async (ro) => {
    if (!window.confirm(`Mark RO #${ro.roNumber} for ${ro.stockNumber} as complete?`)) return;
    try {
      await api.updateRepairOrder(ro.id, { status: 'complete' });
      setLocalROs(prev => prev.filter(r => r.id !== ro.id));
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to mark RO complete:', error);
      alert('Failed to mark complete: ' + (error.message || 'Unknown error'));
    }
  };

  // Handle RO snooze
  const handleROSnooze = async () => {
    if (!snoozeModal.ro) return;
    try {
      await api.snoozeRepairOrder(snoozeModal.ro.id, snoozeDays, snoozeReason);
      alert(`RO #${snoozeModal.ro.roNumber} snoozed for ${snoozeDays} days`);
      setLocalROs(prev => prev.filter(r => r.id !== snoozeModal.ro.id));
      setSnoozeModal({ show: false, ro: null, vehicle: null, type: null });
      setSnoozeDays(3);
      setSnoozeReason('');
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to snooze RO:', error);
      alert('Failed to snooze: ' + (error.message || 'Unknown error'));
    }
  };

  // Handle PRE-CHECK complete (before delivery) - only clears pre-check flags
  const handlePrecheckComplete = async (vehicle) => {
    if (!window.confirm(`Mark ${vehicle.stockNumber} pre-check as complete? It will still need inspection after delivery.`)) return;
    
    try {
      await api.updateVehicle(vehicle.id, { 
        damageFlagged: false, 
        needsFurtherReview: false,
        precheckCompleted: true,
        reconStatus: 'pending_delivery'  // Waiting for delivery
      });
      
      // Immediately update local state - update the vehicle so it's removed from pre-check list
      // but will still show up in inspection list when delivered
      setLocalVehicles(prev => prev.map(v => 
        v.id === vehicle.id 
          ? { ...v, damageFlagged: false, needsFurtherReview: false, precheckCompleted: true, reconStatus: 'pending_delivery', inspectionComplete: false }
          : v
      ));
      
      alert(` ${vehicle.stockNumber} pre-check complete! Will appear for inspection after delivery.`);
      if (onRefresh) await onRefresh();
    } catch (error) {
      console.error('Failed to mark pre-check complete:', error);
      alert('Failed to mark complete: ' + (error.message || 'Unknown error'));
    }
  };

  // Handle POST-DELIVERY INSPECTION complete - fully completes the vehicle
  const handleInspectionComplete = async (vehicle) => {
    if (!window.confirm(`Mark ${vehicle.stockNumber} post-delivery inspection as complete?`)) return;
    
    try {
      await api.updateVehicle(vehicle.id, { 
        damageFlagged: false, 
        needsFurtherReview: false,
        inspectionComplete: true,
        reconStatus: 'complete'
      });
      
      // Immediately update local state to remove from list
      setLocalVehicles(prev => prev.filter(v => v.id !== vehicle.id));
      
      // Also complete any related tasks
      try {
        const tasks = await api.getTasks({ vehicleId: vehicle.id, status: 'pending' });
        for (const task of tasks) {
          await api.updateTask(task.id, { status: 'completed' });
        }
      } catch (taskErr) {
        console.warn('Failed to complete related tasks:', taskErr);
      }
      
      // Notify admin that recon is complete
      try {
        await api.createNotification({
          type: 'recon_complete',
          title: `Recon Complete: ${vehicle.stockNumber}`,
          message: `${vehicle.year} ${vehicle.make} ${vehicle.model} has completed recon inspection`,
          vehicleId: vehicle.id,
          targetRole: 'admin'
        });
      } catch (notifyErr) {
        console.warn('Failed to notify admin:', notifyErr);
      }
      
      alert(` ${vehicle.stockNumber} inspection complete!`);
      if (onRefresh) await onRefresh();
    } catch (error) {
      console.error('Failed to mark complete:', error);
      alert('Failed to mark complete: ' + (error.message || 'Unknown error'));
    }
  };

  // Handle flagged vehicle snooze
  const handleFlaggedSnooze = async () => {
    if (!snoozeModal.vehicle) return;
    try {
      const snoozeUntil = new Date();
      snoozeUntil.setDate(snoozeUntil.getDate() + snoozeDays);
      
      await api.updateVehicle(snoozeModal.vehicle.id, { 
        precheckSnoozedUntil: snoozeUntil.toISOString(),
        precheckSnoozeReason: snoozeReason || null
      });
      
      alert(`${snoozeModal.vehicle.stockNumber} snoozed for ${snoozeDays} days`);
      setSnoozeModal({ show: false, ro: null, vehicle: null, type: null });
      setSnoozeDays(3);
      setSnoozeReason('');
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to snooze:', error);
      alert('Failed to snooze: ' + (error.message || 'Unknown error'));
    }
  };

  useEffect(() => {
    const loadNotifications = async () => {
      try {
        const data = await api.getNotifications({ unreadOnly: 'true', limit: 10 });
        setNotifications(data);
      } catch (e) {
        console.error('Failed to load notifications:', e);
      } finally {
        setLoadingNotifications(false);
      }
    };
    loadNotifications();
  }, []);

  const handleMarkRead = async (id) => {
    try {
      await api.markNotificationRead(id);
      setNotifications(notifications.filter(n => n.id !== id));
    } catch (e) {
      console.error('Failed to mark notification read:', e);
    }
  };

  // Admin only sees: needs further review or high priority (NOT routine recon/inspection stuff)
  const needsAttention = vehicles.filter(v => v.needsFurtherReview || v.priority === 'high');

  // Green Light/Guarantee vehicles that need inspection alerts
  const greenLightVehicles = vehicles.filter(v => {
    if (!v.greenLightGuarantee) return false;
    
    const purchaseDate = new Date(v.purchaseDate);
    const daysSincePurchase = Math.floor((Date.now() - purchaseDate.getTime()) / (1000 * 60 * 60 * 24));
    
    // Alert if over 6 days and not inspected yet
    return daysSincePurchase > 6 && !v.inspectionCompletedAt;
  });

  const handleMarkComplete = async (task) => {
    const taskId = task.id;
    
    // Prompt for completion notes
    const notes = window.prompt('Add any completion notes (optional):');
    if (notes === null) return; // User cancelled
    
    try {
      await api.updateTask(taskId, { status: 'completed', completionNotes: notes || '' });
      // If task is linked to a vehicle, clear pre-check flags so Recon alerts stay in sync
      const vehicleId = task.vehicle_id || task.vehicleId;
      if (vehicleId) {
        try {
          await api.updateVehicle(vehicleId, { damageFlagged: false, needsFurtherReview: false });
        } catch (vehicleErr) {
          console.error('Failed to clear pre-check flags for vehicle', vehicleId, vehicleErr);
        }
      }
      // Optimistically remove from local list; parent will refresh on next data load
      setLocalTasks(prev => prev.filter(t => t.id !== taskId));
    } catch (error) {
      console.error('Failed to mark task complete:', error);
      alert('Failed to mark task complete: ' + (error.message || 'Unknown error'));
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await api.deleteTask(taskId);
      // Remove from local state
      setLocalTasks(prev => prev.filter(t => t.id !== taskId));
    } catch (error) {
      console.error('Failed to delete task:', error);
      alert('Failed to delete task: ' + (error.message || 'Unknown error'));
    }
  };

  const handleDeleteRO = async (roId, roNumber) => {
    if (!window.confirm(`Are you sure you want to delete RO #${roNumber}? This action cannot be undone.`)) return;
    try {
      await api.deleteRepairOrder(roId);
      // Remove from local state
      setRepairOrders(prev => prev.filter(ro => ro.id !== roId));
      alert(`RO #${roNumber} deleted successfully`);
    } catch (error) {
      console.error('Failed to delete RO:', error);
      alert('Failed to delete RO: ' + (error.message || 'Unknown error'));
    }
  };

  const handleSyncPrecheckParts = async (roId, vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    if (!vehicle?.precheckData) {
      alert('No precheck data found for this vehicle');
      return;
    }

    try {
      const precheckData = JSON.parse(vehicle.precheckData);
      const precheckParts = precheckData.partsNeeded || [];
      
      if (precheckParts.length === 0) {
        alert('No parts found in precheck data');
        return;
      }

      // Update the RO with the precheck parts
      await api.updateRepairOrder(roId, {
        partsNeeded: JSON.stringify(precheckParts)
      });

      alert(` Synced ${precheckParts.length} parts from precheck to RO!\n\nParts: ${precheckParts.map(p => p.description).join(', ')}`);
      
      // Reload the RO if it's currently open
      if (selectedRO && selectedRO.id === roId) {
        const updatedRO = await api.getRepairOrder(roId);
        setSelectedRO(updatedRO);
      }
    } catch (error) {
      console.error('Failed to sync precheck parts:', error);
      alert('Failed to sync precheck parts: ' + (error.message || 'Unknown error'));
    }
  };

  const handleTaskClick = (task) => {
    // If task is linked to a vehicle, open that vehicle in the existing vehicle detail flow
    if (task.vehicle_id || task.vehicleId) {
      const vid = task.vehicle_id || task.vehicleId;
      const vehicle = localVehicles.find(v => v.id === vid);
      if (vehicle && onSelect) {
        onSelect(vehicle);
        return;
      }
    }
  };
  
  // Detail role also gets the same To-Do list style - removed special case

  // Get flagged vehicles that need pre-check inspection (only for shop task users)
  const flaggedVehicles = showShopTasks 
    ? localVehicles.filter(v => {
        if (!v.damageFlagged && !v.needsFurtherReview) return false;
        // Filter out snoozed vehicles
        if (v.precheckSnoozedUntil && new Date(v.precheckSnoozedUntil) > new Date()) return false;
        // Filter out delivered vehicles (they go in the inspection list)
        if (v.transportStatus === 'delivered') return false;
        return true;
      })
    : [];

  // Get delivered vehicles that need inspection (only for shop task users)
  const deliveredNeedsInspection = showShopTasks
    ? localVehicles.filter(v => {
        // Must be delivered
        if (v.transportStatus !== 'delivered') return false;
        // Must not be sold/archived/ready
        if (v.status === 'sold' || v.status === 'archived' || v.status === 'ready') return false;
        // Show if: recon_status is needs_inspection or in_recon, OR flagged, OR inspection not complete
        const needsWork = v.reconStatus === 'needs_inspection' || 
                         v.reconStatus === 'in_recon' || 
                         v.damageFlagged || 
                         v.needsFurtherReview ||
                         !v.inspectionComplete;
        if (!needsWork) return false;
        // Filter out snoozed
        if (v.precheckSnoozedUntil && new Date(v.precheckSnoozedUntil) > new Date()) return false;
        return true;
      })
    : [];

  // Build combined to-do list with daily tasks + flagged vehicles + ROs + assigned tasks
  const allTodos = [
    // 1. Clock in FIRST (sortOrder 0) - only for shop task users
    ...(needsClockIn ? [{
      id: 'daily-clockin',
      title: '🕐 Clock In',
      description: 'Please clock in to start your workday',
      priority: 'urgent',
      isDaily: true,
      sortOrder: 0,
      onComplete: handleClockIn
    }] : []),
    // 2a. Flagged vehicles need PRE-CHECK (not delivered yet) - RED/URGENT
    ...flaggedVehicles.map(v => ({
      id: `flagged-${v.id}`,
      title: ` PRE-CHECK: ${v.stockNumber} - ${v.year} ${v.make} ${v.model}`,
      description: v.whatItNeeds || (v.damageFlagged ? 'Flagged damage - needs pre-check before arrival' : 'Needs further review'),
      priority: 'urgent',
      isFlagged: true,
      vehicle: v,
      vehicleId: v.id,
      sortOrder: 1,
      onInspect: () => onSelect && onSelect(v),
      onComplete: () => handlePrecheckComplete(v),
      onSnooze: () => setSnoozeModal({ show: true, vehicle: v, type: 'flagged' })
    })),
    // 2b. Delivered vehicles need INSPECTION - RED/URGENT
    ...deliveredNeedsInspection.map(v => ({
      id: `inspection-${v.id}`,
      title: ` INSPECT: ${v.stockNumber} - ${v.year} ${v.make} ${v.model}`,
      description: v.damageFlagged ? 'Vehicle delivered - flagged for inspection' : 'Vehicle delivered - needs inspection',
      priority: 'urgent',
      isInspection: true,
      vehicle: v,
      vehicleId: v.id,
      sortOrder: 1,
      onInspect: () => onSelect && onSelect(v),
      onComplete: () => handleInspectionComplete(v),
      onSnooze: () => setSnoozeModal({ show: true, vehicle: v, type: 'flagged' })
    })),
    // 3. Parts check (sortOrder 2) - only for shop task users
    ...(needsPartsCheck ? [{
      id: 'daily-parts',
      title: ' Daily Parts Delivery Check',
      description: 'Check receiving area for new parts and update work orders',
      priority: 'high',
      isDaily: true,
      sortOrder: 2,
      onComplete: handlePartsCheckComplete
    }] : []),
    // 4. Assigned Repair Orders (sortOrder 2.5) - BLUE/WORK ORDERS - only for shop task users
    // Filter out waiting_parts and snoozed ROs - they go in a separate collapsed section
    ...(showShopTasks ? localROs
      .filter(ro => ro.status !== 'waiting_parts' && !(ro.snoozedUntil && new Date(ro.snoozedUntil) > new Date()))
      .map(ro => ({
        id: `ro-${ro.id}`,
        title: ` RO #${ro.roNumber}: ${ro.stockNumber}`,
        description: `${ro.vehicleYear} ${ro.vehicleMake} ${ro.vehicleModel} - ${ro.complaint || ro.status}`,
        priority: ro.priority || 'normal',
        isRO: true,
        ro: ro,
        pendingPartsCount: parseInt(ro.pendingPartsCount) || 0,
        pendingParts: ro.pendingParts || [],
        sortOrder: 2.5,
        onComplete: () => handleROComplete(ro),
        onSnooze: () => setSnoozeModal({ show: true, ro })
      })) : []),
    // 5. Regular assigned tasks (sortOrder 3)
    // Filter out inspection/recon tasks for non-shop users - those belong in Recon module only
    ...localTasks
      .filter(t => {
        // If user has shop tasks enabled, show all tasks
        if (showShopTasks) return true;
        // Otherwise, filter out inspection-related tasks (they're for Recon module)
        const title = (t.title || '').toLowerCase();
        const isInspectionTask = title.includes('[new]') || 
                                  title.includes('[flagged]') || 
                                  title.includes('delivered') ||
                                  title.includes('inspection') ||
                                  title.includes('precheck') ||
                                  title.includes('pre-check');
        return !isInspectionTask;
      })
      .map(t => ({ ...t, isDaily: false, sortOrder: 3 }))
  ].sort((a, b) => {
    // First sort by sortOrder, then by priority within same sortOrder
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    const priorityOrder = { urgent: 0, high: 1, normal: 2, low: 3 };
    return (priorityOrder[a.priority] || 2) - (priorityOrder[b.priority] || 2);
  });

  return (
    <div className="space-y-3">
      {/* YOUR TO-DO LIST - Always show at top */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-4 text-white shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-3xl"></span>
          <div>
            <h2 className="text-xl font-bold">Your To-Do List</h2>
            <p className="text-orange-100 text-sm">
              {allTodos.length === 0 ? "You're all caught up!" : `${allTodos.length} item${allTodos.length !== 1 ? 's' : ''} need your attention`}
            </p>
          </div>
        </div>
        
        {allTodos.length > 0 ? (
          <div className="space-y-2">
            {allTodos.map((t, idx) => (
              <div key={t.id} className={`rounded-lg p-3 text-neutral-800 ${
                t.isFlagged ? 'bg-red-50 border-2 border-red-300' : 
                t.isRO ? 'bg-blue-50 border-2 border-blue-300' :
                idx === 0 ? 'bg-white ring-2 ring-yellow-400' : 'bg-white'
              }`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {idx === 0 && <span className="text-xs bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded font-bold">DO FIRST</span>}
                      {t.isDaily && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Daily</span>}
                      {t.isFlagged && <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded font-bold">NEEDS ATTENTION</span>}
                      {t.isRO && <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded font-bold">REPAIR ORDER</span>}
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        t.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                        t.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                        t.priority === 'low' ? 'bg-neutral-100 text-neutral-600' :
                        'bg-blue-100 text-blue-700'
                      }`}>{t.priority}</span>
                      {t.isRO && t.pendingPartsCount > 0 && (
                        <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded font-bold">
                           {t.pendingPartsCount} part{t.pendingPartsCount !== 1 ? 's' : ''} pending
                        </span>
                      )}
                    </div>
                    <div className="font-medium mt-1">{t.title}</div>
                    {t.description && <div className="text-sm text-neutral-500 mt-1">{t.description}</div>}
                    {t.stock_number && <div className="text-xs text-neutral-400 mt-1">Vehicle: {t.stock_number}</div>}
                    {/* Show pending parts for ROs */}
                    {t.isRO && t.pendingParts && t.pendingParts.length > 0 && (
                      <div className="mt-2 text-xs text-orange-700 bg-orange-50 rounded p-2">
                        <div className="font-medium mb-1">Waiting for parts:</div>
                        {t.pendingParts.slice(0, 3).map((p, i) => (
                          <div key={i}>• {p.description} ({p.status}{p.eta ? ` - ETA: ${new Date(p.eta).toLocaleDateString()}` : ''})</div>
                        ))}
                        {t.pendingParts.length > 3 && <div className="text-neutral-500">+{t.pendingParts.length - 3} more...</div>}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    {t.isFlagged ? (
                      <>
                        <button
                          onClick={t.onInspect}
                          className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm whitespace-nowrap"
                        >
                          👁 View
                        </button>
                        <button
                          onClick={t.onComplete}
                          className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm whitespace-nowrap"
                        >
                          ✓ Complete
                        </button>
                        <button
                          onClick={t.onSnooze}
                          className="px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm whitespace-nowrap"
                        >
                          💤 Snooze
                        </button>
                      </>
                    ) : t.isRO ? (
                      <>
                        <button
                          onClick={t.onComplete}
                          className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm whitespace-nowrap"
                        >
                          ✓ Complete
                        </button>
                        <button
                          onClick={t.onSnooze}
                          className="px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm whitespace-nowrap"
                        >
                          💤 Snooze
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => (t.isDaily || t.isFlagged || t.isInspection || t.isRO) ? t.onComplete() : handleMarkComplete(t)}
                        className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm whitespace-nowrap"
                      >
                        ✓ Complete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white/20 rounded-lg p-4 text-center">
            <p className="font-medium">No tasks right now!</p>
          </div>
        )}
      </div>

      {/* Snoozed / Waiting - Collapsed Section */}
      {showShopTasks && localROs.filter(ro => ro.status === 'waiting_parts' || (ro.snoozedUntil && new Date(ro.snoozedUntil) > new Date())).length > 0 && (
        <details className="bg-orange-50 border border-orange-200 rounded-lg">
          <summary className="px-4 py-3 cursor-pointer hover:bg-orange-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-lg">💤</span>
              <span className="font-medium text-orange-700">Snoozed / Waiting</span>
              <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {localROs.filter(ro => ro.status === 'waiting_parts' || (ro.snoozedUntil && new Date(ro.snoozedUntil) > new Date())).length}
              </span>
            </div>
            <span className="text-xs text-orange-500">Click to expand</span>
          </summary>
          <div className="px-4 pb-3 pt-2 border-t border-orange-200">
            <p className="text-xs text-orange-600 mb-2">
              These ROs are snoozed or waiting for parts. They'll return to your active list automatically.
            </p>
            <div className="space-y-2">
              {localROs.filter(ro => ro.status === 'waiting_parts' || (ro.snoozedUntil && new Date(ro.snoozedUntil) > new Date())).map(ro => {
                const isSnoozed = ro.snoozedUntil && new Date(ro.snoozedUntil) > new Date();
                const isWaitingParts = ro.status === 'waiting_parts';
                return (
                  <div key={ro.id} className={`flex items-center justify-between bg-white rounded-lg p-2 border ${isSnoozed ? 'border-purple-200' : 'border-orange-200'}`}>
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="font-mono text-sm font-medium">{ro.stockNumber}</div>
                      <div className="text-sm text-neutral-600">{ro.vehicleYear} {ro.vehicleMake} {ro.vehicleModel}</div>
                      <div className="text-xs text-blue-600 font-medium">RO #{ro.roNumber}</div>
                      {isSnoozed && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                          💤 Snoozed until {new Date(ro.snoozedUntil).toLocaleDateString()}
                        </span>
                      )}
                      {isWaitingParts && ro.pendingPartsCount > 0 && (
                        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                          {ro.pendingPartsCount} part{ro.pendingPartsCount !== 1 ? 's' : ''} on order
                        </span>
                      )}
                      {ro.snoozeReason && <span className="text-xs text-neutral-500 italic">"{ro.snoozeReason}"</span>}
                    </div>
                    <button
                      onClick={async () => {
                        if (window.confirm(`Wake up RO #${ro.roNumber}? This will move it back to your active work list.`)) {
                          try {
                            await api.updateRepairOrder(ro.id, { 
                              status: isWaitingParts ? 'in_progress' : ro.status,
                              snoozedUntil: null,
                              snoozeReason: null
                            });
                            alert(`RO #${ro.roNumber} is now active!`);
                            if (onRefresh) onRefresh();
                          } catch (err) {
                            alert('Failed to update: ' + err.message);
                          }
                        }
                      }}
                      className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 whitespace-nowrap"
                    >
                      Wake Up
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </details>
      )}

      {/* Notifications */}
      {notifications.length > 0 && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <span className="text-blue-800 font-medium">📬 Notifications</span>
            <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded">{notifications.length}</span>
          </div>
          <div className="space-y-2">
            {notifications.map(n => (
              <div key={n.id} className={`p-3 bg-white rounded border ${n.priority === 'high' ? 'border-red-200' : 'border-blue-100'}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="font-medium text-sm">{n.title}</div>
                    <div className="text-xs text-neutral-600 mt-1 whitespace-pre-wrap">{n.message}</div>
                    <div className="text-xs text-neutral-400 mt-1">{new Date(n.created_at).toLocaleDateString()}</div>
                  </div>
                  <button onClick={() => handleMarkRead(n.id)} className="text-xs text-blue-600 hover:underline ml-2">Dismiss</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ARB Deadline Alerts */}
      {arbAlerts.length > 0 && (
        <div className="bg-orange-50 border-2 border-orange-400 rounded-xl p-4 mb-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xl">⚠️</span>
            </div>
            <div className="flex-1">
              <h2 className="font-bold text-orange-800 text-lg">ARB Alerts</h2>
              <p className="text-orange-700 text-sm">
                {arbAlerts.length} vehicle{arbAlerts.length !== 1 ? 's' : ''} need attention
              </p>
            </div>
            <span className="bg-orange-500 text-white text-lg font-bold px-3 py-1 rounded-full flex-shrink-0">
              {arbAlerts.length}
            </span>
          </div>
          <div className="space-y-2">
            {arbAlerts.map(v => {
              const daysLeft = parseInt(v.daysUntilDeadline) || 0;
              const isUrgent = daysLeft <= 1;
              const isInArbitration = v.arbStatus === 'arbitrated';
              
              return (
                <div key={v.id} className={`bg-white rounded-lg p-3 flex items-center justify-between ${isUrgent && !isInArbitration ? 'border-2 border-red-400' : ''} ${isInArbitration ? 'border-2 border-purple-400' : ''}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm font-bold">{v.stockNumber}</span>
                    <span className="text-sm">{v.year} {v.make} {v.model}</span>
                    {isInArbitration ? (
                      <>
                        <span className="text-xs px-2 py-1 rounded font-medium bg-purple-100 text-purple-700">
                          In Arbitration
                        </span>
                        {v.arbType && (
                          <span className="text-xs text-neutral-500">({v.arbType})</span>
                        )}
                      </>
                    ) : (
                      <span className={`text-xs px-2 py-1 rounded font-medium ${isUrgent ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {daysLeft === 0 ? 'TODAY!' : daysLeft === 1 ? '1 day left' : `${daysLeft} days left`}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {isInArbitration ? (
                      <button 
                        onClick={() => setArbResolveModal({ show: true, vehicle: v })}
                        className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                      >
                        Resolve
                      </button>
                    ) : (
                      <>
                        <button 
                          onClick={() => handleArbAction(v.id, 'inspection_complete', 'No issues found')}
                          className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                        >
                          ✓ No Issues
                        </button>
                        <button 
                          onClick={() => {
                            const notes = window.prompt('Enter snooze reason:');
                            if (notes) handleArbAction(v.id, 'snooze', notes);
                          }}
                          className="px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600"
                        >
                          Snooze
                        </button>
                        <button 
                          onClick={() => setArbFileModal({ show: true, vehicle: v })}
                          className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                        >
                          Arbitrate
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ARB File Modal */}
      {arbFileModal.show && arbFileModal.vehicle && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setArbFileModal({ show: false, vehicle: null })}>
          <div className="bg-white w-full max-w-md rounded-xl shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b">
              <h2 className="font-semibold text-lg">File Arbitration</h2>
              <p className="text-sm text-neutral-500">
                {arbFileModal.vehicle.stockNumber} - {arbFileModal.vehicle.year} {arbFileModal.vehicle.make} {arbFileModal.vehicle.model}
              </p>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const arbType = formData.get('arbType');
              const notes = formData.get('notes');
              if (!arbType) { alert('Please select an ARB type'); return; }
              if (!notes?.trim()) { alert('Please describe the issue'); return; }
              handleArbAction(arbFileModal.vehicle.id, 'arbitrate', notes, { arbType });
              setArbFileModal({ show: false, vehicle: null });
            }} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">ARB Type *</label>
                <select name="arbType" className="w-full h-10 px-3 border border-neutral-200 rounded-lg bg-white" required>
                  <option value="">Select type...</option>
                  <option value="Mechanical">Mechanical</option>
                  <option value="Structure">Structure</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Issue Description *</label>
                <textarea name="notes" className="w-full h-24 px-3 py-2 border border-neutral-200 rounded-lg resize-none" placeholder="Describe the issue..." required />
              </div>
              <div className="flex justify-end gap-2 pt-3 border-t">
                <button type="button" onClick={() => setArbFileModal({ show: false, vehicle: null })} className="px-4 py-2 text-neutral-600 hover:bg-neutral-100 rounded-lg">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">File ARB</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ARB Resolve Modal */}
      {arbResolveModal.show && arbResolveModal.vehicle && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setArbResolveModal({ show: false, vehicle: null })}>
          <div className="bg-white w-full max-w-md rounded-xl shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b">
              <h2 className="font-semibold text-lg">Resolve ARB</h2>
              <p className="text-sm text-neutral-500">
                {arbResolveModal.vehicle.stockNumber} - {arbResolveModal.vehicle.year} {arbResolveModal.vehicle.make} {arbResolveModal.vehicle.model}
              </p>
              {arbResolveModal.vehicle.arbType && (
                <p className="text-xs text-purple-600 mt-1">Type: {arbResolveModal.vehicle.arbType}</p>
              )}
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const arbOutcome = formData.get('outcome');
              const arbRecoveryAmount = formData.get('amount') || 0;
              const notes = formData.get('notes') || '';
              if (!arbOutcome) { alert('Please select an outcome'); return; }
              handleArbAction(arbResolveModal.vehicle.id, 'resolve', notes, { arbOutcome, arbRecoveryAmount });
              setArbResolveModal({ show: false, vehicle: null });
            }} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Outcome *</label>
                <select name="outcome" className="w-full h-10 px-3 border border-neutral-200 rounded-lg bg-white" required>
                  <option value="">Select outcome...</option>
                  <option value="Paid">Paid</option>
                  <option value="Partial">Partial</option>
                  <option value="Denied">Denied</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Amount Received</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">$</span>
                  <input type="number" name="amount" className="w-full h-10 pl-7 pr-3 border border-neutral-200 rounded-lg" placeholder="0.00" step="0.01" min="0" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea name="notes" className="w-full h-20 px-3 py-2 border border-neutral-200 rounded-lg resize-none" placeholder="Any additional notes..." />
              </div>
              <div className="flex justify-end gap-2 pt-3 border-t">
                <button type="button" onClick={() => setArbResolveModal({ show: false, vehicle: null })} className="px-4 py-2 text-neutral-600 hover:bg-neutral-100 rounded-lg">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Resolve ARB</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Green Light/Guarantee Inspection Alerts */}
      {greenLightVehicles.length > 0 && (
        <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4 mb-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xl"></span>
            </div>
            <div className="flex-1">
              <h2 className="font-bold text-green-800 text-lg">Green Light Inspection Overdue</h2>
              <p className="text-green-700 text-sm">
                {greenLightVehicles.length} guarantee vehicle{greenLightVehicles.length !== 1 ? 's' : ''} need{greenLightVehicles.length === 1 ? 's' : ''} thorough inspection (over 6 days)
              </p>
            </div>
            <span className="bg-green-500 text-white text-lg font-bold px-3 py-1 rounded-full flex-shrink-0">
              {greenLightVehicles.length}
            </span>
          </div>
          <div className="space-y-2">
            {greenLightVehicles.map(v => {
              const daysSincePurchase = Math.floor((Date.now() - new Date(v.purchaseDate).getTime()) / (1000 * 60 * 60 * 24));
              return (
                <div key={v.id} className="bg-white rounded-lg p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm font-bold">{v.stockNumber}</span>
                    <span className="text-sm">{v.year} {v.make} {v.model}</span>
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                      {daysSincePurchase} days overdue
                    </span>
                  </div>
                  <button 
                    onClick={() => onSelect(v)}
                    className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                  >
                    Inspect Now
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}


      {needsAttention.length > 0 && (
        <div>
          <Header title="Needs Attention" count={needsAttention.length} />
          <Table headers={['Stock', 'Vehicle', 'Status', 'Issue', ...(showFinancials ? ['Cost'] : [])]}>
            {needsAttention.map(v => (
              <tr key={v.id} onClick={() => onSelect(v)} className="border-b border-neutral-100 hover:bg-neutral-100 cursor-pointer">
                <td className="py-2 font-mono text-xs">{v.stockNumber}</td>
                <td className="py-2">{v.year} {v.make} {v.model}</td>
                <td className="py-2 text-xs">{v.status}</td>
                <td className="py-2 text-xs text-neutral-500">{v.priority === 'high' ? 'High priority' : 'Needs review'}</td>
                {showFinancials && <td className="py-2 text-right font-mono text-xs">${getTotalCost(v).toLocaleString()}</td>}
              </tr>
            ))}
          </Table>
        </div>
      )}
      {needsAttention.length === 0 && localTasks.length === 0 && localROs.length === 0 && notifications.length === 0 && <Empty text="Nothing needs attention" />}
      
      {/* Snooze Modal - handles both ROs and flagged vehicles */}
      {snoozeModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-bold mb-4">
              💤 Snooze {snoozeModal.type === 'flagged' 
                ? `${snoozeModal.vehicle?.stockNumber}` 
                : `RO #${snoozeModal.ro?.roNumber}`}
            </h3>
            <p className="text-sm text-neutral-600 mb-4">
              {snoozeModal.type === 'flagged' 
                ? 'Hide this item from your to-do list for a specified number of days.'
                : 'Hide this repair order from your dashboard for a specified number of days. Use this when waiting for parts to arrive.'}
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Snooze for how many days?</label>
              <select
                value={snoozeDays}
                onChange={(e) => setSnoozeDays(parseInt(e.target.value))}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2"
              >
                <option value={1}>1 day</option>
                <option value={2}>2 days</option>
                <option value={3}>3 days</option>
                <option value={5}>5 days</option>
                <option value={7}>1 week</option>
                <option value={14}>2 weeks</option>
                <option value={30}>1 month</option>
              </select>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Reason (optional)</label>
              <input
                type="text"
                value={snoozeReason}
                onChange={(e) => setSnoozeReason(e.target.value)}
                placeholder="e.g., Waiting for parts, vehicle not delivered yet..."
                className="w-full border border-neutral-300 rounded-lg px-3 py-2"
              />
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setSnoozeModal({ show: false, ro: null, vehicle: null, type: null });
                  setSnoozeDays(3);
                  setSnoozeReason('');
                }}
                className="px-4 py-2 text-neutral-600 hover:bg-neutral-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={snoozeModal.type === 'flagged' ? handleFlaggedSnooze : handleROSnooze}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg"
              >
                💤 Snooze for {snoozeDays} days
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Inventory with Add button
const Inventory = ({ vehicles, refreshTrigger, showFinancials, onSelect, onEdit, onRefresh, onSilentRefresh, canEdit, onNavigateToRecon, onStartInspection, onCreateRO, limitedView, onQuickEdit }) => {
  // Persist filters to localStorage so they survive refreshes
  const [categoryFilter, setCategoryFilter] = useState(() => localStorage.getItem('inv_categoryFilter') || 'all');
  const [filter, setFilter] = useState(() => localStorage.getItem('inv_filter') || 'all');
  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem('inv_searchTerm') || '');
  const [sortBy, setSortBy] = useState(() => localStorage.getItem('inv_sortBy') || 'stock');
  const [sortDir, setSortDir] = useState(() => localStorage.getItem('inv_sortDir') || 'asc');
  const [buyerFilter, setBuyerFilter] = useState(() => localStorage.getItem('inv_buyerFilter') || 'all');
  
  // Show Sold toggle - default OFF (sold vehicles hidden)
  const [showSold, setShowSold] = useState(() => localStorage.getItem('inv_showSold') === 'true');
  const [soldVehicles, setSoldVehicles] = useState([]);
  const [loadingSold, setLoadingSold] = useState(false);
  const [updatingExitStrategy, setUpdatingExitStrategy] = useState(null);
  
  // Cost view toggle - default to Hard Costs only (no recon)
  const [showTotalInvested, setShowTotalInvested] = useState(() => localStorage.getItem('inv_showTotalInvested') === 'true');
  
  // Handle inline exit strategy change - use silent refresh to preserve scroll
  const handleExitStrategyChange = async (vehicleId, newEndUse) => {
    setUpdatingExitStrategy(vehicleId);
    try {
      await api.updateVehicle(vehicleId, { endUse: newEndUse });
      // Use silent refresh (no loading state) to preserve scroll position
      if (onSilentRefresh) {
        await onSilentRefresh();
      } else {
        await onRefresh();
      }
    } catch (err) {
      alert('Failed to update exit strategy: ' + err.message);
    } finally {
      setUpdatingExitStrategy(null);
    }
  };
  
  // Save filters to localStorage when they change
  useEffect(() => { localStorage.setItem('inv_categoryFilter', categoryFilter); }, [categoryFilter]);
  useEffect(() => { localStorage.setItem('inv_filter', filter); }, [filter]);
  useEffect(() => { localStorage.setItem('inv_searchTerm', searchTerm); }, [searchTerm]);
  useEffect(() => { localStorage.setItem('inv_sortBy', sortBy); }, [sortBy]);
  useEffect(() => { localStorage.setItem('inv_sortDir', sortDir); }, [sortDir]);
  useEffect(() => { localStorage.setItem('inv_buyerFilter', buyerFilter); }, [buyerFilter]);
  useEffect(() => { localStorage.setItem('inv_showSold', showSold.toString()); }, [showSold]);
  useEffect(() => { localStorage.setItem('inv_showTotalInvested', showTotalInvested.toString()); }, [showTotalInvested]);
  
  // Fetch sold vehicles on mount and when switching to Sold category
  const fetchSoldVehicles = useCallback(() => {
    setLoadingSold(true);
    api.getSoldVehicles()
      .then(soldOnly => {
        setSoldVehicles(Array.isArray(soldOnly) ? soldOnly : []);
      })
      .catch(err => {
        console.error('Failed to fetch sold vehicles:', err);
        setSoldVehicles([]);
      })
      .finally(() => {
        setLoadingSold(false);
      });
  }, []);

  useEffect(() => {
    fetchSoldVehicles();
  }, [fetchSoldVehicles]);
  
  // Refetch sold vehicles when parent refreshes (e.g. after Quick Edit saves costs)
  useEffect(() => {
    if (refreshTrigger != null && refreshTrigger > 0) {
      fetchSoldVehicles();
    }
  }, [refreshTrigger, fetchSoldVehicles]);
  
  const [showShopVehicles, setShowShopVehicles] = useState(false);
  const [showSalvageVehicles, setShowSalvageVehicles] = useState(false);
  const [shopVehicles, setShopVehicles] = useState([]);
  const [showShopForm, setShowShopForm] = useState(false);
  const [shopForm, setShopForm] = useState({ year: '', make: '', model: '', trim: '', vin: '', mileage: '', color: '', purchasePrice: '', estimatedSale: '', whatItNeeds: '' });
  const [shopDecoding, setShopDecoding] = useState(false);
  const [notesModal, setNotesModal] = useState({ isOpen: false, vehicle: null });
  const [notesText, setNotesText] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [showFilterSidebar, setShowFilterSidebar] = useState(false);
  const [deliveredModal, setDeliveredModal] = useState({ isOpen: false, vehicle: null });
  const [transportCost, setTransportCost] = useState('');
  const [unknownCost, setUnknownCost] = useState(false);
  const [auctionTransported, setAuctionTransported] = useState(false);
  const [savingDelivered, setSavingDelivered] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, vehicle: null });
  const [disposalCost, setDisposalCost] = useState('');
  const [deletingVehicle, setDeletingVehicle] = useState(false);
  const [proposalVehicle, setProposalVehicle] = useState(null);
  const [invAdminTodos, setInvAdminTodos] = useState(() => { try { return JSON.parse(localStorage.getItem('inv_admin_todos') || '[]'); } catch { return []; } });
  const [invTodoVehicle, setInvTodoVehicle] = useState(null);
  const [advancedFilters, setAdvancedFilters] = useState(() => {
    try {
      const saved = localStorage.getItem('inv_advancedFilters');
      return saved ? JSON.parse(saved) : {
        titleInOffice: null,
        delivered: null,
        inspected: null,
        hasOpenRO: null,
        inArbitration: null,
        paymentCleared: null,
        isFloored: null,
        hasNotes: null,
        daysOnLot: null,
      };
    } catch { return { titleInOffice: null, delivered: null, inspected: null, hasOpenRO: null, inArbitration: null, paymentCleared: null, isFloored: null, hasNotes: null, daysOnLot: null }; }
  });
  
  // Save advanced filters to localStorage
  useEffect(() => { localStorage.setItem('inv_advancedFilters', JSON.stringify(advancedFilters)); }, [advancedFilters]);
  useEffect(() => { localStorage.setItem('inv_admin_todos', JSON.stringify(invAdminTodos)); }, [invAdminTodos]);
  const invAddTodo = (vehicleId, text) => { if (!text.trim()) return; setInvAdminTodos(prev => [...prev, { id: Date.now(), vehicleId, text: text.trim(), done: false, createdAt: new Date().toISOString() }]); };
  const invToggleTodo = (todoId) => { setInvAdminTodos(prev => prev.map(t => t.id === todoId ? { ...t, done: !t.done } : t)); };
  const invDeleteTodo = (todoId) => { setInvAdminTodos(prev => prev.filter(t => t.id !== todoId)); };
  const invGetTodoCount = (vehicleId) => invAdminTodos.filter(t => t.vehicleId === vehicleId && !t.done).length;

  const printInvAdminTaskList = () => {
    const pendingTodos = invAdminTodos.filter(t => !t.done);
    if (pendingTodos.length === 0) { alert('No pending admin tasks.'); return; }
    const grouped = {};
    pendingTodos.forEach(t => { if (!grouped[t.vehicleId]) grouped[t.vehicleId] = []; grouped[t.vehicleId].push(t); });
    const allVehicles = [...vehicles, ...soldVehicles];
    const rows = Object.entries(grouped).map(([vid, todos]) => {
      const v = allVehicles.find(x => x.id === parseInt(vid));
      const label = v ? `${v.stockNumber} - ${v.year} ${v.make} ${v.model}` : `Vehicle #${vid}`;
      const items = todos.map(t => `<div class="todo"><span class="checkbox">[ ]</span> ${t.text}</div>`).join('');
      return `<div class="vehicle">&gt; ${label}</div>${items}`;
    }).join('');
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Admin Task List - Inventory</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{background:#1a1a2e;color:#0ff;font-family:'Courier New',monospace;padding:30px;font-size:13px}h1{color:#0f0;font-size:18px;margin-bottom:4px}.date{color:#888;font-size:11px;margin-bottom:20px}.vehicle{color:#ff0;font-size:14px;font-weight:bold;margin-top:16px;margin-bottom:4px}.todo{color:#0ff;margin-left:16px;line-height:1.8}.checkbox{color:#0f0}.footer{margin-top:30px;border-top:1px solid #333;padding-top:10px;color:#666;font-size:10px}@media print{body{background:#fff;color:#000}.vehicle{color:#333}.todo{color:#000}.checkbox{color:#000}h1{color:#000}.date{color:#666}.footer{color:#999}}</style>
</head><body><h1>ADMIN TASK LIST - INVENTORY</h1><div class="date">${new Date().toLocaleString()}</div>${rows}<div class="footer">Generated by LotSense | ${pendingTodos.length} pending tasks</div></body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  const { isAdmin } = useAuth();
  const [showQuickAdd, setShowQuickAdd] = useState(false);

  const activeFilterCount = Object.values(advancedFilters).filter(v => v !== null).length;

  const handleSaveNotes = async () => {
    if (!notesModal.vehicle) return;
    setSavingNotes(true);
    try {
      await api.updateVehicle(notesModal.vehicle.id, { notes: notesText });
      setNotesModal({ isOpen: false, vehicle: null });
      onRefresh();
    } catch (err) {
      alert('Failed to save notes: ' + err.message);
    } finally {
      setSavingNotes(false);
    }
  };

  const handleMarkDelivered = async () => {
    if (!deliveredModal.vehicle) return;
    setSavingDelivered(true);
    try {
      const v = deliveredModal.vehicle;
      const isCopart = v.exitStrategy === 'Copart Direct' || v.endUse === 'copart';
      
      const updateData = {
        transportStatus: 'delivered',
        deliveryDate: new Date().toISOString().split('T')[0],
      };
      
      // Handle different scenarios
      if (isCopart) {
        // Copart - just confirm drop-off, no cost
        updateData.transportCost = 0;
      } else if (auctionTransported) {
        // Auction transported - add transport cost to purchase price
        if (transportCost && parseFloat(transportCost) > 0) {
          const newPurchasePrice = (parseFloat(v.purchasePrice) || 0) + parseFloat(transportCost);
          updateData.purchasePrice = newPurchasePrice;
          updateData.transportCost = 0; // Cost is bundled in purchase price
          updateData.transportNotes = `Transport cost of $${transportCost} added to purchase price (auction transported)`;
        }
      } else if (!unknownCost && transportCost) {
        // Regular transport with known cost
        updateData.transportCost = parseFloat(transportCost);
      }
      
      await api.updateVehicle(v.id, updateData);
      
      // If unknown cost (and not Copart/auction), create task for Alex
      if (unknownCost && !isCopart && !auctionTransported) {
        try {
          const users = await api.getUsers();
          const alex = users.find(u => u.email === 'alex@dynamicautosourcing.com');
          
          if (alex) {
            await api.createTask({
              title: `Confirm transport cost for ${v.stockNumber}`,
              description: `Vehicle ${v.year} ${v.make} ${v.model} (${v.stockNumber}) was marked delivered but transport cost is unknown. Please update the transport cost.`,
              assignedTo: alex.id,
              priority: 'high',
              status: 'pending',
              dueDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            });
          }
        } catch (taskErr) {
          console.error('Failed to create task for Alex:', taskErr);
        }
      }
      
      setDeliveredModal({ isOpen: false, vehicle: null });
      setTransportCost('');
      setUnknownCost(false);
      setAuctionTransported(false);
      onRefresh();
    } catch (err) {
      alert('Failed to mark delivered: ' + err.message);
    } finally {
      setSavingDelivered(false);
    }
  };

  // Print Inventory function - prints currently filtered view
  const printInventory = () => {
    if (sorted.length === 0) { alert('No vehicles to print.'); return; }
    
    // Build filter description
    const filterParts = [];
    if (categoryFilter !== 'all') filterParts.push(`Category: ${categoryFilter}`);
    if (filter !== 'all') filterParts.push(`Status: ${filter}`);
    if (searchTerm) filterParts.push(`Search: "${searchTerm}"`);
    if (buyerFilter !== 'all') filterParts.push(`Buyer: ${buyerFilter}`);
    const activeAdvanced = Object.entries(advancedFilters).filter(([,v]) => v !== null);
    if (activeAdvanced.length > 0) filterParts.push(`${activeAdvanced.length} advanced filter(s)`);
    const filterDesc = filterParts.length > 0 ? filterParts.join(' | ') : 'No filters applied';
    
    const rows = sorted.map(v => {
      const hardCost = getHardCosts(v);
      const prc = parseFloat(v.potentialReconCost) || parseFloat(v.reconCost) || 0;
      const displayCost = showTotalInvested ? (hardCost + prc) : hardCost;
      const estSale = parseFloat(v.estimatedSale) || 0;
      const estProfit = estSale - displayCost;
      const days = getDaysOnLot(v.purchaseDate);
      return `<tr>
        <td>${v.stockNumber || ''}</td>
        <td>${v.year || ''} ${v.make || ''} ${v.model || ''}</td>
        <td style="font-size:10px">${v.vin || ''}</td>
        <td class="r">${v.mileage ? Number(v.mileage).toLocaleString() : ''}</td>
        <td>${v.color || ''}</td>
        <td>${v.status || ''}</td>
        ${showFinancials ? `<td class="r">$${displayCost.toLocaleString()}</td>` : ''}
        ${showFinancials ? `<td class="r">$${estSale.toLocaleString()}</td>` : ''}
        ${showFinancials ? `<td class="r" style="color:${estProfit >= 0 ? '#16a34a' : '#dc2626'}">${estProfit >= 0 ? '' : '-'}$${Math.abs(estProfit).toLocaleString()}</td>` : ''}
        <td class="r">${days}d</td>
      </tr>`;
    }).join('');
    
    const totalCost = showFinancials ? sorted.reduce((s, v) => s + (showTotalInvested ? getHardCosts(v) + (parseFloat(v.potentialReconCost) || parseFloat(v.reconCost) || 0) : getHardCosts(v)), 0) : 0;
    const totalEst = showFinancials ? sorted.reduce((s, v) => s + (parseFloat(v.estimatedSale) || 0), 0) : 0;
    const totalProfit = totalEst - totalCost;
    
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Inventory - ${new Date().toLocaleDateString()}</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:Arial,Helvetica,sans-serif;font-size:11px;padding:20px;color:#333}
  h1{font-size:16px;margin-bottom:2px}
  .meta{color:#666;font-size:10px;margin-bottom:12px}
  .filters{background:#f5f5f5;padding:6px 10px;border-radius:4px;font-size:10px;margin-bottom:12px;color:#555}
  table{width:100%;border-collapse:collapse}
  th{text-align:left;font-size:10px;font-weight:600;border-bottom:2px solid #333;padding:4px 6px;white-space:nowrap}
  td{padding:4px 6px;border-bottom:1px solid #ddd;white-space:nowrap}
  .r{text-align:right}
  tfoot td{font-weight:bold;border-top:2px solid #333;padding-top:6px}
  @media print{body{padding:10px}}
</style></head><body>
<h1>LotSense Inventory Report</h1>
<div class="meta">${new Date().toLocaleString()} | ${sorted.length} vehicles</div>
<div class="filters">${filterDesc}</div>
<table>
  <thead><tr>
    <th>Stock</th><th>Vehicle</th><th>VIN</th><th class="r">Odometer</th><th>Color</th><th>Status</th>
    ${showFinancials ? '<th class="r">Cost</th><th class="r">Est. Sale</th><th class="r">Est. Profit</th>' : ''}
    <th class="r">Days</th>
  </tr></thead>
  <tbody>${rows}</tbody>
  ${showFinancials ? `<tfoot><tr>
    <td colspan="6" class="r">Totals:</td>
    <td class="r">$${totalCost.toLocaleString()}</td>
    <td class="r">$${totalEst.toLocaleString()}</td>
    <td class="r" style="color:${totalProfit >= 0 ? '#16a34a' : '#dc2626'}">${totalProfit >= 0 ? '' : '-'}$${Math.abs(totalProfit).toLocaleString()}</td>
    <td></td>
  </tr></tfoot>` : ''}
</table>
</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  // CSV Export function
  const handleExportCSV = async () => {
    try {
      const token = api.getAccessToken();
      
      // Make direct fetch request to get CSV data as text
      const response = await fetch(`${api.API_URL || 'https://lotsense-production.up.railway.app/api'}/inventory/export-csv`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Export failed: ${response.status} ${response.statusText}`);
      }
      
      const csvData = await response.text();
      
      // Create blob and download
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inventory_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('CSV export error:', error);
      alert('Failed to export CSV: ' + error.message);
    }
  };

  // Category counts - use vehicles prop (activeVehicles) which already excludes sold
  // Sold vehicles are fetched separately and only shown in Sold tab
  const inventoryVehicles = vehicles.filter(v => !v.endUse || v.endUse === 'retail' || v.endUse === 'wholesale' || v.endUse === 'parts_only' || v.endUse === 'other');
  const shopPersonalVehicles = vehicles.filter(v => v.endUse === 'shop_personal');
  const dealerDirectVehicles = vehicles.filter(v => v.endUse === 'dealer_direct');
  const salvageVehicles = vehicles.filter(v => v.endUse === 'copart' || v.endUse === 'iaai');
  
  // Filter vehicles by category - sold vehicles ONLY in Sold tab
  const getCategoryVehicles = () => {
    switch (categoryFilter) {
      case 'inventory': return inventoryVehicles;
      case 'shop_personal': return shopPersonalVehicles;
      case 'dealer_direct': return dealerDirectVehicles;
      case 'salvage': return salvageVehicles;
      case 'sold': return soldVehicles; // Only show sold vehicles
      default: return vehicles; // 'all' - vehicles prop already excludes sold
    }
  };
  
  const categoryVehicles = getCategoryVehicles();

  // VIN decoder for shop vehicles
  const decodeShopVin = async (vin) => {
    if (!vin || vin.length !== 17) return;
    setShopDecoding(true);
    try {
      const response = await fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVin/${vin}?format=json`);
      const data = await response.json();
      if (data.Results) {
        const get = (name) => data.Results.find(r => r.Variable === name)?.Value || '';
        setShopForm(prev => ({
          ...prev,
          year: get('Model Year') || prev.year,
          make: get('Make') || prev.make,
          model: get('Model') || prev.model,
          trim: get('Trim') || prev.trim
        }));
      }
    } catch (err) {
      console.error('VIN decode error:', err);
    } finally {
      setShopDecoding(false);
    }
  };

  // Load shop vehicles when expanded
  useEffect(() => {
    if (showShopVehicles) {
      api.getShopVehicles().then(setShopVehicles).catch(console.error);
    }
  }, [showShopVehicles]);

  const handleAddShopVehicle = async (e) => {
    e.preventDefault();
    try {
      await api.createShopVehicle(shopForm);
      setShowShopForm(false);
      setShopForm({ year: '', make: '', model: '', vin: '', mileage: '', color: '', purchasePrice: '', estimatedSale: '', whatItNeeds: '' });
      const updated = await api.getShopVehicles();
      setShopVehicles(updated);
    } catch (err) {
      alert('Failed to add shop vehicle: ' + err.message);
    }
  };

  // Helper to calculate days on lot
  const getDaysOnLot = (purchaseDate) => {
    if (!purchaseDate) return 0;
    const purchase = new Date(purchaseDate);
    const now = new Date();
    return Math.floor((now - purchase) / (1000 * 60 * 60 * 24));
  };

  // Apply status filter, search, and advanced filters (using category-filtered vehicles)
  const filtered = categoryVehicles.filter(v => {
    // When viewing "Sold" category, only show sold vehicles
    // When viewing other categories, exclude sold/disposed vehicles
    const status = (v.status || '').toLowerCase();
    const disposedStatuses = ['sold', 'copart', 'wholesale', 'wholesale_out'];
    if (categoryFilter === 'sold') {
      // In Sold category, only include sold vehicles
      if (status !== 'sold') return false;
    } else {
      // In other categories (incl. Salvage), exclude sold and disposed vehicles
      if (disposedStatuses.includes(status)) return false;
    }
    
    // Status filter (skip for Sold category since all are "sold")
    if (categoryFilter !== 'sold' && filter !== 'all' && v.status !== filter) return false;
    
    // Advanced filters
    if (advancedFilters.titleInOffice !== null) {
      const inOffice = v.titleStatus === 'in_office' || v.titleStatus === 'In Office';
      if (advancedFilters.titleInOffice !== inOffice) return false;
    }
    if (advancedFilters.delivered !== null) {
      const isDelivered = v.transportStatus === 'delivered';
      if (advancedFilters.delivered !== isDelivered) return false;
    }
    if (advancedFilters.inspected !== null) {
      // Copart cars NEVER need inspection - exclude them from "not inspected" filter
      const isCopart = v.endUse === 'copart' || v.endUse === 'salvage';
      if (isCopart) {
        // Copart cars are considered "inspected" (N/A) - only show if filtering for inspected=true
        if (advancedFilters.inspected === false) return false;
      } else {
        const isInspected = !!v.inspectionComplete || !!v.inspectionDate;
        if (advancedFilters.inspected !== isInspected) return false;
      }
    }
    if (advancedFilters.hasOpenRO !== null) {
      const hasRO = !!v.openRos && v.openRos > 0;
      if (advancedFilters.hasOpenRO !== hasRO) return false;
    }
    if (advancedFilters.inArbitration !== null) {
      const inArb = !!v.inArbitration;
      if (advancedFilters.inArbitration !== inArb) return false;
    }
    if (advancedFilters.paymentCleared !== null) {
      const paid = !!v.paymentCleared;
      if (advancedFilters.paymentCleared !== paid) return false;
    }
    if (advancedFilters.isFloored !== null) {
      const floored = !!v.isFloored;
      if (advancedFilters.isFloored !== floored) return false;
    }
    if (advancedFilters.hasNotes !== null) {
      const hasNotes = !!v.notes && v.notes.trim().length > 0;
      if (advancedFilters.hasNotes !== hasNotes) return false;
    }
    if (advancedFilters.daysOnLot !== null) {
      const days = getDaysOnLot(v.purchaseDate);
      switch (advancedFilters.daysOnLot) {
        case '0-30': if (days > 30) return false; break;
        case '30-60': if (days <= 30 || days > 60) return false; break;
        case '60-90': if (days <= 60 || days > 90) return false; break;
        case '90+': if (days <= 90) return false; break;
        default: break;
      }
    }
    
    // Buyer filter
    if (buyerFilter !== 'all') {
      if ((v.buyer || '').toLowerCase() !== buyerFilter.toLowerCase()) return false;
    }
    
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (v.stockNumber || '').toLowerCase().includes(search) ||
             (v.vin || '').toLowerCase().includes(search) ||
             (v.make || '').toLowerCase().includes(search) ||
             (v.model || '').toLowerCase().includes(search) ||
             (v.color || '').toLowerCase().includes(search) ||
             (v.auction || '').toLowerCase().includes(search);
    }
    return true;
  });

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const num = (x) => (x === undefined || x === null || x === '') ? 0 : Number(x);

    switch (sortBy) {
      case 'vehicle':
        const vehicleA = `${a.year || ''} ${a.make || ''} ${a.model || ''}`.trim();
        const vehicleB = `${b.year || ''} ${b.make || ''} ${b.model || ''}`.trim();
        return vehicleA.localeCompare(vehicleB) * dir;
      case 'vin':
        return (a.vin || '').localeCompare(b.vin || '') * dir;
      case 'color':
        return (a.color || '').localeCompare(b.color || '') * dir;
      case 'price':
        return (num(a.estimatedSale) - num(b.estimatedSale)) * dir;
      case 'days':
        return (getDays(a.purchaseDate) - getDays(b.purchaseDate)) * dir;
      case 'cost':
        return (getTotalCost(a) - getTotalCost(b)) * dir;
      case 'profit':
        const profitA = (num(a.estimatedSale)) - getTotalCost(a);
        const profitB = (num(b.estimatedSale)) - getTotalCost(b);
        return (profitA - profitB) * dir;
      case 'odometer':
        return (num(a.mileage) - num(b.mileage)) * dir;
      case 'status':
        return (a.status || '').localeCompare(b.status || '') * dir;
      case 'stock':
      default:
        return (a.stockNumber || '').localeCompare(b.stockNumber || '') * dir;
    }
  });

  const SortHeader = ({ id, label, align = 'left' }) => {
    const active = sortBy === id;
    const arrow = active ? (sortDir === 'asc' ? '▲' : '▼') : '⇅';
    return (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleSort(id);
        }}
        style={{ pointerEvents: 'auto', cursor: 'pointer' }}
        className={`flex items-center gap-1 text-xs hover:text-blue-600 ${align === 'right' ? 'justify-end w-full' : ''} ${active ? 'text-blue-600 font-medium' : ''}`}
      >
        <span>{label}</span>
        <span className={`text-[10px] ${active ? '' : 'opacity-30'}`}>{arrow}</span>
      </button>
    );
  };

  const handleDelete = (vehicle) => {
    setDisposalCost('');
    setDeleteModal({ isOpen: true, vehicle, hardDelete: false });
  };

  const handleConfirmDelete = async (hardDelete = false) => {
    if (!deleteModal.vehicle) return;
    setDeletingVehicle(true);
    try {
      const v = deleteModal.vehicle;
      
      // If there's a disposal cost, log it as an expense (only for soft delete)
      if (!hardDelete && disposalCost && parseFloat(disposalCost) > 0) {
        try {
          const disposalAmount = parseFloat(disposalCost);
          const description = `Disposal cost for ${v.stockNumber} - ${v.year} ${v.make} ${v.model}`;
          const category = 'vehicle_disposal';
          await api.createExpense({
            date: new Date().toISOString().split('T')[0],
            category,
            description,
            amount: disposalAmount,
            vehicleId: v.id,
            costType: 'vehicle',
            lineItems: [{
              amount: disposalAmount,
              category,
              description
            }],
            skipDuplicateCheck: true
          });
        } catch (expErr) {
          console.error('Failed to log disposal expense:', expErr);
        }
      }
      
      // Call delete with hard delete flag if requested
      await api.deleteVehicle(v.id, hardDelete);
      setDeleteModal({ isOpen: false, vehicle: null, hardDelete: false });
      onRefresh();
    } catch (e) {
      alert('Delete failed: ' + e.message);
    } finally {
      setDeletingVehicle(false);
    }
  };

  const categoryButtons = [
    { id: 'all', label: 'All', count: vehicles.length },
    { id: 'inventory', label: 'Inventory', count: inventoryVehicles.length },
    { id: 'shop_personal', label: 'Shop/Personal', count: shopPersonalVehicles.length },
    { id: 'dealer_direct', label: 'Byrider/DD', count: dealerDirectVehicles.length },
    { id: 'salvage', label: 'Salvage', count: salvageVehicles.length },
    { id: 'sold', label: 'Sold', count: soldVehicles.length, special: true },
  ];

  return (
    <div>
      {/* Category Filter Bar */}
      <div className="flex items-center gap-2 mb-3 pb-3 border-b border-neutral-200">
        <span className="text-xs text-neutral-500 font-medium mr-2">Category:</span>
        {categoryButtons.map(btn => (
          <button
            key={btn.id}
            onClick={() => {
              setCategoryFilter(btn.id);
              // When switching to/from Sold category, update showSold state
              if (btn.id === 'sold') {
                setShowSold(true);
                fetchSoldVehicles(); // Refresh sold list when clicking Sold
              } else if (categoryFilter === 'sold') {
                setShowSold(false);
              }
            }}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition ${
              categoryFilter === btn.id 
                ? btn.special ? 'bg-amber-600 text-white' : 'bg-blue-600 text-white'
                : btn.special ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
            }`}
          >
            {btn.label} ({loadingSold && btn.id === 'sold' ? '...' : btn.count})
          </button>
        ))}
      </div>
      
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <Header title="Inventory" count={filtered.length} />
        <input
          type="text"
          placeholder="Search stock, VIN, make, model..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="px-3 py-1.5 text-xs border border-neutral-200 rounded-lg w-56"
        />
        <select value={filter} onChange={e => setFilter(e.target.value)} className="text-xs border border-neutral-200 rounded px-2 py-1.5 bg-white">
          <option value="all">All Statuses</option>
          <option value="new">New</option>
          <option value="delivered">Delivered</option>
          <option value="inspection">Inspection</option>
          <option value="in_recon">In Recon</option>
          <option value="ready">Ready</option>
          <option value="frontline">Frontline</option>
        </select>
        <button 
          onClick={() => setShowFilterSidebar(true)}
          className={`px-4 py-2 text-sm font-medium rounded-lg shadow-sm relative ${
            activeFilterCount > 0 
              ? 'bg-blue-600 text-white hover:bg-blue-700' 
              : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
          }`}
        >
          Filters
          {activeFilterCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>
        <button 
          onClick={handleExportCSV}
          className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 shadow-sm"
        >
          📊 Export CSV
        </button>
        <button 
          onClick={printInventory}
          className="px-4 py-2 bg-neutral-700 text-white text-sm font-medium rounded-lg hover:bg-neutral-800 shadow-sm"
        >
          🖨 Print
        </button>
        <button 
          onClick={printInvAdminTaskList}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 shadow-sm flex items-center gap-2"
        >
          📋 Admin Task List {invAdminTodos.filter(t => !t.done).length > 0 && <span className="bg-white text-blue-700 px-1.5 py-0.5 rounded text-xs font-bold">{invAdminTodos.filter(t => !t.done).length}</span>}
        </button>
        {showFinancials && (
          <button 
            onClick={() => setShowTotalInvested(!showTotalInvested)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition ${
              showTotalInvested 
                ? 'bg-blue-600 text-white' 
                : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
            }`}
            title={showTotalInvested ? 'Showing Cost + PRC' : 'Showing Hard Costs Only'}
          >
            {showTotalInvested ? 'Cost + PRC' : 'Hard Costs'}
          </button>
        )}
        {isAdmin && <button onClick={() => setShowQuickAdd(true)} className="ml-auto px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 shadow-sm">⚡ Quick Add</button>}
        {canEdit && <button onClick={() => onEdit({})} className={`${isAdmin ? '' : 'ml-auto '}px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 shadow-sm`}>+ Add Vehicle</button>}
      </div>
      <div className="overflow-x-auto -mx-2 md:mx-0">
      <table className="w-full text-sm min-w-[900px]">
        <thead>
          <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200">
            <th className="pb-2 font-medium"><button type="button" onClick={() => toggleSort('stock')} className="hover:text-blue-600 cursor-pointer">Stock {sortBy === 'stock' ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</button></th>
            <th className="pb-2 font-medium"><button type="button" onClick={() => toggleSort('vehicle')} className="hover:text-blue-600 cursor-pointer">Vehicle {sortBy === 'vehicle' ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</button></th>
            <th className="pb-2 font-medium"><button type="button" onClick={() => toggleSort('vin')} className="hover:text-blue-600 cursor-pointer">VIN {sortBy === 'vin' ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</button></th>
            <th className="pb-2 font-medium"><button type="button" onClick={() => toggleSort('odometer')} className="hover:text-blue-600 cursor-pointer">Odometer {sortBy === 'odometer' ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</button></th>
            <th className="pb-2 font-medium"><button type="button" onClick={() => toggleSort('color')} className="hover:text-blue-600 cursor-pointer">Color {sortBy === 'color' ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</button></th>
            <th className="pb-2 font-medium"><button type="button" onClick={() => toggleSort('status')} className="hover:text-blue-600 cursor-pointer">Status {sortBy === 'status' ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</button></th>
            <th className="pb-2 font-medium">Exit Strategy</th>
            <th className="pb-2 font-medium text-right"><button type="button" onClick={() => toggleSort('price')} className="hover:text-blue-600 cursor-pointer">Est. Sale {sortBy === 'price' ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</button></th>
            {showFinancials && <th className="pb-2 font-medium text-right"><button type="button" onClick={() => toggleSort('cost')} className="hover:text-blue-600 cursor-pointer">Cost {sortBy === 'cost' ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</button></th>}
            {showFinancials && <th className="pb-2 font-medium text-right" title="Potential Recon Cost from RO estimates">PRC</th>}
            {showFinancials && <th className="pb-2 font-medium text-right"><button type="button" onClick={() => toggleSort('profit')} className="hover:text-blue-600 cursor-pointer">Est. Profit {sortBy === 'profit' ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</button></th>}
            <th className="pb-2 font-medium text-right"><button type="button" onClick={() => toggleSort('days')} className="hover:text-blue-600 cursor-pointer">Days {sortBy === 'days' ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</button></th>
            <th className="pb-2 font-medium">Files</th>
            <th className="pb-2 font-medium">QR</th>
            <th className="pb-2 font-medium">Notes</th>
            <th className="pb-2 font-medium">Todo</th>
            {canEdit && <th className="pb-2 font-medium"></th>}
          </tr>
        </thead>
        <tbody>
        {sorted.map(v => {
          const estSale = parseFloat(v.estimatedSale) || 0;
          const prc = parseFloat(v.potentialReconCost) || parseFloat(v.reconCost) || 0;
          const hardCost = getHardCosts(v);
          const displayCost = showTotalInvested ? (hardCost + prc) : hardCost;
          const estProfit = estSale - displayCost;
          const isDealerDirect = v.endUse === 'dealer_direct';
          const isSold = (v.status || '').toLowerCase() === 'sold';
          const missingAuctionLight = !v.auctionLight;
          const missingArbDeadline = !v.arbDeadline && !isSold;
          const hasMissingFields = missingAuctionLight || missingArbDeadline;
          // Sold vehicles get distinct gray styling, Dealer Direct gets red, missing fields get amber
          const rowClass = isSold
            ? 'border-b border-neutral-300 bg-neutral-100 hover:bg-neutral-200 text-neutral-500'
            : isDealerDirect 
              ? 'border-b border-red-200 bg-red-50 hover:bg-red-100' 
              : hasMissingFields
                ? 'border-b border-amber-200 bg-amber-50/50 hover:bg-amber-100'
                : 'border-b border-neutral-100 hover:bg-neutral-100';
          return (
            <tr key={v.id} className={rowClass}>
              <td className="py-1.5 font-mono text-xs cursor-pointer" onClick={() => onSelect(v)}>
                {isSold && <span className="mr-1 bg-neutral-500 text-white text-[10px] px-1 rounded">SOLD</span>}
                {isDealerDirect && !isSold && <span className="mr-1" title="Dealer Direct - Thorough Inspection Required">⚠️</span>}
                {hasMissingFields && !isDealerDirect && !isSold && (
                  <span className="mr-1" title={`Missing: ${missingAuctionLight ? 'Auction Light' : ''}${missingAuctionLight && missingArbDeadline ? ', ' : ''}${missingArbDeadline ? 'ARB Deadline' : ''}`}>⚡</span>
                )}
                {v.stockNumber}
                {/* Title Status Light */}
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full ml-1.5 cursor-pointer relative group align-middle"
                  style={{ backgroundColor: v.titleStatus === 'in_office' ? '#22c55e' : (v.titleStatus === 'at_auction' || v.titleStatus === 'in_transit') ? '#eab308' : '#ef4444' }}
                  onClick={(e) => { e.stopPropagation(); window.open(window.location.origin + window.location.pathname + '#titles', '_blank'); }}
                >
                  <span className="absolute bottom-full left-0 mb-2 px-2 py-1 bg-neutral-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20 whitespace-nowrap">
                    <span className="font-semibold">Title: {(v.titleStatus || 'absent').replace(/_/g, ' ')}</span>
                    {v.titleNotes && <><br/><span className="text-neutral-300">{v.titleNotes}</span></>}
                  </span>
                </span>
              </td>
              <td className="py-1.5 cursor-pointer" onClick={() => onSelect(v)}>{v.year} {v.make} {v.model}</td>
              <td className="py-1.5"><VinDisplay vin={v.vin} /></td>
              <td className="py-1.5 text-xs">{v.mileage ? `${Number(v.mileage).toLocaleString()} miles` : '-'}</td>
              <td className="py-1.5 text-xs">{v.color || '-'}</td>
              <td className="py-1.5 text-xs">{isSold ? <span className="text-neutral-600 font-medium">Sold</span> : v.status}</td>
              <td className="py-1.5 text-xs">
                <select
                  value={v.endUse || 'retail'}
                  onChange={(e) => { e.stopPropagation(); handleExitStrategyChange(v.id, e.target.value); }}
                  onClick={(e) => e.stopPropagation()}
                  disabled={updatingExitStrategy === v.id}
                  className={`text-xs border rounded px-1 py-0.5 ${
                    v.endUse === 'dealer_direct' ? 'bg-red-100 border-red-300 text-red-700' :
                    v.endUse === 'wholesale' ? 'bg-blue-100 border-blue-300 text-blue-700' :
                    v.endUse === 'copart' || v.endUse === 'iaai' ? 'bg-amber-100 border-amber-300 text-amber-700' :
                    v.endUse === 'shop_personal' ? 'bg-purple-100 border-purple-300 text-purple-700' :
                    'bg-green-100 border-green-300 text-green-700'
                  } ${updatingExitStrategy === v.id ? 'opacity-50' : ''}`}
                >
                  <option value="retail">Retail</option>
                  <option value="wholesale">Wholesale</option>
                  <option value="dealer_direct">Byrider/DD</option>
                  <option value="shop_personal">Shop/Personal</option>
                  <option value="copart">Copart</option>
                  <option value="iaai">IAAI</option>
                  <option value="salvage">Salvage</option>
                  <option value="parts_only">Parts Only</option>
                </select>
              </td>
              <td className="py-1.5 text-right font-mono text-xs text-green-700">{estSale ? `$${estSale.toLocaleString()}` : '-'}</td>
              {showFinancials && (
                <td className="py-1.5 text-right font-mono text-xs">{displayCost ? `$${displayCost.toLocaleString()}` : '-'}</td>
              )}
              {showFinancials && (
                <td className="py-1.5 text-right font-mono text-xs text-purple-600" title="Potential Recon Cost from RO estimates">
                  {(v.potentialReconCost || v.reconCost) ? `$${(parseFloat(v.potentialReconCost) || parseFloat(v.reconCost) || 0).toLocaleString()}` : '-'}
                </td>
              )}
              {showFinancials && (
                <td className={`py-1.5 text-right font-mono text-xs ${estProfit >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                  {estSale ? `$${estProfit.toLocaleString()}` : '-'}
                </td>
              )}
              <td className="py-1.5 text-xs pl-4 text-right">{getDays(v.purchaseDate)}</td>
              <td className="py-1.5 text-center">
                <VehicleFilesButton vehicleId={v.id} vehicle={v} className="text-xs" />
              </td>
              <td className="py-1.5 text-center">
                <QRButton vehicle={v} className="text-xs" />
              </td>
              <td className="py-1.5 text-center">
                <button
                  onClick={(e) => { e.stopPropagation(); setNotesText(v.notes || ''); setNotesModal({ isOpen: true, vehicle: v }); }}
                  className={`text-lg hover:scale-110 transition-transform ${v.notes ? 'text-yellow-500' : 'text-neutral-300 hover:text-yellow-500'}`}
                  title={v.notes ? `Notes: ${v.notes.substring(0, 100)}${v.notes.length > 100 ? '...' : ''}` : 'Add notes'}
                >
                  📝
                </button>
              </td>
              <td className="py-1.5 text-center">
                <button
                  onClick={(e) => { e.stopPropagation(); setInvTodoVehicle(v); }}
                  className="relative text-xs px-2 py-1 bg-neutral-100 text-neutral-600 rounded hover:bg-neutral-200"
                  title="Admin tasks"
                >
                  📋
                  {invGetTodoCount(v.id) > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-bold">{invGetTodoCount(v.id)}</span>
                  )}
                </button>
              </td>
              <td className="py-1.5 text-right text-xs">
                {onStartInspection && (
                  <button 
                    onClick={(e) => { e.stopPropagation(); onStartInspection(v.id); }} 
                    className="text-blue-600 hover:text-blue-800 mr-1 font-medium"
                    title="Start Inspection"
                  >
                    🔧
                  </button>
                )}
                {onCreateRO && (
                  <button 
                    onClick={(e) => { e.stopPropagation(); onCreateRO(v.id); }} 
                    className="text-green-600 hover:text-green-800 mr-2 font-medium"
                    title="Create/View RO"
                  >
                    📋
                  </button>
                )}
                {/* Mark Delivered button - only show if not delivered */}
                {v.transportStatus !== 'delivered' && (
                  <button 
                    onClick={(e) => { e.stopPropagation(); setTransportCost(''); setUnknownCost(false); setAuctionTransported(false); setDeliveredModal({ isOpen: true, vehicle: v }); }} 
                    className="text-purple-600 hover:text-purple-800 mr-2 font-medium text-xs"
                    title="Mark as Delivered"
                  >
                    Mark Del
                  </button>
                )}
                {/* Mark Inspected button - only show if needs inspection */}
                {(v.reconStatus === 'needs_inspection' || v.needsFurtherReview) && (
                  <button 
                    onClick={async (e) => { 
                      e.stopPropagation(); 
                      if (window.confirm('Mark this vehicle as inspected?')) {
                        try {
                          await api.updateVehicle(v.id, { reconStatus: 'inspected', needsFurtherReview: false, inspectionStatus: 'completed' });
                          onRefresh();
                        } catch (err) {
                          alert('Failed to update: ' + err.message);
                        }
                      }
                    }} 
                    className="text-teal-600 hover:text-teal-800 mr-2 font-medium text-xs"
                    title="Mark Inspection Complete"
                  >
                    Mark Insp
                  </button>
                )}
                {/* Quick Edit button for ALL vehicles */}
                {onQuickEdit && v.stockNumber && (
                  <button 
                    onClick={(e) => { e.stopPropagation(); onQuickEdit(v); }} 
                    className="text-orange-600 hover:text-orange-800 mr-2 font-medium"
                    title="Quick Edit"
                  >
                    ⚡
                  </button>
                )}
                {/* Customer Proposal - price quote with Deal Desk math */}
                {showFinancials && !limitedView && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setProposalVehicle(v); }}
                    className="text-indigo-600 hover:text-indigo-800 mr-2 font-medium text-xs"
                    title="Customer Proposal"
                  >
                    Proposal
                  </button>
                )}
                {canEdit && (
                  <>
                    <button onClick={() => onEdit(v)} className="text-neutral-500 hover:text-neutral-800 mr-2">edit</button>
                    <button onClick={() => handleDelete(v)} className="text-red-500 hover:text-red-700">del</button>
                  </>
                )}
              </td>
            </tr>
          );
        })}
        </tbody>
      </table>
      </div>
      {filtered.length === 0 && <Empty text="No vehicles" />}

      {/* Salvage/Copart Vehicles Section */}
      {salvageVehicles.length > 0 && (
        <div className="mt-6 border-t border-neutral-200 pt-4">
          <button
            onClick={() => setShowSalvageVehicles(!showSalvageVehicles)}
            className="flex items-center gap-2 text-sm font-medium text-neutral-600 hover:text-neutral-800"
          >
            <span className={`transform transition-transform ${showSalvageVehicles ? 'rotate-90' : ''}`}>▶</span>
            Salvage/Copart Vehicles ({salvageVehicles.length})
            <span className="text-xs text-amber-600 ml-2">(Copart/IAAI)</span>
          </button>

          {showSalvageVehicles && (
            <div className="mt-3 bg-amber-50 rounded-lg p-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-neutral-500 border-b border-amber-200">
                    <th className="py-2">Stock</th>
                    <th>Vehicle</th>
                    <th>VIN</th>
                    <th>Odometer</th>
                    <th>Destination</th>
                    {showFinancials && <th className="text-right">Est. Sale</th>}
                    {showFinancials && <th className="text-right">Cost</th>}
                    <th className="text-right">Days</th>
                    <th>Files</th>
                    {canEdit && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {salvageVehicles.map(v => {
                    const estSale = parseFloat(v.estimatedSale) || 0;
                    const totalCost = getTotalCost(v);
                    return (
                      <tr key={v.id} className="border-b border-amber-100 hover:bg-amber-100">
                        <td className="py-2 font-mono text-xs cursor-pointer" onClick={() => onSelect(v)}>{v.stockNumber}</td>
                        <td className="py-2 cursor-pointer" onClick={() => onSelect(v)}>{v.year} {v.make} {v.model}</td>
                        <td className="py-2"><VinDisplay vin={v.vin} /></td>
                        <td className="py-2 text-xs">{v.mileage ? `${Number(v.mileage).toLocaleString()} mi` : '-'}</td>
                        <td className="py-2 text-xs">
                          <span className="bg-amber-200 text-amber-800 px-2 py-0.5 rounded text-xs">
                            {v.endUse === 'copart' ? 'Copart' : 'IAAI'}
                          </span>
                        </td>
                        {showFinancials && (
                          <td className="py-2 text-right font-mono text-xs text-green-700">{estSale ? `$${estSale.toLocaleString()}` : '-'}</td>
                        )}
                        {showFinancials && (
                          <td className="py-2 text-right font-mono text-xs">{totalCost ? `$${totalCost.toLocaleString()}` : '-'}</td>
                        )}
                        <td className="py-2 text-xs text-right">{getDays(v.purchaseDate)}</td>
                        <td className="py-2 text-center">
                          <VehicleFilesButton vehicleId={v.id} vehicle={v} className="text-xs" />
                        </td>
                        {canEdit && (
                          <td className="py-2 text-right text-xs">
                            <button onClick={() => onEdit(v)} className="text-neutral-500 hover:text-neutral-800 mr-2">edit</button>
                            <button onClick={() => handleDelete(v)} className="text-red-500 hover:text-red-700">del</button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Shop/Personal Vehicles Section - Admin Only */}
      {isAdmin && (
        <div className="mt-6 border-t border-neutral-200 pt-4">
          <button
            onClick={() => setShowShopVehicles(!showShopVehicles)}
            className="flex items-center gap-2 text-sm font-medium text-neutral-600 hover:text-neutral-800"
          >
            <span className={`transform transition-transform ${showShopVehicles ? 'rotate-90' : ''}`}>▶</span>
            Shop/Personal Vehicles ({shopVehicles.length})
            <span className="text-xs text-neutral-400 ml-2">(Internal Use Only)</span>
          </button>

          {showShopVehicles && (
            <div className="mt-3 bg-neutral-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-neutral-700">Shop Vehicles</span>
                <button
                  onClick={() => setShowShopForm(!showShopForm)}
                  className="text-xs bg-neutral-700 text-white px-3 py-1 rounded hover:bg-neutral-600"
                >
                  + Add Shop Vehicle
                </button>
              </div>

              {/* Add Shop Vehicle Form */}
              {showShopForm && (
                <form onSubmit={handleAddShopVehicle} className="bg-white rounded-lg p-4 mb-4 border border-neutral-200">
                  <div className="flex gap-2 mb-3">
                    <input type="text" placeholder="Enter VIN to auto-fill" value={shopForm.vin} onChange={e => setShopForm({...shopForm, vin: e.target.value.toUpperCase()})} className="flex-1 px-2 py-1.5 text-sm border rounded font-mono" maxLength={17} />
                    <button type="button" onClick={() => decodeShopVin(shopForm.vin)} disabled={shopDecoding || shopForm.vin.length !== 17} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                      {shopDecoding ? 'Decoding...' : 'Decode VIN'}
                    </button>
                  </div>
                  <div className="grid grid-cols-5 gap-3 mb-3">
                    <input type="text" placeholder="Year *" value={shopForm.year} onChange={e => setShopForm({...shopForm, year: e.target.value})} className="px-2 py-1.5 text-sm border rounded" required />
                    <input type="text" placeholder="Make *" value={shopForm.make} onChange={e => setShopForm({...shopForm, make: e.target.value})} className="px-2 py-1.5 text-sm border rounded" required />
                    <input type="text" placeholder="Model *" value={shopForm.model} onChange={e => setShopForm({...shopForm, model: e.target.value})} className="px-2 py-1.5 text-sm border rounded" required />
                    <input type="text" placeholder="Trim" value={shopForm.trim} onChange={e => setShopForm({...shopForm, trim: e.target.value})} className="px-2 py-1.5 text-sm border rounded" />
                    <input type="text" placeholder="Color" value={shopForm.color} onChange={e => setShopForm({...shopForm, color: e.target.value})} className="px-2 py-1.5 text-sm border rounded" />
                  </div>
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <input type="number" placeholder="Mileage" value={shopForm.mileage} onChange={e => setShopForm({...shopForm, mileage: e.target.value})} className="px-2 py-1.5 text-sm border rounded" />
                    <input type="number" placeholder="Purchase Price" value={shopForm.purchasePrice} onChange={e => setShopForm({...shopForm, purchasePrice: e.target.value})} className="px-2 py-1.5 text-sm border rounded" />
                    <input type="number" placeholder="Est. Value" value={shopForm.estimatedSale} onChange={e => setShopForm({...shopForm, estimatedSale: e.target.value})} className="px-2 py-1.5 text-sm border rounded" />
                  </div>
                  <textarea placeholder="Notes / What it needs" value={shopForm.whatItNeeds} onChange={e => setShopForm({...shopForm, whatItNeeds: e.target.value})} className="w-full px-2 py-1.5 text-sm border rounded mb-3" rows={2} />
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setShowShopForm(false)} className="px-3 py-1.5 text-sm text-neutral-600">Cancel</button>
                    <button type="submit" className="px-3 py-1.5 text-sm bg-neutral-800 text-white rounded">Add Vehicle</button>
                  </div>
                </form>
              )}

              {/* Shop Vehicles List */}
              {shopVehicles.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-neutral-500 border-b">
                      <th className="py-2">Stock</th>
                      <th>Vehicle</th>
                      <th>VIN</th>
                      <th>Mileage</th>
                      <th className="text-right">Value</th>
                      <th className="text-right">Open ROs</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {shopVehicles.map(v => (
                      <tr key={v.id} className="border-b border-neutral-100 hover:bg-white">
                        <td className="py-2 font-mono text-xs">{v.stockNumber}</td>
                        <td className="py-2">{v.year} {v.make} {v.model}</td>
                        <td className="py-2"><VinDisplay vin={v.vin} /></td>
                        <td className="py-2 text-xs">{v.mileage ? `${Number(v.mileage).toLocaleString()}` : '-'}</td>
                        <td className="py-2 text-right font-mono text-xs">{v.estimatedSale ? `$${Number(v.estimatedSale).toLocaleString()}` : '-'}</td>
                        <td className="py-2 text-right text-xs">{v.openRos > 0 ? <button onClick={() => onCreateRO && onCreateRO(v.id)} className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded hover:bg-blue-200 cursor-pointer transition-colors">{v.openRos} RO{v.openRos > 1 ? 's' : ''}</button> : '-'}</td>
                        <td className="py-2 text-right space-x-2">
                          <button onClick={() => onSelect(v)} className="text-xs text-blue-600 hover:underline">View</button>
                          {onCreateRO && (
                            <button onClick={() => onCreateRO(v.id)} className="text-xs text-green-600 hover:underline">{v.openRos > 0 ? 'View RO' : 'Create RO'}</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center text-neutral-400 text-sm py-4">No shop vehicles yet</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Customer Proposal Modal */}
      {proposalVehicle && (
        <CustomerProposalModal
          vehicle={proposalVehicle}
          showProfit={showFinancials}
          onClose={() => setProposalVehicle(null)}
        />
      )}

      {/* Notes Modal */}
      {notesModal.isOpen && notesModal.vehicle && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setNotesModal({ isOpen: false, vehicle: null })}>
          <div className="bg-white rounded-lg p-5 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-lg">📝 Notes</h3>
              <button onClick={() => setNotesModal({ isOpen: false, vehicle: null })} className="text-neutral-400 hover:text-neutral-600 text-xl">×</button>
            </div>
            <p className="text-sm text-neutral-500 mb-3">
              {notesModal.vehicle.year} {notesModal.vehicle.make} {notesModal.vehicle.model} - {notesModal.vehicle.stockNumber}
            </p>
            <textarea
              value={notesText}
              onChange={(e) => setNotesText(e.target.value)}
              placeholder="Add notes about this vehicle..."
              className="w-full h-32 px-3 py-2 border border-neutral-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setNotesModal({ isOpen: false, vehicle: null })} className="px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-100 rounded-lg">
                Cancel
              </button>
              <button onClick={handleSaveNotes} disabled={savingNotes} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {savingNotes ? 'Saving...' : 'Save Notes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Todo Modal */}
      {invTodoVehicle && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setInvTodoVehicle(null)}>
          <div className="bg-white w-full max-w-md rounded-xl shadow-xl max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b flex justify-between items-center bg-neutral-50">
              <div>
                <h2 className="font-semibold text-base">Admin Tasks</h2>
                <p className="text-xs text-neutral-500">{invTodoVehicle.stockNumber} — {invTodoVehicle.year} {invTodoVehicle.make} {invTodoVehicle.model}</p>
              </div>
              <button onClick={() => setInvTodoVehicle(null)} className="text-neutral-400 hover:text-neutral-600 text-2xl px-2">×</button>
            </div>
            <div className="p-4 flex gap-2">
              <input
                type="text"
                id="inv-todo-input"
                onKeyDown={(e) => { if (e.key === 'Enter' && e.target.value.trim()) { invAddTodo(invTodoVehicle.id, e.target.value); e.target.value = ''; } }}
                placeholder="Add a task..."
                className="flex-1 px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              <button
                onClick={() => { const inp = document.getElementById('inv-todo-input'); if (inp && inp.value.trim()) { invAddTodo(invTodoVehicle.id, inp.value); inp.value = ''; } }}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
              >Add</button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 pb-4">
              {invAdminTodos.filter(t => t.vehicleId === invTodoVehicle.id).length === 0 && (
                <div className="text-center py-6 text-neutral-400 text-sm">No tasks yet. Add one above.</div>
              )}
              {invAdminTodos.filter(t => t.vehicleId === invTodoVehicle.id && !t.done).map(t => (
                <div key={t.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-neutral-50 group">
                  <input type="checkbox" checked={false} onChange={() => invToggleTodo(t.id)} className="rounded text-blue-600 flex-shrink-0" />
                  <span className="flex-1 text-sm text-neutral-800">{t.text}</span>
                  <button onClick={() => invDeleteTodo(t.id)} className="text-neutral-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-sm">×</button>
                </div>
              ))}
              {invAdminTodos.filter(t => t.vehicleId === invTodoVehicle.id && t.done).length > 0 && (
                <div className="mt-2">
                  <div className="text-[10px] text-neutral-400 uppercase tracking-wider font-semibold mb-1">Completed</div>
                  {invAdminTodos.filter(t => t.vehicleId === invTodoVehicle.id && t.done).map(t => (
                    <div key={t.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-neutral-50 group">
                      <input type="checkbox" checked={true} onChange={() => invToggleTodo(t.id)} className="rounded text-green-600 flex-shrink-0" />
                      <span className="flex-1 text-sm text-neutral-400 line-through">{t.text}</span>
                      <button onClick={() => invDeleteTodo(t.id)} className="text-neutral-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity text-sm">×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mark Delivered Modal */}
      {deliveredModal.isOpen && deliveredModal.vehicle && (() => {
        const v = deliveredModal.vehicle;
        const isCopart = v.exitStrategy === 'Copart Direct' || v.endUse === 'copart';
        
        return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDeliveredModal({ isOpen: false, vehicle: null })}>
          <div className="bg-white rounded-lg p-5 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-lg">{isCopart ? 'Confirm Copart Drop-off' : 'Mark as Delivered'}</h3>
              <button onClick={() => setDeliveredModal({ isOpen: false, vehicle: null })} className="text-neutral-400 hover:text-neutral-600 text-xl">×</button>
            </div>
            <p className="text-sm text-neutral-500 mb-4">
              {v.year} {v.make} {v.model} - {v.stockNumber}
            </p>
            
            {isCopart ? (
              /* Copart - just confirm drop-off */
              <div className="bg-neutral-50 p-3 rounded-lg mb-4">
                <p className="text-sm text-neutral-600">Confirm this vehicle has been dropped off at Copart. No transport cost will be recorded.</p>
              </div>
            ) : (
              /* Regular transport - show cost options */
              <div className="space-y-4">
                {/* Auction Transported option */}
                <label className="flex items-center gap-2 text-sm cursor-pointer p-3 border rounded-lg hover:bg-neutral-50">
                  <input
                    type="checkbox"
                    checked={auctionTransported}
                    onChange={(e) => { setAuctionTransported(e.target.checked); if (e.target.checked) setUnknownCost(false); }}
                    className="w-4 h-4 text-blue-600 rounded border-neutral-300"
                  />
                  <div>
                    <span className="font-medium">Auction Transported</span>
                    <p className="text-xs text-neutral-500">Transport was handled by the auction - cost will be added to purchase price</p>
                  </div>
                </label>
                
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    {auctionTransported ? 'Transport Cost to Add to Purchase Price ($)' : 'Transport Cost ($)'}
                  </label>
                  <input
                    type="number"
                    value={transportCost}
                    onChange={(e) => setTransportCost(e.target.value)}
                    disabled={unknownCost}
                    placeholder="Enter transport cost..."
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-neutral-100 disabled:text-neutral-400"
                  />
                  {auctionTransported && transportCost && (
                    <p className="text-xs text-blue-600 mt-1">
                      New purchase price will be: ${((parseFloat(v.purchasePrice) || 0) + parseFloat(transportCost)).toLocaleString()}
                    </p>
                  )}
                </div>
                
                {!auctionTransported && (
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={unknownCost}
                      onChange={(e) => setUnknownCost(e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded border-neutral-300"
                    />
                    <span>I don't know the transport cost</span>
                  </label>
                )}
                
                {unknownCost && (
                  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                    A task will be created for Alex to confirm the transport cost. If not resolved in 2 days, admin will be notified.
                  </p>
                )}
              </div>
            )}
            
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setDeliveredModal({ isOpen: false, vehicle: null })} className="px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-100 rounded-lg">
                Cancel
              </button>
              <button 
                onClick={handleMarkDelivered} 
                disabled={savingDelivered || (!isCopart && !transportCost && !unknownCost)} 
                className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {savingDelivered ? 'Saving...' : isCopart ? 'Confirm Drop-off' : 'Mark Delivered'}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Delete Vehicle Modal */}
      {deleteModal.isOpen && deleteModal.vehicle && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDeleteModal({ isOpen: false, vehicle: null })}>
          <div className="bg-white rounded-lg p-5 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-lg text-red-600">Delete Vehicle</h3>
              <button onClick={() => setDeleteModal({ isOpen: false, vehicle: null })} className="text-neutral-400 hover:text-neutral-600 text-xl">×</button>
            </div>
            <p className="text-sm text-neutral-600 mb-2">
              Are you sure you want to delete this vehicle?
            </p>
            <div className="bg-neutral-50 p-3 rounded-lg mb-4">
              <p className="font-medium">{deleteModal.vehicle.stockNumber}</p>
              <p className="text-sm text-neutral-500">{deleteModal.vehicle.year} {deleteModal.vehicle.make} {deleteModal.vehicle.model}</p>
              {deleteModal.vehicle.purchasePrice > 0 && (
                <p className="text-sm text-amber-600 mt-1">Purchase Price: ${parseFloat(deleteModal.vehicle.purchasePrice).toLocaleString()}</p>
              )}
            </div>
            
            {/* Soft Delete Section */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-3">
              <p className="text-sm font-medium text-blue-800 mb-1">Archive (Recommended)</p>
              <p className="text-xs text-blue-600 mb-2">Hides vehicle from inventory but preserves all records, notes, and accounting history.</p>
              <div className="mb-2">
                <label className="block text-xs font-medium text-blue-700 mb-1">Disposal/Removal Costs ($)</label>
                <input
                  type="number"
                  value={disposalCost}
                  onChange={(e) => setDisposalCost(e.target.value)}
                  placeholder="Enter any towing, auction fees, etc..."
                  className="w-full px-2 py-1.5 border border-blue-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <button 
                onClick={() => handleConfirmDelete(false)} 
                disabled={deletingVehicle} 
                className="w-full px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {deletingVehicle ? 'Archiving...' : 'Archive Vehicle'}
              </button>
            </div>
            
            {/* Hard Delete Section */}
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm font-medium text-red-800 mb-1">Permanent Delete</p>
              <p className="text-xs text-red-600 mb-2">Completely removes vehicle and ALL related records. Use for test/sample data only. Cannot be undone!</p>
              <button 
                onClick={() => {
                  if (window.confirm('PERMANENT DELETE: This will remove ALL records for this vehicle including notes, files, ROs, and accounting. This CANNOT be undone. Are you sure?')) {
                    handleConfirmDelete(true);
                  }
                }} 
                disabled={deletingVehicle} 
                className="w-full px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deletingVehicle ? 'Deleting...' : 'Permanently Delete'}
              </button>
            </div>
            
            <div className="flex justify-end mt-4">
              <button onClick={() => setDeleteModal({ isOpen: false, vehicle: null })} className="px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-100 rounded-lg">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Add Vehicle Modal (Admin Only) */}
      {showQuickAdd && (
        <QuickAddVehicleModal
          onClose={() => setShowQuickAdd(false)}
          vehicles={vehicles}
          soldVehicles={soldVehicles}
          onRefresh={onRefresh}
        />
      )}

      {/* Filter Sidebar */}
      {showFilterSidebar && (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={() => setShowFilterSidebar(false)}>
          <div className="absolute inset-0 bg-black/30" />
          <div className="relative w-80 bg-white h-full shadow-xl overflow-y-auto" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <h2 className="font-bold text-lg">Advanced Filters</h2>
              <button onClick={() => setShowFilterSidebar(false)} className="text-neutral-400 hover:text-neutral-600 text-2xl">×</button>
            </div>

            <div className="p-4 space-y-6">
              {/* Title Status */}
              <div>
                <h3 className="font-semibold text-sm mb-2">Title Status</h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="titleInOffice" checked={advancedFilters.titleInOffice === null} onChange={() => setAdvancedFilters(f => ({...f, titleInOffice: null}))} />
                    Any
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="titleInOffice" checked={advancedFilters.titleInOffice === true} onChange={() => setAdvancedFilters(f => ({...f, titleInOffice: true}))} />
                    In Office
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="titleInOffice" checked={advancedFilters.titleInOffice === false} onChange={() => setAdvancedFilters(f => ({...f, titleInOffice: false}))} />
                    NOT In Office
                  </label>
                </div>
              </div>

              {/* Buyer Filter */}
              <div>
                <h3 className="font-semibold text-sm mb-2">Buyer</h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="buyer" checked={buyerFilter === 'all'} onChange={() => setBuyerFilter('all')} />
                    All
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="buyer" checked={buyerFilter === 'chris'} onChange={() => setBuyerFilter('chris')} />
                    Chris
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="buyer" checked={buyerFilter === 'alex'} onChange={() => setBuyerFilter('alex')} />
                    Alex
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="buyer" checked={buyerFilter === 'aux'} onChange={() => setBuyerFilter('aux')} />
                    AUX
                  </label>
                </div>
              </div>

              {/* Transport/Delivery */}
              <div>
                <h3 className="font-semibold text-sm mb-2">Transport</h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="delivered" checked={advancedFilters.delivered === null} onChange={() => setAdvancedFilters(f => ({...f, delivered: null}))} />
                    Any
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="delivered" checked={advancedFilters.delivered === true} onChange={() => setAdvancedFilters(f => ({...f, delivered: true}))} />
                    Delivered
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="delivered" checked={advancedFilters.delivered === false} onChange={() => setAdvancedFilters(f => ({...f, delivered: false}))} />
                    NOT Delivered
                  </label>
                </div>
              </div>

              {/* Inspection */}
              <div>
                <h3 className="font-semibold text-sm mb-2">Inspection</h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="inspected" checked={advancedFilters.inspected === null} onChange={() => setAdvancedFilters(f => ({...f, inspected: null}))} />
                    Any
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="inspected" checked={advancedFilters.inspected === true} onChange={() => setAdvancedFilters(f => ({...f, inspected: true}))} />
                    Inspected
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="inspected" checked={advancedFilters.inspected === false} onChange={() => setAdvancedFilters(f => ({...f, inspected: false}))} />
                    NOT Inspected
                  </label>
                </div>
              </div>

              {/* Repair Orders */}
              <div>
                <h3 className="font-semibold text-sm mb-2">Repair Orders</h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="hasOpenRO" checked={advancedFilters.hasOpenRO === null} onChange={() => setAdvancedFilters(f => ({...f, hasOpenRO: null}))} />
                    Any
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="hasOpenRO" checked={advancedFilters.hasOpenRO === true} onChange={() => setAdvancedFilters(f => ({...f, hasOpenRO: true}))} />
                    Has Open RO
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="hasOpenRO" checked={advancedFilters.hasOpenRO === false} onChange={() => setAdvancedFilters(f => ({...f, hasOpenRO: false}))} />
                    No Open RO
                  </label>
                </div>
              </div>

              {/* ARB */}
              <div>
                <h3 className="font-semibold text-sm mb-2">Arbitration</h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="inArbitration" checked={advancedFilters.inArbitration === null} onChange={() => setAdvancedFilters(f => ({...f, inArbitration: null}))} />
                    Any
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="inArbitration" checked={advancedFilters.inArbitration === true} onChange={() => setAdvancedFilters(f => ({...f, inArbitration: true}))} />
                    In Arbitration
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="inArbitration" checked={advancedFilters.inArbitration === false} onChange={() => setAdvancedFilters(f => ({...f, inArbitration: false}))} />
                    No ARB Issues
                  </label>
                </div>
              </div>

              {/* Payment */}
              <div>
                <h3 className="font-semibold text-sm mb-2">Payment</h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="paymentCleared" checked={advancedFilters.paymentCleared === null} onChange={() => setAdvancedFilters(f => ({...f, paymentCleared: null}))} />
                    Any
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="paymentCleared" checked={advancedFilters.paymentCleared === true} onChange={() => setAdvancedFilters(f => ({...f, paymentCleared: true}))} />
                    Payment Cleared
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="paymentCleared" checked={advancedFilters.paymentCleared === false} onChange={() => setAdvancedFilters(f => ({...f, paymentCleared: false}))} />
                    Payment NOT Cleared
                  </label>
                </div>
              </div>

              {/* Flooring */}
              <div>
                <h3 className="font-semibold text-sm mb-2">Flooring</h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="isFloored" checked={advancedFilters.isFloored === null} onChange={() => setAdvancedFilters(f => ({...f, isFloored: null}))} />
                    Any
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="isFloored" checked={advancedFilters.isFloored === true} onChange={() => setAdvancedFilters(f => ({...f, isFloored: true}))} />
                    Floored
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="isFloored" checked={advancedFilters.isFloored === false} onChange={() => setAdvancedFilters(f => ({...f, isFloored: false}))} />
                    Not Floored
                  </label>
                </div>
              </div>

              {/* Notes */}
              <div>
                <h3 className="font-semibold text-sm mb-2">Notes</h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="hasNotes" checked={advancedFilters.hasNotes === null} onChange={() => setAdvancedFilters(f => ({...f, hasNotes: null}))} />
                    Any
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="hasNotes" checked={advancedFilters.hasNotes === true} onChange={() => setAdvancedFilters(f => ({...f, hasNotes: true}))} />
                    Has Notes
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="hasNotes" checked={advancedFilters.hasNotes === false} onChange={() => setAdvancedFilters(f => ({...f, hasNotes: false}))} />
                    No Notes
                  </label>
                </div>
              </div>

              {/* Days on Lot */}
              <div>
                <h3 className="font-semibold text-sm mb-2">Days on Lot</h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="daysOnLot" checked={advancedFilters.daysOnLot === null} onChange={() => setAdvancedFilters(f => ({...f, daysOnLot: null}))} />
                    Any
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="daysOnLot" checked={advancedFilters.daysOnLot === '0-30'} onChange={() => setAdvancedFilters(f => ({...f, daysOnLot: '0-30'}))} />
                    0-30 days
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="daysOnLot" checked={advancedFilters.daysOnLot === '30-60'} onChange={() => setAdvancedFilters(f => ({...f, daysOnLot: '30-60'}))} />
                    30-60 days
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="daysOnLot" checked={advancedFilters.daysOnLot === '60-90'} onChange={() => setAdvancedFilters(f => ({...f, daysOnLot: '60-90'}))} />
                    60-90 days
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="daysOnLot" checked={advancedFilters.daysOnLot === '90+'} onChange={() => setAdvancedFilters(f => ({...f, daysOnLot: '90+'}))} />
                    90+ days
                  </label>
                </div>
              </div>

              {/* Clear All Button */}
              <div className="pt-4 border-t">
                <button
                  onClick={() => setAdvancedFilters({
                    titleInOffice: null,
                    delivered: null,
                    inspected: null,
                    hasOpenRO: null,
                    inArbitration: null,
                    paymentCleared: null,
                    isFloored: null,
                    hasNotes: null,
                    daysOnLot: null,
                  })}
                  className="w-full px-4 py-2 bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 font-medium"
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Photo Upload Component with drag-and-drop, progress, and thumbnails
const PhotoUpload = ({ type, photos, onChange, label, accent = false }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const fileInputRef = React.useRef(null);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    
    const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (imageFiles.length === 0) return;

    setUploading(true);
    setProgress(0);
    
    try {
      const result = await api.uploadPhotos(type, imageFiles, (p) => setProgress(p));
      if (result.files) {
        const newUrls = result.files.map(f => f.url);
        onChange([...photos, ...newUrls]);
      }
    } catch (e) {
      alert('Upload failed: ' + e.message);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleFileSelect = (e) => {
    handleFiles(e.target.files);
    e.target.value = '';
  };

  const addUrl = () => {
    if (urlInput.trim()) {
      onChange([...photos, urlInput.trim()]);
      setUrlInput('');
    }
  };

  const removePhoto = async (index) => {
    const url = photos[index];
    // If it's a server URL, try to delete it
    if (url.includes('/uploads/')) {
      const filename = url.split('/').pop();
      try {
        await api.deletePhoto(type, filename);
      } catch (e) {
        console.error('Failed to delete from server:', e);
      }
    }
    onChange(photos.filter((_, i) => i !== index));
  };

  const borderColor = accent ? 'border-red-200' : 'border-neutral-200';
  const bgColor = accent ? 'bg-red-50' : 'bg-neutral-50';
  const dragBg = dragOver ? (accent ? 'bg-red-100' : 'bg-blue-50') : '';

  return (
    <div className="space-y-2">
      {label && <div className="text-xs text-neutral-500">{label}</div>}
      
      {/* Drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed ${borderColor} ${bgColor} ${dragBg} rounded p-4 text-center cursor-pointer transition-colors`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
        {uploading ? (
          <div className="space-y-2">
            <div className="text-xs text-neutral-600">Uploading... {progress}%</div>
            <div className="w-full bg-neutral-200 rounded-full h-2">
              <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        ) : (
          <div className="text-xs text-neutral-500">
            <div className="font-medium mb-1">Drop photos here or click to browse</div>
            <div>Supports JPEG, PNG, GIF, WebP (max 10MB each)</div>
          </div>
        )}
      </div>

      {/* URL input */}
      <div className="flex gap-2">
        <input
          type="text"
          value={urlInput}
          onChange={e => setUrlInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addUrl())}
          placeholder="Or paste image URL..."
          className={`flex-1 h-7 px-2 text-xs border ${borderColor} rounded`}
        />
        <button type="button" onClick={addUrl} className="px-2 h-7 text-xs border border-neutral-200 rounded hover:bg-neutral-100">Add URL</button>
      </div>

      {/* Thumbnails */}
      {photos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {photos.map((url, i) => (
            <div key={i} className="relative group">
              <img
                src={url}
                alt={`Photo ${i + 1}`}
                className="w-16 h-16 object-cover rounded border border-neutral-200"
                onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23eee" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%23999" font-size="12">Error</text></svg>'; }}
              />
              <button
                type="button"
                onClick={() => removePhoto(i)}
                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Vehicle Form (Create/Edit) - Simplified with quick-add options
const VehicleForm = ({ vehicle, onClose, onSave, isNew, existingStockNumbers = [] }) => {
  const [showPrintPrompt, setShowPrintPrompt] = useState(false);
  const [savedVehicle, setSavedVehicle] = useState(null);
  const [form, setForm] = useState({
    vin: vehicle.vin || '',
    stockNumber: vehicle.stockNumber || '',
    year: vehicle.year || '',
    make: vehicle.make || '',
    model: vehicle.model || '',
    trim: vehicle.trim || '',
    mileage: vehicle.mileage || '',
    color: vehicle.color || '',
    purchasePrice: vehicle.purchasePrice || '',
    estimatedSale: vehicle.estimatedSale || '',
    purchaseDate: vehicle.purchaseDate?.split('T')[0] || new Date().toISOString().split('T')[0],
    arbDeadline: vehicle.arbDeadline?.split('T')[0] || '',
    auctionId: vehicle.auctionId ? String(vehicle.auctionId) : '',
    auction: vehicle.auction || '',
    buyerId: vehicle.buyerId || '',
    paymentMethod: vehicle.paymentMethod || '',
    checkNumber: vehicle.checkNumber || '',
    titleStatus: vehicle.titleStatus || 'absent',
    endUse: vehicle.endUse || 'retail',
    inspectionType: vehicle.inspectionType || 'dealer_direct',
    reconLevel: vehicle.reconLevel !== undefined && vehicle.reconLevel !== null ? vehicle.reconLevel : '',
    vhrStatus: vehicle.vhrStatus || 'not_pulled',
    vhrNotes: vehicle.vhrNotes || '',
    whatItNeeds: vehicle.whatItNeeds || '',
    whatItNeedsPhotos: vehicle.whatItNeedsPhotos || [],
    flagForService: vehicle.damageFlagged || false,
    greenLightGuarantee: vehicle.greenLightGuarantee || false,
    transportVendorId: vehicle.transportVendorId || '',
    transportEstimate: vehicle.transportEstimate || '',
    transportCost: vehicle.transportCost || '',  // Actual transport cost from DB
    transportStatus: vehicle.transportStatus || 'pending',
    auctionLight: vehicle.auctionLight || '',  // Don't default to green - leave empty for new vehicles
    auctionAnnouncements: vehicle.auctionAnnouncements || '',
  });
  
  const [auctions, setAuctions] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [transportVendors, setTransportVendors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [decoding, setDecoding] = useState(false);
  const [error, setError] = useState('');
  const [vinDecoded, setVinDecoded] = useState(!isNew);
  const [vinManualEntry, setVinManualEntry] = useState(false);
  const formUnlocked = !isNew || vinDecoded || vinManualEntry;
  const [stockPreview, setStockPreview] = useState('');
  const [showAddAuction, setShowAddAuction] = useState(false);
  const [showAddBuyer, setShowAddBuyer] = useState(false);
  const [showAddTransport, setShowAddTransport] = useState(false);
  const [newAuction, setNewAuction] = useState({ name: '', abbreviation: '' });
  const [newBuyer, setNewBuyer] = useState({ name: '', fee: '' });
  const [newTransport, setNewTransport] = useState({ name: '', phone: '' });
  const [soldStockNumbers, setSoldStockNumbers] = useState([]);

  useEffect(() => {
    if (!isNew) return;
    api.getSoldVehicles()
      .then(sold => setSoldStockNumbers((sold || []).map(v => v.stockNumber).filter(Boolean)))
      .catch(() => setSoldStockNumbers([]));
  }, [isNew]);

  const allStockNumbers = useMemo(
    () => [...new Set([...existingStockNumbers, ...soldStockNumbers])],
    [existingStockNumbers, soldStockNumbers]
  );

  const END_USE_OPTIONS = [
    { value: 'retail', label: 'Retail' },
    { value: 'wholesale', label: 'Wholesale' },
    { value: 'shop_personal', label: 'Shop/Personal' },
    { value: 'dealer_direct', label: 'Byrider/Dealer Direct' },
    { value: 'copart', label: 'Copart' },
    { value: 'iaai', label: 'IAAI' },
    { value: 'parts_only', label: 'Parts Only' },
    { value: 'other', label: 'Other' },
  ];

  const INSPECTION_TYPE_OPTIONS = [
    { value: 'dealer_direct', label: 'Dealer Direct (Full Inspection)' },
    { value: 'wholesale_platform', label: 'Wholesale Platform (Basic Inspection)' },
  ];

  const isCopartDirect = form.endUse === 'copart' || form.endUse === 'iaai';

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [auctionsData, buyersData, paymentMethodsData, transportData] = await Promise.all([
          api.getAuctions(),
          api.getBuyers(),
          api.getPaymentMethods(),
          api.getTransportVendors()
        ]);
        setAuctions(auctionsData);
        setBuyers(buyersData);
        setPaymentMethods(paymentMethodsData);
        setTransportVendors(transportData);
      } catch (e) {
        console.error('Failed to load data:', e);
      }
    };
    loadData();
  }, []);

  // Decode VIN using NHTSA API
  const decodeVin = async (vin) => {
    if (!vin || vin.length !== 17) {
      setError('VIN must be exactly 17 characters');
      return;
    }
    setDecoding(true);
    setError('');
    try {
      const response = await fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVin/${vin}?format=json`);
      const data = await response.json();
      
      if (!data.Results) {
        setError('Failed to decode VIN');
        return;
      }

      const getValue = (variable) => {
        const result = data.Results.find(r => r.Variable === variable);
        return result?.Value && result.Value !== 'Not Applicable' ? result.Value : '';
      };

      const errorCode = getValue('Error Code');
      if (errorCode && errorCode.includes('1')) {
        setError('Invalid VIN - check digit error');
        return;
      }

      const year = getValue('Model Year');
      const make = getValue('Make');
      const model = getValue('Model');
      const trim = getValue('Trim');

      if (!make || !model) {
        setError('Could not decode make/model from VIN');
        return;
      }

      setForm(f => ({
        ...f,
        year: year || f.year,
        make: make || f.make,
        model: model || f.model,
        trim: trim || f.trim,
      }));
      setVinDecoded(true);
    } catch (e) {
      setError('VIN decode failed: ' + e.message);
    } finally {
      setDecoding(false);
    }
  };

  // Generate unique stock number from VIN and auction
  const generateStockNumber = useCallback((vin, auctionId) => {
    if (!vin || vin.length < 4 || !auctionId) return '';
    
    const auction = auctions.find(a => a.id === parseInt(auctionId));
    if (!auction) return '';
    
    const last4 = vin.slice(-4).toUpperCase();
    const abbrev = auction.abbreviation || auction.name.slice(0, 3).toUpperCase();
    let base = `${last4}-${abbrev}`;
    let stockNum = base;
    let suffix = 2;

    while (allStockNumbers.includes(stockNum)) {
      stockNum = `${base}-${suffix}`;
      suffix++;
    }

    return stockNum;
  }, [allStockNumbers, auctions]);

  // Auto-decode VIN when it reaches 17 characters for new vehicles
  useEffect(() => {
    if (!isNew) return;
    if (vinManualEntry) return;
    if (!form.vin || form.vin.length !== 17) return;
    if (vinDecoded || decoding) return;
    decodeVin(form.vin);
  }, [form.vin, isNew, vinDecoded, decoding, vinManualEntry]);

  // Update stock preview when VIN or auction changes
  useEffect(() => {
    if (isNew && form.vin && form.auctionId) {
      const preview = generateStockNumber(form.vin, form.auctionId);
      setStockPreview(preview);
    }
  }, [form.vin, form.auctionId, isNew, generateStockNumber]);

  // Get selected buyer's fee
  const selectedBuyer = buyers.find(b => b.id === parseInt(form.buyerId));
  const buyerFee = selectedBuyer ? parseFloat(selectedBuyer.fee) || 0 : 0;

  // Quick-add new auction
  const handleAddAuction = async () => {
    if (!newAuction.name || !newAuction.abbreviation) {
      setError('Auction name and abbreviation required');
      return;
    }
    try {
      const created = await api.createVendor({
        name: newAuction.name,
        vendor_type: 'auction',
        abbreviation: newAuction.abbreviation.toUpperCase(),
      });
      setAuctions(prev => [...prev, created]);
      setForm(f => ({ ...f, auctionId: created.id, auction: created.name }));
      setShowAddAuction(false);
      setNewAuction({ name: '', abbreviation: '' });
    } catch (e) {
      setError('Failed to add auction: ' + e.message);
    }
  };

  // Quick-add new buyer
  const handleAddBuyer = async () => {
    if (!newBuyer.name) {
      setError('Buyer name required');
      return;
    }
    try {
      const created = await api.createBuyer({
        name: newBuyer.name,
        fee: parseFloat(newBuyer.fee) || 0,
      });
      setBuyers(prev => [...prev, created]);
      setForm(f => ({ ...f, buyerId: created.id }));
      setShowAddBuyer(false);
      setNewBuyer({ name: '', fee: '' });
    } catch (e) {
      setError('Failed to add buyer: ' + e.message);
    }
  };

  // Quick-add new transport vendor
  const handleAddTransport = async () => {
    if (!newTransport.name) {
      setError('Transport vendor name required');
      return;
    }
    try {
      const created = await api.createVendor({
        name: newTransport.name,
        type: 'transport',
        phone: newTransport.phone || null,
      });
      setTransportVendors(prev => [...prev, created]);
      setForm(f => ({ ...f, transportVendorId: created.id }));
      setShowAddTransport(false);
      setNewTransport({ name: '', phone: '' });
    } catch (e) {
      setError('Failed to add transport vendor: ' + e.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isNew && !formUnlocked) {
      setError('Please decode VIN first or check Pre-1981 / does not decode');
      return;
    }

    if (isNew && vinManualEntry) {
      if (!form.vin || form.vin.length < 4) {
        setError('VIN required (minimum 4 characters for stock number)');
        return;
      }
      if (!form.year || !form.make || !form.model) {
        setError('Year, make, and model are required for manual VIN entry');
        return;
      }
    }

    if (isNew && !form.paymentMethod) {
      setError('Payment method is required');
      return;
    }

    if (isNew && !form.endUse) {
      setError('End use is required');
      return;
    }

    if (isNew && !form.estimatedSale) {
      setError('Estimated sale price is required');
      return;
    }

    // Skip inspection type validation for Copart/IAAI vehicles
    const isCopartDirectSubmit = form.endUse === 'copart' || form.endUse === 'iaai';
    if (isNew && !form.inspectionType && !isCopartDirectSubmit) {
      setError('Inspection type is required');
      return;
    }

    let finalForm = { ...form };
    
    // Convert empty strings to null for date fields to avoid DB errors
    const dateFields = ['purchaseDate', 'saleDate', 'paymentClearedDate', 'inspectionDate', 
                        'detailCompletedDate', 'arbDeadline', 'precheckSnoozedUntil',
                        'transportFeePaidDate', 'buyerFeePaidDate'];
    dateFields.forEach(field => {
      if (finalForm[field] === '' || finalForm[field] === undefined) {
        finalForm[field] = null;
      }
    });
    
    // Convert numeric fields to proper numbers
    if (finalForm.transportCost !== '' && finalForm.transportCost !== null && finalForm.transportCost !== undefined) {
      finalForm.transportCost = parseFloat(finalForm.transportCost) || 0;
    } else {
      delete finalForm.transportCost; // Don't send empty values
    }
    if (finalForm.transportEstimate !== '' && finalForm.transportEstimate !== null && finalForm.transportEstimate !== undefined) {
      finalForm.transportEstimate = parseFloat(finalForm.transportEstimate) || 0;
    }
    
    // Convert ID fields to integers
    if (finalForm.auctionId) {
      finalForm.auctionId = parseInt(finalForm.auctionId) || null;
    }
    if (finalForm.buyerId) {
      finalForm.buyerId = parseInt(finalForm.buyerId) || null;
    }
    if (finalForm.transportVendorId) {
      finalForm.transportVendorId = parseInt(finalForm.transportVendorId) || null;
    }
    if (finalForm.reconLevel !== '' && finalForm.reconLevel !== null && finalForm.reconLevel !== undefined) {
      finalForm.reconLevel = parseInt(finalForm.reconLevel);
    }
    
    // Also set auction name for both new and edit (so it persists correctly)
    if (finalForm.auctionId) {
      const auction = auctions.find(a => a.id === parseInt(finalForm.auctionId));
      if (auction) {
        finalForm.auction = auction.name;
      }
    }
    
    // Send flagForService directly - backend will create task
    // Also keep damageFlagged for backward compatibility
    finalForm.flagForService = form.flagForService;
    finalForm.damageFlagged = form.flagForService;
    finalForm.damageDescription = form.flagForService ? form.whatItNeeds : '';
    finalForm.damagePhotos = form.flagForService ? form.whatItNeedsPhotos : [];
    
    // Green Light/Guarantee field
    finalForm.greenLightGuarantee = form.greenLightGuarantee;

    if (isNew && vinManualEntry && !finalForm.auctionAnnouncements) {
      finalForm.auctionAnnouncements = 'Pre-1981 / VIN does not decode';
    }
    
    if (isNew) {
      if (!form.auctionId) {
        setError('Please select an auction source');
        return;
      }
      finalForm.stockNumber = generateStockNumber(form.vin, form.auctionId);
      const auction = auctions.find(a => a.id === parseInt(form.auctionId));
      finalForm.auction = auction?.name || '';
      
      // Set transport status based on end use
      if (form.endUse === 'copart' || form.endUse === 'iaai') {
        finalForm.transportStatus = 'na_direct';
      }
    }

    if (!finalForm.vin || !finalForm.stockNumber || !finalForm.make || !finalForm.model || !finalForm.mileage || !finalForm.color) {
      setError('VIN, Stock #, Make, Model, Odometer, and Color are required');
      return;
    }

    setSaving(true);
    setError('');
    try {
      if (isNew) {
        const result = await api.createVehicle(finalForm);
        // Show print prompt for new vehicles
        setSavedVehicle({
          ...finalForm,
          id: result?.id,
          stockNumber: finalForm.stockNumber,
          year: finalForm.year,
          make: finalForm.make,
          model: finalForm.model,
          vin: finalForm.vin,
          color: finalForm.color,
          mileage: finalForm.mileage
        });
        setShowPrintPrompt(true);
        onSave(); // Refresh data in background
      } else {
        await api.updateVehicle(vehicle.id, finalForm);
        onSave();
        onClose();
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Show print prompt after vehicle is added
  if (showPrintPrompt && savedVehicle) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
        <div className="bg-white w-full max-w-md rounded-lg shadow-lg" onClick={e => e.stopPropagation()}>
          <div className="p-6 text-center">
            <div className="text-green-600 text-5xl mb-4">✓</div>
            <h2 className="text-xl font-bold mb-2">Vehicle Added!</h2>
            <p className="text-neutral-600 mb-4">
              {savedVehicle.stockNumber} - {savedVehicle.year} {savedVehicle.make} {savedVehicle.model}
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="text-amber-800 font-medium mb-2">🏷️ Print Tags for Shop</div>
              <p className="text-sm text-amber-700">Print key tag and windshield sticker now?</p>
            </div>
            <div className="space-y-2">
              <button
                onClick={() => {
                  setShowPrintPrompt(false);
                  // Open QR modal - we'll use a callback
                  if (window.openQRModal) window.openQRModal(savedVehicle);
                  onClose();
                }}
                className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition"
              >
                🏷️ Print Tags Now
              </button>
              <button
                onClick={onClose}
                className="w-full py-2 text-neutral-600 hover:bg-neutral-100 rounded-lg transition"
              >
                Skip for Now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-auto rounded shadow-lg" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-neutral-200 flex justify-between">
          <span className="font-medium">{isNew ? 'Add Vehicle' : 'Edit Vehicle'}</span>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && <div className="text-red-600 text-sm p-2 bg-red-50 rounded">{error}</div>}
          
          {/* Current Vehicle Info (Edit Mode) */}
          {!isNew && (
            <div className="p-3 bg-blue-50 rounded border border-blue-200">
              <div className="text-sm font-medium text-blue-800">
                Editing: {form.stockNumber} - {form.year} {form.make} {form.model} {form.trim}
              </div>
            </div>
          )}

          {/* Stock Number (Edit Mode) */}
          {!isNew && (
            <div className="p-3 bg-neutral-50 rounded border border-neutral-200">
              <div className="text-xs font-medium text-neutral-600 mb-2">Stock Number</div>
              <input
                type="text"
                value={form.stockNumber}
                onChange={e => set('stockNumber', e.target.value.toUpperCase())}
                className="w-full h-8 px-2 text-sm font-mono border border-neutral-200 rounded uppercase"
                required
              />
            </div>
          )}
          
          {/* VIN */}
          <div className="p-3 bg-neutral-50 rounded border border-neutral-200">
            <div className="text-xs font-medium text-neutral-600 mb-2">VIN</div>
            <div className="flex gap-2">
              <input
                type="text"
                value={form.vin}
                onChange={e => { set('vin', e.target.value.toUpperCase()); if (isNew && !vinManualEntry) setVinDecoded(false); }}
                placeholder={vinManualEntry ? 'VIN (manual entry)' : '17-character VIN'}
                maxLength={17}
                className="flex-1 h-8 px-2 text-sm font-mono border border-neutral-200 rounded uppercase"
              />
              <button
                type="button"
                onClick={() => decodeVin(form.vin)}
                disabled={decoding || form.vin.length !== 17 || vinManualEntry}
                className="px-3 h-8 text-xs bg-neutral-800 text-white rounded hover:bg-neutral-700 disabled:opacity-50"
              >
                {decoding ? 'Decoding...' : 'Decode'}
              </button>
            </div>
            <label className="flex items-center gap-2 text-xs text-neutral-600 mt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={vinManualEntry}
                onChange={e => {
                  setVinManualEntry(e.target.checked);
                  if (e.target.checked) {
                    setVinDecoded(false);
                    setError('');
                  }
                }}
                className="rounded"
              />
              Pre-1981 / VIN does not decode — enter year, make, model manually
            </label>
            <div className="text-xs text-neutral-400 mt-1">{form.vin.length}/17 characters</div>
            {vinDecoded && isNew && !vinManualEntry && (
              <div className="text-xs text-green-600 mt-1">✓ VIN decoded: {form.year} {form.make} {form.model} {form.trim}</div>
            )}
            {vinManualEntry && isNew && (
              <div className="text-xs text-amber-600 mt-1">Manual VIN entry — decode skipped</div>
            )}
          </div>

          {/* Decoded Info */}
          {formUnlocked && (
            <div className="grid grid-cols-4 gap-3">
              <Input label="Year" type="number" value={form.year} onChange={v => set('year', v)} />
              <Input label="Make" value={form.make} onChange={v => set('make', v)} required />
              <Input label="Model" value={form.model} onChange={v => set('model', v)} required />
              <Input label="Trim" value={form.trim} onChange={v => set('trim', v)} />
            </div>
          )}

          {/* Purchase Info */}
          {formUnlocked && (
            <div className="p-3 bg-neutral-50 rounded border border-neutral-200">
              <div className="text-xs font-medium text-neutral-600 mb-2">Purchase Information</div>
              <div className="grid grid-cols-2 gap-3">
                {/* Auction */}
                <div className="col-span-2">
                  <label className="block text-xs text-neutral-500 mb-1">Auction *</label>
                  <div className="flex gap-2 items-center">
                    <button type="button" onClick={() => setShowAddAuction(!showAddAuction)} className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-sm font-medium border border-neutral-200 rounded bg-white hover:bg-neutral-100" title="Add new auction">+</button>
                    <select
                      value={form.auctionId || ''}
                      onChange={e => {
                        set('auctionId', e.target.value);
                      }}
                      className="flex-1 min-w-0 h-8 px-2 text-sm border border-neutral-200 rounded bg-white"
                    >
                      <option value="">Select auction...</option>
                      {auctions.map(a => (
                        <option key={a.id} value={String(a.id)}>{a.name} ({a.abbreviation || '?'})</option>
                      ))}
                    </select>
                  </div>
                  {showAddAuction && (
                    <div className="mt-2 p-2 bg-white border border-neutral-200 rounded">
                      <div className="text-xs font-medium mb-2">Add New Auction</div>
                      <div className="flex gap-2">
                        <input type="text" placeholder="Name (e.g., Copart)" value={newAuction.name} onChange={e => setNewAuction(a => ({ ...a, name: e.target.value }))} className="flex-1 h-7 px-2 text-xs border border-neutral-200 rounded" />
                        <input type="text" placeholder="Abbrev" value={newAuction.abbreviation} onChange={e => setNewAuction(a => ({ ...a, abbreviation: e.target.value.toUpperCase() }))} maxLength={4} className="w-16 h-7 px-2 text-xs border border-neutral-200 rounded uppercase" />
                        <button type="button" onClick={handleAddAuction} className="px-2 h-7 text-xs bg-neutral-800 text-white rounded">Add</button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Buyer */}
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">Buyer *</label>
                  <div className="flex gap-1">
                    <select
                      value={form.buyerId}
                      onChange={e => set('buyerId', e.target.value)}
                      className="flex-1 h-8 px-2 text-sm border border-neutral-200 rounded bg-white"
                    >
                      <option value="">Select buyer...</option>
                      {buyers.map(b => (
                        <option key={b.id} value={b.id}>{b.name} {b.fee > 0 ? `($${b.fee} fee)` : '(no fee)'}</option>
                      ))}
                    </select>
                    <button type="button" onClick={() => setShowAddBuyer(!showAddBuyer)} className="px-2 h-8 text-xs border border-neutral-200 rounded hover:bg-neutral-100">+</button>
                  </div>
                </div>

                <Input label="Purchase Price *" type="number" value={form.purchasePrice} onChange={v => set('purchasePrice', v)} required />
                <Input label="Est. Sale Price *" type="number" value={form.estimatedSale} onChange={v => set('estimatedSale', v)} required />
                <Input label="Odometer *" type="number" value={form.mileage} onChange={v => set('mileage', v)} required />
                <Input label="Color *" value={form.color} onChange={v => set('color', v)} required />
                <Input label="Purchase Date" type="date" value={form.purchaseDate} onChange={v => set('purchaseDate', v)} />
                
                {/* Payment Method - 5 Options Only */}
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">Payment Method *</label>
                  <select
                    value={form.paymentMethod}
                    onChange={e => set('paymentMethod', e.target.value)}
                    className="w-full h-8 px-2 text-sm border border-neutral-200 rounded bg-white"
                    required
                  >
                    <option value="">Select payment method...</option>
                    <option value="Floorplan">Floorplan (1st Choice)</option>
                    <option value="ACH">ACH (Direct from Bank)</option>
                    <option value="Check">Check</option>
                    <option value="Cash">Cash</option>
                    <option value="Title Pending">Title Pending (Pay on Title)</option>
                  </select>
                </div>

                {/* Check Number - only show if payment method is Check */}
                {form.paymentMethod === 'Check' && (
                  <Input label="Check Number (optional)" value={form.checkNumber} onChange={v => set('checkNumber', v)} placeholder="Enter when known" />
                )}

                <Select label="Title Status" value={form.titleStatus} onChange={v => set('titleStatus', v)} options={['absent', 'at_auction', 'in_transit', 'in_office']} />

                {/* End Use */}
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">End Use *</label>
                  <select
                    value={form.endUse}
                    onChange={e => set('endUse', e.target.value)}
                    className="w-full h-8 px-2 text-sm border border-neutral-200 rounded bg-white"
                    required
                  >
                    {END_USE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Auction Lights - Multi-select */}
                <div className="col-span-2">
                  <label className="block text-xs text-neutral-500 mb-1">Auction Light(s) * <span className="text-neutral-400">(select all that apply)</span></label>
                  <div className="flex gap-2">
                    {[
                      { value: 'green', label: '🟢 Green Light', color: 'bg-green-100 border-green-500 text-green-700' },
                      { value: 'yellow', label: '🟡 Yellow (Caution)', color: 'bg-yellow-100 border-yellow-500 text-yellow-700' },
                      { value: 'red', label: '🔴 Red (As-Is)', color: 'bg-red-100 border-red-500 text-red-700' },
                    ].map(opt => {
                      const lights = (form.auctionLight || '').split(',').filter(Boolean);
                      const isSelected = lights.includes(opt.value);
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => {
                            let newLights;
                            if (isSelected) {
                              newLights = lights.filter(l => l !== opt.value);
                            } else {
                              newLights = [...lights, opt.value];
                            }
                            // Sort so display is consistent: green, yellow, red
                            const order = ['green', 'yellow', 'red'];
                            newLights.sort((a, b) => order.indexOf(a) - order.indexOf(b));
                            set('auctionLight', newLights.join(',') || '');  // Allow empty - don't default to green
                          }}
                          className={`flex-1 py-2 text-xs font-medium rounded border-2 transition ${
                            isSelected 
                              ? opt.color 
                              : 'bg-neutral-50 border-neutral-200 text-neutral-500 hover:bg-neutral-100'
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  {form.auctionLight && form.auctionLight.includes(',') && (
                    <div className="mt-1 text-xs text-amber-600">
                      ⚠️ Multiple lights selected: {form.auctionLight.split(',').map(l => l === 'green' ? '🟢' : l === 'yellow' ? '🟡' : '🔴').join(' + ')}
                    </div>
                  )}
                </div>

                {/* Auction Announcements */}
                <div className="col-span-2">
                  <label className="block text-xs text-neutral-500 mb-1">Auction Announcements</label>
                  <textarea
                    value={form.auctionAnnouncements}
                    onChange={e => set('auctionAnnouncements', e.target.value)}
                    placeholder="Frame damage, flood, salvage, engine noise, etc."
                    className="w-full h-16 px-2 py-1 text-sm border border-neutral-200 rounded resize-none"
                  />
                </div>

                {/* Inspection Type - hide for Copart/IAAI */}
                {!isCopartDirect && (
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">Inspection Type *</label>
                    <select
                      value={form.inspectionType}
                      onChange={e => set('inspectionType', e.target.value)}
                      className="w-full h-8 px-2 text-sm border border-neutral-200 rounded bg-white"
                      required
                    >
                      {INSPECTION_TYPE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Recon Level - hide for Copart/IAAI */}
                {!isCopartDirect && (
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">Est. Recon Level</label>
                    <select
                      value={form.reconLevel || 3}
                      onChange={e => set('reconLevel', parseInt(e.target.value))}
                      className="w-full h-8 px-2 text-sm border border-neutral-200 rounded bg-white"
                    >
                      <option value={1}>1 - Minimal (Detail only)</option>
                      <option value={2}>2 - Light</option>
                      <option value={3}>3 - Medium</option>
                      <option value={4}>4 - Heavy</option>
                      <option value={5}>5 - Major</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Quick-add buyer form */}
              {showAddBuyer && (
                <div className="mt-3 p-2 bg-white border border-neutral-200 rounded">
                  <div className="text-xs font-medium mb-2">Add New Buyer</div>
                  <div className="flex gap-2">
                    <input type="text" placeholder="Name" value={newBuyer.name} onChange={e => setNewBuyer(b => ({ ...b, name: e.target.value }))} className="flex-1 h-7 px-2 text-xs border border-neutral-200 rounded" />
                    <input type="number" placeholder="Fee ($)" value={newBuyer.fee} onChange={e => setNewBuyer(b => ({ ...b, fee: e.target.value }))} className="w-20 h-7 px-2 text-xs border border-neutral-200 rounded" />
                    <button type="button" onClick={handleAddBuyer} className="px-2 h-7 text-xs bg-neutral-800 text-white rounded">Add</button>
                  </div>
                </div>
              )}

              {/* Stock number preview & costs */}
              {isNew && stockPreview && (
                <div className="mt-3 p-2 bg-white border border-neutral-200 rounded">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-neutral-500">Stock Number:</div>
                      <div className="text-lg font-mono font-medium">{stockPreview}</div>
                    </div>
                    <div className="text-right text-xs">
                      {form.purchasePrice && <div>Purchase: ${parseFloat(form.purchasePrice).toLocaleString()}</div>}
                      {buyerFee > 0 && <div className="text-orange-600">Buyer fee: ${buyerFee}</div>}
                      <div className="font-medium mt-1">Status: New / Pending Transport</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* VHR Status - hide for Copart/IAAI */}
          {formUnlocked && !isCopartDirect && (
            <div className="p-3 bg-neutral-50 rounded border border-neutral-200">
              <div className="text-xs font-medium text-neutral-600 mb-2">Vehicle History Report</div>
              <div className="grid grid-cols-3 gap-3">
                <Select label="VHR Status" value={form.vhrStatus} onChange={v => set('vhrStatus', v)} options={[
                  { value: 'not_pulled', label: 'Not Pulled' },
                  { value: 'clean', label: 'Clean' },
                  { value: 'issues', label: 'Has Issues' },
                ]} />
                <div className="col-span-2">
                  <label className="block text-xs text-neutral-500 mb-1">VHR Notes</label>
                  <input
                    type="text"
                    value={form.vhrNotes}
                    onChange={e => set('vhrNotes', e.target.value)}
                    placeholder={form.vhrStatus === 'issues' ? 'Describe issues...' : 'Optional notes'}
                    className={`w-full h-8 px-2 text-sm border rounded ${form.vhrStatus === 'issues' ? 'border-red-300 bg-red-50' : 'border-neutral-200'}`}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Transport (skip for Copart/IAAI direct) */}
          {formUnlocked && !isCopartDirect && (
            <div className="p-3 bg-neutral-50 rounded border border-neutral-200">
              <div className="text-xs font-medium text-neutral-600 mb-2">Transport</div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">Transport Vendor</label>
                  <div className="flex gap-1">
                    <select
                      value={form.transportVendorId}
                      onChange={e => set('transportVendorId', e.target.value)}
                      className="flex-1 h-8 px-2 text-sm border border-neutral-200 rounded bg-white"
                    >
                      <option value="">Select vendor...</option>
                      {transportVendors.map(v => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </select>
                    <button type="button" onClick={() => setShowAddTransport(!showAddTransport)} className="px-2 h-8 text-xs border border-neutral-200 rounded hover:bg-neutral-100">+</button>
                  </div>
                </div>
                <Input label="Estimated Cost" type="number" value={form.transportEstimate} onChange={v => set('transportEstimate', v)} placeholder="$" />
                <Input label="Actual Cost" type="number" value={form.transportCost} onChange={v => set('transportCost', v)} placeholder="$" />
                <Select label="Status" value={form.transportStatus} onChange={v => set('transportStatus', v)} options={[
                  { value: 'pending', label: 'Pending' },
                  { value: 'scheduled', label: 'Scheduled' },
                  { value: 'in_transit', label: 'In Transit' },
                  { value: 'delivered', label: 'Delivered' },
                ]} />
              </div>
              
              {/* Quick-add transport vendor */}
              {showAddTransport && (
                <div className="mt-3 p-2 bg-white border border-neutral-200 rounded">
                  <div className="text-xs font-medium mb-2">Add New Transport Vendor</div>
                  <div className="flex gap-2">
                    <input type="text" placeholder="Company name" value={newTransport.name} onChange={e => setNewTransport(t => ({ ...t, name: e.target.value }))} className="flex-1 h-7 px-2 text-xs border border-neutral-200 rounded" />
                    <input type="text" placeholder="Phone (optional)" value={newTransport.phone} onChange={e => setNewTransport(t => ({ ...t, phone: e.target.value }))} className="w-28 h-7 px-2 text-xs border border-neutral-200 rounded" />
                    <button type="button" onClick={handleAddTransport} className="px-2 h-7 text-xs bg-neutral-800 text-white rounded">Add</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Copart/IAAI - no transport needed */}
          {formUnlocked && isCopartDirect && (
            <div className="p-3 bg-amber-50 rounded border border-amber-200">
              <div className="text-xs font-medium text-amber-800">Transport: N/A - {form.endUse === 'copart' ? 'Copart' : 'IAAI'} Direct</div>
              <div className="text-xs text-amber-700 mt-1">
                After submitting, assign this vehicle on the {form.endUse === 'copart' ? 'Copart' : 'IAAI'} site for pickup.
              </div>
            </div>
          )}

          {/* What It Needs - description, photos, flag for service - hide for Copart/IAAI */}
          {formUnlocked && !isCopartDirect && (
            <div className="p-3 bg-neutral-50 rounded border border-neutral-200">
              <div className="text-xs font-medium text-neutral-600 mb-2">What It Needs</div>
              
              <textarea
                value={form.whatItNeeds}
                onChange={e => set('whatItNeeds', e.target.value)}
                placeholder="Describe what work the vehicle needs (visible to Service/Recon)..."
                className="w-full h-20 px-2 py-1 text-sm border border-neutral-200 rounded resize-none"
              />
              
              <PhotoUpload
                type="vehicles"
                photos={form.whatItNeedsPhotos}
                onChange={(photos) => set('whatItNeedsPhotos', photos)}
                label="Photos (drag & drop or click to upload)"
              />

              {/* Flag for Service checkbox */}
              <div className={`mt-3 p-2 rounded border ${form.flagForService ? 'bg-orange-50 border-orange-200' : 'border-neutral-200'}`}>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="flagForService"
                    checked={form.flagForService}
                    onChange={e => set('flagForService', e.target.checked)}
                    className="w-4 h-4"
                  />
                  <label htmlFor="flagForService" className="text-xs font-medium text-neutral-600">
                    Flag for Service/Recon (creates task with notes & photos above)
                  </label>
                </div>
              </div>

              {/* Green Light/Guarantee checkbox */}
              <div className={`mt-3 p-2 rounded border ${form.greenLightGuarantee ? 'bg-green-50 border-green-200' : 'border-neutral-200'}`}>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="greenLightGuarantee"
                    checked={form.greenLightGuarantee}
                    onChange={e => set('greenLightGuarantee', e.target.checked)}
                    className="w-4 h-4"
                  />
                  <label htmlFor="greenLightGuarantee" className="text-xs font-medium text-neutral-600">
                    Green Light/Guarantee Vehicle
                  </label>
                </div>
              </div>

              {/* ARB Deadline */}
              <div className={`mt-3 p-2 rounded border ${form.arbDeadline ? 'bg-orange-50 border-orange-200' : 'border-neutral-200'}`}>
                <div className="flex items-center gap-3">
                  <label htmlFor="arbDeadline" className="text-xs font-medium text-neutral-600 whitespace-nowrap">
                    ARB Deadline:
                  </label>
                  <input
                    type="date"
                    id="arbDeadline"
                    value={form.arbDeadline}
                    onChange={e => set('arbDeadline', e.target.value)}
                    className="flex-1 h-7 px-2 text-sm border border-neutral-200 rounded"
                  />
                  {form.arbDeadline && (
                    <button 
                      type="button" 
                      onClick={() => set('arbDeadline', '')}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      Clear
                    </button>
                  )}
                </div>
                {form.arbDeadline && (
                  <div className="mt-2 text-xs text-orange-700 bg-orange-100 p-2 rounded">
                    Alerts will trigger 3 days and 1 day before this deadline
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-neutral-200">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-neutral-600">Cancel</button>
            <button 
              type="submit" 
              disabled={saving || (isNew && (!formUnlocked || !form.auctionId || !form.paymentMethod || !form.endUse || !form.estimatedSale || (!form.inspectionType && form.endUse !== 'copart' && form.endUse !== 'iaai')))} 
              className="px-4 py-1.5 text-sm bg-neutral-800 text-white rounded hover:bg-neutral-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : isNew ? `Add ${stockPreview || 'Vehicle'}` : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Transport Module - Tech can mark delivered and start inspections
const Transport = ({ onRefresh, vendors, onOpenTransportNotes, onStartInspection }) => {
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const canSeeCosts = isAdmin || currentUser?.role === 'office';
  
  const [transportList, setTransportList] = useState([]);
  const [stats, setStats] = useState({ pending: 0, scheduled: 0, in_transit: 0, delivered: 0, na_direct: 0 });
  const [filter, setFilter] = useState('all'); // all, or specific status
  const [loading, setLoading] = useState(true);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deliveryModal, setDeliveryModal] = useState(null); // For confirming transport cost on delivery
  const [deliveryCost, setDeliveryCost] = useState('');
  const [inspectionPrompt, setInspectionPrompt] = useState(null); // Prompt to start inspection after delivery
  const [sortBy, setSortBy] = useState('days');
  const [sortDir, setSortDir] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');

  const transportVendors = vendors.filter(v => v.type === 'transport');

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const statusFilter = filter === 'all' ? null : (filter === 'all' ? null : filter);
      const [list, statsData] = await Promise.all([
        api.getTransportList(statusFilter ? { status: statusFilter } : {}),
        api.getTransportStats()
      ]);
      
      // Filter for active if needed
      const filteredROs = filter === 'active' 
        ? list.filter(ro => ['open', 'in_progress', 'waiting_parts'].includes(ro.status))
        : list;
      
      setTransportList(filteredROs);
      setStats(statsData);
    } catch (e) {
      console.error('Failed to load transport data:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [filter]);

  // Filter and sort the list
  const filteredList = transportList.filter(v => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (v.stockNumber || '').toLowerCase().includes(search) ||
           (v.make || '').toLowerCase().includes(search) ||
           (v.model || '').toLowerCase().includes(search) ||
           (v.vin || '').toLowerCase().includes(search) ||
           (v.buyerName || '').toLowerCase().includes(search) ||
           (v.transportVendorName || '').toLowerCase().includes(search);
  });

  const sortedList = [...filteredList].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    switch (sortBy) {
      case 'days': 
        const daysA = Number(a.daysSinceEntry) || 0;
        const daysB = Number(b.daysSinceEntry) || 0;
        return (daysA - daysB) * dir;
      case 'stock': return (a.stockNumber || '').localeCompare(b.stockNumber || '') * dir;
      case 'vehicle': 
        const vehicleA = `${a.year || ''} ${a.make || ''} ${a.model || ''}`.trim();
        const vehicleB = `${b.year || ''} ${b.make || ''} ${b.model || ''}`.trim();
        return vehicleA.localeCompare(vehicleB) * dir;
      case 'buyer': return (a.buyerName || '').localeCompare(b.buyerName || '') * dir;
      case 'vendor': return (a.transportVendorName || '').localeCompare(b.transportVendorName || '') * dir;
      case 'status': return (a.transportStatus || '').localeCompare(b.transportStatus || '') * dir;
      case 'eta': 
        const etaA = a.transportEta ? new Date(a.transportEta).getTime() : 0;
        const etaB = b.transportEta ? new Date(b.transportEta).getTime() : 0;
        return (etaA - etaB) * dir;
      default: return 0;
    }
  });

  // Count vehicles pending 5+ days
  const urgentCount = transportList.filter(v => v.transportStatus === 'pending' && (v.daysSinceEntry || 0) >= 5).length;

  const handleStatusChange = async (vehicleId, newStatus, additionalData = {}) => {
    const vehicle = transportList.find(v => v.id === vehicleId);
    const isCopart = vehicle?.endUse === 'copart' || vehicle?.endUse === 'iaai';
    
    // For ALL vehicles marking as delivered, show delivery confirmation modal (to capture transport cost)
    if (newStatus === 'delivered') {
      setDeliveryModal(vehicle);
      return;
    }
    
    try {
      await api.updateTransportStatus(vehicleId, { status: newStatus, ...additionalData });
      loadData();
      onRefresh();
    } catch (e) {
      alert('Update failed: ' + e.message);
    }
  };

  const handleConfirmDelivery = async (snooze = false) => {
    if (!deliveryModal) return;
    
    if (snooze) {
      // Just close modal - will remind later (could add notification here)
      setDeliveryModal(null);
      setDeliveryCost('');
      return;
    }
    
    const isCopart = deliveryModal.endUse === 'copart' || deliveryModal.endUse === 'iaai';
    
    try {
      // Copart vehicles have no transport cost (Copart handles it)
      // For regular + canSeeCosts: send entered cost. For regular + !canSeeCosts: omit so backend preserves existing value.
      const payload = { status: 'delivered' };
      if (isCopart) {
        payload.transportCost = 0;
      } else if (canSeeCosts) {
        payload.transportCost = parseFloat(deliveryCost) || 0;
      }
      await api.updateTransportStatus(deliveryModal.id, payload);
      
      // Only flag for inspection if NOT Copart (Copart vehicles don't need inspection)
      if (!isCopart) {
        await api.updateVehicle(deliveryModal.id, {
          needsFurtherReview: true,
          reconStatus: 'needs_inspection'
        });
      }
      
      // Store vehicle info for inspection prompt
      const deliveredVehicle = { ...deliveryModal };
      setDeliveryModal(null);
      setDeliveryCost('');
      loadData();
      onRefresh();
      
      // Show inspection prompt only for non-Copart vehicles
      if (!isCopart) {
        setInspectionPrompt(deliveredVehicle);
      }
    } catch (e) {
      alert('Update failed: ' + e.message);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingVehicle) return;
    try {
      // Update transport status fields
      await api.updateTransportStatus(editingVehicle.id, {
        status: editForm.status,
        notes: editForm.notes,
        eta: editForm.eta || null,
        scheduledDate: editForm.scheduledDate || null,
        vendorId: editForm.vendorId || null
      });
      // Update transport cost separately via vehicle update
      if (editForm.transportCost !== '' && editForm.transportCost !== editingVehicle.transportCost) {
        await api.updateVehicle(editingVehicle.id, {
          transportCost: parseFloat(editForm.transportCost) || 0
        });
      }
      setEditingVehicle(null);
      loadData();
      onRefresh();
    } catch (e) {
      alert('Update failed: ' + e.message);
    }
  };

  const openEditModal = (vehicle) => {
    setEditingVehicle(vehicle);
    setEditForm({
      status: vehicle.transportStatus || 'pending',
      notes: vehicle.transportNotes || '',
      eta: vehicle.transportEta?.split('T')[0] || '',
      scheduledDate: vehicle.transportScheduledDate?.split('T')[0] || '',
      vendorId: vehicle.transportVendorId || '',
      transportCost: vehicle.transportCost || ''
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      scheduled: 'bg-blue-100 text-blue-800',
      in_transit: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      na_direct: 'bg-neutral-100 text-neutral-600'
    };
    const labels = {
      pending: 'Pending',
      scheduled: 'Scheduled',
      in_transit: 'In Transit',
      delivered: 'Delivered',
      na_direct: 'Copart Direct'
    };
    return <span className={`px-2 py-0.5 text-xs rounded ${badges[status] || badges.pending}`}>{labels[status] || status}</span>;
  };

  const getDaysClass = (days, status) => {
    if (status === 'delivered' || status === 'na_direct') return 'text-neutral-400';
    if (days >= 5) return 'text-red-600 font-bold';
    if (days >= 3) return 'text-orange-600 font-medium';
    return 'text-neutral-600';
  };

  // Calculate lot space planning counts
  const comingToDealer = transportList.filter(v => 
    ['pending', 'scheduled', 'in_transit'].includes(v.transportStatus) && 
    v.exitStrategy !== 'Copart Direct' && v.endUse !== 'copart'
  ).length;
  const goingToCopart = transportList.filter(v => 
    v.exitStrategy === 'Copart Direct' || v.endUse === 'copart'
  ).length;

  return (
    <div className="space-y-4">
      {/* Lot Space Planning Summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-blue-600 font-medium">Coming to Dealer</p>
              <p className="text-2xl font-bold text-blue-800">{comingToDealer}</p>
            </div>
            <div className="text-blue-400 text-3xl">→</div>
          </div>
          <p className="text-xs text-blue-500 mt-1">Vehicles in transit to your lot</p>
        </div>
        <div className="p-3 bg-neutral-50 border border-neutral-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-neutral-600 font-medium">Going to Copart</p>
              <p className="text-2xl font-bold text-neutral-700">{goingToCopart}</p>
            </div>
            <div className="text-neutral-400 text-3xl">↗</div>
          </div>
          <p className="text-xs text-neutral-500 mt-1">Not taking lot space</p>
        </div>
      </div>

      {/* Urgent Alert */}
      {urgentCount > 0 && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <span className="text-red-600 text-lg"></span>
          <div>
            <span className="font-medium text-red-800">{urgentCount} vehicle{urgentCount > 1 ? 's' : ''} pending 5+ days</span>
            <span className="text-red-600 text-sm ml-2">— needs immediate attention</span>
          </div>
          <button 
            onClick={() => setFilter('pending')} 
            className="ml-auto text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
          >
            View Pending
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="flex gap-3 flex-wrap">
        {[
          { key: 'all', label: 'All', color: 'bg-neutral-500', count: transportList.length },
          { key: 'pending', label: 'Pending', color: 'bg-yellow-500', count: stats.pending },
          { key: 'scheduled', label: 'Scheduled', color: 'bg-blue-500', count: stats.scheduled },
          { key: 'in_transit', label: 'In Transit', color: 'bg-purple-500', count: stats.in_transit },
          { key: 'delivered', label: 'Delivered', color: 'bg-green-500', count: stats.delivered },
          { key: 'na_direct', label: 'Copart Direct', color: 'bg-neutral-400', count: stats.na_direct },
        ].map(s => (
          <button
            key={s.key}
            onClick={() => setFilter(s.key === 'all' ? 'all' : s.key)}
            className={`px-3 py-2 rounded-lg border transition-all ${filter === s.key ? 'border-neutral-800 bg-neutral-800 text-white' : 'border-neutral-200 bg-white hover:border-neutral-300'}`}
          >
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${filter === s.key ? 'bg-white' : s.color}`}></div>
              <span className="text-sm font-medium">{s.count || 0}</span>
              <span className={`text-xs ${filter === s.key ? 'text-neutral-300' : 'text-neutral-500'}`}>{s.label}</span>
            </div>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="flex items-center gap-3">
        <input
          type="text"
          placeholder="Search stock, VIN, make, model, buyer, vendor..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="px-3 py-1.5 text-xs border border-neutral-200 rounded-lg w-72"
        />
        {searchTerm && <span className="text-xs text-neutral-500">{sortedList.length} results</span>}
      </div>
      
      {/* List */}
      <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-neutral-400 text-sm">Loading transport data...</div>
        ) : sortedList.length === 0 ? (
          <Empty text={filter === 'all' ? 'No vehicles in transport' : `No ${filter.replace('_', ' ')} vehicles`} />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                <th className="px-3 py-2 font-medium">
                  <button onClick={() => handleSort('stock')} className={`flex items-center gap-1 text-xs hover:text-blue-600 ${sortBy === 'stock' ? 'text-blue-600' : ''}`}>
                    Stock <span className={`text-[10px] ${sortBy === 'stock' ? '' : 'opacity-30'}`}>{sortBy === 'stock' ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</span>
                  </button>
                </th>
                <th className="px-3 py-2 font-medium">
                  <button onClick={() => handleSort('vehicle')} className={`flex items-center gap-1 text-xs hover:text-blue-600 ${sortBy === 'vehicle' ? 'text-blue-600' : ''}`}>
                    Vehicle <span className={`text-[10px] ${sortBy === 'vehicle' ? '' : 'opacity-30'}`}>{sortBy === 'vehicle' ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</span>
                  </button>
                </th>
                <th className="px-3 py-2 font-medium">VIN (Last 8)</th>
                <th className="px-3 py-2 font-medium">
                  <button onClick={() => handleSort('buyer')} className={`flex items-center gap-1 text-xs hover:text-blue-600 ${sortBy === 'buyer' ? 'text-blue-600' : ''}`}>
                    Buyer <span className={`text-[10px] ${sortBy === 'buyer' ? '' : 'opacity-30'}`}>{sortBy === 'buyer' ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</span>
                  </button>
                </th>
                <th className="px-3 py-2 font-medium">
                  <button onClick={() => handleSort('vendor')} className={`flex items-center gap-1 text-xs hover:text-blue-600 ${sortBy === 'vendor' ? 'text-blue-600' : ''}`}>
                    Transport Vendor <span className={`text-[10px] ${sortBy === 'vendor' ? '' : 'opacity-30'}`}>{sortBy === 'vendor' ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</span>
                  </button>
                </th>
                <th className="px-3 py-2 font-medium">
                  <button onClick={() => handleSort('status')} className={`flex items-center gap-1 text-xs hover:text-blue-600 ${sortBy === 'status' ? 'text-blue-600' : ''}`}>
                    Status <span className={`text-[10px] ${sortBy === 'status' ? '' : 'opacity-30'}`}>{sortBy === 'status' ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</span>
                  </button>
                </th>
                <th className="px-3 py-2 font-medium">
                  <button onClick={() => handleSort('eta')} className={`flex items-center gap-1 text-xs hover:text-blue-600 ${sortBy === 'eta' ? 'text-blue-600' : ''}`}>
                    ETA <span className={`text-[10px] ${sortBy === 'eta' ? '' : 'opacity-30'}`}>{sortBy === 'eta' ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</span>
                  </button>
                </th>
                <th className="px-3 py-2 font-medium">
                  <button onClick={() => handleSort('days')} className={`flex items-center gap-1 text-xs hover:text-blue-600 ${sortBy === 'days' ? 'text-blue-600' : ''}`}>
                    Days <span className={`text-[10px] ${sortBy === 'days' ? '' : 'opacity-30'}`}>{sortBy === 'days' ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</span>
                  </button>
                </th>
                <th className="px-3 py-2 font-medium">QR</th>
                <th className="px-3 py-2 font-medium">Notes</th>
                <th className="px-3 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedList.map(v => {
                const days = Math.floor(v.daysSinceEntry) || 0;
                const isUrgent = v.transportStatus === 'pending' && days >= 5;
                
                return (
                  <tr 
                    key={v.id} 
                    className={`border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer ${isUrgent ? 'bg-red-50' : ''}`}
                    onClick={() => {
                      openEditModal(v);
                    }}
                  >
                    <td className="px-3 py-2">
                      <span className="font-mono text-xs">{v.stockNumber}</span>
                      {isUrgent && <span className="ml-1 text-red-500"></span>}
                    </td>
                    <td className="px-3 py-2">{v.year} {v.make} {v.model}</td>
                    <td className="px-3 py-2"><VinDisplay vin={v.vin} /></td>
                    <td className="px-3 py-2 text-xs">{v.buyerName || v.createdByName || '-'}</td>
                    <td className="px-3 py-2 text-xs">{v.transportVendorName || <span className="text-neutral-400">Not assigned</span>}</td>
                    <td className="px-3 py-2">
                      {getStatusBadge(v.transportStatus)}
                      {v.transportStatus === 'delivered' && !v.transportCost && v.endUse !== 'copart' && v.endUse !== 'iaai' && (
                        <span className="ml-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">⚠️ Cost needed</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">{v.transportEta ? new Date(v.transportEta).toLocaleDateString() : '-'}</td>
                    <td className={`px-3 py-2 text-xs ${getDaysClass(days, v.transportStatus)}`}>{days}d</td>
                    <td className="px-3 py-2 text-center">
                      <QRCodePopup vehicle={v} className="text-xs" />
                    </td>
                    <td className="px-3 py-2 text-xs max-w-[150px] truncate text-neutral-500">
                      {v.transportNotes || <span className="text-blue-500 italic">Click to add notes</span>}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <select 
                        value={v.transportStatus} 
                        onChange={(e) => {
                          e.stopPropagation();
                          handleStatusChange(v.id, e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs border border-neutral-200 rounded px-1 py-0.5 mr-2"
                      >
                        <option value="pending">Pending</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="in_transit">In Transit</option>
                        <option value="delivered">Delivered</option>
                      </select>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(v);
                        }} 
                        className="text-blue-600 hover:underline text-xs"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit Modal */}
      {editingVehicle && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setEditingVehicle(null)}>
          <div className="bg-white w-full max-w-md rounded-lg shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-neutral-200">
              <div className="font-medium">Update Transport</div>
              <div className="text-sm text-neutral-500">{editingVehicle.stockNumber} - {editingVehicle.year} {editingVehicle.make} {editingVehicle.model}</div>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs text-neutral-500 mb-1">Status</label>
                <select
                  value={editForm.status}
                  onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}
                  className="w-full h-8 px-2 text-sm border border-neutral-200 rounded bg-white"
                >
                  <option value="pending">Pending</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="in_transit">In Transit</option>
                  <option value="delivered">Delivered</option>
                </select>
              </div>
              
              <div>
                <label className="block text-xs text-neutral-500 mb-1">Transport Vendor</label>
                <select
                  value={editForm.vendorId}
                  onChange={e => setEditForm(f => ({ ...f, vendorId: e.target.value }))}
                  className="w-full h-8 px-2 text-sm border border-neutral-200 rounded bg-white"
                >
                  <option value="">Select vendor...</option>
                  {transportVendors.map(v => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-xs text-neutral-500 mb-1">Scheduled Pickup Date</label>
                <input
                  type="date"
                  value={editForm.scheduledDate}
                  onChange={e => setEditForm(f => ({ ...f, scheduledDate: e.target.value }))}
                  className="w-full h-8 px-2 text-sm border border-neutral-200 rounded"
                />
              </div>
              
              <div>
                <label className="block text-xs text-neutral-500 mb-1">ETA (Estimated Arrival)</label>
                <input
                  type="date"
                  value={editForm.eta}
                  onChange={e => setEditForm(f => ({ ...f, eta: e.target.value }))}
                  className="w-full h-8 px-2 text-sm border border-neutral-200 rounded"
                />
              </div>
              
              <div>
                <label className="block text-xs text-neutral-500 mb-1">Transport Cost ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editForm.transportCost}
                  onChange={e => setEditForm(f => ({ ...f, transportCost: e.target.value }))}
                  className="w-full h-8 px-2 text-sm border border-neutral-200 rounded"
                  placeholder="Enter transport cost..."
                />
              </div>
              
              <div>
                <label className="block text-xs text-neutral-500 mb-1">Notes</label>
                <textarea
                  value={editForm.notes}
                  onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full h-20 px-2 py-1 text-sm border border-neutral-200 rounded resize-none"
                  placeholder="Transport notes..."
                />
              </div>
              
              <div className="flex justify-end gap-2 pt-2 border-t border-neutral-200">
                <button type="button" onClick={() => setEditingVehicle(null)} className="px-3 py-1.5 text-sm text-neutral-600">Cancel</button>
                <button onClick={handleSaveEdit} className="px-4 py-1.5 text-sm bg-neutral-800 text-white rounded hover:bg-neutral-700">
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delivery Confirmation Modal */}
      {deliveryModal && (() => {
        const isCopart = deliveryModal.endUse === 'copart' || deliveryModal.endUse === 'iaai';
        
        return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setDeliveryModal(null)}>
          <div className="bg-white w-full max-w-md rounded-lg shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-neutral-200">
              <div className="font-medium text-lg">
                {isCopart ? 'Confirm Copart Pickup' : (canSeeCosts ? 'Confirm Transport Cost' : 'Mark as Delivered')}
              </div>
              <div className="text-sm text-neutral-500 mt-1">
                {deliveryModal.stockNumber} - {deliveryModal.year} {deliveryModal.make} {deliveryModal.model}
              </div>
            </div>
            <div className="p-4 space-y-4">
              {isCopart ? (
                /* Copart/IAAI - simple confirmation, no cost */
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="text-sm text-amber-800 font-medium">📦 Confirm Copart Pickup</div>
                  <div className="text-xs text-amber-600 mt-1">
                    Confirm that Copart has picked up this vehicle. No transport cost will be recorded.
                  </div>
                </div>
              ) : (
                /* Regular vehicle - show cost input */
                <>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="text-sm text-green-800 font-medium">🚗 Vehicle Arrived!</div>
                    <div className="text-xs text-green-600 mt-1">
                      {canSeeCosts 
                        ? 'Please confirm the actual transport cost to attach to this vehicle.'
                        : 'Confirm this vehicle has been delivered to the lot.'}
                    </div>
                  </div>
                  
                  {/* Only show cost input for admin/office */}
                  {canSeeCosts && (
                    <div>
                      <label className="block text-sm text-neutral-600 mb-1">Actual Transport Cost *</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">$</span>
                        <input
                          type="number"
                          step="0.01"
                          value={deliveryCost}
                          onChange={e => setDeliveryCost(e.target.value)}
                          className="w-full h-10 pl-7 pr-3 text-lg border border-neutral-200 rounded-lg"
                          placeholder="0.00"
                          autoFocus
                        />
                      </div>
                      {deliveryModal.transportEstimate && (
                        <div className="text-xs text-neutral-500 mt-1">
                          Estimated: ${parseFloat(deliveryModal.transportEstimate).toLocaleString()}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => handleConfirmDelivery(true)}
                  className="flex-1 px-4 py-2.5 text-sm border border-neutral-300 text-neutral-600 rounded-lg hover:bg-neutral-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleConfirmDelivery(false)}
                  disabled={!isCopart && canSeeCosts && !deliveryCost}
                  className="flex-1 px-4 py-2.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {isCopart ? '✓ Confirm Pickup' : '✓ Confirm Delivered'}
                </button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Inspection Prompt Modal - shown after delivery */}
      {inspectionPrompt && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setInspectionPrompt(null)}>
          <div className="bg-white w-full max-w-md rounded-lg shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-neutral-200">
              <div className="font-medium text-lg">🔧 Start Inspection?</div>
              <div className="text-sm text-neutral-500 mt-1">
                {inspectionPrompt.stockNumber} - {inspectionPrompt.year} {inspectionPrompt.make} {inspectionPrompt.model}
              </div>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="text-sm text-blue-800 font-medium">Vehicle has been marked as delivered!</div>
                <div className="text-xs text-blue-600 mt-1">Would you like to start the inspection now?</div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setInspectionPrompt(null)}
                  className="flex-1 px-4 py-2.5 text-sm border border-neutral-300 text-neutral-600 rounded-lg hover:bg-neutral-50"
                >
                  Later
                </button>
                <button
                  onClick={() => {
                    if (onStartInspection) {
                      onStartInspection(inspectionPrompt.id);
                    }
                    setInspectionPrompt(null);
                  }}
                  className="flex-1 px-4 py-2.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  🔧 Start Inspection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Recon Module - Full Repair Order System
const Recon = ({ vehicles, onRefresh }) => {
  const [repairOrders, setRepairOrders] = useState([]);
  const [allROs, setAllROs] = useState([]); // Keep all ROs for snoozed section
  const [stats, setStats] = useState({ open: 0, in_progress: 0, waiting_parts: 0, complete: 0, closed: 0, snoozed: 0 });
  const [filter, setFilter] = useState('active'); // active, all, snoozed, or specific status
  const [loading, setLoading] = useState(true);
  const [showCreateRO, setShowCreateRO] = useState(false);
  const [selectedRO, setSelectedRO] = useState(null);
  const [users, setUsers] = useState([]);

  const reconVehicles = vehicles.filter(v => ['delivered', 'inspection', 'in_recon'].includes(v.status));

  // Helper to check if RO is snoozed or waiting
  const isROSnoozedOrWaiting = (ro) => {
    const isSnoozed = ro.snoozedUntil && new Date(ro.snoozedUntil) > new Date();
    const isWaitingParts = ro.status === 'waiting_parts';
    return isSnoozed || isWaitingParts;
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const statusFilter = filter === 'active' || filter === 'snoozed' ? null : (filter === 'all' ? null : filter);
      const [ros, statsData, usersData] = await Promise.all([
        api.getRepairOrders(statusFilter ? { status: statusFilter } : {}),
        api.getRepairOrderStats(),
        api.getUsers().catch(() => [])
      ]);
      
      // Store all ROs for snoozed section
      setAllROs(ros);
      
      // Count snoozed/waiting ROs
      const snoozedCount = ros.filter(isROSnoozedOrWaiting).length;
      
      // Filter based on selected filter
      let filteredROs;
      if (filter === 'active') {
        // Active = open, in_progress but NOT snoozed or waiting_parts
        filteredROs = ros.filter(ro => 
          ['open', 'in_progress'].includes(ro.status) && !isROSnoozedOrWaiting(ro)
        );
      } else if (filter === 'snoozed') {
        // Show only snoozed/waiting
        filteredROs = ros.filter(isROSnoozedOrWaiting);
      } else {
        filteredROs = ros;
      }
      
      setRepairOrders(filteredROs);
      setStats({ ...statsData, snoozed: snoozedCount });
      setUsers(usersData);
    } catch (e) {
      console.error('Failed to load recon data:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [filter]);

  const getStatusBadge = (status) => {
    const badges = {
      open: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      waiting_parts: 'bg-orange-100 text-orange-800',
      complete: 'bg-green-100 text-green-800',
      closed: 'bg-neutral-100 text-neutral-600'
    };
    const labels = {
      open: 'Open',
      in_progress: 'In Progress',
      waiting_parts: 'Waiting Parts',
      complete: 'Complete',
      closed: 'Closed'
    };
    return <span className={`px-2 py-0.5 text-xs rounded ${badges[status] || badges.open}`}>{labels[status] || status}</span>;
  };

  const getPriorityBadge = (priority) => {
    if (priority === 'high') return <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded">HIGH</span>;
    if (priority === 'low') return <span className="px-1.5 py-0.5 text-xs bg-neutral-100 text-neutral-500 rounded">LOW</span>;
    return null;
  };

  // Active count excludes snoozed/waiting
  const activeCount = stats.open + stats.in_progress - stats.snoozed;

  return (
    <div className="space-y-4">
      {/* Header with stats */}
      <div className="flex items-center justify-between">
        <Header title="Repair Orders" count={activeCount > 0 ? activeCount : 0} />
        <button 
          onClick={() => setShowCreateRO(true)}
          className="px-3 py-1.5 text-sm bg-neutral-800 text-white rounded hover:bg-neutral-700"
        >
          + New RO
        </button>
      </div>

      {/* Stats bar */}
      <div className="flex gap-2 text-xs flex-wrap">
        <button 
          onClick={() => setFilter('active')}
          className={`px-3 py-1.5 rounded ${filter === 'active' ? 'bg-neutral-800 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}
        >
          Active ({activeCount > 0 ? activeCount : 0})
        </button>
        <button 
          onClick={() => setFilter('snoozed')}
          className={`px-3 py-1.5 rounded ${filter === 'snoozed' ? 'bg-purple-600 text-white' : 'bg-purple-50 text-purple-700 hover:bg-purple-100'}`}
        >
          💤 Snoozed/Waiting ({stats.snoozed})
        </button>
        <button 
          onClick={() => setFilter('open')}
          className={`px-3 py-1.5 rounded ${filter === 'open' ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
        >
          Open ({stats.open})
        </button>
        <button 
          onClick={() => setFilter('in_progress')}
          className={`px-3 py-1.5 rounded ${filter === 'in_progress' ? 'bg-yellow-600 text-white' : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'}`}
        >
          In Progress ({stats.in_progress})
        </button>
        <button 
          onClick={() => setFilter('complete')}
          className={`px-3 py-1.5 rounded ${filter === 'complete' ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}
        >
          Complete ({stats.complete})
        </button>
        <button 
          onClick={() => setFilter('all')}
          className={`px-3 py-1.5 rounded ${filter === 'all' ? 'bg-neutral-600 text-white' : 'bg-neutral-50 text-neutral-600 hover:bg-neutral-100'}`}
        >
          All
        </button>
      </div>

      {/* RO List */}
      {loading ? (
        <div className="text-neutral-400 text-sm py-8 text-center">Loading...</div>
      ) : repairOrders.length > 0 ? (
        <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                <th className="px-3 py-2 font-medium">RO #</th>
                <th className="px-3 py-2 font-medium">Vehicle</th>
                <th className="px-3 py-2 font-medium">Complaint</th>
                <th className="px-3 py-2 font-medium">Assigned To</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium text-right">Cost</th>
                <th className="px-3 py-2 font-medium text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {repairOrders.map(ro => (
                <tr 
                  key={ro.id} 
                  className="border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer"
                  onClick={() => setSelectedRO(ro)}
                >
                  <td className="px-3 py-2 font-mono text-xs">
                    {ro.roNumber}
                    {getPriorityBadge(ro.priority) && <span className="ml-2">{getPriorityBadge(ro.priority)}</span>}
                  </td>
                  <td className="px-3 py-2">
                    {ro.isCustomerVehicle ? (
                      <div>
                        <div className="text-xs text-purple-600 font-medium">CUSTOMER</div>
                        <div>{ro.customerVehicleYear} {ro.customerVehicleMake} {ro.customerVehicleModel}</div>
                        <div className="text-xs text-neutral-500">{ro.customerName}</div>
                      </div>
                    ) : (
                      <div>
                        <div className="font-mono text-xs text-neutral-500">{ro.stockNumber}</div>
                        <div>{ro.vehicleYear} {ro.vehicleMake} {ro.vehicleModel}</div>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-neutral-600 max-w-xs truncate">{ro.complaint || '-'}</td>
                  <td className="px-3 py-2 text-xs">{ro.assignedToName || '-'}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col gap-1">
                      {getStatusBadge(ro.status)}
                      {ro.snoozedUntil && new Date(ro.snoozedUntil) > new Date() && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">
                          💤 Until {new Date(ro.snoozedUntil).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs">
                    {ro.totalCost > 0 ? `$${parseFloat(ro.totalCost).toLocaleString()}` : '-'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <div className="flex gap-1 justify-center">
                      {isROSnoozedOrWaiting(ro) && (
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (window.confirm(`Wake up RO #${ro.roNumber}?`)) {
                              try {
                                await api.updateRepairOrder(ro.id, { 
                                  status: ro.status === 'waiting_parts' ? 'in_progress' : ro.status,
                                  snoozedUntil: null,
                                  snoozeReason: null
                                });
                                loadData();
                                if (onRefresh) onRefresh();
                              } catch (err) {
                                alert('Failed: ' + err.message);
                              }
                            }
                          }}
                          className="text-xs text-green-600 hover:text-green-800 hover:bg-green-50 px-2 py-1 rounded"
                          title="Wake Up"
                        >
                          Wake
                        </button>
                      )}
                      {currentUser?.role === 'admin' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteRO(ro.id, ro.roNumber);
                          }}
                          className="text-xs text-red-600 hover:text-red-800 hover:bg-red-50 px-2 py-1 rounded"
                          title="Delete RO"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Empty text={`No ${filter === 'active' ? 'active ' : filter !== 'all' ? filter + ' ' : ''}repair orders`} />
      )}

      {/* Vehicles needing RO */}
      {reconVehicles.filter(v => v.status === 'delivered' && v.whatItNeeds).length > 0 && (
        <div className="mt-6">
          <div className="text-sm font-medium text-neutral-700 mb-2">Vehicles Flagged for Service</div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 space-y-2">
            {reconVehicles.filter(v => v.status === 'delivered' && v.whatItNeeds).map(v => (
              <div key={v.id} className="flex items-center justify-between">
                <div>
                  <span className="font-mono text-xs text-neutral-500">{v.stockNumber}</span>
                  <span className="ml-2">{v.year} {v.make} {v.model}</span>
                  <span className="ml-2 text-xs text-neutral-500">- {v.whatItNeeds}</span>
                </div>
                <button 
                  onClick={() => setShowCreateRO({ vehicleId: v.id, vehicle: v })}
                  className="text-xs text-blue-600 hover:underline"
                >
                  Create RO
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create RO Modal */}
      {showCreateRO && (
        <CreateROModal 
          vehicles={vehicles}
          users={users}
          preselectedVehicle={showCreateRO.vehicle}
          onClose={() => setShowCreateRO(false)}
          onSave={() => { setShowCreateRO(false); loadData(); onRefresh(); }}
        />
      )}

      {/* RO Detail Modal */}
      {selectedRO && (
        <RODetailModal 
          ro={selectedRO}
          users={users}
          currentUser={currentUser}
          onClose={() => setSelectedRO(null)}
          onSave={() => { setSelectedRO(null); loadData(); onRefresh(); }}
        />
      )}
    </div>
  );
};

// Create RO Modal
const CreateROModal = ({ vehicles, users, preselectedVehicle, onClose, onSave }) => {
  const [isCustomer, setIsCustomer] = useState(false);
  const [form, setForm] = useState({
    vehicleId: preselectedVehicle?.id || '',
    complaint: preselectedVehicle?.whatItNeeds || '',
    workToBeDone: '',
    partsNeeded: '',
    laborEstimate: '',
    assignedToUserId: '',
    priority: 'normal',
    notes: '',
    // Customer vehicle fields
    customerName: '',
    customerPhone: '',
    customerVehicleYear: '',
    customerVehicleMake: '',
    customerVehicleModel: '',
  });
  const [saving, setSaving] = useState(false);
  const [vehicleSearch, setVehicleSearch] = useState(preselectedVehicle ? `${preselectedVehicle.stockNumber} - ${preselectedVehicle.year} ${preselectedVehicle.make} ${preselectedVehicle.model}` : '');
  const [showVehicleDropdown, setShowVehicleDropdown] = useState(false);

  const shopUsers = users.filter(u => u.role === 'shop' || u.role === 'admin');
  
  // Filter vehicles based on search (stock number, year, make, model, VIN)
  const filteredVehicles = vehicles.filter(v => {
    if (v.status === 'sold') return false;
    if (!vehicleSearch) return true;
    const search = vehicleSearch.toLowerCase();
    return (
      (v.stockNumber || '').toLowerCase().includes(search) ||
      (v.year || '').toString().includes(search) ||
      (v.make || '').toLowerCase().includes(search) ||
      (v.model || '').toLowerCase().includes(search) ||
      (v.vin || '').toLowerCase().includes(search)
    );
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isCustomer && !form.vehicleId) return alert('Please select a vehicle');
    if (isCustomer && (!form.customerName || !form.customerVehicleMake)) return alert('Customer name and vehicle info required');
    
    setSaving(true);
    try {
      // Include precheck parts if available
      let precheckParts = [];
      const selectedVehicle = vehicles?.find(v => v.id === parseInt(form.vehicleId)) || preselectedVehicle;
      
      if (selectedVehicle?.precheckData) {
        try {
          const precheckData = JSON.parse(selectedVehicle.precheckData);
          precheckParts = precheckData.partsNeeded || [];
        } catch (parseErr) {
          console.error('Failed to parse precheck data:', parseErr);
        }
      }

      const roData = {
        ...form,
        isCustomerVehicle: isCustomer,
        vehicleId: isCustomer ? null : parseInt(form.vehicleId),
        laborEstimate: parseFloat(form.laborEstimate) || 0,
        assignedToUserId: form.assignedToUserId ? parseInt(form.assignedToUserId) : null,
        partsNeeded: JSON.stringify(precheckParts) // Stringify precheck parts array
      };
      
      const result = await api.createRepairOrder(roData);
      
      if (precheckParts.length > 0) {
        alert(` RO created successfully with ${precheckParts.length} parts from precheck!\n\nParts: ${precheckParts.map(p => p.description).join(', ')}`);
      }
      
      onSave();
    } catch (e) {
      alert('Failed to create RO: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg shadow-lg" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-neutral-200 flex justify-between items-center sticky top-0 bg-white">
          <span className="font-medium text-lg">Create Repair Order</span>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Vehicle Type Toggle */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setIsCustomer(false)}
              className={`flex-1 py-2 text-sm rounded ${!isCustomer ? 'bg-neutral-800 text-white' : 'bg-neutral-100 text-neutral-600'}`}
            >
              Inventory Vehicle
            </button>
            <button
              type="button"
              onClick={() => setIsCustomer(true)}
              className={`flex-1 py-2 text-sm rounded ${isCustomer ? 'bg-purple-600 text-white' : 'bg-neutral-100 text-neutral-600'}`}
            >
              Customer Vehicle
            </button>
          </div>

          {!isCustomer ? (
            <div className="relative">
              <label className="block text-xs text-neutral-500 mb-1">Search Vehicle *</label>
              <input
                type="text"
                value={vehicleSearch}
                onChange={e => {
                  setVehicleSearch(e.target.value);
                  setShowVehicleDropdown(true);
                  // Clear selection if user is typing something different
                  if (form.vehicleId) {
                    const selected = vehicles.find(v => v.id === parseInt(form.vehicleId));
                    const selectedText = selected ? `${selected.stockNumber} - ${selected.year} ${selected.make} ${selected.model}` : '';
                    if (e.target.value !== selectedText) {
                      setForm(f => ({ ...f, vehicleId: '' }));
                    }
                  }
                }}
                onFocus={() => setShowVehicleDropdown(true)}
                placeholder="Type stock #, year, make, model, or VIN..."
                className="w-full h-9 px-2 text-sm border border-neutral-200 rounded bg-white"
              />
              {form.vehicleId && (
                <span className="absolute right-2 top-7 text-green-600 text-sm">✓</span>
              )}
              {showVehicleDropdown && vehicleSearch && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredVehicles.length === 0 ? (
                    <div className="p-3 text-sm text-neutral-500">No vehicles found</div>
                  ) : (
                    filteredVehicles.slice(0, 20).map(v => (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => {
                          setForm(f => ({ ...f, vehicleId: v.id.toString(), complaint: v.whatItNeeds || f.complaint }));
                          setVehicleSearch(`${v.stockNumber} - ${v.year} ${v.make} ${v.model}`);
                          setShowVehicleDropdown(false);
                        }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-100 border-b border-neutral-100 last:border-0"
                      >
                        <div className="font-medium">{v.stockNumber} - {v.year} {v.make} {v.model}</div>
                        {v.vin && <div className="text-xs text-neutral-500">VIN: <VinDisplay vin={v.vin} /></div>}
                        {v.whatItNeeds && <div className="text-xs text-amber-600 truncate">Needs: {v.whatItNeeds}</div>}
                      </button>
                    ))
                  )}
                  {filteredVehicles.length > 20 && (
                    <div className="p-2 text-xs text-neutral-500 text-center bg-neutral-50">
                      Showing 20 of {filteredVehicles.length} results - type more to narrow
                    </div>
                  )}
                </div>
              )}
              {/* Click outside to close dropdown */}
              {showVehicleDropdown && (
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowVehicleDropdown(false)}
                />
              )}
            </div>
          ) : (
            <div className="space-y-3 p-3 bg-purple-50 rounded-lg">
              <div className="text-xs font-medium text-purple-700">Customer Information</div>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Customer Name *"
                  value={form.customerName}
                  onChange={e => setForm(f => ({ ...f, customerName: e.target.value }))}
                  className="h-9 px-2 text-sm border border-neutral-200 rounded"
                  required={isCustomer}
                />
                <input
                  type="text"
                  placeholder="Phone"
                  value={form.customerPhone}
                  onChange={e => setForm(f => ({ ...f, customerPhone: e.target.value }))}
                  className="h-9 px-2 text-sm border border-neutral-200 rounded"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <input
                  type="text"
                  placeholder="Year"
                  value={form.customerVehicleYear}
                  onChange={e => setForm(f => ({ ...f, customerVehicleYear: e.target.value }))}
                  className="h-9 px-2 text-sm border border-neutral-200 rounded"
                />
                <input
                  type="text"
                  placeholder="Make *"
                  value={form.customerVehicleMake}
                  onChange={e => setForm(f => ({ ...f, customerVehicleMake: e.target.value }))}
                  className="h-9 px-2 text-sm border border-neutral-200 rounded"
                  required={isCustomer}
                />
                <input
                  type="text"
                  placeholder="Model"
                  value={form.customerVehicleModel}
                  onChange={e => setForm(f => ({ ...f, customerVehicleModel: e.target.value }))}
                  className="h-9 px-2 text-sm border border-neutral-200 rounded"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs text-neutral-500 mb-1">Complaint / Concern</label>
            <textarea
              value={form.complaint}
              onChange={e => setForm(f => ({ ...f, complaint: e.target.value }))}
              className="w-full h-20 px-2 py-1 text-sm border border-neutral-200 rounded resize-none"
              placeholder="What's wrong with the vehicle?"
            />
          </div>

          <div>
            <label className="block text-xs text-neutral-500 mb-1">Labor for Work Needed</label>
            <textarea
              value={form.workToBeDone}
              onChange={e => setForm(f => ({ ...f, workToBeDone: e.target.value }))}
              className="w-full h-20 px-2 py-1 text-sm border border-neutral-200 rounded resize-none"
              placeholder="Describe the work needed..."
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-neutral-500 mb-1">Assign To</label>
              <select
                value={form.assignedToUserId}
                onChange={e => setForm(f => ({ ...f, assignedToUserId: e.target.value }))}
                className="w-full h-9 px-2 text-sm border border-neutral-200 rounded bg-white"
              >
                <option value="">Unassigned</option>
                {shopUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-neutral-500 mb-1">Priority</label>
              <select
                value={form.priority}
                onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                className="w-full h-9 px-2 text-sm border border-neutral-200 rounded bg-white"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs text-neutral-500 mb-1">Labor Estimate ($)</label>
            <input
              type="number"
              step="0.01"
              value={form.laborEstimate}
              onChange={e => setForm(f => ({ ...f, laborEstimate: e.target.value }))}
              className="w-full h-9 px-2 text-sm border border-neutral-200 rounded"
              placeholder="0.00"
            />
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-neutral-200">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-neutral-600">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-neutral-800 text-white rounded disabled:opacity-50">
              {saving ? 'Creating...' : 'Create RO'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Outside vendor type options
const VENDOR_TYPES = [
  { value: 'body_shop_outside', label: 'Body Shop (Outside)' },
  { value: 'body_shop_inhouse', label: 'Body Shop (In-House / Claude)' },
  { value: 'pdr', label: 'PDR' },
  { value: 'rim_repair', label: 'Rim Repair' },
  { value: 'interior_repair', label: 'Interior Repair' },
  { value: 'glass', label: 'Glass' },
  { value: 'mechanical_outside', label: 'Mechanical (Outside)' },
  { value: 'detail', label: 'Detail' },
  { value: 'other', label: 'Other' },
];

const APPROVAL_THRESHOLD = 600;

// RO Detail Modal - Enhanced with parts tracking, vendors, and approval
const RODetailModal = ({ ro, users, currentUser, onClose, onSave }) => {
  const [roData, setRoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showAddPart, setShowAddPart] = useState(false);
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [partForm, setPartForm] = useState({ description: '', partNumber: '', quantity: 1, unitCost: '', vendorName: '', orderDate: '', eta: '', trackingUrl: '', status: 'needed', notes: '' });
  const [vendorForm, setVendorForm] = useState({ vendorType: '', vendorName: '', estimatedCost: '', notes: '' });

  const shopUsers = users.filter(u => u.role === 'shop' || u.role === 'admin');

  const loadRO = async () => {
    try {
      const data = await api.getRepairOrder(ro.id);
      
      // Parse partsNeeded if it exists
      if (data.partsNeeded) {
        try {
          // partsNeeded should always be a string from backend, but handle both cases
          data.partsNeeded = typeof data.partsNeeded === 'string' ? JSON.parse(data.partsNeeded) : data.partsNeeded;
        } catch (parseErr) {
          console.error('Failed to parse partsNeeded:', parseErr);
          data.partsNeeded = [];
        }
      } else {
        data.partsNeeded = [];
      }
      
      setRoData(data);
      setForm({
        status: data.status,
        diagnosis: data.diagnosis || '',
        workToBeDone: data.workToBeDone || '',
        laborHours: data.laborHours || '',
        laborRate: data.laborRate || 75,
        assignedToUserId: data.assignedToUserId || '',
        priority: data.priority || 'normal',
        notes: data.notes || ''
      });
    } catch (e) {
      console.error('Failed to load RO:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRO(); }, [ro.id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateRepairOrder(ro.id, {
        ...form,
        laborHours: parseFloat(form.laborHours) || 0,
        laborRate: parseFloat(form.laborRate) || 75,
        assignedToUserId: form.assignedToUserId ? parseInt(form.assignedToUserId) : null
      });
      onSave();
    } catch (e) {
      alert('Failed to update RO: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddPart = async () => {
    if (!partForm.description) return alert('Part description required');
    try {
      await api.addRepairOrderPart(ro.id, {
        ...partForm,
        quantity: parseInt(partForm.quantity) || 1,
        unitCost: parseFloat(partForm.unitCost) || 0
      });
      setShowAddPart(false);
      setPartForm({ description: '', partNumber: '', quantity: 1, unitCost: '', vendorName: '', orderDate: '', eta: '', trackingUrl: '', status: 'needed', notes: '' });
      loadRO();
    } catch (e) {
      alert('Failed to add part: ' + e.message);
    }
  };

  const handleUpdatePartStatus = async (partId, newStatus) => {
    try {
      await api.updateRepairOrderPart(ro.id, partId, { status: newStatus });
      loadRO();
    } catch (e) {
      alert('Failed to update part: ' + e.message);
    }
  };

  const handleDeletePart = async (partId) => {
    if (!window.confirm('Delete this part?')) return;
    try {
      await api.deleteRepairOrderPart(ro.id, partId);
      loadRO();
    } catch (e) {
      alert('Failed to delete part: ' + e.message);
    }
  };

  const handleAddVendor = async () => {
    if (!vendorForm.vendorType) return alert('Vendor type required');
    try {
      await api.addRepairOrderVendor(ro.id, {
        ...vendorForm,
        estimatedCost: parseFloat(vendorForm.estimatedCost) || 0
      });
      setShowAddVendor(false);
      setVendorForm({ vendorType: '', vendorName: '', estimatedCost: '', notes: '' });
      loadRO();
    } catch (e) {
      alert('Failed to add vendor: ' + e.message);
    }
  };

  const handleUpdateVendorStatus = async (vendor, newStatus) => {
    try {
      let actualCost;
      if (newStatus === 'complete') {
        const defaultVal = vendor.actualCost > 0 ? vendor.actualCost : (vendor.estimatedCost || 0);
        const costStr = window.prompt('Enter actual cost ($):', defaultVal ? String(defaultVal) : '');
        if (costStr === null) return; // user cancelled
        actualCost = parseFloat(costStr) || 0;
      }
      await api.updateRepairOrderVendor(ro.id, vendor.id, { status: newStatus, ...(actualCost !== undefined && { actualCost }) });
      loadRO();
    } catch (e) {
      alert('Failed to update vendor: ' + e.message);
    }
  };

  const handleDeleteVendor = async (vendorRecordId) => {
    if (!window.confirm('Remove this vendor?')) return;
    try {
      await api.deleteRepairOrderVendor(ro.id, vendorRecordId);
      loadRO();
    } catch (e) {
      alert('Failed to remove vendor: ' + e.message);
    }
  };

  const handleSubmitForApproval = async () => {
    try {
      await api.submitROForApproval(ro.id);
      alert('Submitted for approval');
      loadRO();
    } catch (e) {
      alert('Failed to submit: ' + e.message);
    }
  };

  const handleApprove = async () => {
    try {
      await api.approveRO(ro.id);
      alert('RO Approved');
      loadRO();
    } catch (e) {
      alert('Failed to approve: ' + e.message);
    }
  };

  const handleDeny = async () => {
    const reason = window.prompt('Enter denial reason:');
    if (!reason) return;
    try {
      await api.denyRO(ro.id, reason);
      alert('RO Denied');
      loadRO();
    } catch (e) {
      alert('Failed to deny: ' + e.message);
    }
  };

  const laborCost = (parseFloat(form.laborHours) || 0) * (parseFloat(form.laborRate) || 75);
  const partsCost = roData?.parts?.reduce((sum, p) => sum + parseFloat(p.totalCost || 0), 0) || 0;
  const vendorsCost = roData?.outsideVendors?.reduce((sum, v) => sum + parseFloat(v.actualCost || v.estimatedCost || 0), 0) || 0;
  const totalCost = laborCost + partsCost + vendorsCost;
  const requiresApproval = totalCost > APPROVAL_THRESHOLD;

  if (loading) return <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"><div className="bg-white p-8 rounded-lg">Loading...</div></div>;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-lg shadow-lg" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-neutral-200 sticky top-0 bg-white z-10">
          <div className="flex justify-between items-start">
            <div>
              <div className="font-mono text-lg font-medium">{roData?.roNumber}</div>
              <div className="text-sm text-neutral-500">
                {roData?.isCustomerVehicle ? (
                  <span className="text-purple-600">{roData.customerName} - {roData.customerVehicleYear} {roData.customerVehicleMake} {roData.customerVehicleModel}</span>
                ) : (
                  <span>{roData?.stockNumber} - {roData?.vehicleYear} {roData?.vehicleMake} {roData?.vehicleModel}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* QR Code linking to vehicle */}
              {roData?.stockNumber && (
                <div className="print:block" title="Scan for vehicle details">
                  <QRCodeSVG 
                    value={`https://lotsense.thewholesalecars.com/car/${roData.stockNumber}`} 
                    size={64}
                    level="M"
                  />
                </div>
              )}
              <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl print:hidden">×</button>
            </div>
          </div>
          
          {/* Approval Banner */}
          {requiresApproval && (
            <div className={`mt-3 p-2 rounded text-sm ${
              roData?.approvalStatus === 'approved' ? 'bg-green-50 border border-green-200 text-green-800' :
              roData?.approvalStatus === 'denied' ? 'bg-red-50 border border-red-200 text-red-800' :
              roData?.approvalStatus === 'pending' ? 'bg-yellow-50 border border-yellow-200 text-yellow-800' :
              'bg-orange-50 border border-orange-200 text-orange-800'
            }`}>
              <div className="flex items-center justify-between">
                <span>
                   Total exceeds $600 - {roData?.approvalStatus === 'approved' ? 'APPROVED' : 
                    roData?.approvalStatus === 'denied' ? 'DENIED' :
                    roData?.approvalStatus === 'pending' ? 'PENDING APPROVAL' : 'Requires Approval'}
                  {roData?.approvedByName && ` by ${roData.approvedByName}`}
                </span>
                {!roData?.approvalStatus && (
                  <button onClick={handleSubmitForApproval} className="text-xs bg-orange-600 text-white px-2 py-1 rounded">Submit for Approval</button>
                )}
                {roData?.approvalStatus === 'pending' && (
                  <div className="flex gap-2">
                    <button onClick={handleApprove} className="text-xs bg-green-600 text-white px-2 py-1 rounded">Approve</button>
                    <button onClick={handleDeny} className="text-xs bg-red-600 text-white px-2 py-1 rounded">Deny</button>
                  </div>
                )}
              </div>
              {roData?.approvalNotes && <div className="text-xs mt-1">Note: {roData.approvalNotes}</div>}
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-4 mt-4 border-b border-neutral-200">
            {['details', 'parts', 'vendors', 'labor'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 py-2 text-sm border-b-2 -mb-px ${activeTab === tab ? 'border-neutral-800 text-neutral-800' : 'border-transparent text-neutral-500'}`}
              >
                {tab === 'vendors' ? 'Outside Vendors' : tab}
                {tab === 'parts' && roData?.parts?.length > 0 && <span className="ml-1 text-xs text-neutral-400">({roData.parts.length})</span>}
                {tab === 'vendors' && roData?.outsideVendors?.length > 0 && <span className="ml-1 text-xs text-neutral-400">({roData.outsideVendors.length})</span>}
              </button>
            ))}
          </div>
        </div>
        <div className="p-4">
          {activeTab === 'details' && (
            <div className="space-y-4">
              {/* Status and Assignment */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">Status</label>
                  <select
                    value={form.status}
                    onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                    className="w-full h-9 px-2 text-sm border border-neutral-200 rounded bg-white"
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="waiting_parts">Waiting Parts</option>
                    <option value="complete">Complete</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">Assigned To</label>
                  <select
                    value={form.assignedToUserId}
                    onChange={e => setForm(f => ({ ...f, assignedToUserId: e.target.value }))}
                    className="w-full h-9 px-2 text-sm border border-neutral-200 rounded bg-white"
                  >
                    <option value="">Unassigned</option>
                    {shopUsers.map(u => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">Priority</label>
                  <select
                    value={form.priority}
                    onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                    className="w-full h-9 px-2 text-sm border border-neutral-200 rounded bg-white"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              {/* Complaint */}
              <div>
                <label className="block text-xs text-neutral-500 mb-1">Complaint</label>
                <div className="p-2 bg-neutral-50 rounded text-sm">{roData?.complaint || 'No complaint recorded'}</div>
              </div>

              {/* Diagnosis */}
              <div>
                <label className="block text-xs text-neutral-500 mb-1">Diagnosis</label>
                <textarea
                  value={form.diagnosis}
                  onChange={e => setForm(f => ({ ...f, diagnosis: e.target.value }))}
                  className="w-full h-20 px-2 py-1 text-sm border border-neutral-200 rounded resize-none"
                  placeholder="What did you find?"
                />
              </div>

              {/* Labor for Work Needed */}
              <div>
                <label className="block text-xs text-neutral-500 mb-1">Labor for Work Needed</label>
                <textarea
                  value={form.workToBeDone}
                  onChange={e => setForm(f => ({ ...f, workToBeDone: e.target.value }))}
                  className="w-full h-20 px-2 py-1 text-sm border border-neutral-200 rounded resize-none"
                />
              </div>

              {/* Outside Vendor */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">Outside Vendor Cost</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.outsideVendorCost}
                    onChange={e => setForm(f => ({ ...f, outsideVendorCost: e.target.value }))}
                    className="w-full h-9 px-2 text-sm border border-neutral-200 rounded"
                    placeholder="PDR, body shop, etc."
                  />
                </div>
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">Vendor Notes</label>
                  <input
                    type="text"
                    value={form.outsideVendorNotes}
                    onChange={e => setForm(f => ({ ...f, outsideVendorNotes: e.target.value }))}
                    className="w-full h-9 px-2 text-sm border border-neutral-200 rounded"
                    placeholder="Which vendor, what work"
                  />
                </div>
              </div>

              {/* Estimate Only / Proceeding Status */}
              {!roData?.isCustomerVehicle && (
                <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="estimateOnlyModal"
                      checked={form.estimateOnly !== false}
                      onChange={e => setForm(f => ({ ...f, estimateOnly: e.target.checked }))}
                      className="w-5 h-5 text-orange-600 border-orange-300 rounded focus:ring-orange-500 focus:ring-2 cursor-pointer mt-0.5"
                    />
                    <label htmlFor="estimateOnlyModal" className="cursor-pointer flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-base font-bold ${form.estimateOnly !== false ? 'text-orange-700' : 'text-green-700'}`}>
                          {form.estimateOnly !== false ? '☐ Estimate Only' : '☑ Proceeding with Repairs'}
                        </span>
                      </div>
                      <div className="text-sm text-neutral-600">
                        {form.estimateOnly !== false ? (
                          <span>Parts list for review, no order placed. <strong className="text-orange-700">Does NOT affect vehicle price.</strong></span>
                        ) : (
                          <span>Parts ordered/approved. <strong className="text-green-700">DOES affect vehicle price.</strong></span>
                        )}
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* Cost Summary */}
              <div className={`rounded-lg p-3 ${requiresApproval ? 'bg-orange-50 border border-orange-200' : 'bg-neutral-50'}`}>
                <div className="flex justify-between items-center mb-2">
                  <div className="text-xs text-neutral-500">RO Summary</div>
                  {requiresApproval && <span className="text-xs text-orange-600 font-medium"> Over $600 - Approval Required</span>}
                </div>
                <div className="grid grid-cols-4 gap-3 text-sm">
                  <div>
                    <div className="text-neutral-500 text-xs">Parts</div>
                    <div className="font-mono">${partsCost.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-neutral-500 text-xs">Labor</div>
                    <div className="font-mono">${laborCost.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-neutral-500 text-xs">Outside Vendors</div>
                    <div className="font-mono">${vendorsCost.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-neutral-500 text-xs">Grand Total</div>
                    <div className={`font-mono font-medium ${requiresApproval ? 'text-orange-700' : 'text-green-700'}`}>${totalCost.toLocaleString()}</div>
                  </div>
                </div>
                {roData?.approvalStatus && (
                  <div className={`mt-2 text-xs ${roData.approvalStatus === 'approved' ? 'text-green-600' : roData.approvalStatus === 'denied' ? 'text-red-600' : 'text-yellow-600'}`}>
                    Status: {roData.approvalStatus.toUpperCase()} {roData.approvedByName && `by ${roData.approvedByName}`}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'parts' && (
            <div className="space-y-3">
              {/* DEBUG TEST ELEMENT - REMOVE LATER */}
              <div className="bg-red-500 text-white p-2 text-xs">
                DEBUG: Parts tab is active - changes loaded!
              </div>
              <div className="flex justify-between items-center">
                <div className="text-sm font-medium">Parts ({(roData?.parts?.length || 0) + (roData?.partsNeeded?.length || 0)})</div>
                <div className="flex gap-2">
                  <button 
                    onClick={() => handleSyncPrecheckParts(roData.id, roData.vehicleId)}
                    className="text-xs bg-blue-600 text-white px-2 py-1 rounded"
                    title="Sync parts from precheck"
                  >
                    Sync Precheck Parts
                  </button>
                  <button onClick={() => setShowAddPart(true)} className="text-xs bg-neutral-800 text-white px-2 py-1 rounded">+ Add Part</button>
                </div>
              </div>
              
              {/* Show precheck parts from partsNeeded field */}
              {roData?.partsNeeded && roData.partsNeeded.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded">From Pre-Check</div>
                  {roData.partsNeeded.map((part, idx) => (
                    <div key={`precheck-${idx}`} className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium">{part.description}</div>
                          <div className="text-xs text-neutral-500">
                            {part.partNumber && <span className="mr-2">#{part.partNumber}</span>}
                            {part.vendor && <span className="mr-2">from {part.vendor}</span>}
                            Qty: {part.quantity || 1}
                            {part.estimatedCost && <span className="ml-2">Est: ${part.estimatedCost}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs bg-green-600 text-white px-2 py-1 rounded">Pre-Check</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {showAddPart && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                  <div className="text-xs font-medium text-blue-700">Add Part</div>
                  <div className="grid grid-cols-2 gap-2">
                    <input type="text" placeholder="Part Name *" value={partForm.description} onChange={e => setPartForm(f => ({ ...f, description: e.target.value }))} className="h-8 px-2 text-sm border rounded" />
                    <input type="text" placeholder="Part Number" value={partForm.partNumber} onChange={e => setPartForm(f => ({ ...f, partNumber: e.target.value }))} className="h-8 px-2 text-sm border rounded" />
                    <input type="number" placeholder="Qty" value={partForm.quantity} onChange={e => setPartForm(f => ({ ...f, quantity: e.target.value }))} className="h-8 px-2 text-sm border rounded" />
                    <input type="number" placeholder="Unit Cost" value={partForm.unitCost} onChange={e => setPartForm(f => ({ ...f, unitCost: e.target.value }))} className="h-8 px-2 text-sm border rounded" />
                    <input type="text" placeholder="Vendor/Source" value={partForm.vendorName} onChange={e => setPartForm(f => ({ ...f, vendorName: e.target.value }))} className="h-8 px-2 text-sm border rounded" />
                    <input type="date" placeholder="Order Date" value={partForm.orderDate} onChange={e => setPartForm(f => ({ ...f, orderDate: e.target.value }))} className="h-8 px-2 text-sm border rounded" />
                    <input type="date" placeholder="ETA" value={partForm.eta} onChange={e => setPartForm(f => ({ ...f, eta: e.target.value }))} className="h-8 px-2 text-sm border rounded" />
                    <input type="text" placeholder="Tracking URL" value={partForm.trackingUrl} onChange={e => setPartForm(f => ({ ...f, trackingUrl: e.target.value }))} className="h-8 px-2 text-sm border rounded" />
                  </div>
                  <textarea 
                    placeholder="Notes (optional)" 
                    value={partForm.notes} 
                    onChange={e => setPartForm(f => ({ ...f, notes: e.target.value }))} 
                    className="w-full h-16 px-2 py-1 text-sm border rounded resize-none"
                  />
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setShowAddPart(false)} className="text-xs text-neutral-500">Cancel</button>
                    <button onClick={handleAddPart} className="text-xs bg-blue-600 text-white px-2 py-1 rounded">Add Part</button>
                  </div>
                </div>
              )}
              
              {roData?.parts?.length > 0 ? (
                <div className="space-y-2">
                  {roData.parts.map(p => (
                    <div key={p.id} className="bg-white border border-neutral-200 rounded-lg p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium">{p.description}</div>
                          <div className="text-xs text-neutral-500">
                            {p.partNumber && <span className="mr-2">#{p.partNumber}</span>}
                            {p.vendorName && <span className="mr-2">from {p.vendorName}</span>}
                            Qty: {p.quantity} × ${parseFloat(p.unitCost || 0).toFixed(2)} = <span className="font-mono font-medium">${parseFloat(p.totalCost || 0).toFixed(2)}</span>
                          </div>
                          {(p.orderDate || p.eta) && (
                            <div className="text-xs text-neutral-400 mt-1">
                              {p.orderDate && <span>Ordered: {p.orderDate?.split('T')[0]}</span>}
                              {p.eta && <span className="ml-2">ETA: {p.eta?.split('T')[0]}</span>}
                            </div>
                          )}
                          {p.notes && <div className="text-xs text-neutral-500 mt-1 italic">Note: {p.notes}</div>}
                          {p.trackingUrl && <a href={p.trackingUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">Track Order →</a>}
                        </div>
                        <div className="flex items-center gap-2">
                          <select value={p.status} onChange={e => handleUpdatePartStatus(p.id, e.target.value)} className="text-xs border rounded px-1 py-0.5">
                            <option value="needed">Needed</option>
                            <option value="ordered">Ordered</option>
                            <option value="shipped">Shipped</option>
                            <option value="arrived">Arrived</option>
                            <option value="installed">Installed</option>
                          </select>
                          <button onClick={() => handleDeletePart(p.id)} className="text-xs text-red-500 hover:text-red-700">×</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-neutral-400 text-sm text-center py-4">No parts added yet</div>
              )}
              
              <div className="bg-neutral-50 rounded p-2 text-right">
                <span className="text-xs text-neutral-500">Parts Total: </span>
                <span className="font-mono font-medium">${partsCost.toLocaleString()}</span>
              </div>
            </div>
          )}

          {activeTab === 'vendors' && (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <div className="text-sm font-medium">Outside Vendors ({roData?.outsideVendors?.length || 0})</div>
                <button onClick={() => setShowAddVendor(true)} className="text-xs bg-neutral-800 text-white px-2 py-1 rounded">+ Add Vendor</button>
              </div>
              
              {showAddVendor && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-2">
                  <div className="text-xs font-medium text-purple-700">Add Outside Vendor</div>
                  <div className="grid grid-cols-2 gap-2">
                    <select value={vendorForm.vendorType} onChange={e => setVendorForm(f => ({ ...f, vendorType: e.target.value }))} className="h-8 px-2 text-sm border rounded bg-white">
                      <option value="">Select Type *</option>
                      {VENDOR_TYPES.map(vt => <option key={vt.value} value={vt.value}>{vt.label}</option>)}
                    </select>
                    <input type="text" placeholder="Vendor Name" value={vendorForm.vendorName} onChange={e => setVendorForm(f => ({ ...f, vendorName: e.target.value }))} className="h-8 px-2 text-sm border rounded" />
                    <input type="number" placeholder="Estimated Cost" value={vendorForm.estimatedCost} onChange={e => setVendorForm(f => ({ ...f, estimatedCost: e.target.value }))} className="h-8 px-2 text-sm border rounded" />
                    <input type="text" placeholder="Notes" value={vendorForm.notes} onChange={e => setVendorForm(f => ({ ...f, notes: e.target.value }))} className="h-8 px-2 text-sm border rounded" />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setShowAddVendor(false)} className="text-xs text-neutral-500">Cancel</button>
                    <button onClick={handleAddVendor} className="text-xs bg-purple-600 text-white px-2 py-1 rounded">Add Vendor</button>
                  </div>
                </div>
              )}
              
              {roData?.outsideVendors?.length > 0 ? (
                <div className="space-y-2">
                  {roData.outsideVendors.map(v => {
                    const typeLabel = VENDOR_TYPES.find(vt => vt.value === v.vendorType)?.label || v.vendorType;
                    return (
                      <div key={v.id} className="bg-white border border-neutral-200 rounded-lg p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-medium">{typeLabel}</div>
                            <div className="text-xs text-neutral-500">
                              {v.vendorName && <span className="mr-2">{v.vendorName}</span>}
                              Est: <span className="font-mono">${parseFloat(v.estimatedCost || 0).toFixed(2)}</span>
                              {v.actualCost > 0 && <span className="ml-2">Actual: <span className="font-mono">${parseFloat(v.actualCost).toFixed(2)}</span></span>}
                            </div>
                            {v.scheduledDate && <div className="text-xs text-neutral-400">Scheduled: {v.scheduledDate?.split('T')[0]}</div>}
                            {v.notes && <div className="text-xs text-neutral-400 mt-1">{v.notes}</div>}
                          </div>
                          <div className="flex items-center gap-2">
                            <select value={v.status} onChange={e => handleUpdateVendorStatus(v, e.target.value)} className="text-xs border rounded px-1 py-0.5">
                              <option value="pending">Pending</option>
                              <option value="scheduled">Scheduled</option>
                              <option value="in_progress">In Progress</option>
                              <option value="complete">Complete</option>
                            </select>
                            <button onClick={() => handleDeleteVendor(v.id)} className="text-xs text-red-500 hover:text-red-700">×</button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-neutral-400 text-sm text-center py-4">No outside vendors added</div>
              )}
              
              <div className="bg-neutral-50 rounded p-2 text-right">
                <span className="text-xs text-neutral-500">Vendors Total: </span>
                <span className="font-mono font-medium">${vendorsCost.toLocaleString()}</span>
              </div>
            </div>
          )}

          {activeTab === 'labor' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">Labor Hours</label>
                  <input
                    type="number"
                    step="0.25"
                    value={form.laborHours}
                    onChange={e => setForm(f => ({ ...f, laborHours: e.target.value }))}
                    className="w-full h-9 px-2 text-sm border border-neutral-200 rounded"
                  />
                </div>
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">Labor Rate ($/hr)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.laborRate}
                    onChange={e => setForm(f => ({ ...f, laborRate: e.target.value }))}
                    className="w-full h-9 px-2 text-sm border border-neutral-200 rounded"
                  />
                </div>
              </div>
              <div className="bg-neutral-50 rounded p-3">
                <div className="text-xs text-neutral-500">Labor Total</div>
                <div className="text-lg font-mono font-medium">${laborCost.toLocaleString()}</div>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-neutral-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 text-sm text-neutral-600">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm bg-neutral-800 text-white rounded disabled:opacity-50">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Titles
// ── TitlePrintModal ───────────────────────────────────────────────────────────
// Replaced by the unified Pre-Print screen: same pack tap-to-pick, per-field fill-in
// boxes (no more shared/colliding values), batch mode, and the before-you-print checklist.
const TitlePrintModal = ({ vehicles: vehicleList, onClose }) => (
  <PrintPacketModal mode="titles" vehicles={vehicleList} onClose={onClose} />
);
// ── DealerSettingsModal ───────────────────────────────────────────────────────
const DealerSettingsModal = ({ onClose }) => {
  const [form, setForm] = React.useState({ name: '', address: '', city: '', state: '', zip: '', phone: '', license: '' });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    api.getDealerSettings().then(data => {
      if (data) setForm({
        name: data.name || '',
        address: data.address || '',
        city: data.city || '',
        state: data.state || '',
        zip: data.zip || '',
        phone: data.phone || '',
        license: data.license || '',
      });
    }).catch(() => setError('Failed to load dealer settings')).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true); setError(''); setSaved(false);
    try {
      await api.updateDealerSettings(form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="px-6 pt-5 pb-4 border-b flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-lg">Dealer Settings</h2>
            <p className="text-xs text-gray-500">These values auto-fill on every generated form</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>
        {loading ? (
          <div className="px-6 py-8 text-center text-sm text-gray-500">Loading...</div>
        ) : (
          <div className="px-6 py-4 space-y-3">
            {error && <p className="text-xs text-red-500">{error}</p>}
            {[
              { key: 'name', label: 'Dealer Name' },
              { key: 'address', label: 'Street Address' },
              { key: 'city', label: 'City' },
              { key: 'state', label: 'State' },
              { key: 'zip', label: 'ZIP Code' },
              { key: 'phone', label: 'Phone' },
              { key: 'license', label: 'UD Number (Dealer License #)', highlight: true },
            ].map(({ key, label, highlight }) => (
              <div key={key}>
                <label className={`block text-xs font-medium mb-1 ${highlight ? 'text-blue-700' : 'text-gray-600'}`}>{label}</label>
                <input
                  type="text"
                  value={form[key]}
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  className={`w-full border rounded-lg px-3 py-2 text-sm ${highlight ? 'border-blue-300 bg-blue-50 font-mono' : 'border-gray-300'}`}
                />
              </div>
            ))}
          </div>
        )}
        <div className="px-6 pb-5 flex justify-between items-center border-t pt-4">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm">Cancel</button>
          <div className="flex items-center gap-3">
            {saved && <span className="text-xs text-green-600 font-medium">✓ Saved</span>}
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

const Titles = ({ vehicles, onRefresh, onOpenTitleNotes }) => {
  // Persist active tab to localStorage
  const getPersistedTab = () => { try { return localStorage.getItem('titlesActiveTab') || 'missing'; } catch { return 'missing'; } };
  const [activeTab, setActiveTabState] = useState(getPersistedTab);
  const [showFormTemplates, setShowFormTemplates] = useState(false);
  const [showDealerSettings, setShowDealerSettings] = useState(false);
  const setActiveTab = (tab) => { setActiveTabState(tab); try { localStorage.setItem('titlesActiveTab', tab); } catch {} };
  const [sortBy, setSortBy] = useState('days');
  const [sortDir, setSortDir] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  // Print modal state
  const [printVehicles, setPrintVehicles] = useState(null); // null = closed, array = open
  // Batch selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Title history
  const [titleHistory, setTitleHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySearch, setHistorySearch] = useState('');

  useEffect(() => {
    if (activeTab === 'history' && titleHistory.length === 0) {
      setHistoryLoading(true);
      api.getTitleHistory(300).then(data => {
        setTitleHistory(data || []);
      }).catch(() => {}).finally(() => setHistoryLoading(false));
    }
  }, [activeTab]);
  
  // Exclude customer vehicles from titles - they don't have titles to track
  const needsTitle = vehicles.filter(v =>
    v.titleStatus !== 'in_office' &&
    v.titleStatus !== 'complete' &&
    v.vehicleType !== 'customer_vehicle' &&
    !(v.stockNumber && v.stockNumber.startsWith('CUST-'))
  );
  // In Office: all vehicles with title physically in office, including sold ones
  // (sold vehicles stay here until Deal Desk Title checkbox is checked)
  const inOffice = vehicles.filter(v =>
    v.titleStatus === 'in_office' &&
    v.vehicleType !== 'customer_vehicle' &&
    !(v.stockNumber && v.stockNumber.startsWith('CUST-'))
  );
  
  // Apply filters
  const filtered = needsTitle.filter(v => {
    if (statusFilter !== 'all' && v.titleStatus !== statusFilter) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (v.stockNumber || '').toLowerCase().includes(search) ||
             (v.make || '').toLowerCase().includes(search) ||
             (v.model || '').toLowerCase().includes(search) ||
             (v.vin || '').toLowerCase().includes(search);
    }
    return true;
  });
  
  // Apply sorting
  const sorted = [...filtered].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    switch (sortBy) {
      case 'stock': return (a.stockNumber || '').localeCompare(b.stockNumber || '') * dir;
      case 'vehicle': return (`${a.year} ${a.make} ${a.model}`).localeCompare(`${b.year} ${b.make} ${b.model}`) * dir;
      case 'status': return (a.titleStatus || '').localeCompare(b.titleStatus || '') * dir;
      case 'days': return (getDays(a.purchaseDate) - getDays(b.purchaseDate)) * dir;
      case 'auction': return (a.auction || '').localeCompare(b.auction || '') * dir;
      default: return 0;
    }
  });
  
  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };
  
  const SortHeader = ({ id, label }) => {
    const active = sortBy === id;
    const arrow = active ? (sortDir === 'asc' ? '▲' : '▼') : '⇅';
    return (
      <button 
        onClick={(e) => { e.stopPropagation(); toggleSort(id); }} 
        className={`flex items-center gap-1 text-xs font-semibold hover:text-blue-600 ${active ? 'text-blue-600' : ''}`}
        style={{ cursor: 'pointer' }}
      >
        {label}
        <span className={`text-[10px] ${active ? '' : 'opacity-30'}`}>{arrow}</span>
      </button>
    );
  };

  const updateTitle = async (id, titleStatus) => {
    try {
      await api.updateVehicle(id, { titleStatus });
      onRefresh();
    } catch (e) {
      alert('Update failed: ' + e.message);
    }
  };

  return (
    <div>
      {/* Tabs */}
      <div className="flex items-center gap-4 mb-4 border-b border-neutral-200">
        <button
          onClick={() => setActiveTab('missing')}
          className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'missing' 
              ? 'border-blue-500 text-blue-600' 
              : 'border-transparent text-neutral-500 hover:text-neutral-700'
          }`}
        >
          Missing Titles ({needsTitle.length})
        </button>
        <button
          onClick={() => setActiveTab('inOffice')}
          className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'inOffice' 
              ? 'border-green-500 text-green-600' 
              : 'border-transparent text-neutral-500 hover:text-neutral-700'
          }`}
        >
          In Office ({inOffice.length})
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'history'
              ? 'border-neutral-700 text-neutral-800'
              : 'border-transparent text-neutral-500 hover:text-neutral-700'
          }`}
        >
          History
        </button>
        <button onClick={() => setShowFormTemplates(true)} className="pb-2 text-sm font-medium border-b-2 border-transparent text-neutral-500 hover:text-neutral-700">
          Form Templates
        </button>
        <button onClick={() => setShowDealerSettings(true)} className="pb-2 text-sm font-medium border-b-2 border-transparent text-neutral-500 hover:text-neutral-700">
          Dealer Settings
        </button>
      </div>

      {/* Form Templates Modal - titlesOnly: only title packs (Out of State Vehicle, Repo Papers) */}
      {showFormTemplates && <FormTemplateManager onClose={() => setShowFormTemplates(false)} titlesOnly />}
      {/* Dealer Settings Modal */}
      {showDealerSettings && <DealerSettingsModal onClose={() => setShowDealerSettings(false)} />}

      {/* Title Print Modal */}
      {printVehicles && <TitlePrintModal vehicles={printVehicles} onClose={() => { setPrintVehicles(null); setSelectedIds(new Set()); }} />}

      {activeTab === 'missing' && (
        <>
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <Header title="Missing Titles" count={filtered.length} />
            <input
              type="text"
              placeholder="Search stock, make, VIN..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="px-3 py-1.5 text-xs border border-neutral-200 rounded-lg w-48"
            />
            <select 
              value={statusFilter} 
              onChange={e => setStatusFilter(e.target.value)}
              className="text-xs border border-neutral-200 rounded px-2 py-1.5 bg-white"
            >
              <option value="all">All Statuses</option>
              <option value="absent">Absent</option>
              <option value="at_auction">At Auction</option>
              <option value="in_transit">In Transit</option>
            </select>
            {selectedIds.size > 0 && (
              <button
                onClick={() => setPrintVehicles(vehicles.filter(v => selectedIds.has(v.id)))}
                className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700"
              >
                Print Forms for {selectedIds.size} Selected
              </button>
            )}
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200">
                <th className="pb-2 w-6">
                  <input type="checkbox"
                    checked={sorted.length > 0 && sorted.every(v => selectedIds.has(v.id))}
                    onChange={e => setSelectedIds(e.target.checked ? new Set(sorted.map(v => v.id)) : new Set())}
                    onClick={e => e.stopPropagation()}
                  />
                </th>
                <th className="pb-2 font-medium">
                  <button onClick={() => toggleSort('stock')} className={`flex items-center gap-1 text-xs font-semibold hover:text-blue-600 ${sortBy === 'stock' ? 'text-blue-600' : ''}`}>
                    Stock <span className={`text-[10px] ${sortBy === 'stock' ? '' : 'opacity-30'}`}>{sortBy === 'stock' ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</span>
                  </button>
                </th>
                <th className="pb-2 font-medium">
                  <button onClick={() => toggleSort('vehicle')} className={`flex items-center gap-1 text-xs font-semibold hover:text-blue-600 ${sortBy === 'vehicle' ? 'text-blue-600' : ''}`}>
                    Vehicle <span className={`text-[10px] ${sortBy === 'vehicle' ? '' : 'opacity-30'}`}>{sortBy === 'vehicle' ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</span>
                  </button>
                </th>
                <th className="pb-2 font-medium text-xs">VIN</th>
                <th className="pb-2 font-medium">
                  <button onClick={() => toggleSort('status')} className={`flex items-center gap-1 text-xs font-semibold hover:text-blue-600 ${sortBy === 'status' ? 'text-blue-600' : ''}`}>
                    Status <span className={`text-[10px] ${sortBy === 'status' ? '' : 'opacity-30'}`}>{sortBy === 'status' ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</span>
                  </button>
                </th>
                <th className="pb-2 font-medium">
                  <button onClick={() => toggleSort('days')} className={`flex items-center gap-1 text-xs font-semibold hover:text-blue-600 ${sortBy === 'days' ? 'text-blue-600' : ''}`}>
                    Days <span className={`text-[10px] ${sortBy === 'days' ? '' : 'opacity-30'}`}>{sortBy === 'days' ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</span>
                  </button>
                </th>
                <th className="pb-2 font-medium">
                  <button onClick={() => toggleSort('auction')} className={`flex items-center gap-1 text-xs font-semibold hover:text-blue-600 ${sortBy === 'auction' ? 'text-blue-600' : ''}`}>
                    Auction <span className={`text-[10px] ${sortBy === 'auction' ? '' : 'opacity-30'}`}>{sortBy === 'auction' ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</span>
                  </button>
                </th>
                <th className="pb-2 font-medium text-xs">QR</th>
                <th className="pb-2 font-medium text-xs">Notes</th>
                <th className="pb-2 font-medium text-xs">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(v => (
                <tr 
                  key={v.id} 
                  className={`border-b border-neutral-100 cursor-pointer hover:bg-gray-50 ${selectedIds.has(v.id) ? 'bg-blue-50' : v.status === 'sold' ? 'bg-amber-50' : ''}`}
                  onClick={() => onOpenTitleNotes(v)}
                >
                  <td className="py-1.5" onClick={e => e.stopPropagation()}>
                    <input type="checkbox"
                      checked={selectedIds.has(v.id)}
                      onChange={e => setSelectedIds(prev => {
                        const next = new Set(prev);
                        e.target.checked ? next.add(v.id) : next.delete(v.id);
                        return next;
                      })}
                    />
                  </td>
                  <td className="py-1.5 font-mono text-xs">
                    {v.stockNumber}
                    {v.status === 'sold' && <span className="ml-1 text-[10px] bg-amber-200 text-amber-800 px-1 rounded">SOLD</span>}
                  </td>
                  <td className="py-1.5">{v.year} {v.make} {v.model}</td>
                  <td className="py-1.5"><VinDisplay vin={v.vin} /></td>
                  <td className="py-1.5 text-xs">{v.titleStatus}</td>
                  <td className="py-1.5 text-xs">{getDays(v.purchaseDate)}</td>
                  <td className="py-1.5 text-xs text-neutral-500">{v.auction || '-'}</td>
                  <td className="py-1.5 text-center">
                    <QRCodePopup vehicle={v} className="text-xs" />
                  </td>
                  <td className="py-1.5 text-xs max-w-[150px] truncate text-neutral-500">
                    {v.titleNotes || <span className="text-blue-500 italic">Click to add notes</span>}
                  </td>
                  <td className="py-1.5 text-xs" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <select 
                        value={v.titleStatus} 
                        onChange={e => updateTitle(v.id, e.target.value)} 
                        className="text-xs border border-neutral-200 rounded px-1 py-0.5"
                      >
                        <option value="absent">Absent</option>
                        <option value="at_auction">At Auction</option>
                        <option value="in_transit">In Transit</option>
                        <option value="in_office">In Office</option>
                      </select>
                      <button
                        onClick={() => setPrintVehicles([v])}
                        className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200 whitespace-nowrap"
                        title="Print title forms"
                      >
                        🖨 Print
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {sorted.length === 0 && <Empty text={searchTerm || statusFilter !== 'all' ? 'No matching titles' : 'All titles in office'} />}
        </>
      )}

      {activeTab === 'inOffice' && (
        <>
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <Header title="Titles In Office" count={inOffice.length} />
            <input
              type="text"
              placeholder="Search stock, make, VIN..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="px-3 py-1.5 text-xs border border-neutral-200 rounded-lg w-48"
            />
            {selectedIds.size > 0 && (
              <button
                onClick={() => setPrintVehicles(vehicles.filter(v => selectedIds.has(v.id)))}
                className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700"
              >
                Print Forms for {selectedIds.size} Selected
              </button>
            )}
          </div>
          {(() => {
            const inOfficeFiltered = inOffice.filter(v => {
              if (!searchTerm) return true;
              const search = searchTerm.toLowerCase();
              return (v.stockNumber || '').toLowerCase().includes(search) ||
                     (v.make || '').toLowerCase().includes(search) ||
                     (v.model || '').toLowerCase().includes(search) ||
                     (v.vin || '').toLowerCase().includes(search);
            });
            return (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200">
                    <th className="pb-2 w-6">
                      <input type="checkbox"
                        checked={inOfficeFiltered.length > 0 && inOfficeFiltered.every(v => selectedIds.has(v.id))}
                        onChange={e => setSelectedIds(e.target.checked ? new Set(inOfficeFiltered.map(v => v.id)) : new Set())}
                      />
                    </th>
                    <th className="pb-2 font-medium">Stock</th>
                    <th className="pb-2 font-medium">Vehicle</th>
                    <th className="pb-2 font-medium text-xs">VIN</th>
                    <th className="pb-2 font-medium">Days In Office</th>
                    <th className="pb-2 font-medium">Auction</th>
                    <th className="pb-2 font-medium text-xs">QR</th>
                    <th className="pb-2 font-medium text-xs">Notes</th>
                    <th className="pb-2 font-medium text-xs">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {inOfficeFiltered.map(v => (
                    <tr
                      key={v.id}
                      className={`border-b border-neutral-100 cursor-pointer hover:bg-gray-50 ${selectedIds.has(v.id) ? 'bg-blue-50' : ''}`}
                      onClick={() => onOpenTitleNotes(v)}
                    >
                      <td className="py-1.5" onClick={e => e.stopPropagation()}>
                        <input type="checkbox"
                          checked={selectedIds.has(v.id)}
                          onChange={e => setSelectedIds(prev => {
                            const next = new Set(prev);
                            e.target.checked ? next.add(v.id) : next.delete(v.id);
                            return next;
                          })}
                        />
                      </td>
                      <td className="py-1.5 font-mono text-xs">{v.stockNumber}</td>
                      <td className="py-1.5">{v.year} {v.make} {v.model}</td>
                      <td className="py-1.5"><VinDisplay vin={v.vin} /></td>
                      <td className="py-1.5 text-xs text-green-600">{getDays(v.purchaseDate)} days</td>
                      <td className="py-1.5 text-xs text-neutral-500">{v.auction || '-'}</td>
                      <td className="py-1.5 text-center"><QRCodePopup vehicle={v} className="text-xs" /></td>
                      <td className="py-1.5 text-xs max-w-[150px] truncate text-neutral-500">
                        {v.titleNotes || <span className="text-blue-500 italic">Click to add notes</span>}
                      </td>
                      <td className="py-1.5 text-xs" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <select
                            value={v.titleStatus}
                            onChange={e => updateTitle(v.id, e.target.value)}
                            className="text-xs border border-neutral-200 rounded px-1 py-0.5"
                          >
                            <option value="in_office">In Office</option>
                            <option value="absent">Absent</option>
                            <option value="at_auction">At Auction</option>
                            <option value="in_transit">In Transit</option>
                          </select>
                          <button
                            onClick={() => setPrintVehicles([v])}
                            className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200 whitespace-nowrap"
                            title="Print title forms"
                          >
                            🖨 Print
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          })()}
          {inOffice.length === 0 && <Empty text="No titles in office" />}
        </>
      )}

      {activeTab === 'history' && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-sm font-semibold text-neutral-700">Title History</h2>
            <input
              type="text"
              placeholder="Search stock, VIN, user..."
              value={historySearch}
              onChange={e => setHistorySearch(e.target.value)}
              className="px-3 py-1.5 text-xs border border-neutral-200 rounded-lg w-56"
            />
            <button
              onClick={() => { setTitleHistory([]); setHistoryLoading(true); api.getTitleHistory(300).then(d => setTitleHistory(d || [])).catch(() => {}).finally(() => setHistoryLoading(false)); }}
              className="px-2 py-1 text-xs text-neutral-500 hover:text-neutral-700 border border-neutral-200 rounded-lg"
            >
              Refresh
            </button>
          </div>

          {historyLoading && <div className="text-sm text-neutral-400 py-8 text-center">Loading history...</div>}

          {!historyLoading && (() => {
            const eventLabels = {
              title_sent: 'Title Complete (Deal Desk)',
              title_unchecked: 'Title Unchecked (Deal Desk)',
              title_checked_in: 'Title Checked In (In Office)',
              title_status_changed: 'Status Changed',
            };
            const eventColors = {
              title_sent: 'bg-green-100 text-green-700',
              title_unchecked: 'bg-yellow-100 text-yellow-700',
              title_checked_in: 'bg-blue-100 text-blue-700',
              title_status_changed: 'bg-neutral-100 text-neutral-600',
            };

            const filtered = titleHistory.filter(e => {
              if (!historySearch) return true;
              const s = historySearch.toLowerCase();
              return (e.stock_number || '').toLowerCase().includes(s) ||
                     (e.vin || '').toLowerCase().includes(s) ||
                     (e.user_name || '').toLowerCase().includes(s) ||
                     (e.make || '').toLowerCase().includes(s) ||
                     (e.model || '').toLowerCase().includes(s);
            });

            if (filtered.length === 0) return <div className="text-sm text-neutral-400 py-8 text-center">No title events found</div>;

            return (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200">
                    <th className="pb-2 font-medium">Date</th>
                    <th className="pb-2 font-medium">Event</th>
                    <th className="pb-2 font-medium">Vehicle</th>
                    <th className="pb-2 font-medium">Stock</th>
                    <th className="pb-2 font-medium">VIN</th>
                    <th className="pb-2 font-medium">By</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(e => (
                    <tr key={e.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                      <td className="py-2 text-xs text-neutral-500 whitespace-nowrap">
                        {new Date(e.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        {' '}
                        <span className="text-neutral-400">{new Date(e.event_date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                      </td>
                      <td className="py-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${eventColors[e.event_type] || 'bg-neutral-100 text-neutral-600'}`}>
                          {eventLabels[e.event_type] || e.event_type}
                        </span>
                      </td>
                      <td className="py-2 text-xs">{e.year} {e.make} {e.model}</td>
                      <td className="py-2 font-mono text-xs">{e.stock_number || '-'}</td>
                      <td className="py-2"><VinDisplay vin={e.vin} /></td>
                      <td className="py-2 text-xs text-neutral-500">{e.user_name || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          })()}
        </div>
      )}
    </div>
  );
};

// Shop Expenses - For Tony's card purchases
const ShopExpenses = ({ currentUser, vehicles, vendors, onRefresh }) => {
  const [expenses, setExpenses] = useState([]);
  const [balance, setBalance] = useState({ balance: 0, expenseCount: 0 });
  const [showForm, setShowForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'office';

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Admin/office see everyone's expenses; others see their own
        const [expenseList, balanceData] = await Promise.all([
          api.getShopExpenses(isAdmin ? undefined : currentUser.id),
          api.getShopExpenseBalance(currentUser.id)
        ]);
        setExpenses(expenseList || []);
        setBalance(balanceData || { balance: 0, expenseCount: 0 });
      } catch (e) {
        console.error('Failed to load shop expenses:', e);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [currentUser.id, isAdmin]);

  const employeeNames = [...new Set(expenses.map(e => e.user_name).filter(Boolean))].sort();

  const filteredExpenses = expenses.filter(exp => {
    if (employeeFilter && exp.user_name !== employeeFilter) return false;
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    return (
      (exp.description || '').toLowerCase().includes(q) ||
      (exp.notes || '').toLowerCase().includes(q) ||
      (exp.user_name || '').toLowerCase().includes(q) ||
      (exp.stock_number || '').toLowerCase().includes(q) ||
      String(exp.amount || '').includes(q)
    );
  });

  const handleSubmit = async (formData) => {
    try {
      await api.createShopExpense(formData);
      setShowForm(false);
      refreshData();
    } catch (e) {
      alert('Failed to save expense: ' + e.message);
    }
  };

  const refreshData = async () => {
    const [expenseList, balanceData] = await Promise.all([
      api.getShopExpenses(isAdmin ? undefined : currentUser.id),
      api.getShopExpenseBalance(currentUser.id)
    ]);
    setExpenses(expenseList || []);
    setBalance(balanceData || { balance: 0, expenseCount: 0 });
  };

  const handleDelete = async (exp) => {
    if (!confirm(`Delete expense "${exp.description}" for $${parseFloat(exp.amount).toFixed(2)}?`)) return;
    try {
      await api.deleteShopExpense(exp.id);
      refreshData();
    } catch (e) {
      alert('Failed to delete: ' + e.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Expenses</h1>
        <button onClick={() => setShowForm(true)} className="px-4 py-2 bg-neutral-800 text-white rounded-lg hover:bg-neutral-700">+ Add Expense</button>
      </div>

      {balance.balance > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="text-sm text-amber-700">Personal Purchases (will be deducted from paycheck)</div>
          <div className="text-2xl font-bold text-amber-800">${balance.balance.toFixed(2)}</div>
          <div className="text-xs text-amber-600">{balance.expenseCount} item{balance.expenseCount !== 1 ? 's' : ''} this pay period</div>
        </div>
      )}

      {/* Search + filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search purchases by description, notes, amount..."
          className="flex-1 min-w-52 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
        />
        {isAdmin && employeeNames.length > 1 && (
          <select
            value={employeeFilter}
            onChange={e => setEmployeeFilter(e.target.value)}
            className="px-3 py-2 border border-neutral-200 rounded-lg text-sm bg-white"
          >
            <option value="">All Employees</option>
            {employeeNames.map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        )}
        {(searchTerm || employeeFilter) && (
          <span className="text-xs text-neutral-500">{filteredExpenses.length} of {expenses.length}</span>
        )}
      </div>

      {loading ? (
        <div className="text-center py-8 text-neutral-500">Loading...</div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                <th className="px-3 py-2 font-medium">Date</th>
                {isAdmin && <th className="px-3 py-2 font-medium">Employee</th>}
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">Description</th>
                <th className="px-3 py-2 font-medium">Vehicle</th>
                <th className="px-3 py-2 font-medium text-right">Amount</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium w-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredExpenses.map(exp => (
                <tr key={exp.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                  <td className="px-3 py-2 text-xs">{new Date(exp.created_at).toLocaleDateString()}</td>
                  {isAdmin && <td className="px-3 py-2 text-xs">{exp.user_name || '-'}</td>}
                  <td className="px-3 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      exp.expense_type === 'personal' ? 'bg-amber-100 text-amber-700' :
                      exp.expense_type === 'inventory' ? 'bg-blue-100 text-blue-700' :
                      exp.expense_type === 'shop_supplies' ? 'bg-green-100 text-green-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {exp.expense_type === 'shop_supplies' ? 'Shop Supplies' : exp.expense_type?.charAt(0).toUpperCase() + exp.expense_type?.slice(1)}
                    </span>
                  </td>
                  <td className="px-3 py-2">{exp.description}</td>
                  <td className="px-3 py-2 text-xs font-mono">{exp.stock_number || '-'}</td>
                  <td className="px-3 py-2 text-right font-mono">${parseFloat(exp.amount).toFixed(2)}</td>
                  <td className="px-3 py-2 text-xs">
                    {exp.needs_review && !exp.reviewed_at ? (
                      <span className="text-amber-600">Pending Review</span>
                    ) : exp.payroll_processed ? (
                      <span className="text-green-600">Processed</span>
                    ) : (
                      <span className="text-neutral-500">-</span>
                    )}
                  </td>
                  <td className="px-3 py-2 flex gap-2">
                    <button
                      onClick={() => setEditingExpense(exp)}
                      className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(exp)}
                      className="text-xs text-red-600 hover:text-red-800 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredExpenses.length === 0 && (
            <div className="text-center py-8 text-neutral-500">
              {expenses.length === 0 ? 'No expenses recorded' : 'No expenses match your search'}
            </div>
          )}
        </div>
      )}

      {showForm && <ShopExpenseForm vehicles={vehicles} vendors={vendors} onClose={() => setShowForm(false)} onSave={handleSubmit} />}
      
      {/* Edit Expense Modal */}
      {editingExpense && (
        <EditShopExpenseModal 
          expense={editingExpense} 
          vehicles={vehicles} 
          vendors={vendors}
          onClose={() => setEditingExpense(null)} 
          onSave={() => { setEditingExpense(null); refreshData(); }} 
        />
      )}
    </div>
  );
};

// Shop Expense Form Modal
const ShopExpenseForm = ({ vehicles, vendors, onClose, onSave }) => {
  const [form, setForm] = useState({ 
    expenseType: 'inventory', 
    vehicleId: '', 
    vendorId: '',
    vendorName: '',
    bankAccountId: '',
    description: '', 
    amount: '', 
    notes: '' 
  });
  const [saving, setSaving] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);

  useEffect(() => {
    if (form.expenseType === 'personal') return;
    api.getBankAccounts().then(accounts => {
      setBankAccounts(accounts);
      if (accounts.length > 0) {
        const defaultAccount = accounts.find(a => a.name.toLowerCase().includes('checking')) || accounts[0];
        setForm(f => ({ ...f, bankAccountId: defaultAccount.id.toString() }));
      }
    }).catch(console.error);
  }, [form.expenseType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description || !form.amount) return alert('Description and amount are required');
    if (form.expenseType !== 'personal' && !form.vendorId && !form.vendorName) return alert('Please select or enter a vendor');
    if (form.expenseType !== 'personal' && !form.bankAccountId) return alert('Please select a bank account');
    setSaving(true);
    try {
      const payload = {
        ...form,
        vehicleId: form.vehicleId || null,
        amount: parseFloat(form.amount),
      };
      if (form.expenseType !== 'personal' && form.bankAccountId) {
        payload.bankAccountId = parseInt(form.bankAccountId);
      } else {
        delete payload.bankAccountId;
      }
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-lg shadow-lg" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-neutral-200 flex justify-between items-center">
          <span className="font-medium">Add Expense</span>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Type</label>
            <select value={form.expenseType} onChange={e => setForm(f => ({ ...f, expenseType: e.target.value, vehicleId: '' }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg">
              <option value="inventory">Inventory (Vehicle)</option>
              <option value="shop_supplies">Shop Supplies</option>
              <option value="unknown">Unknown (Needs Review)</option>
              <option value="personal">Personal Purchase</option>
            </select>
          </div>
          {form.expenseType === 'inventory' && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Vehicle</label>
              <select value={form.vehicleId} onChange={e => setForm(f => ({ ...f, vehicleId: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg">
                <option value="">Select vehicle...</option>
                {vehicles.filter(v => v.status !== 'sold' && v.status !== 'archived').map(v => (
                  <option key={v.id} value={v.id}>{v.stockNumber} - {v.year} {v.make} {v.model}</option>
                ))}
              </select>
            </div>
          )}
          {form.expenseType !== 'personal' && (
            <>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Vendor</label>
                <select value={form.vendorId} onChange={e => setForm(f => ({ ...f, vendorId: e.target.value, vendorName: '' }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg">
                  <option value="">Select vendor...</option>
                  {(vendors || []).map(v => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                  <option value="other">Other (type below)</option>
                </select>
                {form.vendorId === 'other' && (
                  <input type="text" value={form.vendorName} onChange={e => setForm(f => ({ ...f, vendorName: e.target.value }))} placeholder="Enter vendor name" className="w-full px-3 py-2 border border-neutral-200 rounded-lg mt-2" />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Paid From Account</label>
                <select value={form.bankAccountId} onChange={e => setForm(f => ({ ...f, bankAccountId: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg">
                  <option value="">Select account...</option>
                  {bankAccounts.map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.name}</option>
                  ))}
                </select>
              </div>
            </>
          )}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Description</label>
            <input type="text" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder={form.expenseType === 'personal' ? 'What did you buy?' : 'Part name, service, etc.'} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Amount</label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-neutral-500">$</span>
              <input type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} className="w-full pl-7 pr-3 py-2 border border-neutral-200 rounded-lg" required />
            </div>
          </div>
          {(form.expenseType === 'unknown' || form.expenseType === 'shop_supplies') && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Notes</label>
              <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" rows={2} placeholder="What was this purchase for?" />
            </div>
          )}
          {form.expenseType === 'personal' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">This will be deducted from your paycheck at end of week.</div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-neutral-600 hover:text-neutral-800">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 bg-neutral-800 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50">{saving ? 'Saving...' : 'Save Expense'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Shop Expense Modal
const EditShopExpenseModal = ({ expense, vehicles, vendors, onClose, onSave }) => {
  const [form, setForm] = useState({
    description: expense.description || '',
    amount: expense.amount || '',
    vehicleId: expense.vehicle_id || '',
    vendorId: expense.vendor_id || '',
    notes: expense.notes || ''
  });
  const [saving, setSaving] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);

  useEffect(() => {
    api.getBankAccounts().then(setBankAccounts).catch(console.error);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description || !form.amount) return alert('Description and amount required');
    setSaving(true);
    try {
      await api.updateShopExpense(expense.id, form);
      onSave();
    } catch (err) {
      alert('Failed to update: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-lg shadow-lg" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-neutral-200 flex justify-between items-center">
          <span className="font-medium">Edit Expense</span>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Description</label>
            <input 
              type="text" 
              value={form.description} 
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))} 
              className="w-full px-3 py-2 border border-neutral-200 rounded-lg" 
              required 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Amount</label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-neutral-500">$</span>
              <input 
                type="number" 
                step="0.01" 
                value={form.amount} 
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} 
                className="w-full pl-7 pr-3 py-2 border border-neutral-200 rounded-lg" 
                required 
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Vehicle (optional)</label>
            <select 
              value={form.vehicleId} 
              onChange={e => setForm(f => ({ ...f, vehicleId: e.target.value }))} 
              className="w-full px-3 py-2 border border-neutral-200 rounded-lg"
            >
              <option value="">No vehicle</option>
              {vehicles.filter(v => v.status !== 'sold' && v.status !== 'archived').map(v => (
                <option key={v.id} value={v.id}>{v.stockNumber} - {v.year} {v.make} {v.model}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Vendor (optional)</label>
            <select 
              value={form.vendorId} 
              onChange={e => setForm(f => ({ ...f, vendorId: e.target.value }))} 
              className="w-full px-3 py-2 border border-neutral-200 rounded-lg"
            >
              <option value="">No vendor</option>
              {(vendors || []).map(v => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Notes</label>
            <textarea 
              value={form.notes} 
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} 
              className="w-full px-3 py-2 border border-neutral-200 rounded-lg" 
              rows={2} 
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-neutral-600 hover:text-neutral-800">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 bg-neutral-800 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50">
              {saving ? 'Saving...' : 'Update Expense'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Vendors Sub-Tab for Accounting Module
const VendorsSubTab = ({ vendors, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [vendorSpend, setVendorSpend] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const SortHeader = ({ id, label }) => {
    const active = sortBy === id;
    const arrow = active ? (sortDir === 'asc' ? '▲' : '▼') : '⇅';
    return (
      <th className="px-3 py-2 font-medium">
        <button onClick={() => toggleSort(id)} className={`flex items-center gap-1 text-xs hover:text-blue-600 ${active ? 'text-blue-600' : ''}`}>
          {label}
          <span className={`text-[10px] ${active ? '' : 'opacity-30'}`}>{arrow}</span>
        </button>
      </th>
    );
  };

  const filterAndSort = (data) => {
    let filtered = data;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = data.filter(v => 
        (v.name || '').toLowerCase().includes(search) ||
        (v.vendor_type || '').toLowerCase().includes(search) ||
        (v.contact_name || '').toLowerCase().includes(search) ||
        (v.phone || '').toLowerCase().includes(search)
      );
    }
    return [...filtered].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      switch (sortBy) {
        case 'name': return (a.name || '').localeCompare(b.name || '') * dir;
        case 'type': return (a.vendor_type || '').localeCompare(b.vendor_type || '') * dir;
        case 'contact': return (a.contact_name || '').localeCompare(b.contact_name || '') * dir;
        case 'phone': return (a.phone || '').localeCompare(b.phone || '') * dir;
        case 'spend': 
          const spendA = parseFloat(vendorSpend[a.id]?.totalSpend || 0);
          const spendB = parseFloat(vendorSpend[b.id]?.totalSpend || 0);
          return (spendA - spendB) * dir;
        default: return 0;
      }
    });
  };

  useEffect(() => {
    const loadVendorSpend = async () => {
      try {
        const spend = await api.getVendorSpend();
        const spendMap = {};
        (spend || []).forEach(s => { spendMap[s.vendorId] = s; });
        setVendorSpend(spendMap);
      } catch (e) {
        console.error('Failed to load vendor spend:', e);
      }
    };
    loadVendorSpend();
  }, [vendors]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    try {
      await api.deleteVendor(id);
      onRefresh();
    } catch (e) {
      alert('Delete failed: ' + e.message);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <input
          type="text"
          placeholder="Search vendors..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="px-3 py-1.5 text-xs border border-neutral-200 rounded-lg w-48"
        />
        <span className="text-sm text-neutral-500">{filterAndSort(vendors).length} vendors</span>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="ml-auto text-xs bg-neutral-800 text-white px-3 py-1 rounded hover:bg-neutral-700">+ Add Vendor</button>
      </div>
      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200 bg-neutral-50">
              <SortHeader id="name" label="Name" />
              <SortHeader id="type" label="Type" />
              <SortHeader id="contact" label="Contact" />
              <SortHeader id="phone" label="Phone" />
              <SortHeader id="spend" label="Total Spend" />
              <th className="px-3 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {filterAndSort(vendors).map(v => (
              <tr key={v.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                <td className="px-3 py-2 font-medium">{v.name}</td>
                <td className="px-3 py-2 text-xs">{v.vendor_type}</td>
                <td className="px-3 py-2 text-xs">{v.contact_name || '-'}</td>
                <td className="px-3 py-2 text-xs">{v.phone || '-'}</td>
                <td className="px-3 py-2 text-xs font-mono">{vendorSpend[v.id] ? `$${parseFloat(vendorSpend[v.id].totalSpend || 0).toLocaleString()}` : '-'}</td>
                <td className="px-3 py-2 text-right text-xs">
                  <button onClick={() => { setEditing(v); setShowForm(true); }} className="text-neutral-500 hover:text-neutral-800 mr-2">edit</button>
                  <button onClick={() => handleDelete(v.id)} className="text-red-500 hover:text-red-700">del</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {vendors.length === 0 && <Empty text="No vendors" />}
      {showForm && <VendorForm vendor={editing} onClose={() => setShowForm(false)} onSave={() => { setShowForm(false); onRefresh(); }} />}
    </div>
  );
};

// Buyer/Transport Fees Sub-Tab for Accounting Module
const BuyerTransportFeesSubTab = ({ onRefresh, bankAccounts = [], isAdmin }) => {
  const [data, setData] = useState({ vehicles: [], summary: {}, vendorId: null });
  const [loading, setLoading] = useState(true);
  const [selectedTransport, setSelectedTransport] = useState([]);
  const [selectedBuyerFees, setSelectedBuyerFees] = useState([]);
  const [selectedInputFees, setSelectedInputFees] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('check');
  const [bankAccountId, setBankAccountId] = useState('');
  const [notes, setNotes] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [processing, setProcessing] = useState(false);
  const [zeroingOut, setZeroingOut] = useState(false);
  const [editingFee, setEditingFee] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [savingFee, setSavingFee] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const result = await api.getAlexBilling();
      setData(result || { vehicles: [], summary: {}, vendorId: null });
    } catch (e) {
      console.error('Failed to load billing data:', e);
    } finally {
      setLoading(false);
    }
  };

  const unpaidTransportAll = data.vehicles.filter(v => !v.transportFeePaid && v.transportCost > 0);
  const unpaidBuyerFeesAll = data.vehicles.filter(v => !v.buyerFeePaid && v.buyerFee > 0);
  const unpaidInputFeesAll = data.vehicles.filter(v => !v.inputFeePaid && v.inputFee > 0);

  const matchesSearch = (v) => {
    if (!searchTerm.trim()) return true;
    const search = searchTerm.trim().toLowerCase();
    const searchNumeric = search.replace(/[^0-9.]/g, '');

    const tags = [];
    if ((v.transportCost || 0) > 0) tags.push('transport dat');
    if ((v.buyerFee || 0) > 0) tags.push('buyer fee alex dynamic sourcing');
    if ((v.inputFee || 0) > 0) tags.push('input fee');
    if (v.buyerFee === 200) tags.push('buy');
    if (v.buyerFee && v.buyerFee !== 200) tags.push('entry');

    const haystack = [
      tags.join(' '),
      v.stockNumber,
      v.vin,
      v.year,
      v.make,
      v.model,
      v.auction,
      v.buyer,
      v.transportStatus,
      v.purchaseDate,
      v.purchasePrice,
      v.transportCost,
      v.buyerFee,
      v.inputFee,
      v.transportFeeNotes,
      v.buyerFeeNotes,
      v.inputFeeNotes,
    ]
      .filter(x => x !== null && x !== undefined)
      .join(' ')
      .toLowerCase();

    if (haystack.includes(search)) return true;

    if (searchNumeric) {
      const transportNorm = String(v.transportCost ?? '').replace(/[^0-9.]/g, '');
      const buyerFeeNorm = String(v.buyerFee ?? '').replace(/[^0-9.]/g, '');
      const inputFeeNorm = String(v.inputFee ?? '').replace(/[^0-9.]/g, '');
      const purchaseNorm = String(v.purchasePrice ?? '').replace(/[^0-9.]/g, '');
      return (
        transportNorm.includes(searchNumeric) ||
        buyerFeeNorm.includes(searchNumeric) ||
        inputFeeNorm.includes(searchNumeric) ||
        purchaseNorm.includes(searchNumeric)
      );
    }

    return false;
  };

  const unpaidTransport = unpaidTransportAll.filter(matchesSearch);
  const unpaidBuyerFees = unpaidBuyerFeesAll.filter(matchesSearch);
  const unpaidInputFees = unpaidInputFeesAll.filter(matchesSearch);

  const toggleTransport = (id) => {
    setSelectedTransport(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleBuyerFee = (id) => {
    setSelectedBuyerFees(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleInputFee = (id) => {
    setSelectedInputFees(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const selectAllTransport = () => {
    const visibleIds = unpaidTransport.map(v => v.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedTransport.includes(id));
    if (allVisibleSelected) {
      setSelectedTransport(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      setSelectedTransport(prev => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const selectAllBuyerFees = () => {
    const visibleIds = unpaidBuyerFees.map(v => v.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedBuyerFees.includes(id));
    if (allVisibleSelected) {
      setSelectedBuyerFees(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      setSelectedBuyerFees(prev => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const selectAllInputFees = () => {
    const visibleIds = unpaidInputFees.map(v => v.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedInputFees.includes(id));
    if (allVisibleSelected) {
      setSelectedInputFees(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      setSelectedInputFees(prev => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const handlePayTransport = async () => {
    if (selectedTransport.length === 0) return;
    if (!bankAccountId) {
      alert('Please select a bank account to pay from');
      return;
    }
    setProcessing(true);
    try {
      const payments = selectedTransport.map(id => ({ vehicleId: id, fees: ['transport'] }));
      await api.payAlexBilling({
        payments,
        paymentMethod,
        bankAccountId: parseInt(bankAccountId),
        notes
      });
      setSelectedTransport([]);
      setNotes('');
      loadData();
      if (onRefresh) onRefresh();
      alert('Transport fees marked as paid!');
    } catch (e) {
      alert('Failed to process payment: ' + e.message);
    } finally {
      setProcessing(false);
    }
  };

  const handlePayBuyerFees = async () => {
    if (selectedBuyerFees.length === 0) return;
    if (!bankAccountId) {
      alert('Please select a bank account to pay from');
      return;
    }
    setProcessing(true);
    try {
      const payments = selectedBuyerFees.map(id => ({ vehicleId: id, fees: ['buyer_fee'] }));
      await api.payAlexBilling({
        payments,
        paymentMethod,
        bankAccountId: parseInt(bankAccountId),
        notes
      });
      setSelectedBuyerFees([]);
      setNotes('');
      loadData();
      if (onRefresh) onRefresh();
      alert('Buyer fees marked as paid!');
    } catch (e) {
      alert('Failed to process payment: ' + e.message);
    } finally {
      setProcessing(false);
    }
  };

  const handlePayInputFees = async () => {
    if (selectedInputFees.length === 0) return;
    if (!bankAccountId) {
      alert('Please select a bank account to pay from');
      return;
    }
    setProcessing(true);
    try {
      const payments = selectedInputFees.map(id => ({ vehicleId: id, fees: ['input_fee'] }));
      await api.payAlexBilling({
        payments,
        paymentMethod,
        bankAccountId: parseInt(bankAccountId),
        notes
      });
      setSelectedInputFees([]);
      setNotes('');
      loadData();
      if (onRefresh) onRefresh();
      alert('Input fees marked as paid!');
    } catch (e) {
      alert('Failed to process payment: ' + e.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleZeroOutAll = async () => {
    if (!isAdmin) return;
    if (!confirm('Zero out ALL transport, buyer, and input fees on every vehicle? You are all paid up. This cannot be undone.')) return;
    setZeroingOut(true);
    try {
      const res = await api.zeroOutAllFees();
      loadData();
      if (onRefresh) onRefresh();
      alert(`Done! Zeroed out fees on ${res?.updated ?? 0} vehicles.`);
    } catch (e) {
      alert('Failed: ' + e.message);
    } finally {
      setZeroingOut(false);
    }
  };

  const handleZeroOutSelected = async () => {
    if (!isAdmin) return;

    const feeMap = new Map();
    selectedTransport.forEach((id) => {
      if (!feeMap.has(id)) feeMap.set(id, new Set());
      feeMap.get(id).add('transport');
    });
    selectedBuyerFees.forEach((id) => {
      if (!feeMap.has(id)) feeMap.set(id, new Set());
      feeMap.get(id).add('buyer_fee');
    });
    selectedInputFees.forEach((id) => {
      if (!feeMap.has(id)) feeMap.set(id, new Set());
      feeMap.get(id).add('input_fee');
    });

    const payments = Array.from(feeMap.entries()).map(([vehicleId, feeSet]) => ({
      vehicleId: parseInt(vehicleId, 10),
      fees: Array.from(feeSet)
    }));

    if (payments.length === 0) {
      alert('Please select at least one fee to zero out.');
      return;
    }

    if (!confirm(`Zero out selected fees for ${payments.length} vehicle(s)? This will set selected fee amounts to $0 and mark them paid.`)) return;

    setZeroingOut(true);
    try {
      const res = await api.zeroOutSelectedFees({ payments });
      setSelectedTransport([]);
      setSelectedBuyerFees([]);
      setSelectedInputFees([]);
      loadData();
      if (onRefresh) onRefresh();
      alert(`Done! Zeroed out selected fees on ${res?.updated ?? 0} vehicles.`);
    } catch (e) {
      alert('Failed: ' + e.message);
    } finally {
      setZeroingOut(false);
    }
  };

  const selectedTransportTotal = unpaidTransportAll
    .filter(v => selectedTransport.includes(v.id))
    .reduce((sum, v) => sum + v.transportCost, 0);

  const selectedBuyerFeesTotal = unpaidBuyerFeesAll
    .filter(v => selectedBuyerFees.includes(v.id))
    .reduce((sum, v) => sum + v.buyerFee, 0);

  const selectedInputFeesTotal = unpaidInputFeesAll
    .filter(v => selectedInputFees.includes(v.id))
    .reduce((sum, v) => sum + v.inputFee, 0);

  const selectedFeeCount = selectedTransport.length + selectedBuyerFees.length + selectedInputFees.length;

  const feeEditConfig = {
    transport: { label: 'Transport Fee', amountKey: 'transportCost', notesKey: 'transportFeeNotes' },
    buyer_fee: { label: 'Buyer Fee', amountKey: 'buyerFee', notesKey: 'buyerFeeNotes' },
    input_fee: { label: 'Input Fee', amountKey: 'inputFee', notesKey: 'inputFeeNotes' }
  };

  const openFeeEdit = (vehicle, feeType, e) => {
    e.stopPropagation();
    const config = feeEditConfig[feeType];
    setEditingFee({ vehicle, feeType });
    setEditAmount(String(vehicle[config.amountKey] ?? ''));
    setEditNotes(vehicle[config.notesKey] || '');
  };

  const closeFeeEdit = () => {
    setEditingFee(null);
    setEditAmount('');
    setEditNotes('');
  };

  const handleSaveFeeEdit = async () => {
    if (!editingFee) return;
    const parsed = parseFloat(editAmount);
    if (Number.isNaN(parsed) || parsed < 0) {
      alert('Enter an amount of 0 or greater');
      return;
    }
    setSavingFee(true);
    try {
      await api.updateAlexBillingFee({
        vehicleId: editingFee.vehicle.id,
        feeType: editingFee.feeType,
        amount: parsed,
        notes: editNotes
      });
      closeFeeEdit();
      loadData();
      if (onRefresh) onRefresh();
    } catch (e) {
      alert('Failed to update fee: ' + e.message);
    } finally {
      setSavingFee(false);
    }
  };

  if (loading) return <div className="text-center py-8 text-neutral-500">Loading billing data...</div>;

  return (
    <div className="space-y-3">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <div className="text-xs text-neutral-500">Unpaid Transport</div>
          <div className="text-2xl font-bold text-amber-600">${data.summary.totalUnpaidTransport?.toLocaleString() || 0}</div>
          <div className="text-xs text-neutral-400">{data.summary.vehicleCountTransport || 0} vehicles</div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <div className="text-xs text-neutral-500">Unpaid Buyer Fees ($200)</div>
          <div className="text-2xl font-bold text-amber-600">${data.summary.totalUnpaidBuyerFees?.toLocaleString() || 0}</div>
          <div className="text-xs text-neutral-400">{data.summary.vehicleCountBuyerFees || 0} vehicles</div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <div className="text-xs text-neutral-500">Unpaid Input Fees ($35)</div>
          <div className="text-2xl font-bold text-amber-600">${data.summary.totalUnpaidInputFees?.toLocaleString() || 0}</div>
          <div className="text-xs text-neutral-400">{data.summary.vehicleCountInputFees || 0} vehicles</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <div className="text-xs text-neutral-500">Selected Transport</div>
          <div className="text-2xl font-bold text-blue-600">${selectedTransportTotal.toLocaleString()}</div>
          <div className="text-xs text-neutral-400">{selectedTransport.length} selected</div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <div className="text-xs text-neutral-500">Selected Buyer Fees</div>
          <div className="text-2xl font-bold text-blue-600">${selectedBuyerFeesTotal.toLocaleString()}</div>
          <div className="text-xs text-neutral-400">{selectedBuyerFees.length} selected</div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <div className="text-xs text-neutral-500">Selected Input Fees</div>
          <div className="text-2xl font-bold text-blue-600">${selectedInputFeesTotal.toLocaleString()}</div>
          <div className="text-xs text-neutral-400">{selectedInputFees.length} selected</div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search anything (stock#, auction, buyer, year/make/model, amount...)"
          className="px-3 py-2 text-sm border border-neutral-200 rounded-lg w-96 max-w-full"
        />
        <div className="text-xs text-neutral-500">
          Showing {unpaidTransport.length + unpaidBuyerFees.length + unpaidInputFees.length} matches
        </div>
      </div>

      {/* Payment Controls */}
      <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 flex items-center gap-4 flex-wrap">
        <div>
          <label className="text-xs text-neutral-500 block mb-1">Pay From Account *</label>
          <select value={bankAccountId} onChange={e => setBankAccountId(e.target.value)} className="px-2 py-1 text-sm border border-neutral-200 rounded bg-white" required>
            <option value="">Select account...</option>
            {bankAccounts.map(acc => (
              <option key={acc.id} value={acc.id}>{acc.name} (${parseFloat(acc.balance || 0).toLocaleString()})</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-neutral-500 block mb-1">Payment Method</label>
          <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} className="px-2 py-1 text-sm border border-neutral-200 rounded">
            <option value="check">Check</option>
            <option value="ach">ACH</option>
            <option value="cash">Cash</option>
            <option value="card">Card</option>
          </select>
        </div>
        <div className="flex-1">
          <label className="text-xs text-neutral-500 block mb-1">Notes (optional)</label>
          <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Check #, invoice #, etc." className="w-full px-2 py-1 text-sm border border-neutral-200 rounded" />
        </div>
        <div className="flex gap-2">
          <button
            onClick={handlePayTransport}
            disabled={selectedTransport.length === 0 || processing}
            className="px-4 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Pay DAT Auto Transport (${selectedTransportTotal.toLocaleString()})
          </button>
          <button
            onClick={handlePayBuyerFees}
            disabled={selectedBuyerFees.length === 0 || processing}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Pay Dynamic Auto Sourcing (${selectedBuyerFeesTotal.toLocaleString()})
          </button>
          <button
            onClick={handlePayInputFees}
            disabled={selectedInputFees.length === 0 || processing}
            className="px-4 py-2 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Pay Input Fees (${selectedInputFeesTotal.toLocaleString()})
          </button>
          {isAdmin && (
            <button
              onClick={handleZeroOutSelected}
              disabled={zeroingOut || processing || selectedFeeCount === 0}
              className="px-4 py-2 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Zero out only selected transport/buyer/input fees (admin only)"
            >
              {zeroingOut ? 'Zeroing...' : `Zero Out Selected (${selectedFeeCount})`}
            </button>
          )}
          {isAdmin && (
            <button
              onClick={handleZeroOutAll}
              disabled={zeroingOut || processing}
              className="px-4 py-2 bg-amber-600 text-white text-sm rounded hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Zero out all transport/buyer/input fees (admin only)"
            >
              {zeroingOut ? 'Zeroing...' : 'Zero Out All Fees'}
            </button>
          )}
        </div>
      </div>

      {/* Transport Fees Table */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold">Transport Fees (DAT Auto Transport)</h3>
          <button onClick={selectAllTransport} className="text-xs text-blue-600 hover:underline">
            {unpaidTransport.length > 0 && unpaidTransport.every(v => selectedTransport.includes(v.id)) ? 'Deselect All' : 'Select All'}
          </button>
        </div>
        {unpaidTransport.length > 0 ? (
          <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                  <th className="px-3 py-2 w-10">
                    <input 
                      type="checkbox" 
                      checked={unpaidTransport.length > 0 && unpaidTransport.every(v => selectedTransport.includes(v.id))}
                      onChange={selectAllTransport}
                    />
                  </th>
                  <th className="px-3 py-2 font-medium">Vehicle</th>
                  <th className="px-3 py-2 font-medium">Purchase Date</th>
                  <th className="px-3 py-2 font-medium">Transport Status</th>
                  <th className="px-3 py-2 font-medium text-right">Amount</th>
                  <th className="px-3 py-2 font-medium w-16"></th>
                </tr>
              </thead>
              <tbody>
                {unpaidTransport.map(v => (
                  <tr key={v.id} className={`border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer ${selectedTransport.includes(v.id) ? 'bg-blue-50' : ''}`} onClick={() => toggleTransport(v.id)}>
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={selectedTransport.includes(v.id)} onChange={() => toggleTransport(v.id)} onClick={e => e.stopPropagation()} />
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{v.stockNumber}</div>
                      <div className="text-xs text-neutral-500">{v.year} {v.make} {v.model}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">{v.purchaseDate?.split('T')[0] || '-'}</td>
                    <td className="px-3 py-2 text-xs">
                      <span className={`px-2 py-0.5 rounded ${v.transportStatus === 'delivered' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                        {v.transportStatus || 'pending'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="font-mono font-medium">${v.transportCost.toLocaleString()}</div>
                      {v.transportFeeNotes && <div className="text-xs text-neutral-400 truncate max-w-[160px] ml-auto">{v.transportFeeNotes}</div>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={(e) => openFeeEdit(v, 'transport', e)}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-neutral-50 font-medium">
                  <td colSpan={4} className="px-3 py-2 text-right">Total Unpaid:</td>
                  <td className="px-3 py-2 text-right font-mono">${data.summary.totalUnpaidTransport?.toLocaleString() || 0}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <Empty text="No unpaid transport fees" />
        )}
      </div>

      {/* Buyer Fees Table */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold">Buyer Fees (Dynamic Auto Sourcing)</h3>
          <button onClick={selectAllBuyerFees} className="text-xs text-blue-600 hover:underline">
            {unpaidBuyerFees.length > 0 && unpaidBuyerFees.every(v => selectedBuyerFees.includes(v.id)) ? 'Deselect All' : 'Select All'}
          </button>
        </div>
        {unpaidBuyerFees.length > 0 ? (
          <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                  <th className="px-3 py-2 w-10">
                    <input 
                      type="checkbox" 
                      checked={unpaidBuyerFees.length > 0 && unpaidBuyerFees.every(v => selectedBuyerFees.includes(v.id))}
                      onChange={selectAllBuyerFees}
                    />
                  </th>
                  <th className="px-3 py-2 font-medium">Vehicle</th>
                  <th className="px-3 py-2 font-medium">Buyer</th>
                  <th className="px-3 py-2 font-medium">Purchase Date</th>
                  <th className="px-3 py-2 font-medium text-right">Fee</th>
                  <th className="px-3 py-2 font-medium w-16"></th>
                </tr>
              </thead>
              <tbody>
                {unpaidBuyerFees.map(v => (
                  <tr key={v.id} className={`border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer ${selectedBuyerFees.includes(v.id) ? 'bg-blue-50' : ''}`} onClick={() => toggleBuyerFee(v.id)}>
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={selectedBuyerFees.includes(v.id)} onChange={() => toggleBuyerFee(v.id)} onClick={e => e.stopPropagation()} />
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{v.stockNumber}</div>
                      <div className="text-xs text-neutral-500">{v.year} {v.make} {v.model}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <span className={`px-2 py-0.5 rounded ${v.buyerFee === 200 ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                        {v.buyerFee === 200 ? 'Alex (Buy)' : 'Alex (Entry)'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">{v.purchaseDate?.split('T')[0] || '-'}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="font-mono font-medium">${v.buyerFee.toLocaleString()}</div>
                      {v.buyerFeeNotes && <div className="text-xs text-neutral-400 truncate max-w-[160px] ml-auto">{v.buyerFeeNotes}</div>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={(e) => openFeeEdit(v, 'buyer_fee', e)}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-neutral-50 font-medium">
                  <td colSpan={4} className="px-3 py-2 text-right">Total Unpaid:</td>
                  <td className="px-3 py-2 text-right font-mono">${data.summary.totalUnpaidBuyerFees?.toLocaleString() || 0}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <Empty text="No unpaid buyer fees" />
        )}
      </div>

      {/* Input Fees Table */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold">Input Fees ($35 - Chris/AUX cars)</h3>
          <button onClick={selectAllInputFees} className="text-xs text-blue-600 hover:underline">
            {unpaidInputFees.length > 0 && unpaidInputFees.every(v => selectedInputFees.includes(v.id)) ? 'Deselect All' : 'Select All'}
          </button>
        </div>
        {unpaidInputFees.length > 0 ? (
          <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                  <th className="px-3 py-2 w-10">
                    <input 
                      type="checkbox" 
                      checked={unpaidInputFees.length > 0 && unpaidInputFees.every(v => selectedInputFees.includes(v.id))}
                      onChange={selectAllInputFees}
                    />
                  </th>
                  <th className="px-3 py-2 font-medium">Vehicle</th>
                  <th className="px-3 py-2 font-medium">Buyer</th>
                  <th className="px-3 py-2 font-medium">Purchase Date</th>
                  <th className="px-3 py-2 font-medium text-right">Fee</th>
                  <th className="px-3 py-2 font-medium w-16"></th>
                </tr>
              </thead>
              <tbody>
                {unpaidInputFees.map(v => (
                  <tr key={v.id} className={`border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer ${selectedInputFees.includes(v.id) ? 'bg-purple-50' : ''}`} onClick={() => toggleInputFee(v.id)}>
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={selectedInputFees.includes(v.id)} onChange={() => toggleInputFee(v.id)} onClick={e => e.stopPropagation()} />
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{v.stockNumber}</div>
                      <div className="text-xs text-neutral-500">{v.year} {v.make} {v.model}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <span className="px-2 py-0.5 rounded bg-neutral-100 text-neutral-700">{v.buyer || '-'}</span>
                    </td>
                    <td className="px-3 py-2 text-xs">{v.purchaseDate?.split('T')[0] || '-'}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="font-mono font-medium">${v.inputFee.toLocaleString()}</div>
                      {v.inputFeeNotes && <div className="text-xs text-neutral-400 truncate max-w-[160px] ml-auto">{v.inputFeeNotes}</div>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={(e) => openFeeEdit(v, 'input_fee', e)}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-neutral-50 font-medium">
                  <td colSpan={4} className="px-3 py-2 text-right">Total Unpaid:</td>
                  <td className="px-3 py-2 text-right font-mono">${data.summary.totalUnpaidInputFees?.toLocaleString() || 0}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <Empty text="No unpaid input fees" />
        )}
      </div>

      {editingFee && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={closeFeeEdit}>
          <div className="bg-white rounded-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Edit {feeEditConfig[editingFee.feeType].label}</h2>
              <button type="button" onClick={closeFeeEdit} className="text-neutral-400 hover:text-neutral-600">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <div className="font-medium">{editingFee.vehicle.stockNumber}</div>
                <div className="text-sm text-neutral-500">
                  {editingFee.vehicle.year} {editingFee.vehicle.make} {editingFee.vehicle.model}
                </div>
              </div>
              <div>
                <label className="text-xs text-neutral-500 block mb-1">Amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editAmount}
                  onChange={e => setEditAmount(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg"
                />
              </div>
              <div>
                <label className="text-xs text-neutral-500 block mb-1">Notes (optional)</label>
                <textarea
                  value={editNotes}
                  onChange={e => setEditNotes(e.target.value)}
                  placeholder="Invoice difference, extra stop, etc."
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeFeeEdit}
                  disabled={savingFee}
                  className="px-4 py-2 text-sm border border-neutral-200 rounded-lg hover:bg-neutral-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveFeeEdit}
                  disabled={savingFee}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {savingFee ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Admin Weekly Tasks Component
const AdminWeeklyTasks = ({ currentUser, vehicles, onNavigate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeDay, setActiveDay] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [showAddTask, setShowAddTask] = useState(false);
  const [showSaleList, setShowSaleList] = useState(false);
  const [showGasReceipt, setShowGasReceipt] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [completingTask, setCompletingTask] = useState(null);
  const [taskNotes, setTaskNotes] = useState('');
  const [newTask, setNewTask] = useState({ title: '', dayOfWeek: 'monday', frequency: 'weekly', isPersonal: false });
  const [gasForm, setGasForm] = useState({ date: new Date().toISOString().split('T')[0], amount: '', purpose: '', vehicleId: '', bankAccountId: '' });
  const [saleListData, setSaleListData] = useState(null);
  const [printReceipt, setPrintReceipt] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [taskMenu, setTaskMenu] = useState(null);

  const isAdmin = currentUser?.role === 'admin';
  const isBuyer = currentUser?.role === 'buyer';

  // Get current day of week
  const today = new Date();
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const currentDay = dayNames[today.getDay()];

  useEffect(() => {
    loadData();
    setActiveDay(currentDay === 'saturday' || currentDay === 'sunday' ? 'monday' : currentDay);
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [result, accounts, cats, vends] = await Promise.all([
        api.getWeeklyTasks(),
        api.getBankAccounts(),
        api.getAccountingCategories(),
        api.getVendors()
      ]);
      setData(result);
      setBankAccounts(accounts || []);
      setCategories(cats || []);
      setVendors(vends || []);
      // If no tasks exist, seed defaults
      if (result.tasks && Object.values(result.tasks).every(arr => arr.length === 0)) {
        await api.seedDefaultTasks();
        const refreshed = await api.getWeeklyTasks();
        setData(refreshed);
      }
    } catch (e) {
      console.error('Failed to load tasks:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (task, forNextWeek = false) => {
    if (task.requires_notes && !taskNotes) {
      setCompletingTask(task);
      return;
    }
    try {
      // If completing for next week, calculate next week's start
      let weekToComplete = data.weekStart;
      if (forNextWeek) {
        const nextWeek = new Date(data.weekStart);
        nextWeek.setDate(nextWeek.getDate() + 7);
        weekToComplete = nextWeek.toISOString().split('T')[0];
      }
      await api.completeTask(task.id, { notes: taskNotes, weekStart: weekToComplete });
      setTaskNotes('');
      setCompletingTask(null);
      loadData();
    } catch (e) {
      alert('Failed: ' + e.message);
    }
  };

  const handleUncomplete = async (task) => {
    if (!isAdmin) return;
    try {
      await api.uncompleteTask(task.id, data.weekStart);
      loadData();
    } catch (e) {
      alert('Failed: ' + e.message);
    }
  };

  const handleAddTask = async () => {
    try {
      await api.createTaskTemplate(newTask);
      setShowAddTask(false);
      setNewTask({ title: '', dayOfWeek: 'monday', frequency: 'weekly', isPersonal: false });
      loadData();
    } catch (e) {
      alert('Failed: ' + e.message);
    }
  };

  const handleCreateGasReceipt = async () => {
    try {
      const result = await api.createGasReceipt(gasForm);
      setPrintReceipt(result);
      setShowGasReceipt(false);
      setGasForm({ date: new Date().toISOString().split('T')[0], amount: '', purpose: '', vehicleId: '', bankAccountId: '' });
      loadData(); // Refresh to show updated bank balance
      const deductMsg = result.deducted ? `\n\n✓ $${gasForm.amount} deducted from ${result.bankAccountName}` : '';
      alert(`Gas receipt created!${deductMsg}\n\nEntered into accounting.`);
    } catch (e) {
      alert('Failed: ' + e.message);
    }
  };

  const loadSaleList = async () => {
    try {
      const result = await api.getSaleList();
      setSaleListData(result);
      setShowSaleList(true);
    } catch (e) {
      alert('Failed to load sale list');
    }
  };

  const handleAddToSaleList = async (vehicleId) => {
    try {
      await api.addToSaleList({ vehicleId });
      loadSaleList();
    } catch (e) {
      alert('Failed: ' + e.message);
    }
  };

  const handleRemoveFromSaleList = async (id) => {
    try {
      await api.removeFromSaleList(id);
      loadSaleList();
    } catch (e) {
      alert('Failed: ' + e.message);
    }
  };

  const handleUpdateSaleListVehicle = async (id, field, value) => {
    try {
      await api.updateSaleListVehicle(id, { [field]: value });
      // Update local state immediately for responsiveness
      // Map camelCase to snake_case for local state (DB returns snake_case)
      const stateField = field === 'runNumber' ? 'run_number' : field;
      setSaleListData(prev => ({
        ...prev,
        vehicles: prev.vehicles.map(v => v.id === id ? { ...v, [stateField]: value } : v)
      }));
    } catch (e) {
      alert('Failed: ' + e.message);
    }
  };

  // Navigate to linked module
  const handleLinkClick = (linkTo) => {
    const moduleMap = {
      'titles': 'titles',
      'inventory': 'inventory',
      'sales': 'dealdesk',
      'arbs': 'inventory', // with filter
      'payroll': { module: 'accounting', tab: 'payroll' },
      'accounting': 'accounting',
      'expenses': 'expenses',
      'returns': 'partsroom',
      'vehicles_out': 'inventory',
      'sale_list': () => loadSaleList(),
      'gas_receipts': () => setShowGasReceipt(true),
      'bills': 'accounting',
    };
    const target = moduleMap[linkTo];
    if (typeof target === 'function') {
      target();
    } else if (target && typeof target === 'object' && onNavigate) {
      // Navigate to module with specific tab
      onNavigate(target.module, target.tab);
    } else if (target && onNavigate) {
      onNavigate(target);
    }
  };

  if (loading) return <div className="text-center py-8 text-neutral-500">Loading tasks...</div>;
  if (!data) return <div className="text-center py-8 text-red-500">Failed to load tasks</div>;

  const { tasks, counts, isBiweeklyWeek, isEndOfMonth, isEndOfYear, weekStart } = data;
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

  const getTaskCount = (day) => {
    const dayTasks = tasks[day] || [];
    const completed = dayTasks.filter(t => t.completion_id).length;
    return { total: dayTasks.length, completed };
  };

  const getDayStatus = (day) => {
    const { total, completed } = getTaskCount(day);
    if (total === 0) return 'empty';
    if (completed === total) return 'done';
    if (day === currentDay) return 'active';
    return 'pending';
  };

  const handleEditTask = async (task, updates) => {
    try {
      await api.updateTaskTemplate(task.id, updates);
      setEditingTask(null);
      loadData();
    } catch (e) {
      alert('Failed: ' + e.message);
    }
  };

  const handlePushToTomorrow = async (task) => {
    const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const currentIndex = dayOrder.indexOf(task.day_of_week);
    const nextDay = currentIndex < 4 ? dayOrder[currentIndex + 1] : 'monday';
    await handleEditTask(task, { dayOfWeek: nextDay });
    setTaskMenu(null);
  };

  const handleDeleteTask = async (task) => {
    if (!confirm('Delete this task?')) return;
    try {
      await api.deleteTaskTemplate(task.id);
      loadData();
    } catch (e) {
      alert('Failed: ' + e.message);
    }
  };

  const renderTask = (task) => {
    const isComplete = !!task.completion_id;
    const canComplete = isAdmin || (isBuyer && task.can_buyer_complete);
    
    return (
      <div key={task.id} className={`flex items-center gap-3 p-3 rounded-lg border ${isComplete ? 'bg-green-50 border-green-200' : 'bg-white border-neutral-200 hover:border-neutral-300'}`}>
        {/* Checkbox */}
        <input
          type="checkbox"
          checked={isComplete}
          onChange={() => isComplete ? handleUncomplete(task) : handleComplete(task)}
          disabled={!canComplete && !isComplete}
          className="w-5 h-5 rounded border-2 border-neutral-400 text-green-600 focus:ring-green-500 cursor-pointer flex-shrink-0"
        />
        
        {/* Task Content */}
        <div className="flex-1 min-w-0">
          <div className={`font-medium ${isComplete ? 'text-green-700 line-through' : ''}`}>{task.title}</div>
          {task.description && <div className="text-xs text-neutral-500">{task.description}</div>}
          {isComplete && task.completed_by_name && (
            <div className="text-xs text-green-600 mt-1">
              ✓ {task.completed_by_name} · {new Date(task.completed_at).toLocaleDateString()}
            </div>
          )}
          {task.completion_notes && <div className="text-xs text-neutral-500 mt-1 italic">"{task.completion_notes}"</div>}
        </div>

        {/* Tags */}
        <div className="flex items-center gap-2">
          {task.frequency === 'biweekly' && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Bi-weekly</span>}
          {task.is_personal && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Personal</span>}
        </div>

        {/* Link Button */}
        {task.link_to && (
          <button
            onClick={() => handleLinkClick(task.link_to)}
            className="text-xs bg-neutral-100 hover:bg-neutral-200 px-2 py-1 rounded"
          >
            {counts[task.link_to] !== undefined && <span className="font-medium text-blue-600 mr-1">{counts[task.link_to]}</span>}
            Go →
          </button>
        )}

        {/* Actions Menu */}
        {isAdmin && (
          <div className="relative">
            <button 
              onClick={() => setTaskMenu(taskMenu === task.id ? null : task.id)}
              className="w-7 h-7 flex items-center justify-center text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded"
            >
              ⋮
            </button>
            {taskMenu === task.id && (
              <div className="absolute right-0 top-8 bg-white border border-neutral-200 rounded-lg shadow-lg py-1 z-10 min-w-[180px]">
                <button onClick={() => { setEditingTask(task); setTaskMenu(null); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-neutral-100">
                  ✏️ Edit
                </button>
                <button onClick={() => handlePushToTomorrow(task)} className="w-full text-left px-3 py-1.5 text-sm hover:bg-neutral-100">
                  → Push to Tomorrow
                </button>
                {/* Allow completing Monday tasks on Friday for next week */}
                {(currentDay === 'friday' || currentDay === 'thursday') && task.day_of_week === 'monday' && !task.completion_id && (
                  <button onClick={() => { handleComplete(task, true); setTaskMenu(null); }} className="w-full text-left px-3 py-1.5 text-sm text-green-600 hover:bg-green-50">
                    ✓ Complete for Next Week
                  </button>
                )}
                <button onClick={() => handleDeleteTask(task)} className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50">
                  🗑️ Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Weekly Tasks</h1>
          <div className="text-sm text-neutral-500">Week of {new Date(weekStart + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <>
              <button onClick={() => setShowGasReceipt(true)} className="px-3 py-1.5 text-sm bg-amber-100 text-amber-700 rounded hover:bg-amber-200">
                ⛽ Gas Receipt
              </button>
              <button onClick={() => setShowAddExpense(true)} className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200">
                + Add Expense
              </button>
              <button onClick={loadSaleList} className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200">
                Sale List
              </button>
              <button onClick={() => setShowAddTask(true)} className="px-3 py-1.5 text-sm bg-neutral-800 text-white rounded hover:bg-neutral-700">
                + Add Task
              </button>
            </>
          )}
        </div>
      </div>

      {/* Status Indicators */}
      <div className="flex gap-2 flex-wrap">
        {isBiweeklyWeek && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">Bi-weekly Week</span>}
        {isEndOfMonth && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">End of Month</span>}
        {isEndOfYear && <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">Year End</span>}
      </div>

      {/* Day Tabs */}
      <div className="flex gap-1 border-b border-neutral-200">
        {days.map(day => {
          const status = getDayStatus(day);
          const { total, completed } = getTaskCount(day);
          return (
            <button
              key={day}
              onClick={() => setActiveDay(day)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px capitalize ${
                activeDay === day ? 'border-neutral-800 text-neutral-800' : 'border-transparent text-neutral-500 hover:text-neutral-700'
              }`}
            >
              {day.slice(0, 3)}
              {total > 0 && (
                <span className={`ml-1 text-xs font-normal ${
                  status === 'done' ? 'text-green-600' :
                  day === currentDay ? 'text-blue-600' : 'text-neutral-400'
                }`}>
                  {status === 'done' ? '✓' : `${completed} of ${total}`}
                </span>
              )}
            </button>
          );
        })}
        {/* Always show special period tabs */}
        {tasks.monthly?.length > 0 && (
          <button onClick={() => setActiveDay('monthly')} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${activeDay === 'monthly' ? 'border-orange-600 text-orange-600' : 'border-transparent text-orange-400'} ${!isEndOfMonth ? 'opacity-50' : ''}`}>
            Month End {isEndOfMonth && '●'}
          </button>
        )}
        {tasks.yearly?.length > 0 && (
          <button onClick={() => setActiveDay('yearly')} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${activeDay === 'yearly' ? 'border-red-600 text-red-600' : 'border-transparent text-red-400'} ${!isEndOfYear ? 'opacity-50' : ''}`}>
            Year End {isEndOfYear && '●'}
          </button>
        )}
      </div>

      {/* Task List */}
      <div className="space-y-2">
        {(tasks[activeDay] || []).length > 0 ? (
          tasks[activeDay].map(renderTask)
        ) : (
          <div className="text-center py-8 text-neutral-400">No tasks for {activeDay}</div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-neutral-200 rounded-lg p-3">
          <div className="text-xs text-neutral-500">Titles Pending</div>
          <div className="text-xl font-bold text-amber-600">{counts.titles || 0}</div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-3">
          <div className="text-xs text-neutral-500">Vehicles Out</div>
          <div className="text-xl font-bold text-blue-600">{counts.vehiclesOut || 0}</div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-3">
          <div className="text-xs text-neutral-500">Open Arbs</div>
          <div className="text-xl font-bold text-red-600">{counts.arbs || 0}</div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-3">
          <div className="text-xs text-neutral-500">Pending Payroll</div>
          <div className="text-xl font-bold text-green-600">{counts.pendingPayroll || 0}</div>
        </div>
      </div>

      {/* Notes Modal for Task Completion */}
      {completingTask && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setCompletingTask(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Complete: {completingTask.title}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Notes {completingTask.requires_notes && <span className="text-red-500">*</span>}</label>
                <textarea
                  value={taskNotes}
                  onChange={e => setTaskNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg"
                  rows={3}
                  placeholder="Add notes..."
                />
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setCompletingTask(null)} className="px-4 py-2 text-sm text-neutral-600">Cancel</button>
                <button
                  onClick={() => handleComplete(completingTask)}
                  disabled={completingTask.requires_notes && !taskNotes}
                  className="px-4 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50"
                >
                  Complete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Task Modal */}
      {showAddTask && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowAddTask(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Add New Task</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Task Title</label>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={e => setNewTask(t => ({ ...t, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg"
                  placeholder="Enter task..."
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Day</label>
                  <select value={newTask.dayOfWeek} onChange={e => setNewTask(t => ({ ...t, dayOfWeek: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg">
                    <option value="monday">Monday</option>
                    <option value="tuesday">Tuesday</option>
                    <option value="wednesday">Wednesday</option>
                    <option value="thursday">Thursday</option>
                    <option value="friday">Friday</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Frequency</label>
                  <select value={newTask.frequency} onChange={e => setNewTask(t => ({ ...t, frequency: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg">
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Bi-weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={newTask.isPersonal} onChange={e => setNewTask(t => ({ ...t, isPersonal: e.target.checked }))} />
                <span className="text-sm">Personal task (not business)</span>
              </label>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowAddTask(false)} className="px-4 py-2 text-sm text-neutral-600">Cancel</button>
                <button onClick={handleAddTask} disabled={!newTask.title} className="px-4 py-2 bg-neutral-800 text-white text-sm rounded hover:bg-neutral-700 disabled:opacity-50">
                  Add Task
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      {editingTask && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setEditingTask(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Edit Task</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Task Title</label>
                <input
                  type="text"
                  defaultValue={editingTask.title}
                  onChange={e => setEditingTask(t => ({ ...t, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Description</label>
                <textarea
                  defaultValue={editingTask.description || ''}
                  onChange={e => setEditingTask(t => ({ ...t, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Day</label>
                  <select 
                    value={editingTask.day_of_week || ''} 
                    onChange={e => setEditingTask(t => ({ ...t, day_of_week: e.target.value }))} 
                    className="w-full px-3 py-2 border border-neutral-200 rounded-lg"
                  >
                    <option value="">No specific day</option>
                    <option value="monday">Monday</option>
                    <option value="tuesday">Tuesday</option>
                    <option value="wednesday">Wednesday</option>
                    <option value="thursday">Thursday</option>
                    <option value="friday">Friday</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Frequency</label>
                  <select 
                    value={editingTask.frequency} 
                    onChange={e => setEditingTask(t => ({ ...t, frequency: e.target.value }))} 
                    className="w-full px-3 py-2 border border-neutral-200 rounded-lg"
                  >
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Bi-weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setEditingTask(null)} className="px-4 py-2 text-sm text-neutral-600">Cancel</button>
                <button 
                  onClick={() => handleEditTask(editingTask, { 
                    title: editingTask.title, 
                    description: editingTask.description,
                    dayOfWeek: editingTask.day_of_week,
                    frequency: editingTask.frequency
                  })} 
                  className="px-4 py-2 bg-neutral-800 text-white text-sm rounded hover:bg-neutral-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gas Receipt Modal */}
      {showGasReceipt && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowGasReceipt(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">⛽ Generate Speedway Gas Receipt</h3>
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                <div className="font-medium text-amber-800">Speedway #4521</div>
                <div className="text-amber-600">2845 W Central Ave, Toledo, OH 43606</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={gasForm.date}
                    onChange={e => setGasForm(f => ({ ...f, date: e.target.value }))}
                    className="w-full px-3 py-2 border border-neutral-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Amount ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={gasForm.amount}
                    onChange={e => setGasForm(f => ({ ...f, amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-neutral-200 rounded-lg"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Purpose</label>
                <input
                  type="text"
                  value={gasForm.purpose}
                  onChange={e => setGasForm(f => ({ ...f, purpose: e.target.value }))}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg"
                  placeholder="Transport, Delivery, etc."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Vehicle (optional)</label>
                <select value={gasForm.vehicleId} onChange={e => setGasForm(f => ({ ...f, vehicleId: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg">
                  <option value="">No specific vehicle</option>
                  {vehicles.filter(v => v.status !== 'sold').map(v => (
                    <option key={v.id} value={v.id}>{v.stockNumber} - {v.year} {v.make} {v.model}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Paid From Account</label>
                <select value={gasForm.bankAccountId} onChange={e => setGasForm(f => ({ ...f, bankAccountId: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg">
                  <option value="">Select account...</option>
                  {bankAccounts.map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowGasReceipt(false)} className="px-4 py-2 text-sm text-neutral-600">Cancel</button>
                <button
                  onClick={handleCreateGasReceipt}
                  disabled={!gasForm.amount || !gasForm.bankAccountId}
                  className="px-4 py-2 bg-amber-600 text-white text-sm rounded hover:bg-amber-700 disabled:opacity-50"
                >
                  Create Receipt
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Print Receipt Modal */}
      {printReceipt && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 print:bg-white print:inset-auto" onClick={() => setPrintReceipt(null)}>
          <div className="bg-white rounded-lg shadow-xl w-96 p-0 print:shadow-none print:w-full" onClick={e => e.stopPropagation()}>
            <div id="receipt-content" className="p-6 font-mono text-sm text-center bg-white">
              <div className="text-2xl font-bold mb-1">SPEEDWAY</div>
              <div className="text-base">Store #4521</div>
              <div>2845 W Central Ave</div>
              <div>Toledo, OH 43606</div>
              <div>(419) 473-9876</div>
              <div className="border-t-2 border-dashed border-neutral-400 my-4"></div>
              <div className="text-base">{new Date(printReceipt.receipt?.date || new Date()).toLocaleDateString()}</div>
              <div className="text-xs text-neutral-500">{new Date().toLocaleTimeString()}</div>
              <div className="border-t-2 border-dashed border-neutral-400 my-4"></div>
              <div className="text-left space-y-2">
                <div className="flex justify-between text-base">
                  <span>UNLEADED REG</span>
                  <span className="font-bold">${parseFloat(printReceipt.receipt?.amount || 0).toFixed(2)}</span>
                </div>
                <div className="text-neutral-500">@ $3.299/gal</div>
                <div className="text-neutral-500">≈ {(parseFloat(printReceipt.receipt?.amount || 0) / 3.299).toFixed(3)} GAL</div>
              </div>
              <div className="border-t-2 border-dashed border-neutral-400 my-4"></div>
              <div className="flex justify-between text-lg font-bold">
                <span>TOTAL</span>
                <span>${parseFloat(printReceipt.receipt?.amount || 0).toFixed(2)}</span>
              </div>
              <div className="border-t-2 border-dashed border-neutral-400 my-4"></div>
              <div className="text-base">VISA CREDIT ****1234</div>
              <div className="text-xs text-neutral-500 mt-1">APPROVED</div>
              <div className="mt-4 text-neutral-600 font-bold">Receipt #{printReceipt.receiptNumber}</div>
              {printReceipt.receipt?.purpose && (
                <div className="mt-2 text-neutral-500 text-xs">Purpose: {printReceipt.receipt.purpose}</div>
              )}
              <div className="mt-4 text-xs text-neutral-400">THANK YOU FOR YOUR BUSINESS</div>
            </div>
            <div className="p-4 border-t border-neutral-200 flex gap-2 print:hidden">
              <button 
                onClick={() => {
                  const receiptContent = document.getElementById('receipt-content').innerHTML;
                  const printWindow = window.open('', '_blank');
                  printWindow.document.write(`
                    <!DOCTYPE html>
                    <html>
                    <head>
                      <title>Gas Receipt - ${printReceipt.receiptNumber}</title>
                      <style>
                        body { font-family: 'Courier New', monospace; padding: 20px; max-width: 300px; margin: 0 auto; }
                        .text-2xl { font-size: 24px; }
                        .text-lg { font-size: 18px; }
                        .text-base { font-size: 16px; }
                        .text-sm { font-size: 14px; }
                        .text-xs { font-size: 12px; }
                        .font-bold { font-weight: bold; }
                        .text-center { text-align: center; }
                        .text-left { text-align: left; }
                        .my-4 { margin: 16px 0; }
                        .mt-1, .mt-2, .mt-4 { margin-top: 8px; }
                        .mb-1 { margin-bottom: 4px; }
                        .space-y-2 > * + * { margin-top: 8px; }
                        .border-dashed { border-style: dashed; border-top-width: 2px; border-color: #999; }
                        .text-neutral-400, .text-neutral-500, .text-neutral-600 { color: #666; }
                        .flex { display: flex; }
                        .justify-between { justify-content: space-between; }
                      </style>
                    </head>
                    <body>
                      ${receiptContent}
                    </body>
                    </html>
                  `);
                  printWindow.document.close();
                  printWindow.focus();
                  setTimeout(() => { printWindow.print(); }, 250);
                }} 
                className="flex-1 px-4 py-2 bg-neutral-800 text-white rounded font-medium"
              >
                🖨️ Print Receipt
              </button>
              <button onClick={() => setPrintReceipt(null)} className="px-4 py-2 text-neutral-600 hover:bg-neutral-100 rounded">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Sale List Modal */}
      {showSaleList && saleListData && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowSaleList(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-neutral-200 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold">Sale List - Week of {new Date(saleListData.saleList.week_start).toLocaleDateString()}</h3>
                <div className="text-sm text-neutral-500">{saleListData.vehicles.length} vehicles on list</div>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => {
                    const printWindow = window.open('', '_blank');
                    const vehiclesHtml = saleListData.vehicles.map(v => `
                      <tr>
                        <td style="border:1px solid #ddd;padding:8px;font-family:monospace">${v.stock_number}</td>
                        <td style="border:1px solid #ddd;padding:8px">${v.year} ${v.make} ${v.model}</td>
                        <td style="border:1px solid #ddd;padding:8px;text-align:right">$${(v.estimated_sale || 0).toLocaleString()}</td>
                        <td style="border:1px solid #ddd;padding:8px;text-align:center">${v.lane || '-'}</td>
                        <td style="border:1px solid #ddd;padding:8px;text-align:center">${v.run_number || '-'}</td>
                        <td style="border:1px solid #ddd;padding:8px;font-size:12px">${v.notes || ''}</td>
                      </tr>
                    `).join('');
                    printWindow.document.write(`
                      <!DOCTYPE html>
                      <html>
                      <head>
                        <title>Sale List - Week of ${new Date(saleListData.saleList.week_start).toLocaleDateString()}</title>
                        <style>
                          body { font-family: Arial, sans-serif; padding: 20px; }
                          h1 { text-align: center; margin-bottom: 5px; }
                          .subtitle { text-align: center; color: #666; margin-bottom: 20px; }
                          table { width: 100%; border-collapse: collapse; }
                          th { background: #f5f5f5; border: 1px solid #ddd; padding: 10px; text-align: left; }
                          @media print { body { padding: 0; } }
                        </style>
                      </head>
                      <body>
                        <h1>THE WHOLESALE CARS</h1>
                        <div class="subtitle">Sale List - Week of ${new Date(saleListData.saleList.week_start).toLocaleDateString()}</div>
                        <table>
                          <thead>
                            <tr>
                              <th>Stock #</th>
                              <th>Vehicle</th>
                              <th style="text-align:right">Est. Sale</th>
                              <th style="text-align:center">Lane</th>
                              <th style="text-align:center">Run #</th>
                              <th>Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            ${vehiclesHtml}
                          </tbody>
                        </table>
                        <div style="margin-top:20px;text-align:center;color:#999;font-size:12px">
                          ${saleListData.vehicles.length} vehicles • Printed ${new Date().toLocaleString()}
                        </div>
                      </body>
                      </html>
                    `);
                    printWindow.document.close();
                    printWindow.focus();
                    setTimeout(() => printWindow.print(), 250);
                  }}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  🖨️ Print
                </button>
                <button onClick={() => setShowSaleList(false)} className="text-neutral-400 hover:text-neutral-600 text-xl">×</button>
              </div>
            </div>
            <div className="p-4 overflow-auto max-h-[60vh]">
              {/* Vehicles on list */}
              {saleListData.vehicles.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-medium mb-2">On Sale List</h4>
                  <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                          <th className="px-3 py-2">Stock #</th>
                          <th className="px-3 py-2">Vehicle</th>
                          <th className="px-3 py-2">Est. Sale</th>
                          <th className="px-3 py-2">Lane</th>
                          <th className="px-3 py-2">Run #</th>
                          <th className="px-3 py-2">Notes</th>
                          <th className="px-3 py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {saleListData.vehicles.map(v => (
                          <tr key={v.id} className="border-b border-neutral-100">
                            <td className="px-3 py-2 font-mono">{v.stock_number}</td>
                            <td className="px-3 py-2">{v.year} {v.make} {v.model}</td>
                            <td className="px-3 py-2 font-mono">${(v.estimated_sale || 0).toLocaleString()}</td>
                            <td className="px-2 py-1">
                              <input
                                type="text"
                                value={v.lane || ''}
                                onChange={(e) => handleUpdateSaleListVehicle(v.id, 'lane', e.target.value)}
                                className="w-16 px-2 py-1 text-sm border border-neutral-200 rounded focus:border-blue-500 focus:outline-none"
                                placeholder="Lane"
                              />
                            </td>
                            <td className="px-2 py-1">
                              <input
                                type="text"
                                value={v.run_number || ''}
                                onChange={(e) => handleUpdateSaleListVehicle(v.id, 'runNumber', e.target.value)}
                                className="w-16 px-2 py-1 text-sm border border-neutral-200 rounded focus:border-blue-500 focus:outline-none"
                                placeholder="Run #"
                              />
                            </td>
                            <td className="px-2 py-1">
                              <input
                                type="text"
                                value={v.notes || ''}
                                onChange={(e) => handleUpdateSaleListVehicle(v.id, 'notes', e.target.value)}
                                className="w-full min-w-[150px] px-2 py-1 text-sm border border-neutral-200 rounded focus:border-blue-500 focus:outline-none"
                                placeholder="Notes / needs done..."
                              />
                            </td>
                            <td className="px-3 py-2">
                              <button onClick={() => handleRemoveFromSaleList(v.id)} className="text-xs text-red-600 hover:text-red-800">Remove</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {/* Available vehicles */}
              <div>
                <h4 className="font-medium mb-2">Available Vehicles</h4>
                <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                        <th className="px-3 py-2">Stock #</th>
                        <th className="px-3 py-2">Vehicle</th>
                        <th className="px-3 py-2">Est. Sale</th>
                        <th className="px-3 py-2">Recon</th>
                        <th className="px-3 py-2">Status</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {saleListData.available.map(v => (
                        <tr key={v.id} className="border-b border-neutral-100">
                          <td className="px-3 py-2 font-mono">{v.stock_number}</td>
                          <td className="px-3 py-2">{v.year} {v.make} {v.model}</td>
                          <td className="px-3 py-2 font-mono">${(v.estimated_sale || 0).toLocaleString()}</td>
                          <td className="px-3 py-2">
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              v.recon_status === 'complete' ? 'bg-green-100 text-green-700' :
                              v.recon_status === 'in_progress' ? 'bg-blue-100 text-blue-700' : 'bg-neutral-100'
                            }`}>
                              {v.recon_status || 'pending'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs">{v.status}</td>
                          <td className="px-3 py-2">
                            <button onClick={() => handleAddToSaleList(v.id)} className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700">
                              Add
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Expense Modal */}
      {showAddExpense && (
        <ExpenseForm
          vehicles={vehicles}
          vendors={vendors}
          categories={categories}
          bankAccounts={bankAccounts}
          autoPrint={true}
          onClose={() => setShowAddExpense(false)}
          onSave={() => {
            setShowAddExpense(false);
            loadData();
          }}
        />
      )}
    </div>
  );
};

// Buying Power Dashboard Component
const BuyingPowerDashboard = ({ onRefresh, bankAccounts = [], onOpenBankAccount, refreshTrigger }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingTitles, setPendingTitles] = useState([]);
  const [pendingAuctionPayments, setPendingAuctionPayments] = useState([]);
  const [showTitleModal, setShowTitleModal] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(null);
  const [selectedBankAccount, setSelectedBankAccount] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadData();
  }, [refreshTrigger]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [buyingPower, titles, payments] = await Promise.all([
        api.getBuyingPower(),
        api.getPendingTitles(),
        api.getPendingAuctionPayments()
      ]);
      setData(buyingPower);
      setPendingTitles(titles || []);
      setPendingAuctionPayments(payments || []);
      // Set default bank account
      if (bankAccounts.length > 0 && !selectedBankAccount) {
        const checking = bankAccounts.find(b => b.name.toLowerCase().includes('checking'));
        setSelectedBankAccount(checking?.id?.toString() || bankAccounts[0]?.id?.toString() || '');
      }
    } catch (e) {
      console.error('Failed to load buying power data:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleTitleReceived = async () => {
    if (!showTitleModal) return;
    setProcessing(true);
    try {
      await api.markTitleReceived(showTitleModal.id, {
        bankAccountId: selectedBankAccount
      });
      setShowTitleModal(null);
      loadData();
      if (onRefresh) onRefresh();
      alert(`Title received! $${showTitleModal.purchasePrice.toLocaleString()} deducted.`);
    } catch (e) {
      alert('Failed: ' + e.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleAuctionPaymentReceived = async () => {
    if (!showPaymentModal) return;
    setProcessing(true);
    try {
      await api.markAuctionPaymentReceived(showPaymentModal.id, {
        bankAccountId: selectedBankAccount
      });
      setShowPaymentModal(null);
      loadData();
      if (onRefresh) onRefresh();
      alert(`Payment received! $${showPaymentModal.salePrice.toLocaleString()} deposited.`);
    } catch (e) {
      alert('Failed: ' + e.message);
    } finally {
      setProcessing(false);
    }
  };

  if (loading || !data) return <div className="text-center py-8 text-neutral-500">Loading buying power...</div>;

  const { buyingPower, inventory } = data;
  
  // Determine color based on buying power
  const getBuyingPowerColor = (amount) => {
    if (amount >= 50000) return 'text-green-600';
    if (amount >= 20000) return 'text-amber-600';
    return 'text-red-600';
  };

  const getBuyingPowerBg = (amount) => {
    if (amount >= 50000) return 'bg-green-50 border-green-200';
    if (amount >= 20000) return 'bg-amber-50 border-amber-200';
    return 'bg-red-50 border-red-200';
  };

  const reconLevelLabel = (level) => {
    const labels = { 1: 'Minimal', 2: 'Light', 3: 'Medium', 4: 'Heavy', 5: 'Major' };
    return labels[level] || 'Medium';
  };

  return (
    <div className="space-y-3">
      {/* Main Buying Power Card */}
      <div className={`p-6 rounded-lg border-2 ${getBuyingPowerBg(buyingPower.total)}`}>
        <div className="flex items-center justify-between">
          <div>
                        <div className="flex items-center gap-2">
              <div className="text-sm text-neutral-600 font-medium">Available Buying Power</div>
              <div className="relative group">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-neutral-400"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
                <div className="absolute bottom-full mb-2 w-64 bg-black text-white text-xs rounded py-2 px-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                  (Bank Balance - Buffer) + Floorplan Available - Title Pending - Pending Drafts + AR - Payoffs Due - CC Debt
                  <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-black"></div>
                </div>
              </div>
            </div>
            <div className={`text-4xl font-bold ${getBuyingPowerColor(buyingPower.total)}`}>
              ${buyingPower.total.toLocaleString()}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-neutral-500">Inventory</div>
            <div className="text-2xl font-bold text-neutral-800">{inventory.count} vehicles</div>
          </div>
        </div>
      </div>

      {/* Buying Power Breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-neutral-200 rounded-lg p-3">
          <div className="text-xs text-neutral-500">Bank Balance</div>
          <div className="text-lg font-mono font-medium text-green-600">+${buyingPower.bankBalance.toLocaleString()}</div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-3">
          <div className="text-xs text-neutral-500">Buffer (Reserved)</div>
          <div className="text-lg font-mono font-medium text-red-600">-${buyingPower.buffer.toLocaleString()}</div>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
          <div className="text-xs text-purple-600">Floorplan Available</div>
          <div className="text-lg font-mono font-medium text-purple-700">+${buyingPower.floorplanAvailable.toLocaleString()}</div>
          <div className="text-xs text-purple-500">${buyingPower.floorplanUsed.toLocaleString()} / ${buyingPower.floorplanLimit.toLocaleString()} used</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="text-xs text-amber-600">Title Pending Owed</div>
          <div className="text-lg font-mono font-medium text-amber-700">-${(buyingPower.titlePendingOwed || 0).toLocaleString()}</div>
          <div className="text-xs text-amber-500">{buyingPower.titlePendingCount || 0} vehicles</div>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
          <div className="text-xs text-orange-600">Pending Drafts</div>
          <div className="text-lg font-mono font-medium text-orange-700">-${(buyingPower.pendingPaymentsTotal || 0).toLocaleString()}</div>
          <div className="text-xs text-orange-500">{buyingPower.pendingPaymentsCount || 0} payments</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="text-xs text-blue-600">Accounts Receivable</div>
          <div className="text-lg font-mono font-medium text-blue-700">+${buyingPower.auctionReceivables.toLocaleString()}</div>
          <div className="text-xs text-blue-500">{buyingPower.auctionReceivablesCount} pending</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="text-xs text-red-600">Floorplan Payoffs Due</div>
          <div className="text-lg font-mono font-medium text-red-700">-${buyingPower.floorplanPayoffsDue.toLocaleString()}</div>
          <div className="text-xs text-red-500">{buyingPower.floorplanPayoffsDueCount} vehicles</div>
        </div>
        {(buyingPower.creditCardDebt || 0) > 0 && (
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
            <div className="text-xs text-rose-600">Credit Card Debt</div>
            <div className="text-lg font-mono font-medium text-rose-700">-${buyingPower.creditCardDebt.toLocaleString()}</div>
          </div>
        )}
      </div>

      {/* Inventory Health */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <div className="text-xs text-neutral-500">Total Inventory Value</div>
          <div className="text-xl font-mono font-medium text-neutral-800">${inventory.totalValue.toLocaleString()}</div>
          <div className="text-xs text-neutral-400">Est. sale prices</div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <div className="text-xs text-neutral-500">Total Cost Basis</div>
          <div className="text-xl font-mono font-medium text-neutral-800">${inventory.totalCostBasis.toLocaleString()}</div>
          <div className="text-xs text-neutral-400">Purchase + recon + fees</div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <div className="text-xs text-neutral-500">Projected Profit</div>
          <div className={`text-xl font-mono font-medium ${inventory.projectedProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            ${inventory.projectedProfit.toLocaleString()}
          </div>
          <div className="text-xs text-neutral-400">Value - Cost</div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <div className="text-xs text-neutral-500">Avg Recon Level</div>
          <div className="text-xl font-medium text-neutral-800">{inventory.avgReconLevel} - {reconLevelLabel(Math.round(inventory.avgReconLevel))}</div>
          <div className="text-xs text-neutral-400">1=Minimal, 5=Major</div>
        </div>
      </div>

      {/* Recon Level Breakdown */}
      <div className="bg-white border border-neutral-200 rounded-lg p-4">
        <div className="text-sm font-medium mb-3">Inventory by Recon Level</div>
        <div className="grid grid-cols-4 gap-4">
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{inventory.reconLevelBreakdown.light}</div>
            <div className="text-xs text-green-700">Level 1-2 (Light)</div>
          </div>
          <div className="text-center p-3 bg-amber-50 rounded-lg">
            <div className="text-2xl font-bold text-amber-600">{inventory.reconLevelBreakdown.medium}</div>
            <div className="text-xs text-amber-700">Level 3 (Medium)</div>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">{inventory.reconLevelBreakdown.heavy}</div>
            <div className="text-xs text-red-700">Level 4-5 (Heavy)</div>
          </div>
          <div className="text-center p-3 bg-slate-100 rounded-lg">
            <div className="text-2xl font-bold text-slate-600">{inventory.copartCount || 0}</div>
            <div className="text-xs text-slate-700">Copart/Salvage</div>
          </div>
        </div>
      </div>

      {/* Pending Titles */}
      {pendingTitles.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-2 text-amber-700">Title Pending - Awaiting Payment ({pendingTitles.length})</h3>
          <div className="bg-amber-50 border border-amber-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-amber-700 border-b border-amber-200 bg-amber-100">
                  <th className="px-3 py-2 font-medium">Vehicle</th>
                  <th className="px-3 py-2 font-medium">Purchase Date</th>
                  <th className="px-3 py-2 font-medium">Days Pending</th>
                  <th className="px-3 py-2 font-medium text-right">Amount Owed</th>
                  <th className="px-3 py-2 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {pendingTitles.map(v => (
                  <tr key={v.id} className="border-b border-amber-100">
                    <td className="px-3 py-2">
                      <div className="font-medium">{v.stockNumber}</div>
                      <div className="text-xs text-neutral-500">{v.year} {v.make} {v.model}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">{v.purchaseDate?.split('T')[0] || '-'}</td>
                    <td className="px-3 py-2 text-xs font-medium">{v.daysPending} days</td>
                    <td className="px-3 py-2 text-right font-mono">${v.purchasePrice.toLocaleString()}</td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => setShowTitleModal(v)}
                        className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                      >
                        Title Received
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pending Auction Payments */}
      {pendingAuctionPayments.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-2 text-blue-700">Auction Receivables - Awaiting Payment ({pendingAuctionPayments.length})</h3>
          <div className="bg-blue-50 border border-blue-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-blue-700 border-b border-blue-200 bg-blue-100">
                  <th className="px-3 py-2 font-medium">Vehicle</th>
                  <th className="px-3 py-2 font-medium">Sale Date</th>
                  <th className="px-3 py-2 font-medium">Days Pending</th>
                  <th className="px-3 py-2 font-medium text-right">Amount Due</th>
                  <th className="px-3 py-2 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {pendingAuctionPayments.map(v => (
                  <tr key={v.id} className="border-b border-blue-100">
                    <td className="px-3 py-2">
                      <div className="font-medium">{v.stockNumber}</div>
                      <div className="text-xs text-neutral-500">{v.year} {v.make} {v.model}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">{v.saleDate?.split('T')[0] || '-'}</td>
                    <td className="px-3 py-2 text-xs font-medium">{v.daysPending} days</td>
                    <td className="px-3 py-2 text-right font-mono">${v.salePrice.toLocaleString()}</td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => setShowPaymentModal(v)}
                        className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                      >
                        Payment Received
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Title Received Modal */}
      {showTitleModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowTitleModal(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Title Received - Process Payment</h3>
            <div className="space-y-4">
              <div className="bg-neutral-50 rounded-lg p-3">
                <div className="font-medium">{showTitleModal.stockNumber}</div>
                <div className="text-sm text-neutral-500">{showTitleModal.year} {showTitleModal.make} {showTitleModal.model}</div>
                <div className="text-lg font-mono font-medium mt-2">${showTitleModal.purchasePrice.toLocaleString()}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Pay From Account</label>
                <select value={selectedBankAccount} onChange={e => setSelectedBankAccount(e.target.value)} className="w-full px-3 py-2 border border-neutral-200 rounded-lg">
                  {bankAccounts.map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setShowTitleModal(null)} className="px-4 py-2 text-sm text-neutral-600">Cancel</button>
                <button
                  onClick={handleTitleReceived}
                  disabled={processing}
                  className="px-4 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50"
                >
                  {processing ? 'Processing...' : `Pay $${showTitleModal.purchasePrice.toLocaleString()}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Auction Payment Received Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowPaymentModal(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Auction Payment Received</h3>
            <div className="space-y-4">
              <div className="bg-neutral-50 rounded-lg p-3">
                <div className="font-medium">{showPaymentModal.stockNumber}</div>
                <div className="text-sm text-neutral-500">{showPaymentModal.year} {showPaymentModal.make} {showPaymentModal.model}</div>
                <div className="text-lg font-mono font-medium text-green-600 mt-2">+${showPaymentModal.salePrice.toLocaleString()}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Deposit to Account</label>
                <select value={selectedBankAccount} onChange={e => setSelectedBankAccount(e.target.value)} className="w-full px-3 py-2 border border-neutral-200 rounded-lg">
                  {bankAccounts.map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setShowPaymentModal(null)} className="px-4 py-2 text-sm text-neutral-600">Cancel</button>
                <button
                  onClick={handleAuctionPaymentReceived}
                  disabled={processing}
                  className="px-4 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50"
                >
                  {processing ? 'Processing...' : `Deposit $${showPaymentModal.salePrice.toLocaleString()}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Floorplan Sub-Tab for Accounting Module
const FloorplanSubTab = ({ onRefresh }) => {
  const [data, setData] = useState({ vehicles: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [editingPayoff, setEditingPayoff] = useState(null);
  const [payoffModal, setPayoffModal] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('ACH');
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState('');

  useEffect(() => {
    loadData();
    api.getBankAccounts().then(accounts => {
      setBankAccounts(accounts || []);
      const checking = (accounts || []).find(a => a.name.toLowerCase().includes('checking'));
      setSelectedBankAccount(checking?.id?.toString() || (accounts?.[0]?.id?.toString() || ''));
    }).catch(console.error);
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const result = await api.getFloorplan();
      setData(result || { vehicles: [], summary: {} });
    } catch (e) {
      console.error('Failed to load floorplan data:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePayoff = async (vehicleId, amount) => {
    try {
      await api.updateFloorplanPayoff(vehicleId, amount);
      setEditingPayoff(null);
      loadData();
    } catch (e) {
      alert('Failed to update payoff amount: ' + e.message);
    }
  };

  const handlePayoff = async () => {
    if (!payoffModal) return;
    setProcessing(true);
    try {
      await api.payoffFloorplan(payoffModal.id, {
        payoffAmount: payoffModal.payoffAmount || payoffModal.purchasePrice,
        paymentMethod,
        notes,
        bankAccountId: selectedBankAccount
      });
      setPayoffModal(null);
      setNotes('');
      loadData();
      if (onRefresh) onRefresh();
      alert('Floorplan marked as paid!');
    } catch (e) {
      alert('Failed to process payoff: ' + e.message);
    } finally {
      setProcessing(false);
    }
  };

  const activeVehicles = data.vehicles.filter(v => !v.paid);
  const paidVehicles = data.vehicles.filter(v => v.paid);
  const needsPayoff = activeVehicles.filter(v => v.status === 'sold');

  if (loading) return <div className="text-center py-8 text-neutral-500">Loading floorplan data...</div>;

  return (
    <div className="space-y-3">
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <div className="text-xs text-neutral-500">Active Floored</div>
          <div className="text-2xl font-bold text-purple-600">${data.summary.totalFlooredAmount?.toLocaleString() || 0}</div>
          <div className="text-xs text-neutral-400">{data.summary.totalFloored || 0} vehicles</div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <div className="text-xs text-neutral-500">Needs Payoff</div>
          <div className="text-2xl font-bold text-amber-600">{needsPayoff.length}</div>
          <div className="text-xs text-neutral-400">Sold, awaiting payoff</div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <div className="text-xs text-neutral-500">Paid Off (All Time)</div>
          <div className="text-2xl font-bold text-green-600">{data.summary.totalPaidOff || 0}</div>
          <div className="text-xs text-neutral-400">vehicles</div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <div className="text-xs text-neutral-500">Provider</div>
          <div className="text-lg font-bold text-neutral-800">1st Choice Financial</div>
          <div className="text-xs text-neutral-400">Floorplan provider</div>
        </div>
      </div>

      {/* Needs Payoff Section */}
      {needsPayoff.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-2 text-amber-700">Needs Payoff ({needsPayoff.length})</h3>
          <div className="bg-amber-50 border border-amber-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-amber-700 border-b border-amber-200 bg-amber-100">
                  <th className="px-3 py-2 font-medium">Vehicle</th>
                  <th className="px-3 py-2 font-medium">Sale Date</th>
                  <th className="px-3 py-2 font-medium">Days Floored</th>
                  <th className="px-3 py-2 font-medium text-right">Purchase Price</th>
                  <th className="px-3 py-2 font-medium text-right">Payoff Amount</th>
                  <th className="px-3 py-2 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {needsPayoff.map(v => (
                  <tr key={v.id} className="border-b border-amber-100">
                    <td className="px-3 py-2">
                      <div className="font-medium">{v.stockNumber}</div>
                      <div className="text-xs text-neutral-500">{v.year} {v.make} {v.model}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">{v.saleDate?.split('T')[0] || '-'}</td>
                    <td className="px-3 py-2 text-xs font-medium">{v.daysFloored} days</td>
                    <td className="px-3 py-2 text-right font-mono">${v.purchasePrice.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">
                      {editingPayoff === v.id ? (
                        <input
                          type="number"
                          defaultValue={v.payoffAmount || v.purchasePrice}
                          onBlur={e => handleUpdatePayoff(v.id, parseFloat(e.target.value))}
                          onKeyDown={e => e.key === 'Enter' && handleUpdatePayoff(v.id, parseFloat(e.target.value))}
                          className="w-24 px-2 py-1 text-sm border border-neutral-300 rounded text-right"
                          autoFocus
                        />
                      ) : (
                        <span 
                          onClick={() => setEditingPayoff(v.id)} 
                          className="font-mono cursor-pointer hover:text-blue-600"
                          title="Click to edit"
                        >
                          ${(v.payoffAmount || v.purchasePrice).toLocaleString()}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => setPayoffModal(v)}
                        className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                      >
                        Pay Off
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Active Floored Vehicles */}
      <div>
        <h3 className="text-lg font-semibold mb-2">Active Floored Vehicles ({activeVehicles.filter(v => v.status !== 'sold').length})</h3>
        {activeVehicles.filter(v => v.status !== 'sold').length > 0 ? (
          <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                  <th className="px-3 py-2 font-medium">Vehicle</th>
                  <th className="px-3 py-2 font-medium">Purchase Date</th>
                  <th className="px-3 py-2 font-medium">Days Floored</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium text-right">Purchase Price</th>
                  <th className="px-3 py-2 font-medium text-right">Est. Payoff</th>
                  <th className="px-3 py-2 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {activeVehicles.filter(v => v.status !== 'sold').map(v => (
                  <tr key={v.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                    <td className="px-3 py-2">
                      <div className="font-medium">{v.stockNumber}</div>
                      <div className="text-xs text-neutral-500">{v.year} {v.make} {v.model}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">{v.purchaseDate?.split('T')[0] || '-'}</td>
                    <td className="px-3 py-2 text-xs">
                      <span className={`font-medium ${v.daysFloored > 60 ? 'text-red-600' : v.daysFloored > 30 ? 'text-amber-600' : 'text-neutral-600'}`}>
                        {v.daysFloored} days
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700">{v.status}</span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">${v.purchasePrice.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">
                      {editingPayoff === v.id ? (
                        <input
                          type="number"
                          defaultValue={v.payoffAmount || v.purchasePrice}
                          onBlur={e => handleUpdatePayoff(v.id, parseFloat(e.target.value))}
                          onKeyDown={e => e.key === 'Enter' && handleUpdatePayoff(v.id, parseFloat(e.target.value))}
                          className="w-24 px-2 py-1 text-sm border border-neutral-300 rounded text-right"
                          autoFocus
                        />
                      ) : (
                        <span 
                          onClick={() => setEditingPayoff(v.id)} 
                          className="font-mono cursor-pointer hover:text-blue-600"
                          title="Click to edit"
                        >
                          ${(v.payoffAmount || v.purchasePrice).toLocaleString()}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => setPayoffModal(v)}
                        className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                      >
                        Pay Off
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-neutral-50 font-medium">
                  <td colSpan={6} className="px-3 py-2 text-right">Total Active:</td>
                  <td className="px-3 py-2 text-right font-mono">
                    ${activeVehicles.filter(v => v.status !== 'sold').reduce((sum, v) => sum + (v.payoffAmount || v.purchasePrice), 0).toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <Empty text="No active floored vehicles" />
        )}
      </div>

      {/* Paid Off History */}
      {paidVehicles.length > 0 && (
        <details className="group">
          <summary className="text-lg font-semibold mb-2 cursor-pointer hover:text-neutral-600">
            Paid Off History ({paidVehicles.length}) <span className="text-sm font-normal text-neutral-400">click to expand</span>
          </summary>
          <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden mt-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                  <th className="px-3 py-2 font-medium">Vehicle</th>
                  <th className="px-3 py-2 font-medium">Paid Date</th>
                  <th className="px-3 py-2 font-medium">Days Floored</th>
                  <th className="px-3 py-2 font-medium text-right">Payoff Amount</th>
                </tr>
              </thead>
              <tbody>
                {paidVehicles.map(v => (
                  <tr key={v.id} className="border-b border-neutral-100">
                    <td className="px-3 py-2">
                      <div className="font-medium">{v.stockNumber}</div>
                      <div className="text-xs text-neutral-500">{v.year} {v.make} {v.model}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">{v.paidDate?.split('T')[0] || '-'}</td>
                    <td className="px-3 py-2 text-xs">{v.daysFloored} days</td>
                    <td className="px-3 py-2 text-right font-mono">${(v.payoffAmount || v.purchasePrice).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {/* Payoff Modal */}
      {payoffModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setPayoffModal(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Pay Off Floorplan</h3>
            <div className="space-y-4">
              <div className="bg-neutral-50 rounded-lg p-3">
                <div className="font-medium">{payoffModal.stockNumber}</div>
                <div className="text-sm text-neutral-500">{payoffModal.year} {payoffModal.make} {payoffModal.model}</div>
                <div className="text-sm mt-2">
                  <span className="text-neutral-500">Purchase Price:</span> 
                  <span className="font-mono ml-2">${payoffModal.purchasePrice.toLocaleString()}</span>
                </div>
                <div className="text-sm">
                  <span className="text-neutral-500">Days Floored:</span> 
                  <span className="font-medium ml-2">{payoffModal.daysFloored} days</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Payoff Amount</label>
                <input
                  type="number"
                  value={payoffModal.payoffAmount || payoffModal.purchasePrice}
                  onChange={e => setPayoffModal({ ...payoffModal, payoffAmount: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Pay From Account</label>
                <select value={selectedBankAccount} onChange={e => setSelectedBankAccount(e.target.value)} className="w-full px-3 py-2 border border-neutral-200 rounded-lg">
                  {bankAccounts.map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.name} (${parseFloat(acc.balance || 0).toLocaleString()})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Payment Method</label>
                <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} className="w-full px-3 py-2 border border-neutral-200 rounded-lg">
                  <option value="ACH">ACH</option>
                  <option value="Wire">Wire</option>
                  <option value="Check">Check</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Notes (optional)</label>
                <input
                  type="text"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Reference #, etc."
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setPayoffModal(null)} className="px-4 py-2 text-sm text-neutral-600 hover:text-neutral-800">Cancel</button>
                <button
                  onClick={handlePayoff}
                  disabled={processing}
                  className="px-4 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50"
                >
                  {processing ? 'Processing...' : `Pay Off $${(payoffModal.payoffAmount || payoffModal.purchasePrice).toLocaleString()}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Payroll Sub-Tab for Accounting Module
const PayrollSubTab = ({ onRefresh }) => {
  const [timeEntries, setTimeEntries] = useState([]);
  const [shopExpenses, setShopExpenses] = useState([]);
  const [users, setUsers] = useState([]);
  const [showPayModal, setShowPayModal] = useState(null);
  const [paidUsers, setPaidUsers] = useState({}); // Track who has been paid this session
  const [payments, setPayments] = useState([]); // Saved payment history from payroll_payments
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [historySearch, setHistorySearch] = useState('');
  const [expandedPaymentId, setExpandedPaymentId] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(() => {
    // Default to PREVIOUS week (for Monday morning payroll review)
    const now = new Date();
    const day = now.getDay();
    // Get this week's Monday
    const thisMonday = new Date(now);
    thisMonday.setDate(now.getDate() - day + (day === 0 ? -6 : 1));
    // Go back 7 days to get last week's Monday
    thisMonday.setDate(thisMonday.getDate() - 7);
    return thisMonday.toISOString().split('T')[0];
  });
  const [loading, setLoading] = useState(true);

  const loadPayments = useCallback(async () => {
    try {
      const list = await api.getPayrollPayments();
      setPayments(list || []);
    } catch (e) {
      console.error('Failed to load payment history:', e);
    }
  }, []);

  useEffect(() => {
    const loadPayrollData = async () => {
      setLoading(true);
      try {
        const [entries, expenses, userList] = await Promise.all([
          api.getTimeEntriesForWeek(selectedWeek),
          api.getShopExpensesForWeek(selectedWeek),
          api.getUsers()
        ]);
        setTimeEntries(entries || []);
        setShopExpenses(expenses || []);
        setUsers(userList || []);
      } catch (e) {
        console.error('Failed to load payroll data:', e);
      } finally {
        setLoading(false);
      }
    };
    loadPayrollData();
    loadPayments();
  }, [selectedWeek, loadPayments]);

  // Saved payment for a user/week (survives page reloads, unlike paidUsers)
  const findSavedPayment = (userId) =>
    payments.find(p => p.user_id === userId && String(p.week_start || '').slice(0, 10) === selectedWeek);

  const filteredPayments = payments.filter(p => {
    const q = historySearch.trim().toLowerCase();
    if (!q) return true;
    const details = typeof p.details === 'string' ? p.details : JSON.stringify(p.details || {});
    return (
      (p.user_name || '').toLowerCase().includes(q) ||
      (p.notes || '').toLowerCase().includes(q) ||
      (p.payment_method || '').toLowerCase().includes(q) ||
      String(p.week_start || '').slice(0, 10).includes(q) ||
      String(p.amount_paid || '').includes(q) ||
      details.toLowerCase().includes(q)
    );
  });

  const money = (n) => `$${(parseFloat(n) || 0).toFixed(2)}`;

  const printWeekDetail = (user) => {
    const data = userHours[user.id] || { totalHours: 0, entries: [] };
    const dedItems = userDeductions[user.id]?.items || [];
    const dedTotal = userDeductions[user.id]?.total || 0;
    const wage = parseFloat(user.hourlyWage) || 0;
    const gross = data.totalHours * wage;
    const entryRows = (data.entries || []).map(e => {
      const ci = new Date(e.clockIn || e.clock_in);
      const co = (e.clockOut || e.clock_out) ? new Date(e.clockOut || e.clock_out) : null;
      const hrs = co ? ((co - ci) / 3600000) - ((e.tookLunch || e.took_lunch) ? 0.5 : 0) : 0;
      const flag = (e.isManualEntry || e.is_manual_entry) ? ' (manual)' : ((e.editedAt || e.edited_at) ? ' (edited)' : '');
      return `<tr><td>${ci.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}${flag}</td><td>${ci.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} - ${co ? co.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '-'}</td><td class="r">${hrs.toFixed(2)} hrs</td></tr>`;
    }).join('');
    const purchaseRows = dedItems.map(i =>
      `<tr><td>${new Date(i.created_at || i.createdAt).toLocaleDateString()}</td><td>${i.description || ''}</td><td class="r">${money(i.amount)}</td></tr>`
    ).join('');
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>Payroll Detail - ${user.name} - Week of ${selectedWeek}</title>
      <style>body{font-family:sans-serif;padding:24px;max-width:620px;margin:0 auto}h2{margin:0 0 4px}h3{font-size:14px;border-bottom:2px solid #111;padding-bottom:4px;margin:20px 0 6px}table{width:100%;border-collapse:collapse;font-size:13px}td{padding:5px 4px;border-bottom:1px solid #ddd}.r{text-align:right;font-family:monospace}.total td{font-weight:bold;font-size:16px;border-top:2px solid #111;border-bottom:none}</style>
      </head><body>
      <h2>Payroll Detail — ${user.name}</h2>
      <div style="color:#666;font-size:13px">Week of ${selectedWeek} through ${weekEnd.toISOString().split('T')[0]}</div>
      <h3>Time Entries</h3>
      <table>${entryRows || '<tr><td>No entries</td></tr>'}</table>
      <h3>Personal Purchases</h3>
      <table>${purchaseRows || '<tr><td>None this week</td></tr>'}</table>
      <h3>Pay</h3>
      <table>
        <tr><td>Total hours</td><td class="r">${data.totalHours.toFixed(2)} hrs</td></tr>
        <tr><td>Hourly wage</td><td class="r">${money(wage)}</td></tr>
        <tr><td>Gross pay</td><td class="r">${money(gross)}</td></tr>
        <tr><td>Personal purchases (${dedItems.length})</td><td class="r">-${money(dedTotal)}</td></tr>
        <tr class="total"><td>Owed</td><td class="r">${money(gross - dedTotal)}</td></tr>
      </table>
      </body></html>
    `);
    win.document.close();
    win.print();
  };

  const printSavedPayment = (p) => {
    const d = (typeof p.details === 'string' ? JSON.parse(p.details || '{}') : p.details) || {};
    const purchaseRows = (d.purchases || []).map(i =>
      `<tr><td>${i.date ? new Date(i.date).toLocaleDateString() : ''}</td><td>${i.description || ''}</td><td class="r">${money(i.amount)}</td></tr>`
    ).join('');
    const entryRows = (d.entries || []).map(e =>
      `<tr><td>${e.day || ''}</td><td>${e.times || ''}</td><td class="r">${e.hours != null ? Number(e.hours).toFixed(2) + ' hrs' : ''}</td></tr>`
    ).join('');
    const payRows = (d.payments || []).map(pm =>
      `<tr><td>${pm.method || ''}${pm.checkNumber ? ' #' + pm.checkNumber : ''}</td><td class="r">${money(pm.amount)}</td></tr>`
    ).join('');
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>Payroll Payment - ${p.user_name} - ${String(p.week_start || '').slice(0, 10)}</title>
      <style>body{font-family:sans-serif;padding:24px;max-width:620px;margin:0 auto}h2{margin:0 0 4px}h3{font-size:14px;border-bottom:2px solid #111;padding-bottom:4px;margin:20px 0 6px}table{width:100%;border-collapse:collapse;font-size:13px}td{padding:5px 4px;border-bottom:1px solid #ddd}.r{text-align:right;font-family:monospace}.total td{font-weight:bold;font-size:16px;border-top:2px solid #111;border-bottom:none}</style>
      </head><body>
      <h2>Payroll Payment — ${p.user_name}</h2>
      <div style="color:#666;font-size:13px">Week of ${String(p.week_start || '').slice(0, 10)} · Paid ${new Date(p.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}${p.paid_by_name ? ' by ' + p.paid_by_name : ''}</div>
      ${entryRows ? `<h3>Time Entries</h3><table>${entryRows}</table>` : ''}
      ${purchaseRows ? `<h3>Personal Purchases Deducted</h3><table>${purchaseRows}</table>` : ''}
      <h3>Pay</h3>
      <table>
        ${p.hours != null ? `<tr><td>Hours</td><td class="r">${parseFloat(p.hours).toFixed(2)} hrs</td></tr>` : ''}
        ${p.hourly_wage != null ? `<tr><td>Hourly wage</td><td class="r">${money(p.hourly_wage)}</td></tr>` : ''}
        ${p.gross_pay != null ? `<tr><td>Gross pay</td><td class="r">${money(p.gross_pay)}</td></tr>` : ''}
        ${p.deductions != null && parseFloat(p.deductions) > 0 ? `<tr><td>Deductions</td><td class="r">-${money(p.deductions)}</td></tr>` : ''}
        <tr class="total"><td>Amount paid</td><td class="r">${money(p.amount_paid)}</td></tr>
      </table>
      ${payRows ? `<h3>Paid Via</h3><table>${payRows}</table>` : ''}
      ${p.notes ? `<div style="margin-top:12px;font-size:13px;color:#444">Notes: ${p.notes}</div>` : ''}
      </body></html>
    `);
    win.document.close();
    win.print();
  };

  const userHours = {};
  timeEntries.forEach(entry => {
    if (!userHours[entry.userId]) userHours[entry.userId] = { totalHours: 0, lunchMinutes: 0, entries: [] };
    const hours = entry.clockOut ? (new Date(entry.clockOut) - new Date(entry.clockIn)) / (1000 * 60 * 60) : 0;
    const lunchDeduction = entry.tookLunch ? 0.5 : 0; // 30 minutes
    userHours[entry.userId].totalHours += hours - lunchDeduction;
    userHours[entry.userId].lunchMinutes += entry.tookLunch ? 30 : 0;
    userHours[entry.userId].entries.push(entry);
  });

  const userDeductions = {};
  shopExpenses.filter(e => (e.expenseType || e.expense_type) === 'personal').forEach(expense => {
    const uid = expense.userId || expense.user_id;
    if (!userDeductions[uid]) userDeductions[uid] = { total: 0, items: [] };
    userDeductions[uid].total += parseFloat(expense.amount || 0);
    userDeductions[uid].items.push(expense);
  });

  // Include all employees and contractors
  const payableUsers = users.filter(u => u.role === 'shop' || u.role === 'detail' || u.role === 'buyer');

  const handlePay = async (userId, amount, method, notes) => {
    try {
      const numericAmount = parseFloat(amount);
      if (Number.isNaN(numericAmount) || numericAmount <= 0) {
        alert('Invalid amount for payroll payment');
        return;
      }
      const description = `Payroll - ${users.find(u => u.id === userId)?.name || 'Employee'}`;
      const memo = `Week of ${selectedWeek}. ${notes || ''}`;
      await api.createExpense({
        description,
        amount: numericAmount,
        category: 'Payroll',
        paymentMethod: method,
        memo,
        costType: 'operating',
        lineItems: [{
          amount: numericAmount,
          category: 'Payroll',
          description
        }],
        skipDuplicateCheck: true
      });
      setShowPayModal(null);
      onRefresh();
      alert('Payment recorded successfully');
    } catch (e) {
      alert('Failed to record payment: ' + e.message);
    }
  };

  // Week navigation helpers
  const goToPreviousWeek = () => {
    const prev = new Date(selectedWeek);
    prev.setDate(prev.getDate() - 7);
    setSelectedWeek(prev.toISOString().split('T')[0]);
  };
  
  const goToNextWeek = () => {
    const next = new Date(selectedWeek);
    next.setDate(next.getDate() + 7);
    setSelectedWeek(next.toISOString().split('T')[0]);
  };

  const goToThisWeek = () => {
    const now = new Date();
    const day = now.getDay();
    const thisMonday = new Date(now);
    thisMonday.setDate(now.getDate() - day + (day === 0 ? -6 : 1));
    setSelectedWeek(thisMonday.toISOString().split('T')[0]);
  };

  const goToLastWeek = () => {
    const now = new Date();
    const day = now.getDay();
    const thisMonday = new Date(now);
    thisMonday.setDate(now.getDate() - day + (day === 0 ? -6 : 1));
    thisMonday.setDate(thisMonday.getDate() - 7);
    setSelectedWeek(thisMonday.toISOString().split('T')[0]);
  };

  // Format week display
  const weekEnd = new Date(selectedWeek);
  weekEnd.setDate(weekEnd.getDate() + 6);
  const weekDisplay = `${new Date(selectedWeek).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button onClick={goToPreviousWeek} className="px-2 py-1 text-sm border border-neutral-200 rounded hover:bg-neutral-100">←</button>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{weekDisplay}</span>
            <input type="date" value={selectedWeek} onChange={e => setSelectedWeek(e.target.value)} className="px-2 py-1 text-sm border border-neutral-200 rounded w-36" />
          </div>
          <button onClick={goToNextWeek} className="px-2 py-1 text-sm border border-neutral-200 rounded hover:bg-neutral-100">→</button>
          <div className="flex gap-1 ml-2">
            <button onClick={goToLastWeek} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200">Last Week</button>
            <button onClick={goToThisWeek} className="px-2 py-1 text-xs bg-neutral-100 text-neutral-700 rounded hover:bg-neutral-200">This Week</button>
          </div>
        </div>
        <button onClick={() => setShowPayModal({ type: 'contractor' })} className="text-xs bg-neutral-800 text-white px-3 py-1 rounded hover:bg-neutral-700">+ Pay Contractor</button>
      </div>
      {loading ? (
        <div className="text-center py-8 text-neutral-500">Loading payroll data...</div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                <th className="px-3 py-2 font-medium">Employee</th>
                <th className="px-3 py-2 font-medium">Role</th>
                <th className="px-3 py-2 font-medium text-right">Hours Worked</th>
                <th className="px-3 py-2 font-medium text-right">Lunch Breaks</th>
                <th className="px-3 py-2 font-medium text-right">Personal Deductions</th>
                <th className="px-3 py-2 font-medium text-right">Net Hours</th>
                <th className="px-3 py-2 font-medium text-center">Status</th>
                <th className="px-3 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {payableUsers.map(user => {
                const data = userHours[user.id] || { totalHours: 0, lunchMinutes: 0, entries: [] };
                const deductions = userDeductions[user.id]?.total || 0;
                const dedItems = userDeductions[user.id]?.items || [];
                const savedPayment = findSavedPayment(user.id);
                const paid = paidUsers[`${user.id}-${selectedWeek}`] || (savedPayment ? { date: savedPayment.created_at } : null);
                const wage = parseFloat(user.hourlyWage) || 0;
                const gross = data.totalHours * wage;
                const isExpanded = expandedUserId === user.id;
                return (
                  <React.Fragment key={user.id}>
                  <tr className={`border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer ${paid ? 'bg-green-50' : ''}`} onClick={() => setExpandedUserId(isExpanded ? null : user.id)}>
                    <td className="px-3 py-2 font-medium">
                      <span className="text-neutral-400 mr-1 text-xs">{isExpanded ? '▼' : '▶'}</span>
                      {user.name}
                    </td>
                    <td className="px-3 py-2 text-xs capitalize">{user.role}</td>
                    <td className="px-3 py-2 text-right font-mono">{data.totalHours.toFixed(1)} hrs</td>
                    <td className="px-3 py-2 text-right font-mono text-neutral-500">{data.lunchMinutes > 0 ? `${data.lunchMinutes} min` : '-'}</td>
                    <td className="px-3 py-2 text-right font-mono text-red-600">{deductions > 0 ? `-$${deductions.toFixed(2)}` : '-'}</td>
                    <td className="px-3 py-2 text-right font-mono font-medium">{data.totalHours.toFixed(1)} hrs</td>
                    <td className="px-3 py-2 text-center">
                      {paid ? (
                        <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                          ✓ Paid {new Date(paid.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      ) : data.totalHours > 0 ? (
                        <span className="text-xs text-amber-600">Pending</span>
                      ) : (
                        <span className="text-xs text-neutral-400">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right" onClick={e => e.stopPropagation()}>
                      {paid ? (
                        <button onClick={() => savedPayment ? printSavedPayment(savedPayment) : setShowPayModal({ user, hours: data.totalHours, deductions, deductionItems: dedItems, entries: data.entries, reprint: paidUsers[`${user.id}-${selectedWeek}`] })} className="text-xs text-neutral-500 hover:text-neutral-700">Reprint</button>
                      ) : (
                        <button onClick={() => setShowPayModal({ user, hours: data.totalHours, deductions, deductionItems: dedItems, entries: data.entries })} className="text-xs text-blue-600 hover:text-blue-800">Pay</button>
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="border-b border-neutral-200 bg-neutral-50/70">
                      <td colSpan={8} className="px-4 py-3">
                        <div className="grid md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <div className="font-semibold text-xs text-neutral-500 uppercase mb-1">Time Entries</div>
                            {(data.entries || []).length === 0 ? (
                              <div className="text-neutral-400 text-xs">No entries this week</div>
                            ) : (data.entries || []).map(e => {
                              const ci = new Date(e.clockIn || e.clock_in);
                              const co = (e.clockOut || e.clock_out) ? new Date(e.clockOut || e.clock_out) : null;
                              const hrs = co ? ((co - ci) / 3600000) - ((e.tookLunch || e.took_lunch) ? 0.5 : 0) : 0;
                              const edited = e.editedAt || e.edited_at;
                              const manual = e.isManualEntry || e.is_manual_entry;
                              return (
                                <div key={e.id} className="flex justify-between py-0.5">
                                  <span>
                                    {ci.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}{' '}
                                    <span className="text-neutral-400 text-xs">{ci.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} – {co ? co.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : 'working'}</span>
                                    {manual && <span className="ml-1 text-xs font-semibold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded-full">manual</span>}
                                    {edited && !manual && <span className="ml-1 text-xs font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">edited</span>}
                                  </span>
                                  <span className="font-mono">{hrs.toFixed(2)}</span>
                                </div>
                              );
                            })}
                          </div>
                          <div>
                            <div className="font-semibold text-xs text-neutral-500 uppercase mb-1">Personal Purchases</div>
                            {dedItems.length === 0 ? (
                              <div className="text-neutral-400 text-xs">None this week</div>
                            ) : dedItems.map(i => (
                              <div key={i.id} className="flex justify-between py-0.5">
                                <span>{i.description} <span className="text-neutral-400 text-xs">{new Date(i.created_at || i.createdAt).toLocaleDateString()}</span></span>
                                <span className="font-mono text-red-600">-{money(i.amount)}</span>
                              </div>
                            ))}
                            <div className="mt-2 pt-2 border-t border-neutral-200 space-y-0.5">
                              <div className="flex justify-between"><span>Hours × wage ({data.totalHours.toFixed(2)} × {money(wage)})</span><span className="font-mono">{money(gross)}</span></div>
                              <div className="flex justify-between text-red-600"><span>Deductions</span><span className="font-mono">-{money(deductions)}</span></div>
                              <div className="flex justify-between font-bold"><span>Owed</span><span className="font-mono">{money(gross - deductions)}</span></div>
                              {wage === 0 && <div className="text-xs text-amber-600">No hourly wage set — edit this user in Admin → Users</div>}
                            </div>
                            <button onClick={() => printWeekDetail(user)} className="mt-2 text-xs px-3 py-1.5 border border-neutral-300 rounded hover:bg-white">Print Detail</button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {payableUsers.length === 0 && <div className="text-center py-8 text-neutral-500">No employees found</div>}
        </div>
      )}

      {/* Payment History - searchable record of every payroll payment */}
      <div className="mt-6 bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <div className="px-3 py-2 border-b border-neutral-200 bg-neutral-50 flex items-center justify-between gap-3 flex-wrap">
          <span className="font-medium text-sm">Payment History ({payments.length})</span>
          <input
            type="text"
            value={historySearch}
            onChange={e => setHistorySearch(e.target.value)}
            placeholder="Search by name, week, amount, purchase..."
            className="px-3 py-1.5 text-sm border border-neutral-200 rounded-lg w-72 max-w-full"
          />
        </div>
        {filteredPayments.length === 0 ? (
          <div className="text-center py-6 text-neutral-400 text-sm">
            {payments.length === 0 ? 'No payments recorded yet — payments made from here will appear in this history' : 'No payments match your search'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200">
                <th className="px-3 py-2 font-medium">Paid On</th>
                <th className="px-3 py-2 font-medium">Employee</th>
                <th className="px-3 py-2 font-medium">Week Of</th>
                <th className="px-3 py-2 font-medium text-right">Hours</th>
                <th className="px-3 py-2 font-medium text-right">Deductions</th>
                <th className="px-3 py-2 font-medium text-right">Paid</th>
                <th className="px-3 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.map(p => {
                const pDetails = (typeof p.details === 'string' ? (() => { try { return JSON.parse(p.details); } catch { return {}; } })() : p.details) || {};
                const isOpen = expandedPaymentId === p.id;
                return (
                  <React.Fragment key={p.id}>
                  <tr className="border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer" onClick={() => setExpandedPaymentId(isOpen ? null : p.id)}>
                    <td className="px-3 py-2 text-xs">{new Date(p.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                    <td className="px-3 py-2 font-medium">{p.user_name}</td>
                    <td className="px-3 py-2 text-xs font-mono">{String(p.week_start || '').slice(0, 10) || '-'}</td>
                    <td className="px-3 py-2 text-right font-mono">{p.hours != null ? parseFloat(p.hours).toFixed(1) : '-'}</td>
                    <td className="px-3 py-2 text-right font-mono text-red-600">{p.deductions != null && parseFloat(p.deductions) > 0 ? `-${money(p.deductions)}` : '-'}</td>
                    <td className="px-3 py-2 text-right font-mono font-medium">{money(p.amount_paid)}</td>
                    <td className="px-3 py-2 text-right" onClick={e => e.stopPropagation()}>
                      <button onClick={() => printSavedPayment(p)} className="text-xs text-blue-600 hover:text-blue-800">Print</button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="border-b border-neutral-200 bg-neutral-50/70">
                      <td colSpan={7} className="px-4 py-3 text-sm">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="space-y-0.5">
                            {p.hours != null && <div className="flex justify-between"><span>Hours × wage ({parseFloat(p.hours).toFixed(2)} × {money(p.hourly_wage)})</span><span className="font-mono">{money(p.gross_pay)}</span></div>}
                            {p.deductions != null && parseFloat(p.deductions) > 0 && <div className="flex justify-between text-red-600"><span>Deductions</span><span className="font-mono">-{money(p.deductions)}</span></div>}
                            <div className="flex justify-between font-bold"><span>Paid</span><span className="font-mono">{money(p.amount_paid)}</span></div>
                            {(pDetails.payments || []).map((pm, i) => (
                              <div key={i} className="flex justify-between text-xs text-neutral-500"><span>{pm.method}{pm.checkNumber ? ` #${pm.checkNumber}` : ''}</span><span className="font-mono">{money(pm.amount)}</span></div>
                            ))}
                            {p.paid_by_name && <div className="text-xs text-neutral-400 pt-1">Recorded by {p.paid_by_name}</div>}
                            {p.notes && <div className="text-xs text-neutral-500">Notes: {p.notes}</div>}
                          </div>
                          <div>
                            <div className="font-semibold text-xs text-neutral-500 uppercase mb-1">Purchases Deducted</div>
                            {(pDetails.purchases || []).length === 0 ? (
                              <div className="text-neutral-400 text-xs">None</div>
                            ) : (pDetails.purchases || []).map((i, idx) => (
                              <div key={idx} className="flex justify-between py-0.5">
                                <span>{i.description}</span>
                                <span className="font-mono text-red-600">-{money(i.amount)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pay Modal */}
      {showPayModal && (
        <PayEmployeeModal
          user={showPayModal.user}
          hours={showPayModal.hours}
          deductions={showPayModal.deductions}
          deductionItems={showPayModal.deductionItems}
          entries={showPayModal.entries}
          isContractor={showPayModal.type === 'contractor'}
          reprint={showPayModal.reprint}
          onClose={() => setShowPayModal(null)}
          onPay={handlePay}
          onRefresh={onRefresh}
          onPaid={(userId, receiptData) => {
            setPaidUsers(prev => ({
              ...prev,
              [`${userId}-${selectedWeek}`]: { date: new Date().toISOString(), ...receiptData }
            }));
            loadPayments();
          }}
          selectedWeek={selectedWeek}
        />
      )}
    </div>
  );
};

// Reports Module
const ReportsModule = () => {
  const [activeReport, setActiveReport] = useState('pnl');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    return {
      start: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0],
      end: now.toISOString().split('T')[0]
    };
  });

  const reports = [
    { id: 'performance', label: 'Performance' },
    { id: 'pnl', label: 'P&L Summary' },
    { id: 'utilities', label: 'Utilities' },
    { id: 'inventory', label: 'Inventory Aging' },
    { id: 'recon', label: 'Recon Costs' },
    { id: 'transactions', label: 'Transactions' },
    { id: 'payroll', label: 'Payroll' },
    { id: 'vendors', label: 'Vendor Spend' }
  ];
  // Performance range: preset chips + optional custom dates + bucket granularity
  const [perf, setPerf] = useState({ preset: '1Y', granularity: 'auto', start: '', end: '' });

  const PERF_PRESETS = ['1D', '5D', '10D', '30D', '90D', 'MTD', 'YTD', '1Y', '2Y', 'Custom'];

  const perfComputeRange = () => {
    const toStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const today = new Date();
    const endDate = toStr(today);
    const daysBack = (n) => { const d = new Date(today); d.setDate(d.getDate() - (n - 1)); return toStr(d); };
    switch (perf.preset) {
      case '1D': return { startDate: endDate, endDate };
      case '5D': return { startDate: daysBack(5), endDate };
      case '10D': return { startDate: daysBack(10), endDate };
      case '30D': return { startDate: daysBack(30), endDate };
      case '90D': return { startDate: daysBack(90), endDate };
      case 'MTD': return { startDate: toStr(new Date(today.getFullYear(), today.getMonth(), 1)), endDate };
      case 'YTD': return { startDate: toStr(new Date(today.getFullYear(), 0, 1)), endDate };
      case '1Y': { const d = new Date(today); d.setFullYear(d.getFullYear() - 1); return { startDate: toStr(d), endDate }; }
      case '2Y': { const d = new Date(today); d.setFullYear(d.getFullYear() - 2); return { startDate: toStr(d), endDate }; }
      case 'Custom': return { startDate: perf.start || daysBack(30), endDate: perf.end || endDate };
      default: return { startDate: daysBack(365), endDate };
    }
  };

  const perfGranularity = (startDate, endDate) => {
    if (perf.granularity !== 'auto') return perf.granularity;
    const days = Math.max(1, Math.round((new Date(endDate) - new Date(startDate)) / 86400000) + 1);
    if (days <= 45) return 'day';
    if (days <= 190) return 'week';
    return 'month';
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      let data;
      switch (activeReport) {
        case 'performance': {
          const { startDate, endDate } = perfComputeRange();
          data = await api.getPerformanceReport({ startDate, endDate, granularity: perfGranularity(startDate, endDate) });
          break;
        }
        case 'pnl':
          data = await api.getPnLReport(dateRange.start, dateRange.end);
          break;
        case 'inventory':
          data = await api.getInventoryAgingReport();
          break;
        case 'recon':
          data = await api.getReconCostsReport(dateRange.start, dateRange.end);
          break;
        case 'transactions':
          data = await api.getTransactionsReport(dateRange.start, dateRange.end);
          break;
        case 'payroll':
          data = await api.getPayrollReport(dateRange.start, dateRange.end);
          break;
        case 'vendors':
          data = await api.getVendorSpendReport(dateRange.start, dateRange.end);
          break;
        case 'utilities':
          data = { loaded: true };
          break;
        default:
          data = null;
      }
      setReportData(data);
    } catch (e) {
      console.error('Failed to load report:', e);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, [activeReport, dateRange.start, dateRange.end, perf]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Reports</h1>
        {activeReport === 'performance' ? (
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <div className="flex items-center gap-1 flex-wrap">
              {PERF_PRESETS.map(p => (
                <button key={p} onClick={() => setPerf(prev => ({ ...prev, preset: p }))}
                  className={`px-2 py-1 text-xs rounded ${perf.preset === p ? 'bg-neutral-800 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}>
                  {p}
                </button>
              ))}
            </div>
            {perf.preset === 'Custom' && (
              <div className="flex items-center gap-1.5">
                <input type="date" value={perf.start} onChange={e => setPerf(prev => ({ ...prev, start: e.target.value }))} className="px-2 py-1 text-xs border border-neutral-200 rounded" />
                <span className="text-neutral-400 text-xs">to</span>
                <input type="date" value={perf.end} onChange={e => setPerf(prev => ({ ...prev, end: e.target.value }))} className="px-2 py-1 text-xs border border-neutral-200 rounded" />
              </div>
            )}
            <div className="flex items-center gap-1 border-l border-neutral-200 pl-2">
              {['auto', 'day', 'week', 'month'].map(g => (
                <button key={g} onClick={() => setPerf(prev => ({ ...prev, granularity: g }))}
                  title="Bucket size for the table rows"
                  className={`px-2 py-1 text-xs rounded capitalize ${perf.granularity === g ? 'bg-blue-600 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}>
                  {g}
                </button>
              ))}
            </div>
          </div>
        ) : activeReport !== 'utilities' && (
          <div className="flex items-center gap-3">
            <input type="date" value={dateRange.start} onChange={e => setDateRange(d => ({ ...d, start: e.target.value }))} className="px-2 py-1 text-sm border border-neutral-200 rounded" />
            <span className="text-neutral-400">to</span>
            <input type="date" value={dateRange.end} onChange={e => setDateRange(d => ({ ...d, end: e.target.value }))} className="px-2 py-1 text-sm border border-neutral-200 rounded" />
          </div>
        )}
      </div>

      {/* Report Tabs */}
      <div className="flex gap-2 border-b border-neutral-200">
        {reports.map(r => (
          <button key={r.id} onClick={() => setActiveReport(r.id)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px ${activeReport === r.id ? 'border-neutral-800 text-neutral-800' : 'border-transparent text-neutral-500 hover:text-neutral-700'}`}>
            {r.label}
          </button>
        ))}
      </div>

      {activeReport === 'utilities' ? (
        <UtilitiesReport />
      ) : loading ? (
        <div className="text-center py-8 text-neutral-500">Loading report...</div>
      ) : !reportData ? (
        <div className="text-center py-8 text-neutral-500">No data available</div>
      ) : (
        <>
          {/* Performance — where cash went (the 5 boxes). Accounting P&L is P&L Summary. */}
          {activeReport === 'performance' && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white border border-neutral-200 rounded-lg p-4">
                  <div className="text-xs text-neutral-500">Units Sold</div>
                  <div className="text-2xl font-bold">{reportData.totals?.unitsSold || 0}</div>
                </div>
                <div className="bg-white border border-neutral-200 rounded-lg p-4">
                  <div className="text-xs text-neutral-500">Revenue</div>
                  <div className="text-2xl font-bold">${Math.round(reportData.totals?.revenue || 0).toLocaleString()}</div>
                </div>
                <div className="bg-white border border-neutral-200 rounded-lg p-4">
                  <div className="text-xs text-neutral-500">Gross Profit</div>
                  <div className="text-2xl font-bold">${Math.round(reportData.totals?.grossProfit || 0).toLocaleString()}</div>
                </div>
              </div>

              <div className="grid grid-cols-6 gap-3">
                {[
                  { key: 'overhead', label: 'Overhead' },
                  { key: 'intoCars', label: 'Into Cars' },
                  { key: 'labor', label: 'Labor' },
                  { key: 'consumables', label: 'Consumables' },
                  { key: 'extra', label: 'Extra' },
                  { key: 'spendTotal', label: 'Total Spend' },
                ].map(card => (
                  <div key={card.key} className="bg-white border border-neutral-200 rounded-lg p-3">
                    <div className="text-xs text-neutral-500">{card.label}</div>
                    <div className={`text-xl font-bold ${card.key === 'spendTotal' ? 'text-neutral-900' : 'text-red-600'}`}>
                      ${Math.round(reportData.totals?.[card.key] || 0).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                      <th className="px-3 py-2 font-medium">{reportData.granularity === 'day' ? 'Day' : reportData.granularity === 'week' ? 'Week of' : 'Month'}</th>
                      <th className="px-3 py-2 font-medium text-right">Units</th>
                      <th className="px-3 py-2 font-medium text-right">Revenue</th>
                      <th className="px-3 py-2 font-medium text-right">Overhead</th>
                      <th className="px-3 py-2 font-medium text-right">Into Cars</th>
                      <th className="px-3 py-2 font-medium text-right">Labor</th>
                      <th className="px-3 py-2 font-medium text-right">Consumables</th>
                      <th className="px-3 py-2 font-medium text-right">Extra</th>
                      <th className="px-3 py-2 font-medium text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(reportData.series || []).slice().reverse().map(m => (
                      <tr key={m.month} className="border-b border-neutral-100 last:border-0">
                        <td className="px-3 py-2 font-medium">{m.month}</td>
                        <td className="px-3 py-2 text-right">{m.unitsSold}</td>
                        <td className="px-3 py-2 text-right font-mono">${Math.round(m.revenue).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-mono text-red-600">${Math.round(m.overhead || 0).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-mono text-red-600">${Math.round(m.intoCars || 0).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-mono text-red-600">${Math.round(m.labor || 0).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-mono text-red-600">${Math.round(m.consumables || 0).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-mono text-red-600">${Math.round(m.extra || 0).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-mono font-bold">${Math.round(m.spendTotal || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-white border border-neutral-200 rounded-lg p-4">
                <h3 className="font-medium mb-3">Spend by Category ({reportData.start} to {reportData.end})</h3>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                  {(reportData.operatingByCategory || []).slice(0, 20).map(cat => (
                    <div key={cat.category} className="flex justify-between">
                      <span>{cat.category}</span>
                      <span className="font-mono">${Math.round(cat.total).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-neutral-400 mt-3">
                  These buckets come from the box picked on the expense form. They do not change accounting. P&amp;L Summary is the accounting profit report and counts every expense. Old tags map as: Vehicle Cost → Into Cars, Operating → Overhead, Non-Essential / No Overhead Effect → Extra. Bill-checklist payments with no expense row count as Overhead.
                </p>
              </div>
            </div>
          )}

          {/* P&L Report */}
          {activeReport === 'pnl' && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-white border border-neutral-200 rounded-lg p-4">
                  <div className="text-xs text-neutral-500">Units Sold</div>
                  <div className="text-2xl font-bold">{reportData.revenue?.unitsSold || 0}</div>
                </div>
                <div className="bg-white border border-neutral-200 rounded-lg p-4">
                  <div className="text-xs text-neutral-500">Gross Revenue</div>
                  <div className="text-2xl font-bold">${(reportData.revenue?.grossRevenue || 0).toLocaleString()}</div>
                </div>
                <div className="bg-white border border-neutral-200 rounded-lg p-4">
                  <div className="text-xs text-neutral-500">Total Expenses</div>
                  <div className="text-2xl font-bold text-red-600">${(reportData.expenses?.total || 0).toLocaleString()}</div>
                </div>
                <div className={`border rounded-lg p-4 ${(reportData.netProfit || 0) >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="text-xs text-neutral-500">Net Profit</div>
                  <div className={`text-2xl font-bold ${(reportData.netProfit || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    ${(reportData.netProfit || 0).toLocaleString()}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white border border-neutral-200 rounded-lg p-4">
                  <h3 className="font-medium mb-3">Revenue Breakdown</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span>Gross Revenue</span><span className="font-mono">${(reportData.revenue?.grossRevenue || 0).toLocaleString()}</span></div>
                    <div className="flex justify-between"><span>Cost of Goods</span><span className="font-mono text-red-600">-${(reportData.revenue?.totalCost || 0).toLocaleString()}</span></div>
                    <div className="flex justify-between"><span>Pack Fees</span><span className="font-mono">${(reportData.revenue?.packTotal || 0).toLocaleString()}</span></div>
                    <div className="flex justify-between border-t pt-2 font-medium"><span>Gross Profit</span><span className="font-mono">${(reportData.revenue?.grossProfit || 0).toLocaleString()}</span></div>
                  </div>
                </div>
                <div className="bg-white border border-neutral-200 rounded-lg p-4">
                  <h3 className="font-medium mb-3">Expenses by Category</h3>
                  <div className="space-y-2 text-sm">
                    {(reportData.expenses?.byCategory || []).slice(0, 8).map(cat => (
                      <div key={cat.category} className="flex justify-between">
                        <span>{cat.category || 'Uncategorized'}</span>
                        <span className="font-mono">${cat.total.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Inventory Aging */}
          {activeReport === 'inventory' && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                {(reportData.summary?.buckets || []).map(b => (
                  <div key={b.range} className={`border rounded-lg p-4 ${b.range === '90+' ? 'bg-red-50 border-red-200' : 'bg-white border-neutral-200'}`}>
                    <div className="text-xs text-neutral-500">{b.range} Days</div>
                    <div className="text-2xl font-bold">{b.count}</div>
                    <div className="text-xs text-neutral-500">${b.value.toLocaleString()}</div>
                  </div>
                ))}
              </div>
              <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                      <th className="px-3 py-2 font-medium">Stock #</th>
                      <th className="px-3 py-2 font-medium">Vehicle</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2 font-medium text-right">Days</th>
                      <th className="px-3 py-2 font-medium text-right">Cost</th>
                      <th className="px-3 py-2 font-medium text-right">Est. Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(reportData.vehicles || []).slice(0, 20).map(v => (
                      <tr key={v.id} className="border-b border-neutral-100">
                        <td className="px-3 py-2 font-mono">{v.stock_number}</td>
                        <td className="px-3 py-2">{v.year} {v.make} {v.model}</td>
                        <td className="px-3 py-2"><span className="text-xs px-2 py-0.5 rounded bg-neutral-100">{v.status}</span></td>
                        <td className={`px-3 py-2 text-right font-mono ${v.days_in_inventory > 60 ? 'text-red-600 font-medium' : ''}`}>{Math.floor(v.days_in_inventory)}</td>
                        <td className="px-3 py-2 text-right font-mono">${parseFloat(v.purchase_price || 0).toLocaleString()}</td>
                        <td className={`px-3 py-2 text-right font-mono ${parseFloat(v.estimated_profit) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          ${parseFloat(v.estimated_profit || 0).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Recon Costs */}
          {activeReport === 'recon' && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white border border-neutral-200 rounded-lg p-4">
                  <div className="text-xs text-neutral-500">Total Vehicles</div>
                  <div className="text-2xl font-bold">{reportData.summary?.totalVehicles || 0}</div>
                </div>
                <div className="bg-white border border-neutral-200 rounded-lg p-4">
                  <div className="text-xs text-neutral-500">Total Recon Cost</div>
                  <div className="text-2xl font-bold">${(reportData.summary?.totalReconCost || 0).toLocaleString()}</div>
                </div>
                <div className="bg-white border border-neutral-200 rounded-lg p-4">
                  <div className="text-xs text-neutral-500">Average per Vehicle</div>
                  <div className="text-2xl font-bold">${(reportData.summary?.averageReconCost || 0).toLocaleString()}</div>
                </div>
              </div>
              <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                      <th className="px-3 py-2 font-medium">Stock #</th>
                      <th className="px-3 py-2 font-medium">Vehicle</th>
                      <th className="px-3 py-2 font-medium text-right">Purchase</th>
                      <th className="px-3 py-2 font-medium text-right">Recon</th>
                      <th className="px-3 py-2 font-medium text-right">Transport</th>
                      <th className="px-3 py-2 font-medium text-right">Total Cost</th>
                      <th className="px-3 py-2 font-medium text-right">Est. Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(reportData.vehicles || []).slice(0, 20).map(v => {
                      const totalCost = parseFloat(v.purchase_price || 0) +
                        parseFloat(v.buyer_fee || 0) +
                        parseFloat(v.input_fee || 0) +
                        parseFloat(v.recon_cost || 0) +
                        parseFloat(v.transport_cost || 0) +
                        parseFloat(v.pack_fee || 0);
                      return (
                        <tr key={v.id} className="border-b border-neutral-100">
                          <td className="px-3 py-2 font-mono">{v.stock_number}</td>
                          <td className="px-3 py-2">{v.year} {v.make} {v.model}</td>
                          <td className="px-3 py-2 text-right font-mono">${parseFloat(v.purchase_price || 0).toLocaleString()}</td>
                          <td className="px-3 py-2 text-right font-mono">${parseFloat(v.recon_cost || 0).toLocaleString()}</td>
                          <td className="px-3 py-2 text-right font-mono">${parseFloat(v.transport_cost || 0).toLocaleString()}</td>
                          <td className="px-3 py-2 text-right font-mono font-medium">${totalCost.toLocaleString()}</td>
                          <td className={`px-3 py-2 text-right font-mono ${parseFloat(v.estimated_profit) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            ${parseFloat(v.estimated_profit || 0).toLocaleString()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Transactions */}
          {activeReport === 'transactions' && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="text-xs text-green-700">Total Income</div>
                  <div className="text-2xl font-bold text-green-800">${(reportData.summary?.totalIncome || 0).toLocaleString()}</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="text-xs text-red-700">Total Expenses</div>
                  <div className="text-2xl font-bold text-red-800">${(reportData.summary?.totalExpenses || 0).toLocaleString()}</div>
                </div>
                <div className={`border rounded-lg p-4 ${(reportData.summary?.netCashFlow || 0) >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="text-xs text-neutral-500">Net Cash Flow</div>
                  <div className={`text-2xl font-bold ${(reportData.summary?.netCashFlow || 0) >= 0 ? 'text-green-800' : 'text-red-800'}`}>
                    ${(reportData.summary?.netCashFlow || 0).toLocaleString()}
                  </div>
                </div>
              </div>
              <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                      <th className="px-3 py-2 font-medium">Date</th>
                      <th className="px-3 py-2 font-medium">Type</th>
                      <th className="px-3 py-2 font-medium">Description</th>
                      <th className="px-3 py-2 font-medium">Category</th>
                      <th className="px-3 py-2 font-medium text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(reportData.transactions || []).slice(0, 30).map((t, i) => (
                      <tr key={i} className="border-b border-neutral-100">
                        <td className="px-3 py-2 text-xs">{new Date(t.date).toLocaleDateString()}</td>
                        <td className="px-3 py-2">
                          <span className={`text-xs px-2 py-0.5 rounded ${t.type === 'sale' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {t.type}
                          </span>
                        </td>
                        <td className="px-3 py-2">{t.description}</td>
                        <td className="px-3 py-2 text-xs">{t.category}</td>
                        <td className={`px-3 py-2 text-right font-mono ${parseFloat(t.amount) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          ${Math.abs(parseFloat(t.amount)).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Payroll */}
          {activeReport === 'payroll' && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white border border-neutral-200 rounded-lg p-4">
                  <div className="text-xs text-neutral-500">Total Hours</div>
                  <div className="text-2xl font-bold">{(reportData.summary?.totalHours || 0).toFixed(1)}</div>
                </div>
                <div className="bg-white border border-neutral-200 rounded-lg p-4">
                  <div className="text-xs text-neutral-500">Total Deductions</div>
                  <div className="text-2xl font-bold text-red-600">${(reportData.summary?.totalDeductions || 0).toLocaleString()}</div>
                </div>
                <div className="bg-white border border-neutral-200 rounded-lg p-4">
                  <div className="text-xs text-neutral-500">Payments Made</div>
                  <div className="text-2xl font-bold">${(reportData.summary?.paymentsMade || 0).toLocaleString()}</div>
                </div>
              </div>
              <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                      <th className="px-3 py-2 font-medium">Employee</th>
                      <th className="px-3 py-2 font-medium">Role</th>
                      <th className="px-3 py-2 font-medium text-right">Days Worked</th>
                      <th className="px-3 py-2 font-medium text-right">Hours</th>
                      <th className="px-3 py-2 font-medium text-right">Lunch Days</th>
                      <th className="px-3 py-2 font-medium text-right">Deductions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(reportData.employees || []).map(e => (
                      <tr key={e.user_id} className="border-b border-neutral-100">
                        <td className="px-3 py-2 font-medium">{e.name}</td>
                        <td className="px-3 py-2 text-xs capitalize">{e.role}</td>
                        <td className="px-3 py-2 text-right font-mono">{e.days_worked}</td>
                        <td className="px-3 py-2 text-right font-mono">{e.total_hours.toFixed(1)}</td>
                        <td className="px-3 py-2 text-right font-mono">{e.lunch_days}</td>
                        <td className="px-3 py-2 text-right font-mono text-red-600">{e.deductions > 0 ? `-$${e.deductions.toFixed(2)}` : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Vendor Spend */}
          {activeReport === 'vendors' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white border border-neutral-200 rounded-lg p-4">
                  <div className="text-xs text-neutral-500">Total Vendors</div>
                  <div className="text-2xl font-bold">{reportData.summary?.totalVendors || 0}</div>
                </div>
                <div className="bg-white border border-neutral-200 rounded-lg p-4">
                  <div className="text-xs text-neutral-500">Total Spend</div>
                  <div className="text-2xl font-bold">${(reportData.summary?.totalSpend || 0).toLocaleString()}</div>
                </div>
              </div>
              <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                      <th className="px-3 py-2 font-medium">Vendor</th>
                      <th className="px-3 py-2 font-medium">Type</th>
                      <th className="px-3 py-2 font-medium text-right">Transactions</th>
                      <th className="px-3 py-2 font-medium text-right">Total Spend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(reportData.vendors || []).map(v => (
                      <tr key={v.id} className="border-b border-neutral-100">
                        <td className="px-3 py-2 font-medium">{v.name}</td>
                        <td className="px-3 py-2 text-xs">{v.type}</td>
                        <td className="px-3 py-2 text-right font-mono">{v.transaction_count}</td>
                        <td className="px-3 py-2 text-right font-mono font-medium">${v.total_spend.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Pay Employee/Contractor Modal
const PayEmployeeModal = ({ user, hours, deductions, deductionItems, entries, isContractor, reprint, onClose, onPay, onRefresh, onPaid, selectedWeek }) => {
  const [form, setForm] = useState({
    name: user?.name || '',
    vendorId: '',
    service: isContractor ? '' : 'Weekly Payroll',
    notes: '',
    date: new Date().toISOString().split('T')[0],
    // Split payment support
    cashAmount: '',
    checkAmount: '',
    checkNumber: ''
  });
  const [saving, setSaving] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [printReceipt, setPrintReceipt] = useState(reprint || null); // If reprint passed, show immediately
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');

  // Calculate pay based on hours and hourly wage
  const [hourlyRate, setHourlyRate] = useState(user?.hourlyWage || 26);
  const grossPay = hours ? hours * hourlyRate : 0;
  const netPay = grossPay - (deductions || 0);

  useEffect(() => {
    Promise.all([
      api.getBankAccounts(),
      api.getVendors()
    ]).then(([accounts, vendorList]) => {
      setBankAccounts(accounts || []);
      setVendors((vendorList || []).filter(v => v.type === 'contractor' || !v.type));
    }).catch(console.error);
  }, []);

  // Pre-fill amount for employees with 50/50 split
  useEffect(() => {
    if (!isContractor && netPay > 0) {
      const halfPay = (netPay / 2).toFixed(2);
      setForm(f => ({ ...f, cashAmount: halfPay, checkAmount: halfPay }));
    }
  }, [isContractor, netPay]);

  const totalAmount = parseFloat(form.cashAmount || 0) + parseFloat(form.checkAmount || 0);

  const handleAddVendor = async () => {
    if (!newVendorName.trim()) return;
    try {
      const created = await api.createVendor({ name: newVendorName, type: 'contractor' });
      setVendors(prev => [...prev, created]);
      setForm(f => ({ ...f, vendorId: created.id.toString(), name: created.name }));
      setShowAddVendor(false);
      setNewVendorName('');
    } catch (e) {
      alert('Failed to add vendor: ' + e.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (totalAmount <= 0) return alert('Enter a payment amount');
    if (isContractor && !form.name && !form.vendorId) return alert('Select or enter a contractor');
    if (isContractor && !form.service) return alert('Service description is required');
    
    setSaving(true);
    try {
      const payments = [];
      const cashAccount = bankAccounts.find(a => a.name.toLowerCase().includes('cash'));
      const checkAccount = bankAccounts.find(a => a.name.toLowerCase().includes('checking') || a.name.toLowerCase().includes('business'));

      // Create cash payment if any
      if (parseFloat(form.cashAmount || 0) > 0) {
        if (!cashAccount) {
          alert('Cash Drawer account not found');
          setSaving(false);
          return;
        }
        payments.push({
          amount: parseFloat(form.cashAmount),
          bankAccountId: cashAccount.id,
          accountName: cashAccount.name,
          method: 'Cash'
        });
      }

      // Create check payment if any
      if (parseFloat(form.checkAmount || 0) > 0) {
        if (!checkAccount) {
          alert('Checking account not found');
          setSaving(false);
          return;
        }
        payments.push({
          amount: parseFloat(form.checkAmount),
          bankAccountId: checkAccount.id,
          accountName: checkAccount.name,
          method: 'Check',
          checkNumber: form.checkNumber
        });
      }

      const result = await api.createContractorPayment({
        name: form.name || vendors.find(v => v.id === parseInt(form.vendorId))?.name || user?.name,
        vendorId: form.vendorId ? parseInt(form.vendorId) : null,
        payments,
        totalAmount,
        service: form.service,
        notes: form.notes,
        date: form.date,
        isContractor,
        userId: user?.id,
        hours,
        hourlyWage: hourlyRate
      });

      // Save searchable payment record + settle this week's personal purchases.
      // Does not touch bank balances (the payment above already handled money).
      if (!isContractor && user?.id) {
        try {
          await api.recordPayrollPayment({
            userId: user.id,
            weekStart: selectedWeek,
            hours,
            hourlyWage: hourlyRate,
            grossPay,
            deductions: deductions || 0,
            netPay,
            amountPaid: totalAmount,
            paymentMethod: payments.map(p => p.method).join(' + '),
            notes: form.notes,
            details: {
              payments: payments.map(p => ({ method: p.method, amount: p.amount, checkNumber: p.checkNumber || null })),
              purchases: (deductionItems || []).map(i => ({ description: i.description, amount: i.amount, date: i.created_at || i.createdAt })),
              entries: (entries || []).map(e => {
                const ci = new Date(e.clockIn || e.clock_in);
                const co = (e.clockOut || e.clock_out) ? new Date(e.clockOut || e.clock_out) : null;
                const hrs = co ? ((co - ci) / 3600000) - ((e.tookLunch || e.took_lunch) ? 0.5 : 0) : 0;
                return {
                  day: ci.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
                  times: `${ci.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} - ${co ? co.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '-'}`,
                  hours: Math.round(hrs * 100) / 100
                };
              })
            }
          });
        } catch (recordErr) {
          console.error('Payment made but failed to save history record:', recordErr);
        }
      }
      
      // Show print receipt
      const receiptData = {
        ...result,
        name: form.name || vendors.find(v => v.id === parseInt(form.vendorId))?.name || user?.name,
        totalAmount,
        payments,
        service: form.service,
        date: form.date,
        hours,
        hourlyWage: hourlyRate,
        deductions,
        entries,
        selectedWeek
      };
      
      // Mark as paid FIRST (before any refresh that might unmount us)
      if (user?.id && onPaid) {
        onPaid(user.id, receiptData);
      }
      
      // Set receipt to show confirmation - this must happen!
      setPrintReceipt(receiptData);
      
      // Don't call onRefresh here - it can cause the modal to unmount
      // The refresh will happen when the user closes the receipt
    } catch (err) {
      console.error('Payment error:', err);
      alert('Failed: ' + (err.details || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    const paymentsHtml = printReceipt.payments?.map(p => 
      `<div class="row"><span class="label">${p.method}${p.checkNumber ? ` #${p.checkNumber}` : ''} <span style="font-size:11px;color:#666">(${p.accountName || 'Account'})</span>:</span><span class="value">$${parseFloat(p.amount).toFixed(2)}</span></div>`
    ).join('') || '';
    
    // Generate day-by-day breakdown - handle both camelCase and snake_case
    const entriesHtml = printReceipt.entries?.map(e => {
      const clockInVal = e.clockIn || e.clock_in;
      const clockOutVal = e.clockOut || e.clock_out;
      const tookLunchVal = e.tookLunch || e.took_lunch;
      if (!clockInVal) return '';
      const clockIn = new Date(clockInVal);
      const clockOut = clockOutVal ? new Date(clockOutVal) : null;
      const hrs = clockOut ? ((clockOut - clockIn) / (1000 * 60 * 60)) : 0;
      const lunchDeduct = tookLunchVal ? 0.5 : 0;
      const netHrs = hrs - lunchDeduct;
      const dayName = clockIn.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      const timeIn = clockIn.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      const timeOut = clockOut ? clockOut.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '-';
      return `<div class="day-row"><span>${dayName}</span><span>${timeIn} - ${timeOut}</span><span>${netHrs.toFixed(1)} hrs${tookLunchVal ? ' (lunch)' : ''}</span></div>`;
    }).join('') || '';
    
    const receiptHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Payment Receipt - ${printReceipt.receiptNumber}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 500px; margin: 0 auto; }
          .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 20px; }
          .company { font-size: 24px; font-weight: bold; }
          .title { font-size: 18px; color: #666; margin-top: 5px; }
          .details { margin: 20px 0; }
          .row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #eee; }
          .day-row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 12px; color: #555; gap: 10px; }
          .day-row span:first-child { flex: 1; }
          .day-row span:nth-child(2) { flex: 1; text-align: center; }
          .day-row span:last-child { flex: 0 0 80px; text-align: right; font-weight: 500; }
          .label { color: #666; }
          .value { font-weight: bold; }
          .total { font-size: 20px; margin-top: 20px; padding-top: 20px; border-top: 2px solid #333; }
          .footer { margin-top: 40px; text-align: center; color: #999; font-size: 12px; }
          .signature { margin-top: 60px; border-top: 1px solid #333; width: 250px; }
          .sig-label { font-size: 12px; color: #666; margin-top: 5px; }
          .section { margin-top: 15px; padding-top: 15px; border-top: 1px dashed #ccc; }
          .section-title { font-weight: bold; margin-bottom: 10px; font-size: 14px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company">THE WHOLESALE CARS</div>
          <div class="title">${isContractor ? 'CONTRACTOR' : 'EMPLOYEE'} PAY STUB</div>
        </div>
        <div class="details">
          <div class="row"><span class="label">Receipt #:</span><span class="value">${printReceipt.receiptNumber}</span></div>
          <div class="row"><span class="label">Pay Date:</span><span class="value">${new Date(printReceipt.date).toLocaleDateString()}</span></div>
          ${printReceipt.selectedWeek ? `<div class="row"><span class="label">Pay Period:</span><span class="value">Week of ${new Date(printReceipt.selectedWeek).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span></div>` : ''}
          <div class="row"><span class="label">Employee:</span><span class="value">${printReceipt.name}</span></div>
          <div class="row"><span class="label">Description:</span><span class="value">${printReceipt.service}</span></div>
          ${printReceipt.entries?.length > 0 ? `
          <div class="section">
            <div class="section-title">Time Worked:</div>
            ${entriesHtml}
            <div class="row" style="margin-top:8px;"><span class="label">Total Hours:</span><span class="value">${printReceipt.hours.toFixed(1)} hrs</span></div>
          </div>
          ` : ''}
          ${printReceipt.hours ? `
          <div class="section">
            <div class="section-title">Pay Calculation:</div>
            <div class="row"><span class="label">Hourly Rate:</span><span class="value">$${printReceipt.hourlyWage.toFixed(2)}/hr</span></div>
            <div class="row"><span class="label">Gross Pay:</span><span class="value">$${(printReceipt.hours * printReceipt.hourlyWage).toFixed(2)}</span></div>
            ${printReceipt.deductions > 0 ? `<div class="row"><span class="label">Deductions:</span><span class="value" style="color:red">-$${printReceipt.deductions.toFixed(2)}</span></div>` : ''}
          </div>
          ` : ''}
          <div class="section">
            <div class="section-title">Payment Method:</div>
            ${paymentsHtml}
          </div>
        </div>
        <div class="row total"><span>TOTAL PAID:</span><span>$${parseFloat(printReceipt.totalAmount).toFixed(2)}</span></div>
        <div style="margin-top: 50px;">
          <div class="signature"></div>
          <div class="sig-label">Recipient Signature</div>
        </div>
        <div class="footer">
          <p>Thank you!</p>
          <p>4001 W Alexis Rd, Toledo, OH 43623</p>
        </div>
      </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(receiptHtml);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  // Show receipt after successful payment
  if (printReceipt) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => { setPrintReceipt(null); if (onRefresh) onRefresh(); onClose(); }}>
        <div className="bg-white w-full max-w-md rounded-lg shadow-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          <div className="p-6">
            <div className="text-center">
              <div className="text-green-600 text-5xl mb-4">✓</div>
              <h3 className="text-xl font-bold mb-2">Payment Recorded!</h3>
              <p className="text-neutral-600 mb-4">Receipt #{printReceipt.receiptNumber}</p>
            </div>
            <div className="bg-neutral-50 rounded-lg p-4 text-left mb-4 space-y-2">
              <div className="flex justify-between"><span className="text-neutral-600">Paid To:</span><span className="font-medium">{printReceipt.name}</span></div>
              <div className="flex justify-between"><span className="text-neutral-600">For:</span><span>{printReceipt.service}</span></div>
              {printReceipt.selectedWeek && (
                <div className="flex justify-between"><span className="text-neutral-600">Pay Period:</span><span>Week of {new Date(printReceipt.selectedWeek).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span></div>
              )}
              
              {/* Day breakdown */}
              {printReceipt.entries?.length > 0 && (
                <div className="pt-2 border-t border-neutral-200 mt-2">
                  <div className="text-xs font-medium text-neutral-500 mb-1">Hours Breakdown:</div>
                  {printReceipt.entries.map((e, i) => {
                    const clockInVal = e.clockIn || e.clock_in;
                    const clockOutVal = e.clockOut || e.clock_out;
                    const tookLunchVal = e.tookLunch || e.took_lunch;
                    if (!clockInVal) return null;
                    const clockIn = new Date(clockInVal);
                    const clockOut = clockOutVal ? new Date(clockOutVal) : null;
                    const hrs = clockOut ? ((clockOut - clockIn) / (1000 * 60 * 60)) : 0;
                    const lunchDeduct = tookLunchVal ? 0.5 : 0;
                    const netHrs = hrs - lunchDeduct;
                    const dayName = clockIn.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                    return (
                      <div key={i} className="flex justify-between text-xs py-0.5">
                        <span className="text-neutral-500">{dayName}</span>
                        <span className="font-mono">{netHrs.toFixed(1)} hrs{tookLunchVal ? ' (lunch)' : ''}</span>
                      </div>
                    );
                  })}
                  <div className="flex justify-between text-sm font-medium pt-1 border-t border-neutral-200 mt-1">
                    <span>Total Hours:</span>
                    <span className="font-mono">{printReceipt.hours?.toFixed(1)} hrs</span>
                  </div>
                </div>
              )}
              
              {/* Pay calculation */}
              {printReceipt.hours > 0 && (
                <div className="pt-2 border-t border-neutral-200 mt-2">
                  <div className="flex justify-between text-sm"><span className="text-neutral-600">Rate:</span><span>${printReceipt.hourlyWage?.toFixed(2)}/hr</span></div>
                  <div className="flex justify-between text-sm"><span className="text-neutral-600">Gross:</span><span>${(printReceipt.hours * printReceipt.hourlyWage).toFixed(2)}</span></div>
                  {printReceipt.deductions > 0 && (
                    <div className="flex justify-between text-sm"><span className="text-neutral-600">Deductions:</span><span className="text-red-600">-${printReceipt.deductions.toFixed(2)}</span></div>
                  )}
                </div>
              )}
              
              {/* Payment methods */}
              <div className="pt-2 border-t border-neutral-200 mt-2">
                {printReceipt.payments?.map((p, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-neutral-600">{p.method}{p.checkNumber ? ` #${p.checkNumber}` : ''} <span className="text-xs">({p.accountName})</span>:</span>
                    <span>${parseFloat(p.amount).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              
              <div className="flex justify-between pt-2 border-t border-neutral-200"><span className="font-medium">Total Paid:</span><span className="font-bold text-lg">${parseFloat(printReceipt.totalAmount).toFixed(2)}</span></div>
            </div>
            <div className="flex gap-2">
              <button onClick={handlePrint} className="flex-1 px-4 py-2 bg-neutral-800 text-white rounded-lg font-medium">🖨️ Print Pay Stub</button>
              <button onClick={() => { setPrintReceipt(null); if (onRefresh) onRefresh(); onClose(); }} className="px-4 py-2 text-neutral-600 hover:bg-neutral-100 rounded-lg">Done</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white w-full max-w-lg rounded-lg shadow-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-neutral-200 flex justify-between items-center sticky top-0 bg-white">
          <span className="font-medium text-lg">{isContractor ? 'Pay Contractor' : `Pay ${user?.name}`}</span>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-2xl">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Employee hours summary */}
          {!isContractor && hours !== undefined && (
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <div className="text-sm font-medium text-blue-800 mb-2">Pay Calculation</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between items-center"><span>Hours:</span><span className="font-mono">{hours?.toFixed(1)}</span></div>
                <div className="flex justify-between items-center">
                  <span>Rate:</span>
                  <div className="flex items-center gap-1">
                    <span className="text-neutral-500">$</span>
                    <input 
                      type="number" 
                      step="0.01" 
                      value={hourlyRate} 
                      onChange={e => setHourlyRate(parseFloat(e.target.value) || 0)} 
                      className="w-16 px-1 py-0.5 text-right font-mono border border-blue-300 rounded bg-white"
                    />
                    <span className="text-neutral-500">/hr</span>
                  </div>
                </div>
                <div className="flex justify-between"><span>Gross:</span><span className="font-mono">${grossPay.toFixed(2)}</span></div>
                {deductions > 0 && <div className="flex justify-between text-red-600"><span>Deductions:</span><span className="font-mono">-${deductions.toFixed(2)}</span></div>}
              </div>
              <div className="flex justify-between mt-2 pt-2 border-t border-blue-200 font-bold text-blue-900">
                <span>Net Pay:</span><span className="text-lg">${netPay.toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* Contractor selection */}
          {isContractor && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Contractor/Vendor</label>
              <div className="flex gap-2">
                <select 
                  value={form.vendorId} 
                  onChange={e => {
                    const vendor = vendors.find(v => v.id === parseInt(e.target.value));
                    setForm(f => ({ ...f, vendorId: e.target.value, name: vendor?.name || '' }));
                  }} 
                  className="flex-1 px-3 py-2 border border-neutral-200 rounded-lg"
                >
                  <option value="">Select existing vendor...</option>
                  {vendors.map(v => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
                <button type="button" onClick={() => setShowAddVendor(true)} className="px-3 py-2 bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200">+ New</button>
              </div>
              {showAddVendor && (
                <div className="mt-2 flex gap-2">
                  <input 
                    type="text" 
                    value={newVendorName} 
                    onChange={e => setNewVendorName(e.target.value)} 
                    placeholder="New vendor name..." 
                    className="flex-1 px-3 py-2 border border-neutral-200 rounded-lg"
                  />
                  <button type="button" onClick={handleAddVendor} className="px-3 py-2 bg-green-600 text-white rounded-lg">Add</button>
                  <button type="button" onClick={() => setShowAddVendor(false)} className="px-3 py-2 text-neutral-500">Cancel</button>
                </div>
              )}
              {!form.vendorId && (
                <input 
                  type="text" 
                  value={form.name} 
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))} 
                  className="w-full mt-2 px-3 py-2 border border-neutral-200 rounded-lg" 
                  placeholder="Or enter name manually..." 
                />
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Date</label>
              <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Service/For</label>
              <input type="text" value={form.service} onChange={e => setForm(f => ({ ...f, service: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" placeholder="Weekly Payroll, Detail, etc." />
            </div>
          </div>

          {/* Split Payment */}
          <div className="bg-neutral-50 p-4 rounded-lg space-y-3">
            <div className="text-sm font-medium text-neutral-700">Payment (can split between cash & check)</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-neutral-500 mb-1">Cash Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-neutral-500">$</span>
                  <input 
                    type="number" 
                    step="0.01" 
                    value={form.cashAmount} 
                    onChange={e => setForm(f => ({ ...f, cashAmount: e.target.value }))} 
                    className="w-full pl-7 pr-3 py-2 border border-neutral-200 rounded-lg" 
                    placeholder="0.00"
                  />
                </div>
                {parseFloat(form.cashAmount || 0) > 0 && (
                  <div className="text-xs text-amber-600 mt-1">
                    → From: {bankAccounts.find(a => a.name.toLowerCase().includes('cash'))?.name || 'Cash Drawer'}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs text-neutral-500 mb-1">Check Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-neutral-500">$</span>
                  <input 
                    type="number" 
                    step="0.01" 
                    value={form.checkAmount} 
                    onChange={e => setForm(f => ({ ...f, checkAmount: e.target.value }))} 
                    className="w-full pl-7 pr-3 py-2 border border-neutral-200 rounded-lg" 
                    placeholder="0.00"
                  />
                </div>
                {parseFloat(form.checkAmount || 0) > 0 && (
                  <div className="text-xs text-blue-600 mt-1">
                    → From: {bankAccounts.find(a => a.name.toLowerCase().includes('checking') || a.name.toLowerCase().includes('business'))?.name || 'Business Checking'}
                  </div>
                )}
              </div>
            </div>
            {parseFloat(form.checkAmount || 0) > 0 && (
              <div>
                <label className="block text-xs text-neutral-500 mb-1">Check Number</label>
                <input 
                  type="text" 
                  value={form.checkNumber} 
                  onChange={e => setForm(f => ({ ...f, checkNumber: e.target.value }))} 
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg" 
                  placeholder="Check #"
                />
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-neutral-200 font-medium">
              <span>Total Payment:</span>
              <span className={`text-lg ${totalAmount > 0 ? 'text-green-600' : 'text-neutral-400'}`}>${totalAmount.toFixed(2)}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Notes (optional)</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" rows={2} placeholder="Additional details..." />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-neutral-600 hover:text-neutral-800">Cancel</button>
            <button type="submit" disabled={saving || totalAmount <= 0} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium">
              {saving ? 'Processing...' : `Pay $${totalAmount.toFixed(2)}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Print Forms Modal — replaced by the unified Pre-Print screen (PrintPacketModal):
// pack selector with make-default, fill summaries, per-field fill-in boxes.
const PrintFormsModal = ({ deal, onClose }) => (
  <PrintPacketModal mode="deal" deal={deal} onClose={onClose} />
);

// Deal Detail View Modal - View comprehensive deal information
const DealDetailModal = ({ deal, onClose, onEdit, onEditVehicle, onRefresh, onSendToBhph, canAccessBhph }) => {
  const [payments, setPayments] = useState([]);
  const [costAdjustments, setCostAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showPrintForms, setShowPrintForms] = useState(false);
  const [newPayment, setNewPayment] = useState({ amount: '', paymentMethod: 'cash', referenceNumber: '', notes: '' });
  const [addingPayment, setAddingPayment] = useState(false);

  useEffect(() => {
    const loadPayments = async () => {
      try {
        const data = await api.getDealPayments(deal.id);
        setPayments(data || []);
      } catch (err) {
        console.error('Failed to load payments:', err);
      } finally {
        setLoading(false);
      }
    };
    loadPayments();
  }, [deal.id]);

  useEffect(() => {
    if (!deal?.vehicle_id) {
      return;
    }

    api.getVehicleCostAdjustments(deal.vehicle_id, { dealId: deal.id })
      .then((rows) => {
        const list = Array.isArray(rows) ? rows : [];
        setCostAdjustments(list);
      })
      .catch(() => {});
  }, [deal?.id, deal?.vehicle_id, deal?.cost_adjustments_total]);

  const totalPaid = payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const totalDue = parseFloat(deal.total_due || deal.sale_price || 0);
  const balanceRemaining = totalDue - totalPaid;

  // Cost calculations
  const purchasePrice = parseFloat(deal.purchase_price || 0);
  const buyerFee = parseFloat(deal.buyer_fee || 0);
  const inputFee = parseFloat(deal.input_fee || 0);
  const reconCost = parseFloat(deal.recon_cost || 0);
  const transportCost = parseFloat(deal.transport_cost || 0);
  const packFee = parseFloat(
    deal.pack_amount_effective ??
    deal.pack_amount ??
    deal.pack_fee ??
    (['copart', 'salvage'].includes((deal.end_use || '').toLowerCase()) || deal.sale_type === 'copart' ? 75 : 325)
  ) || 0;
  const calculatedCost = purchasePrice + buyerFee + inputFee + reconCost + transportCost + packFee;
  const totalCost = Number.isFinite(parseFloat(deal.total_cost)) ? parseFloat(deal.total_cost) : calculatedCost;
  const salePrice = parseFloat(deal.sale_price || 0);
  const profit = Number.isFinite(parseFloat(deal.profit)) ? parseFloat(deal.profit) : salePrice - totalCost;

  const saleTypeLabels = {
    retail: 'Retail',
    wholesale_direct: 'Wholesale Direct',
    wholesale_auction: 'Wholesale Auction',
    copart: 'Copart'
  };

  const adjustmentTypeLabels = {
    recon: 'Recon',
    additional: 'Additional',
    transport: 'Transport',
    buyer_fee: 'Buyer Fee',
    input_fee: 'Input Fee',
    pack_fee: 'Pack Fee'
  };

  const handleAddPayment = async () => {
    if (!newPayment.amount || parseFloat(newPayment.amount) <= 0) {
      alert('Enter a valid payment amount');
      return;
    }
    setAddingPayment(true);
    try {
      const result = await api.addDealPayment(deal.id, {
        amount: parseFloat(newPayment.amount),
        paymentMethod: newPayment.paymentMethod,
        referenceNumber: newPayment.referenceNumber,
        notes: newPayment.notes
      });
      setPayments(prev => [result.payment, ...prev]);
      setNewPayment({ amount: '', paymentMethod: 'cash', referenceNumber: '', notes: '' });
      setShowAddPayment(false);
      if (result.isPaidInFull) {
        alert('Deal is now paid in full!');
      }
      onRefresh?.();
    } catch (e) {
      alert('Error adding payment: ' + e.message);
    } finally {
      setAddingPayment(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      await api.deleteDealPayment(deal.id, paymentId);
      setPayments(prev => prev.filter(p => p.id !== paymentId));
      onRefresh?.();
    } catch (e) {
      alert(`Error deleting payment: ${e.message}${e.details ? ` (${e.details})` : ''}`);
    }
  };

  const handleMarkPaid = async () => {
    if (!window.confirm(`Mark this deal as paid in full? This will record a payment of $${balanceRemaining.toFixed(2)} for the remaining balance.`)) return;
    try {
      await api.markDealPaid(deal.id, 'Marked as paid in full');
      alert('Deal marked as paid in full!');
      onRefresh?.();
      onClose();
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const printForms = () => {
    setShowPrintForms(true);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-lg shadow-xl overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-4 border-b border-neutral-200 bg-neutral-50 flex justify-between items-start">
          <div>
            <div className="font-semibold text-lg">Deal #{deal.id}</div>
            <div className="text-sm text-neutral-600">{deal.year} {deal.make} {deal.model} - Stock #{deal.stock_number}</div>
            <div className="text-xs text-neutral-500 mt-1">VIN: {deal.vin}</div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 text-xs rounded ${balanceRemaining <= 0 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
              {balanceRemaining <= 0 ? '✓ Paid' : `Balance: $${balanceRemaining.toLocaleString()}`}
            </span>
            <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-2xl">&times;</button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column - Deal Info */}
            <div className="space-y-4">
              {/* Buyer Info */}
              <div className="bg-neutral-50 rounded-lg p-4">
                <h3 className="font-medium text-sm text-neutral-500 mb-2">Buyer Information</h3>
                <div className="text-sm space-y-1">
                  <div className="font-medium">{deal.buyer_name || '-'}</div>
                  {deal.buyer_address && <div>{deal.buyer_address}</div>}
                  {(deal.buyer_city || deal.buyer_state || deal.buyer_zip) && (
                    <div>{deal.buyer_city}{deal.buyer_city && deal.buyer_state ? ', ' : ''}{deal.buyer_state} {deal.buyer_zip}</div>
                  )}
                  {deal.buyer_phone && <div>📞 {deal.buyer_phone}</div>}
                  {deal.buyer_email && <div>✉️ {deal.buyer_email}</div>}
                </div>
                {deal.co_buyer_name && (
                  <div className="mt-3 pt-3 border-t border-neutral-200">
                    <div className="text-xs text-neutral-500">Co-Buyer</div>
                    <div className="font-medium">{deal.co_buyer_name}</div>
                  </div>
                )}
              </div>

              {/* Sale Info */}
              <div className="bg-neutral-50 rounded-lg p-4">
                <h3 className="font-medium text-sm text-neutral-500 mb-2">Sale Details</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <div className="text-neutral-500 text-xs">Sale Type</div>
                    <div className="font-medium">
                      {saleTypeLabels[deal.sale_type] || deal.sale_type}
                      {deal.is_dealer_direct && <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-1 rounded">DD</span>}
                    </div>
                  </div>
                  <div>
                    <div className="text-neutral-500 text-xs">Sale Date</div>
                    <div className="font-medium">{deal.sale_date ? new Date(deal.sale_date).toLocaleDateString() : '-'}</div>
                  </div>
                  <div>
                    <div className="text-neutral-500 text-xs">Created By</div>
                    <div className="font-medium">{deal.created_by_name || '-'}</div>
                  </div>
                  <div>
                    <div className="text-neutral-500 text-xs">Status</div>
                    <div className="font-medium">{deal.status || 'active'}</div>
                  </div>
                </div>
              </div>

              {/* Cost Breakdown */}
              <div className="bg-neutral-50 rounded-lg p-4">
                <h3 className="font-medium text-sm text-neutral-500 mb-2">Cost Breakdown</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span>Purchase Price:</span><span className="font-mono">${purchasePrice.toLocaleString()}</span></div>
                  {buyerFee > 0 && <div className="flex justify-between"><span>+ Buyer Fee:</span><span className="font-mono">${buyerFee.toLocaleString()}</span></div>}
                  {inputFee > 0 && <div className="flex justify-between"><span>+ Input Fee:</span><span className="font-mono">${inputFee.toLocaleString()}</span></div>}
                  {reconCost > 0 && <div className="flex justify-between"><span>+ Recon:</span><span className="font-mono">${reconCost.toLocaleString()}</span></div>}
                  {transportCost > 0 && <div className="flex justify-between"><span>+ Transport:</span><span className="font-mono">${transportCost.toLocaleString()}</span></div>}
                  {packFee > 0 && <div className="flex justify-between"><span>+ Pack Fee:</span><span className="font-mono">${packFee.toLocaleString()}</span></div>}
                  {costAdjustments.length > 0 && (
                    <>
                      <div className="border-t border-neutral-200 my-2"></div>
                      <div className="text-xs font-medium text-neutral-500">Added Recon / Additional Entries</div>
                      {costAdjustments.map((adj) => (
                        <div key={adj.id} className="flex items-start justify-between gap-3 text-xs">
                          <span className="text-neutral-600">
                            + {adjustmentTypeLabels[adj.cost_type] || adj.cost_type || 'Adjustment'}
                            {adj.note ? ` - ${adj.note}` : ''}
                          </span>
                          <span className="font-mono whitespace-nowrap">
                            ${(parseFloat(adj.amount || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                      ))}
                    </>
                  )}
                  <div className="border-t border-neutral-300 my-2"></div>
                  <div className="flex justify-between font-medium"><span>Total Cost:</span><span className="font-mono">${totalCost.toLocaleString()}</span></div>
                  <div className={`flex justify-between font-bold text-lg ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    <span>PROFIT:</span>
                    <span className="font-mono">{profit >= 0 ? '+' : ''}${profit.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Financial Summary & Payments */}
            <div className="space-y-4">
              {/* Financial Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-medium text-sm text-blue-700 mb-2">Financial Summary</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span>Sale Price:</span><span className="font-mono">${salePrice.toLocaleString()}</span></div>
                  {deal.doc_fee > 0 && <div className="flex justify-between"><span>+ Doc Fee:</span><span className="font-mono">${parseFloat(deal.doc_fee).toLocaleString()}</span></div>}
                  {deal.title_fee > 0 && <div className="flex justify-between"><span>+ Title Fee:</span><span className="font-mono">${parseFloat(deal.title_fee).toLocaleString()}</span></div>}
                  {deal.temp_tag_fee > 0 && <div className="flex justify-between"><span>+ Temp Tag:</span><span className="font-mono">${parseFloat(deal.temp_tag_fee).toLocaleString()}</span></div>}
                  {deal.sales_tax > 0 && <div className="flex justify-between"><span>+ Sales Tax:</span><span className="font-mono">${parseFloat(deal.sales_tax).toLocaleString()}</span></div>}
                  <div className="border-t border-blue-300 my-2"></div>
                  <div className="flex justify-between font-bold"><span>Total Due:</span><span className="font-mono">${totalDue.toLocaleString()}</span></div>
                  <div className="flex justify-between text-green-600"><span>- Payments:</span><span className="font-mono">-${totalPaid.toLocaleString()}</span></div>
                  <div className="border-t border-blue-300 my-2"></div>
                  <div className={`flex justify-between font-bold text-lg ${balanceRemaining <= 0 ? 'text-green-600' : 'text-amber-600'}`}>
                    <span>Balance:</span>
                    <span className="font-mono">${Math.max(0, balanceRemaining).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Payment History */}
              <div className="bg-white border border-neutral-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-medium text-sm">Payment History</h3>
                  <div className="flex gap-2">
                    {balanceRemaining > 0 && (
                      <button 
                        onClick={handleMarkPaid}
                        className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                      >
                        ✓ Mark Paid in Full
                      </button>
                    )}
                    <button 
                      onClick={() => setShowAddPayment(!showAddPayment)}
                      className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                    >
                      + Add Payment
                    </button>
                  </div>
                </div>

                {/* Add Payment Form */}
                {showAddPayment && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div>
                        <label className="block text-xs text-neutral-500 mb-1">Amount *</label>
                        <div className="relative">
                          <span className="absolute left-2 top-1.5 text-neutral-500 text-sm">$</span>
                          <input 
                            type="number" 
                            value={newPayment.amount}
                            onChange={e => setNewPayment(p => ({ ...p, amount: e.target.value }))}
                            className="w-full pl-5 pr-2 py-1 text-sm border border-neutral-200 rounded"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-neutral-500 mb-1">Method</label>
                        <select 
                          value={newPayment.paymentMethod}
                          onChange={e => setNewPayment(p => ({ ...p, paymentMethod: e.target.value }))}
                          className="w-full px-2 py-1 text-sm border border-neutral-200 rounded"
                        >
                          <option value="cash">Cash</option>
                          <option value="check">Check</option>
                          <option value="ach">ACH</option>
                          <option value="card">Credit Card</option>
                          <option value="wire">Wire</option>
                          <option value="financing">Financing</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-neutral-500 mb-1">Check/Ref #</label>
                        <input 
                          type="text" 
                          value={newPayment.referenceNumber}
                          onChange={e => setNewPayment(p => ({ ...p, referenceNumber: e.target.value }))}
                          className="w-full px-2 py-1 text-sm border border-neutral-200 rounded"
                          placeholder="Optional"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-neutral-500 mb-1">Notes</label>
                        <input 
                          type="text" 
                          value={newPayment.notes}
                          onChange={e => setNewPayment(p => ({ ...p, notes: e.target.value }))}
                          className="w-full px-2 py-1 text-sm border border-neutral-200 rounded"
                          placeholder="Optional"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button 
                        onClick={handleAddPayment}
                        disabled={addingPayment}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        {addingPayment ? 'Adding...' : 'Add Payment'}
                      </button>
                      <button 
                        onClick={() => setShowAddPayment(false)}
                        className="px-3 py-1 border border-neutral-300 text-sm rounded hover:bg-neutral-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Payment List */}
                {loading ? (
                  <div className="text-center py-4 text-neutral-500 text-sm">Loading payments...</div>
                ) : payments.length === 0 ? (
                  <div className="text-center py-4 text-neutral-400 text-sm">No payments recorded</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-neutral-500 border-b">
                          <th className="pb-2">Date</th>
                          <th className="pb-2 text-right">Amount</th>
                          <th className="pb-2">Method</th>
                          <th className="pb-2">Note</th>
                          <th className="pb-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map(p => (
                          <tr key={p.id} className="border-b border-neutral-100">
                            <td className="py-2">{p.payment_date ? new Date(p.payment_date).toLocaleDateString() : '-'}</td>
                            <td className="py-2 text-right font-mono text-green-600">${parseFloat(p.amount).toLocaleString()}</td>
                            <td className="py-2 capitalize">
                              {p.payment_method}
                              {p.reference_number && <span className="text-neutral-500"> #{p.reference_number}</span>}
                            </td>
                            <td className="py-2 text-neutral-500 text-xs">{p.notes || '-'}</td>
                            <td className="py-2">
                              <button onClick={() => handleDeletePayment(p.id)} className="text-red-500 hover:text-red-700 text-xs">×</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-neutral-200 bg-neutral-50 flex justify-between">
          <button onClick={onClose} className="px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-100">
            Close
          </button>
          <div className="flex gap-2">
            <button onClick={printForms} className="px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-100">
              🖨️ Print Forms
            </button>
            {canAccessBhph && onSendToBhph && (
              <button
                onClick={() => { onSendToBhph(deal); onClose(); }}
                className="px-4 py-2 border border-stone-400 text-stone-800 rounded-lg hover:bg-stone-50"
              >
                Send to BHPH
              </button>
            )}
            {onEditVehicle && (
              <button onClick={() => { onClose(); onEditVehicle(); }} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                Edit Vehicle / Costs / ROs
              </button>
            )}
            <button onClick={() => { onClose(); onEdit?.(deal); }} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Edit Deal
            </button>
          </div>
        </div>
      </div>

      {/* Print Forms Selection Modal */}
      {showPrintForms && (
        <PrintFormsModal deal={deal} onClose={() => setShowPrintForms(false)} />
      )}
    </div>
  );
};

// Edit Deal Modal - Edit any deal at any time
const EditDealModal = ({ deal, onClose, onSave, onSendToBhph, canAccessBhph }) => {
  const [taxRates, setTaxRates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [newPayment, setNewPayment] = useState({ amount: '', paymentMethod: 'cash', notes: '' });
  const [savedCustomers, setSavedCustomers] = useState([]);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [showPrintPacket, setShowPrintPacket] = useState(false);
  const [vehicleCosts, setVehicleCosts] = useState({ purchasePrice: '', reconCost: '', transportCost: '' });
  const [form, setForm] = useState(() => {
    const parts = (deal.buyer_name || '').trim().split(/\s+/).filter(Boolean);
    const first = parts[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1] : '';
    const middle = parts.length > 2 ? parts.slice(1, -1).join(' ') : '';
    return {
      buyerFirstName: first,
      buyerMiddleName: middle,
      buyerLastName: last,
      buyerAddress: deal.buyer_address || '',
      buyerCity: deal.buyer_city || '',
      buyerState: deal.buyer_state || '',
      buyerZip: deal.buyer_zip || '',
      buyerPhone: deal.buyer_phone || '',
      buyerEmail: deal.buyer_email || '',
      buyerDlNumber: deal.buyer_dl_number || '',
      buyerDlState: deal.buyer_dl_state || '',
      dealerLicense: deal.dealer_license || '',
      purchasePrice: deal.sale_price || '',
      tradeInValue: deal.trade_in_value || 0,
      docFee: deal.doc_fee || 300,
      salesTaxAmount: deal.sales_tax_amount || '',
      county: deal.county || 'Lucas',
      isOutOfState: deal.is_out_of_state || false,
      // Buyer's home-state rate for out-of-state deals (prefilled from the stored rate)
      outOfStateTaxRate: deal.is_out_of_state ? (parseFloat(deal.tax_rate) || 0) : 0,
      paymentMethod: deal.payment_method || '',
      notes: deal.notes || '',
      saleDate: deal.sale_date ? (typeof deal.sale_date === 'string' ? deal.sale_date.split('T')[0] : new Date(deal.sale_date).toISOString().split('T')[0]) : ''
    };
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [taxData, paymentsData, customersData, vehicleData] = await Promise.all([
          api.getTaxRates(),
          api.getDealPayments(deal.id),
          api.getDealCustomers().catch(() => []),
          deal.vehicle_id ? api.getVehicle(deal.vehicle_id).catch(() => null) : Promise.resolve(null)
        ]);
        setTaxRates(taxData.rates || taxData);
        setPayments(paymentsData || []);
        setSavedCustomers(customersData || []);
        if (vehicleData) {
          setVehicleCosts({
            purchasePrice: vehicleData.purchasePrice ?? vehicleData.purchase_price ?? '',
            reconCost: vehicleData.reconCost ?? vehicleData.recon_cost ?? '',
            transportCost: vehicleData.transportCost ?? vehicleData.transport_cost ?? ''
          });
        }
      } catch (err) {
        console.error(err);
      } finally {
        setDataLoading(false);
      }
    };
    loadData();
  }, [deal.id]);

  const handleSelectCustomer = (cust) => {
    if (!cust) return;
    const c = cust;
    const parts = (c.name || '').trim().split(/\s+/).filter(Boolean);
    const first = parts[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1] : '';
    const middle = parts.length > 2 ? parts.slice(1, -1).join(' ') : '';
    setForm(f => ({
      ...f,
      buyerFirstName: first,
      buyerMiddleName: middle,
      buyerLastName: last,
      buyerAddress: c.address || '',
      buyerCity: c.city || '',
      buyerState: c.state || '',
      buyerZip: c.zip || '',
      buyerPhone: c.phone || '',
      buyerEmail: c.email || '',
      buyerDlNumber: c.dl_number || c.dlNumber || '',
      buyerDlState: c.dl_state || c.dlState || '',
      dealerLicense: c.dealer_license || c.dealerLicense || '',
      notes: c.notes || f.notes
    }));
  };

  const handleSaveAsCustomer = async () => {
    const buyerName = [form.buyerFirstName, form.buyerMiddleName, form.buyerLastName].filter(Boolean).join(' ').trim();
    if (!buyerName) {
      alert('Enter buyer name first');
      return;
    }
    setSavingCustomer(true);
    try {
      await api.saveDealCustomer({
        name: buyerName,
        address: form.buyerAddress || null,
        city: form.buyerCity || null,
        state: form.buyerState || null,
        zip: form.buyerZip || null,
        phone: form.buyerPhone || null,
        email: form.buyerEmail || null,
        dlNumber: form.buyerDlNumber || null,
        dlState: form.buyerDlState || null,
        dealerLicense: form.dealerLicense || null,
        notes: form.notes || null
      });
      const updated = await api.getDealCustomers();
      setSavedCustomers(updated || []);
      alert('Customer saved for future use');
    } catch (e) {
      alert('Failed to save: ' + e.message);
    } finally {
      setSavingCustomer(false);
    }
  };

  const totalPaid = payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const totalDue = parseFloat(deal.total_due || deal.sale_price || 0);
  const balanceRemaining = totalDue - totalPaid;

  const handleAddPayment = async () => {
    if (!newPayment.amount || parseFloat(newPayment.amount) <= 0) {
      alert('Enter a valid payment amount');
      return;
    }
    setLoading(true);
    try {
      const result = await api.addDealPayment(deal.id, {
        amount: parseFloat(newPayment.amount),
        paymentMethod: newPayment.paymentMethod,
        notes: newPayment.notes
      });
      setPayments(prev => [result.payment, ...prev]);
      setNewPayment({ amount: '', paymentMethod: 'cash', notes: '' });
      setShowAddPayment(false);
      if (result.isPaidInFull) {
        alert('Deal is now paid in full!');
      }
    } catch (e) {
      alert('Error adding payment: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      await api.deleteDealPayment(deal.id, paymentId);
      setPayments(prev => prev.filter(p => p.id !== paymentId));
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const buyerName = [form.buyerFirstName, form.buyerMiddleName, form.buyerLastName].filter(Boolean).join(' ').trim();
      await api.updateDeal(deal.id, { ...form, buyerName });
      if (deal.vehicle_id) {
        const vcUpdate = {};
        if (vehicleCosts.purchasePrice !== '') vcUpdate.purchasePrice = parseFloat(vehicleCosts.purchasePrice) || 0;
        if (vehicleCosts.reconCost !== '') vcUpdate.reconCost = parseFloat(vehicleCosts.reconCost) || 0;
        if (vehicleCosts.transportCost !== '') vcUpdate.transportCost = parseFloat(vehicleCosts.transportCost) || 0;
        if (Object.keys(vcUpdate).length > 0) await api.updateVehicle(deal.vehicle_id, vcUpdate);
      }
      onSave?.();
    } catch (e) {
      alert('Error saving: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const isRetail = deal.sale_type === 'retail';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl max-h-[90vh] rounded-lg shadow-xl overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-neutral-200 flex justify-between items-center bg-neutral-50">
          <div>
            <div className="font-semibold text-lg">Edit Deal #{deal.id}</div>
            <div className="text-sm text-neutral-500">{deal.stock_number} - {deal.year} {deal.make} {deal.model}</div>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-2xl">&times;</button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {dataLoading ? (
            <div className="text-center py-8 text-neutral-500">Loading...</div>
          ) : (
          <>
          {/* Buyer Info */}
          <div className="border-b pb-4">
            <h3 className="font-medium mb-3">{isRetail ? 'Buyer' : 'Dealer'} Information</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs text-neutral-500 mb-1">{isRetail ? 'Buyer Name' : 'Dealer Name'}</label>
                <div className="flex gap-2 mb-2">
                  <select
                    value=""
                    onChange={e => { const v = e.target.value; if (v) { const c = savedCustomers.find(x => x.id === parseInt(v)); if (c) handleSelectCustomer(c); } }}
                    className="flex-shrink-0 w-32 px-2 py-2 border border-neutral-200 rounded-lg text-sm bg-neutral-50"
                  >
                    <option value="">Saved...</option>
                    {savedCustomers.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <button type="button" onClick={handleSaveAsCustomer} disabled={savingCustomer || !form.buyerFirstName?.trim()} className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded hover:bg-green-200 disabled:opacity-50" title="Save for future deals">Save</button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs text-neutral-400 mb-1">First *</label>
                    <input tabIndex={1} value={form.buyerFirstName} onChange={e => setForm(f => ({ ...f, buyerFirstName: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" placeholder="First" />
                  </div>
                  <div>
                    <label className="block text-xs text-neutral-400 mb-1">Middle</label>
                    <input tabIndex={2} value={form.buyerMiddleName} onChange={e => setForm(f => ({ ...f, buyerMiddleName: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" placeholder="Middle" />
                  </div>
                  <div>
                    <label className="block text-xs text-neutral-400 mb-1">Last *</label>
                    <input tabIndex={3} value={form.buyerLastName} onChange={e => setForm(f => ({ ...f, buyerLastName: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" placeholder="Last" />
                  </div>
                </div>
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-neutral-500 mb-1">Address</label>
                <input tabIndex={4} value={form.buyerAddress} onChange={e => setForm(f => ({ ...f, buyerAddress: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" />
              </div>
              <div>
                <label className="block text-xs text-neutral-500 mb-1">City</label>
                <input tabIndex={5} value={form.buyerCity} onChange={e => setForm(f => ({ ...f, buyerCity: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">State</label>
                  <input tabIndex={6} value={form.buyerState} onChange={e => setForm(f => ({ ...f, buyerState: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" maxLength={2} />
                </div>
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">ZIP</label>
                  <input tabIndex={7} value={form.buyerZip} onChange={e => setForm(f => ({ ...f, buyerZip: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-neutral-500 mb-1">Phone</label>
                <input tabIndex={8} value={form.buyerPhone} onChange={e => setForm(f => ({ ...f, buyerPhone: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" />
              </div>
              {isRetail && (
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">Email</label>
                  <input tabIndex={9} value={form.buyerEmail} onChange={e => setForm(f => ({ ...f, buyerEmail: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" />
                </div>
              )}
              {!isRetail && (
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">Dealer License #</label>
                  <input value={form.dealerLicense} onChange={e => setForm(f => ({ ...f, dealerLicense: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" />
                </div>
              )}
              {isRetail && (
                <>
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">Driver's License #</label>
                    <input value={form.buyerDlNumber} onChange={e => setForm(f => ({ ...f, buyerDlNumber: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">DL State</label>
                    <input value={form.buyerDlState} onChange={e => setForm(f => ({ ...f, buyerDlState: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" maxLength={2} />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Sale Date */}
          <div className="border-b pb-4">
            <h3 className="font-medium mb-3">Sale Details</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-neutral-500 mb-1">Date of Sale</label>
                <input type="date" value={form.saleDate} onChange={e => setForm(f => ({ ...f, saleDate: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" />
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="border-b pb-4">
            <h3 className="font-medium mb-3">Pricing</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-neutral-500 mb-1">Sale Price</label>
                <input type="number" value={form.purchasePrice} onChange={e => setForm(f => ({ ...f, purchasePrice: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" />
              </div>
              {isRetail && (
                <>
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">Trade-In Value</label>
                    <input type="number" value={form.tradeInValue} onChange={e => setForm(f => ({ ...f, tradeInValue: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">Doc Fee</label>
                    <input type="number" value={form.docFee} onChange={e => setForm(f => ({ ...f, docFee: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">Sales Tax Collected</label>
                    <input type="number" step="0.01" value={form.salesTaxAmount || ''} onChange={e => setForm(f => ({ ...f, salesTaxAmount: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" placeholder="0.00" />
                  </div>
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">County (for tax)</label>
                    <select value={form.county} onChange={e => setForm(f => ({ ...f, county: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg">
                      {Object.keys(taxRates || {}).sort().map(c => (
                        <option key={c} value={c}>{c} ({taxRates[c]}%)</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={form.isOutOfState} onChange={e => setForm(f => ({ ...f, isOutOfState: e.target.checked }))} />
                      <span className="text-sm">Out of State</span>
                    </label>
                  </div>
                  {form.isOutOfState && (
                    <div className="col-span-2">
                      <label className="block text-xs text-neutral-500 mb-1">Tax Rate % <span className="text-neutral-400">(type it — Michigan = 6, tax-exempt = 0)</span></label>
                      <input
                        type="number" step="0.25" min="0" max="15"
                        value={form.outOfStateTaxRate}
                        onChange={e => setForm(f => ({ ...f, outOfStateTaxRate: e.target.value === '' ? '' : parseFloat(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border border-neutral-200 rounded-lg font-mono"
                        placeholder="0"
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Vehicle Costs */}
          <div className="border-b pb-4">
            <h3 className="font-medium mb-3">Vehicle Costs</h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-neutral-500 mb-1">Purchase Price</label>
                <div className="relative">
                  <span className="absolute left-2 top-2 text-neutral-400 text-sm">$</span>
                  <input type="number" step="0.01" value={vehicleCosts.purchasePrice} onChange={e => setVehicleCosts(v => ({ ...v, purchasePrice: e.target.value }))} className="w-full pl-5 pr-2 py-2 border border-neutral-200 rounded-lg" placeholder="0.00" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-neutral-500 mb-1">Recon Cost</label>
                <div className="relative">
                  <span className="absolute left-2 top-2 text-neutral-400 text-sm">$</span>
                  <input type="number" step="0.01" value={vehicleCosts.reconCost} onChange={e => setVehicleCosts(v => ({ ...v, reconCost: e.target.value }))} className="w-full pl-5 pr-2 py-2 border border-neutral-200 rounded-lg" placeholder="0.00" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-neutral-500 mb-1">Transport Cost</label>
                <div className="relative">
                  <span className="absolute left-2 top-2 text-neutral-400 text-sm">$</span>
                  <input type="number" step="0.01" value={vehicleCosts.transportCost} onChange={e => setVehicleCosts(v => ({ ...v, transportCost: e.target.value }))} className="w-full pl-5 pr-2 py-2 border border-neutral-200 rounded-lg" placeholder="0.00" />
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs text-neutral-500 mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" rows={3} />
          </div>

          {/* Payment History / Invoice Section */}
          <div className="border-t pt-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-medium">Payment History</h3>
              <button 
                onClick={() => setShowAddPayment(!showAddPayment)}
                className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
              >
                + Add Payment
              </button>
            </div>
            
            {/* Invoice Summary */}
            <div className="bg-neutral-50 rounded-lg p-3 mb-3">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-neutral-500 text-xs">Total Due</div>
                  <div className="font-mono font-medium">${totalDue.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-neutral-500 text-xs">Total Paid</div>
                  <div className="font-mono font-medium text-green-600">${totalPaid.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-neutral-500 text-xs">Balance</div>
                  <div className={`font-mono font-bold ${balanceRemaining > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                    ${balanceRemaining.toLocaleString()}
                  </div>
                </div>
              </div>
              {balanceRemaining > 0 && (
                <div className="mt-2 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                  Balance Due - Customer owes ${balanceRemaining.toLocaleString()}
                </div>
              )}
              {balanceRemaining <= 0 && totalPaid > 0 && (
                <div className="mt-2 text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                  ✓ Paid in Full
                </div>
              )}
            </div>

            {/* Add Payment Form */}
            {showAddPayment && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">Amount</label>
                    <div className="relative">
                      <span className="absolute left-2 top-1.5 text-neutral-500 text-sm">$</span>
                      <input 
                        type="number" 
                        value={newPayment.amount}
                        onChange={e => setNewPayment(p => ({ ...p, amount: e.target.value }))}
                        className="w-full pl-5 pr-2 py-1 text-sm border border-neutral-200 rounded"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">Method</label>
                    <select 
                      value={newPayment.paymentMethod}
                      onChange={e => setNewPayment(p => ({ ...p, paymentMethod: e.target.value }))}
                      className="w-full px-2 py-1 text-sm border border-neutral-200 rounded"
                    >
                      <option value="cash">Cash</option>
                      <option value="check">Check</option>
                      <option value="card">Card</option>
                      <option value="wire">Wire</option>
                      <option value="financing">Financing</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">Notes</label>
                    <input 
                      type="text" 
                      value={newPayment.notes}
                      onChange={e => setNewPayment(p => ({ ...p, notes: e.target.value }))}
                      className="w-full px-2 py-1 text-sm border border-neutral-200 rounded"
                      placeholder="Optional"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={handleAddPayment}
                    disabled={loading}
                    className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Adding...' : 'Record Payment'}
                  </button>
                  <button 
                    onClick={() => setShowAddPayment(false)}
                    className="text-xs text-neutral-500 hover:text-neutral-700"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Payment List */}
            {payments.length > 0 ? (
              <div className="border border-neutral-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 text-xs text-neutral-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-left">Method</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2 text-left">Notes</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map(p => (
                      <tr key={p.id} className="border-t border-neutral-100">
                        <td className="px-3 py-2">{new Date(p.payment_date).toLocaleDateString()}</td>
                        <td className="px-3 py-2 capitalize">{p.payment_method}</td>
                        <td className="px-3 py-2 text-right font-mono text-green-600">${parseFloat(p.amount).toLocaleString()}</td>
                        <td className="px-3 py-2 text-neutral-500 text-xs">{p.notes || '-'}</td>
                        <td className="px-3 py-2 text-right">
                          <button 
                            onClick={() => handleDeletePayment(p.id)}
                            className="text-red-500 hover:text-red-700 text-xs"
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-4 text-neutral-400 text-sm">No payments recorded yet</div>
            )}
          </div>
          </>
          )}
        </div>

        <div className="p-4 border-t border-neutral-200 flex justify-between">
          <button onClick={onClose} className="px-4 py-2 text-neutral-600 hover:text-neutral-800">Cancel</button>
          <div className="flex gap-2">
            <button 
              onClick={() => setShowPrintPacket(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Print Forms
            </button>
            {canAccessBhph && onSendToBhph && (
              <button
                type="button"
                onClick={() => { onSendToBhph(deal); onClose(); }}
                className="px-4 py-2 border border-stone-400 text-stone-800 rounded-lg hover:bg-stone-50"
              >
                Send to BHPH
              </button>
            )}
            <button onClick={handleSave} disabled={loading} className="px-4 py-2 bg-neutral-800 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50">
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
        {showPrintPacket && <PrintPacketModal mode="deal" deal={deal} onClose={() => setShowPrintPacket(false)} />}
      </div>
    </div>
  );
};

// Sale Type Choice Modal with Vehicle Search
const SaleTypeChoiceModal = ({ selectedVehicle, availableVehicles, onSelectVehicle, onRetail, onWholesale, onClose }) => {
  const [vehicleSearch, setVehicleSearch] = useState('');
  
  const filteredVehicles = availableVehicles.filter(v => {
    if (!vehicleSearch) return true;
    const search = vehicleSearch.toLowerCase();
    return (
      v.stockNumber?.toLowerCase().includes(search) ||
      v.vin?.toLowerCase().includes(search) ||
      String(v.year).includes(search) ||
      v.make?.toLowerCase().includes(search) ||
      v.model?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-2">Sell Vehicle</h3>
        {selectedVehicle.id ? (
          <div className="text-sm text-neutral-500 mb-4 flex justify-between items-center">
            <span>{selectedVehicle.stockNumber} - {selectedVehicle.year} {selectedVehicle.make} {selectedVehicle.model}</span>
            <button onClick={() => onSelectVehicle({})} className="text-xs text-blue-600 hover:text-blue-800">Change</button>
          </div>
        ) : (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Search Vehicle</label>
            <input 
              type="text"
              placeholder="Search by stock #, VIN, year, make, model..."
              value={vehicleSearch}
              onChange={e => setVehicleSearch(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-200 rounded-lg mb-2"
              autoFocus
            />
            <div className="max-h-48 overflow-y-auto border border-neutral-200 rounded-lg">
              {filteredVehicles.length === 0 ? (
                <div className="p-3 text-sm text-neutral-500 text-center">No vehicles found</div>
              ) : (
                filteredVehicles.slice(0, 20).map(v => (
                  <div 
                    key={v.id}
                    onClick={() => onSelectVehicle(v)}
                    className="p-2 hover:bg-blue-50 cursor-pointer border-b border-neutral-100 last:border-b-0"
                  >
                    <div className="font-medium text-sm">{v.stockNumber} - {v.year} {v.make} {v.model}</div>
                    <div className="text-xs"><VinDisplay vin={v.vin} /></div>
                  </div>
                ))
              )}
              {filteredVehicles.length > 20 && (
                <div className="p-2 text-xs text-neutral-400 text-center">+{filteredVehicles.length - 20} more - refine search</div>
              )}
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <button 
            onClick={() => { if (selectedVehicle.id) onRetail(selectedVehicle); }}
            disabled={!selectedVehicle.id}
            className="p-4 border-2 border-neutral-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="text-2xl mb-2">🏪</div>
            <div className="font-medium">Retail Sale</div>
            <div className="text-xs text-neutral-500 mt-1">Full form pack, tax, fees</div>
          </button>
          <button 
            onClick={() => { if (selectedVehicle.id) onWholesale(selectedVehicle); }}
            disabled={!selectedVehicle.id}
            className="p-4 border-2 border-neutral-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="text-2xl mb-2">🏭</div>
            <div className="font-medium">Wholesale</div>
            <div className="text-xs text-neutral-500 mt-1">Dealer-to-dealer, bill of sale only</div>
          </button>
        </div>
        <button onClick={onClose} className="mt-4 w-full py-2 text-neutral-500 hover:text-neutral-700">Cancel</button>
      </div>
    </div>
  );
};

// Completed Deals Report - fetches from dedicated report endpoint
const CompletedDealsReport = ({ onClose, startDate, endDate, onStartDateChange, onEndDateChange, all, title }) => {
  const [reportDeals, setReportDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  const saleTypeLabels = { retail: 'Retail', wholesale_direct: 'Wholesale Direct', wholesale_auction: 'Wholesale Auction', copart: 'Copart' };
  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '-';
  const formatMoney = (v) => '$' + parseFloat(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.getDealsReport(startDate, endDate, { all }).then(data => {
      if (!cancelled) setReportDeals(data || []);
    }).catch(err => {
      console.error('Report fetch error:', err);
      if (!cancelled) setReportDeals([]);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [startDate, endDate, all]);

  const totals = reportDeals.reduce((acc, d) => {
    const purchasePrice = parseFloat(d.purchase_price || 0);
    const buyerFee = parseFloat(d.buyer_fee || 0);
    const inputFee = parseFloat(d.input_fee || 0);
    const reconCost = parseFloat(d.recon_cost || 0);
    const transportCost = parseFloat(d.transport_cost || 0);
    const packAmount = parseFloat(d.pack_amount_effective ?? d.pack_amount ?? 0);
    const salePrice = parseFloat(d.sale_price || 0);
    const docFee = parseFloat(d.doc_fee || 0);
    const salesTax = parseFloat(d.sales_tax || d.sales_tax_amount || 0);
    const titleFee = parseFloat(d.title_fee || 0);
    const tempTagFee = parseFloat(d.temp_tag_fee || 0);
    const parsedTotalDue = parseFloat(d.total_due);
    const totalDue = Number.isFinite(parsedTotalDue)
      ? parsedTotalDue
      : (salePrice + docFee + salesTax + titleFee + tempTagFee);
    const balanceDue = parseFloat(d.balance_due || 0);

    acc.purchasePrice += purchasePrice;
    acc.buyerFee += buyerFee;
    acc.inputFee += inputFee;
    acc.reconCost += reconCost;
    acc.transportCost += transportCost;
    acc.packAmount += packAmount;
    acc.revenue += salePrice;
    acc.cost += parseFloat(d.total_cost || 0);
    acc.profit += parseFloat(d.profit || 0);
    acc.docFee += docFee;
    acc.salesTax += salesTax;
    acc.titleFee += titleFee;
    acc.tempTagFee += tempTagFee;
    acc.totalDue += totalDue;
    acc.balanceDue += balanceDue;
    return acc;
  }, {
    purchasePrice: 0, buyerFee: 0, inputFee: 0, reconCost: 0, transportCost: 0, packAmount: 0,
    revenue: 0, cost: 0, profit: 0,
    docFee: 0, salesTax: 0, titleFee: 0, tempTagFee: 0, totalDue: 0, balanceDue: 0
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-5xl rounded-xl shadow-xl max-h-[95vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-lg">{title || 'Completed Deals Report'}</h3>
          <div className="flex items-center gap-3">
            <label className="text-xs text-slate-500">From</label>
            <input type="date" value={startDate} onChange={e => onStartDateChange(e.target.value)} className="px-2 py-1 text-sm border border-slate-200 rounded" />
            <label className="text-xs text-slate-500">To</label>
            <input type="date" value={endDate} onChange={e => onEndDateChange(e.target.value)} className="px-2 py-1 text-sm border border-slate-200 rounded" />
            <button
              onClick={() => {
                if (reportDeals.length === 0) return;
                const headers = [
                  'Stock #','Year','Make','Model','VIN','Our Buyer','Sale Type','Sold To','Sale Date',
                  'Purchase Price','Buyer Fee','Input Fee','Recon','Transport','Pack','Total Cost',
                  'Sale Price','Doc Fee','Sales Tax','Title Fee','Temp Tag Fee','Total Due','Balance Due',
                  'Profit','Margin %','Auction Source','Payment Method','Bank Account','Title Status',
                  'Floorplan','Floorplan Paid'
                ];
                const escCsv = v => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s; };
                const rows = reportDeals.map(d => {
                  const tc = parseFloat(d.total_cost || 0); const sp = parseFloat(d.sale_price || 0); const pr = parseFloat(d.profit || 0);
                  return [
                    d.stock_number, d.year, d.make, d.model, d.vin, d.vehicle_buyer || '',
                    saleTypeLabels[d.sale_type] || d.sale_type, d.buyer_name || '',
                    d.sale_date ? new Date(d.sale_date).toLocaleDateString() : '',
                    parseFloat(d.purchase_price || 0).toFixed(2),
                    parseFloat(d.buyer_fee || 0).toFixed(2),
                    parseFloat(d.input_fee || 0).toFixed(2),
                    parseFloat(d.recon_cost || 0).toFixed(2),
                    parseFloat(d.transport_cost || 0).toFixed(2),
                    parseFloat((d.pack_amount_effective ?? d.pack_amount) || 0).toFixed(2),
                    tc.toFixed(2),
                    sp.toFixed(2),
                    parseFloat(d.doc_fee || 0).toFixed(2),
                    parseFloat(d.sales_tax || d.sales_tax_amount || 0).toFixed(2),
                    parseFloat(d.title_fee || 0).toFixed(2),
                    parseFloat(d.temp_tag_fee || 0).toFixed(2),
                    parseFloat(d.total_due || 0).toFixed(2),
                    parseFloat(d.balance_due || 0).toFixed(2),
                    pr.toFixed(2),
                    d.margin || '0.0',
                    d.auction_source || '',
                    d.payment_method || '',
                    d.bank_account_name || '',
                    d.title_status || '',
                    parseFloat(d.floorplan_payoff_amount || 0).toFixed(2),
                    d.floorplan_paid ? 'Yes' : 'No'
                  ].map(escCsv);
                });
                const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
                const blob = new Blob([csv], { type: 'text/csv' });
                const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `deals-report-${new Date().toISOString().split('T')[0]}.csv`; a.click();
              }}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium"
              disabled={loading || reportDeals.length === 0}
            >
              CSV
            </button>
            <button
              onClick={() => {
                const printContent = document.getElementById('deal-report-printable');
                if (!printContent) return;
                const w = window.open('', '_blank');
                w.document.write(`<html><head><title>Completed Deals Report</title><style>
                  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 11px; color: #1e293b; margin: 20px; }
                  h1 { font-size: 18px; margin-bottom: 4px; }
                  @media print { body { margin: 10px; } }
                </style></head><body>${printContent.innerHTML}</body></html>`);
                w.document.close();
                w.print();
              }}
              className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 text-sm font-medium"
              disabled={loading}
            >
              Print
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl">&times;</button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-6" id="deal-report-printable">
          <h1 style={{ fontSize: '18px', fontWeight: 700, marginBottom: 2 }}>{title || 'Completed Deals Report'}</h1>
          <div style={{ color: '#64748b', fontSize: '12px', marginBottom: 16 }}>
            {startDate && endDate ? `${formatDate(startDate)} — ${formatDate(endDate)}` : 'All dates'} | {reportDeals.length} deal{reportDeals.length !== 1 ? 's' : ''}
          </div>

          {loading && <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>Loading report...</div>}

          {!loading && reportDeals.map(deal => {
            const purchasePrice = parseFloat(deal.purchase_price || 0);
            const buyerFee = parseFloat(deal.buyer_fee || 0);
            const inputFee = parseFloat(deal.input_fee || 0);
            const transportCost = parseFloat(deal.transport_cost || 0);
            const reconCost = parseFloat(deal.recon_cost || 0);
            const packAmount = parseFloat(deal.pack_amount_effective ?? deal.pack_amount ?? 0);
            const totalCost = parseFloat(deal.total_cost || 0);
            const salePrice = parseFloat(deal.sale_price || 0);
            const docFee = parseFloat(deal.doc_fee || 0);
            const salesTax = parseFloat(deal.sales_tax || deal.sales_tax_amount || 0);
            const titleFee = parseFloat(deal.title_fee || 0);
            const tempTagFee = parseFloat(deal.temp_tag_fee || 0);
            const parsedTotalDue = parseFloat(deal.total_due);
            const totalDue = Number.isFinite(parsedTotalDue)
              ? parsedTotalDue
              : (salePrice + docFee + salesTax + titleFee + tempTagFee);
            const parsedBalanceDue = parseFloat(deal.balance_due);
            const balanceDue = Number.isFinite(parsedBalanceDue) ? parsedBalanceDue : 0;
            const profit = parseFloat(deal.profit || 0);
            const margin = deal.margin || (salePrice > 0 ? ((profit / salePrice) * 100).toFixed(1) : '0.0');
            const fees = (() => { try { return typeof deal.fees === 'string' ? JSON.parse(deal.fees || '[]') : (deal.fees || []); } catch { return []; } })();
            const feesTotal = fees.reduce((s, f) => s + parseFloat(f.amount || 0), 0);
            const floorplanAmt = parseFloat(deal.floorplan_payoff_amount || 0);

            return (
              <div key={deal.id} style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: 12, marginBottom: 12, pageBreakInside: 'avoid' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', paddingBottom: 8, marginBottom: 8 }}>
                  <div>
                    <span style={{ fontWeight: 700, fontSize: 13 }}>{deal.stock_number}</span>
                    <span style={{ marginLeft: 8 }}>{deal.year} {deal.make} {deal.model}</span>
                    {deal.vin && <span style={{ marginLeft: 8, color: '#94a3b8', fontSize: 10, fontFamily: 'monospace' }}>{deal.vin}</span>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: profit >= 0 ? '#16a34a' : '#dc2626' }}>{formatMoney(profit)}</span>
                    <span style={{ marginLeft: 6, fontSize: 10, color: '#94a3b8' }}>{margin}%</span>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, fontSize: 11 }}>
                  <div>
                    <div style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase' }}>Bought From</div>
                    <div>{deal.auction_source || '-'}</div>
                    <div style={{ fontSize: 10, color: '#64748b' }}>Purchased: {formatDate(deal.purchase_date)}</div>
                    <div style={{ fontSize: 10, color: '#64748b' }}>Our Buyer: <strong>{deal.vehicle_buyer || '-'}</strong></div>
                  </div>
                  <div>
                    <div style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase' }}>Sold To</div>
                    <div>{deal.buyer_name || '-'}</div>
                    <div style={{ fontSize: 10, color: '#64748b' }}>{saleTypeLabels[deal.sale_type]} | {formatDate(deal.sale_date)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase' }}>Payment</div>
                    <div>{deal.payment_method || '-'}</div>
                    <div style={{ fontSize: 10, color: '#64748b' }}>{deal.bank_account_name || '-'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase' }}>Cost Breakdown</div>
                    <div>Purchase: {formatMoney(purchasePrice)}</div>
                    <div>Buyer Fee: {formatMoney(buyerFee)}</div>
                    <div>Input Fee: {formatMoney(inputFee)}</div>
                    <div>Transport: {formatMoney(transportCost)}</div>
                    <div>Recon: {formatMoney(reconCost)}</div>
                    <div>Pack: {formatMoney(packAmount)}</div>
                    {fees.length > 0 && fees.map((f, i) => <div key={i}>{f.name || 'Fee'}: {formatMoney(f.amount)}</div>)}
                    {feesTotal > 0 && <div style={{ fontWeight: 600 }}>Fees Total: {formatMoney(feesTotal)}</div>}
                    <div style={{ fontWeight: 700, borderTop: '1px solid #e2e8f0', marginTop: 2, paddingTop: 2 }}>Total Cost: {formatMoney(totalCost)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase' }}>Sale</div>
                    <div style={{ fontWeight: 700 }}>Sale Price: {formatMoney(salePrice)}</div>
                    {docFee > 0 && <div>Doc Fee: {formatMoney(docFee)}</div>}
                    {salesTax > 0 && <div>Sales Tax: {formatMoney(salesTax)}</div>}
                    {titleFee > 0 && <div>Title Fee: {formatMoney(titleFee)}</div>}
                    {tempTagFee > 0 && <div>Temp Tag Fee: {formatMoney(tempTagFee)}</div>}
                    <div>Total Due: {formatMoney(totalDue)}</div>
                    {balanceDue > 0 && <div style={{ color: '#d97706' }}>Balance Due: {formatMoney(balanceDue)}</div>}
                    <div style={{ fontWeight: 700, color: profit >= 0 ? '#16a34a' : '#dc2626' }}>Gross Profit: {formatMoney(profit)}</div>
                    <div>Margin: {margin}%</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase' }}>Status</div>
                    <div>Title: {deal.title_status || (deal.title_sent ? 'Sent' : 'Pending')}</div>
                    {floorplanAmt > 0 && <div>Floorplan: {formatMoney(floorplanAmt)} {deal.floorplan_paid ? '(Paid)' : '(Unpaid)'}</div>}
                  </div>
                </div>
              </div>
            );
          })}

          {!loading && reportDeals.length === 0 && <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>No deals found{startDate || endDate ? ' in this date range' : ''}</div>}

          {!loading && reportDeals.length > 0 && (
            <div style={{ border: '2px solid #0d9488', borderRadius: 8, padding: 16, marginTop: 20, pageBreakInside: 'avoid' }}>
              <h2 style={{ fontSize: 14, marginBottom: 12, color: '#0d9488', fontWeight: 700 }}>Grand Totals — Detailed Breakdown</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', marginBottom: 6, fontWeight: 600 }}>Cost Totals</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '2px 12px', fontSize: 11 }}>
                    <span>Total Purchase Price</span><span style={{ fontFamily: 'monospace', textAlign: 'right' }}>{formatMoney(totals.purchasePrice)}</span>
                    <span>Total Buyer Fee</span><span style={{ fontFamily: 'monospace', textAlign: 'right' }}>{formatMoney(totals.buyerFee)}</span>
                    <span>Total Input Fee</span><span style={{ fontFamily: 'monospace', textAlign: 'right' }}>{formatMoney(totals.inputFee)}</span>
                    <span>Total Recon Cost</span><span style={{ fontFamily: 'monospace', textAlign: 'right' }}>{formatMoney(totals.reconCost)}</span>
                    <span>Total Transport Cost</span><span style={{ fontFamily: 'monospace', textAlign: 'right' }}>{formatMoney(totals.transportCost)}</span>
                    <span>Total Pack Amount</span><span style={{ fontFamily: 'monospace', textAlign: 'right', fontWeight: 600 }}>{formatMoney(totals.packAmount)}</span>
                    <span style={{ borderTop: '1px solid #e2e8f0', paddingTop: 4, marginTop: 2, fontWeight: 700 }}>Total Cost</span><span style={{ fontFamily: 'monospace', textAlign: 'right', fontWeight: 700, borderTop: '1px solid #e2e8f0', paddingTop: 4, marginTop: 2 }}>{formatMoney(totals.cost)}</span>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', marginBottom: 6, fontWeight: 600 }}>Sale Totals</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '2px 12px', fontSize: 11 }}>
                    <span>Total Sale Price</span><span style={{ fontFamily: 'monospace', textAlign: 'right' }}>{formatMoney(totals.revenue)}</span>
                    <span>Total Doc Fee</span><span style={{ fontFamily: 'monospace', textAlign: 'right' }}>{formatMoney(totals.docFee)}</span>
                    <span>Total Sales Tax</span><span style={{ fontFamily: 'monospace', textAlign: 'right' }}>{formatMoney(totals.salesTax)}</span>
                    <span>Total Title Fee</span><span style={{ fontFamily: 'monospace', textAlign: 'right' }}>{formatMoney(totals.titleFee)}</span>
                    <span>Total Temp Tag Fee</span><span style={{ fontFamily: 'monospace', textAlign: 'right' }}>{formatMoney(totals.tempTagFee)}</span>
                    <span>Total Due</span><span style={{ fontFamily: 'monospace', textAlign: 'right' }}>{formatMoney(totals.totalDue)}</span>
                    <span>Total Balance Due</span><span style={{ fontFamily: 'monospace', textAlign: 'right', color: totals.balanceDue > 0 ? '#d97706' : 'inherit' }}>{formatMoney(totals.balanceDue)}</span>
                  </div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, textAlign: 'center', marginTop: 16, paddingTop: 16, borderTop: '1px solid #e2e8f0' }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{reportDeals.length}</div>
                  <div style={{ fontSize: 9, color: '#64748b', textTransform: 'uppercase' }}>Units Sold</div>
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{formatMoney(totals.revenue)}</div>
                  <div style={{ fontSize: 9, color: '#64748b', textTransform: 'uppercase' }}>Total Revenue</div>
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{formatMoney(totals.cost)}</div>
                  <div style={{ fontSize: 9, color: '#64748b', textTransform: 'uppercase' }}>Total Cost</div>
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: totals.profit >= 0 ? '#16a34a' : '#dc2626' }}>{formatMoney(totals.profit)}</div>
                  <div style={{ fontSize: 9, color: '#64748b', textTransform: 'uppercase' }}>Total Profit</div>
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{reportDeals.length > 0 ? formatMoney(totals.profit / reportDeals.length) : '$0.00'}</div>
                  <div style={{ fontSize: 9, color: '#64748b', textTransform: 'uppercase' }}>Avg Profit/Unit</div>
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{totals.revenue > 0 && reportDeals.length > 0 ? ((totals.profit / totals.revenue) * 100).toFixed(1) : '0.0'}%</div>
                  <div style={{ fontSize: 9, color: '#64748b', textTransform: 'uppercase' }}>Avg Margin %</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AddDealCostModal = ({ deal, onClose, onSave }) => {
  const [costType, setCostType] = useState('recon');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const costTypeLabels = {
    transport: 'Transport Cost',
    buyer_fee: 'Buyer Fee',
    input_fee: 'Input Fee',
    pack_fee: 'Pack Fee',
    recon: 'Recon Cost',
    additional: 'Additional Cost'
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!deal) return;
    setSaving(true);
    try {
      await onSave?.({ deal, costType, amount, note });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[70]" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-lg shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold">Add Recon / Additional Cost</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <div className="text-xs text-slate-500">
            {deal?.stock_number} - {deal?.year} {deal?.make} {deal?.model}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Cost Type</label>
            <select value={costType} onChange={e => setCostType(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg">
              {Object.entries(costTypeLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Amount to Add</label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-slate-400">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                className="w-full pl-7 pr-3 py-2 border border-slate-200 rounded-lg"
                placeholder="0.00"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Note (optional)</label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg"
              rows={2}
              placeholder="Why this cost was added..."
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-slate-600 hover:text-slate-800">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50">
              {saving ? 'Saving...' : 'Add Cost Entry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Batch Clear Payment Modal - Clear multiple vehicles in one ACH/batch transaction
// Batch Deal Deposit Modal - create one bank deposit for multiple sold deals
const BatchDealDepositModal = ({ deals, bankAccounts, onLoadBankAccounts, onClose, onSave }) => {
  const [paymentMethod, setPaymentMethod] = useState('Check');
  const [bankAccountId, setBankAccountId] = useState('');
  const [batchReference, setBatchReference] = useState('');
  const [notes, setNotes] = useState('');
  const [extraAmount, setExtraAmount] = useState('');
  const [extraDescription, setExtraDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!bankAccounts || bankAccounts.length === 0) {
      onLoadBankAccounts?.();
    }
  }, [bankAccounts, onLoadBankAccounts]);

  const dealsTotal = deals.reduce((sum, d) => {
    const due = parseFloat(d.total_due || d.sale_price || 0);
    const paid = parseFloat(d.total_paid || 0);
    return sum + Math.max(0, due - paid);
  }, 0);

  const extraAmountNum = parseFloat(extraAmount) || 0;
  const grandTotal = dealsTotal + extraAmountNum;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!bankAccountId) {
      alert('Please select a bank account for the batch deposit.');
      return;
    }
    if (grandTotal <= 0) {
      alert('Total deposit amount must be greater than zero.');
      return;
    }
    const extraDeposits = extraAmountNum > 0
      ? [{ amount: extraAmountNum, description: extraDescription.trim() || 'Unknown bank deposit', category: 'Other Income' }]
      : [];
    setSaving(true);
    try {
      await onSave({ bankAccountId, paymentMethod, batchReference, notes, extraDeposits });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]" onClick={onClose}>
      <div className="bg-white w-full max-w-lg rounded-lg shadow-lg" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex justify-between items-center">
          <h2 className="text-base font-semibold">Batch Deposit – {deals.length} deal{deals.length === 1 ? '' : 's'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="text-xs text-slate-500">
            Deals with remaining balance will be marked paid. Already fully-paid deals are skipped automatically.
          </div>

          {/* Deal list */}
          <div className="bg-slate-50 border border-slate-200 rounded p-3 max-h-32 overflow-y-auto text-xs">
            {deals.map(d => (
              <div key={d.id} className="flex justify-between">
                <span>{d.stock_number} – {d.year} {d.make} {d.model}</span>
                <span className="font-mono">${parseFloat(d.sale_price || 0).toLocaleString()}</span>
              </div>
            ))}
            {deals.length > 0 && (
              <div className="flex justify-between font-semibold pt-2 mt-1 border-t border-slate-200">
                <span>Deals subtotal</span>
                <span className="font-mono">${dealsTotal.toLocaleString()}</span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-600 mb-1">Payment Method</label>
              <select
                value={paymentMethod}
                onChange={e => setPaymentMethod(e.target.value)}
                className="w-full h-9 px-2 text-sm border border-slate-300 rounded"
              >
                <option value="ACH">ACH</option>
                <option value="Check">Check</option>
                <option value="Wire">Wire</option>
                <option value="Cash">Cash</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1">Deposit To</label>
              <select
                value={bankAccountId}
                onChange={e => setBankAccountId(e.target.value)}
                className="w-full h-9 px-2 text-sm border border-slate-300 rounded"
              >
                <option value="">Select bank account…</option>
                {bankAccounts.map(a => (
                  <option key={a.id} value={a.id}>{a.name}{a.accountType ? ` (${a.accountType})` : ''}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs text-slate-600 mb-1">Batch Reference (optional)</label>
            <input
              type="text"
              value={batchReference}
              onChange={e => setBatchReference(e.target.value)}
              className="w-full h-9 px-2 text-sm border border-slate-300 rounded"
              placeholder="ACH file ID, deposit slip #, etc."
            />
          </div>

          <div>
            <label className="block text-xs text-slate-600 mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded resize-none"
              rows={2}
            />
          </div>

          {/* Extra / unknown deposit section */}
          <div className="border border-dashed border-slate-300 rounded p-3 space-y-2 bg-slate-50">
            <div className="text-xs font-medium text-slate-600">Additional bank deposit (optional)</div>
            <div className="text-xs text-slate-400">Use this to add an unknown or unmatched item from the bank statement into the same deposit.</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={extraAmount}
                  onChange={e => setExtraAmount(e.target.value)}
                  className="w-full h-9 px-2 text-sm border border-slate-300 rounded"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Description</label>
                <input
                  type="text"
                  value={extraDescription}
                  onChange={e => setExtraDescription(e.target.value)}
                  className="w-full h-9 px-2 text-sm border border-slate-300 rounded"
                  placeholder="Unknown deposit / bank item"
                />
              </div>
            </div>
          </div>

          {/* Combined total and actions */}
          <div className="flex items-center justify-between pt-1">
            <div className="text-xs text-slate-600 space-y-0.5">
              {extraAmountNum > 0 && (
                <div>Deals: <span className="font-mono">${dealsTotal.toLocaleString()}</span> + Extra: <span className="font-mono">${extraAmountNum.toLocaleString()}</span></div>
              )}
              <div className="font-semibold">
                Combined deposit:&nbsp;
                <span className="font-mono text-teal-700">${grandTotal.toLocaleString()}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs border border-slate-300 rounded-lg hover:bg-slate-50">
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || !bankAccountId || grandTotal <= 0}
                className="px-3 py-1.5 text-xs bg-teal-600 text-white rounded-lg disabled:opacity-50 hover:bg-teal-500"
              >
                {saving ? 'Posting...' : `Post Batch Deposit ($${grandTotal.toLocaleString()})`}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// Deal Desk Module - Track vehicle sales
const DealDesk = ({ vehicles, onRefresh, refreshTrigger, openNewSaleTrigger = 0, onEditVehicleFromDeal, canEditVehicle, onSendToBhph, canAccessBhph }) => {
  const { t } = useLanguage();
  const [deals, setDeals] = useState([]);
  const [ar, setAR] = useState({ deals: [], totalAR: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const getPersisted = (key, fallback) => {
    try {
      const v = localStorage.getItem(key);
      return v ?? fallback;
    } catch {
      return fallback;
    }
  };
  const saveScroll = () => {
    try { sessionStorage.setItem('dealDeskScrollY', String(window.scrollY || 0)); } catch {}
  };
  const restoreScroll = () => {
    try {
      const raw = sessionStorage.getItem('dealDeskScrollY');
      const y = raw === null ? NaN : parseInt(raw, 10);
      if (!Number.isNaN(y)) {
        requestAnimationFrame(() => window.scrollTo({ top: y, behavior: 'auto' }));
      }
    } catch {}
  };
  // Persist active tab to localStorage
  const getPersistedTab = () => { try { return localStorage.getItem('dealDeskActiveTab') || 'active'; } catch { return 'active'; } };
  const [activeTab, setActiveTabState] = useState(getPersistedTab);
  const [showVindex, setShowVindex] = useState(false);
  const setActiveTab = (tab) => { setActiveTabState(tab); try { localStorage.setItem('dealDeskActiveTab', tab); } catch {} };
  const [checklistFilter, setChecklistFilter] = useState(() => getPersisted('dealDeskChecklistFilter', 'all')); // all, needs_costs, needs_title, needs_sheet, not_picked_up, unpaid
  const [showSellModal, setShowSellModal] = useState(null);
  const [showRetailModal, setShowRetailModal] = useState(null);
  const [showWholesaleModal, setShowWholesaleModal] = useState(null);
  const [showSaleTypeChoice, setShowSaleTypeChoice] = useState(null);
  const [editDeal, setEditDeal] = useState(null);
  const [viewDeal, setViewDeal] = useState(null);
  const [printPacketDeal, setPrintPacketDeal] = useState(null); // deal whose Pre-Print screen is open
  const [searchTerm, setSearchTerm] = useState(() => getPersisted('dealDeskSearchTerm', ''));
  const [sortBy, setSortBy] = useState(() => getPersisted('dealDeskSortBy', 'date'));
  const [sortDir, setSortDir] = useState(() => getPersisted('dealDeskSortDir', 'desc'));
  const [showFormTemplates, setShowFormTemplates] = useState(false);
  const [showARBreakdown, setShowARBreakdown] = useState(false);
  const [pendingPaidDeal, setPendingPaidDeal] = useState(null);
  const [markPaidPaymentMethod, setMarkPaidPaymentMethod] = useState('Check');
  const [markPaidBatchDeposit, setMarkPaidBatchDeposit] = useState(false);
  const [markPaidBankAccount, setMarkPaidBankAccount] = useState('');
  const [markPaidBankAccounts, setMarkPaidBankAccounts] = useState([]);
  const [selectedBatchDealIds, setSelectedBatchDealIds] = useState([]);
  const [showBatchDepositModal, setShowBatchDepositModal] = useState(false);
  const [showPreSaleCalc, setShowPreSaleCalc] = useState(false);
  const [showPrintReport, setShowPrintReport] = useState(false);
  const [showAllSalesReport, setShowAllSalesReport] = useState(false);
  const [dealCostModal, setDealCostModal] = useState({ isOpen: false, deal: null });
  const [reportStartDate, setReportStartDate] = useState(() => {
    const d = new Date(); d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [reportEndDate, setReportEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  const reopenNewSaleChooser = () => {
    // Defer open by one tick so the click that closed the previous modal
    // does not immediately bubble and close the chooser backdrop.
    window.setTimeout(() => setShowSaleTypeChoice({}), 0);
  };

  // Auto-open New Sale chooser when triggered from the home screen quick action
  useEffect(() => {
    if (openNewSaleTrigger > 0) {
      window.setTimeout(() => setShowSaleTypeChoice({}), 100);
    }
  }, [openNewSaleTrigger]);

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  useEffect(() => { try { localStorage.setItem('dealDeskChecklistFilter', checklistFilter); } catch {} }, [checklistFilter]);
  useEffect(() => { try { localStorage.setItem('dealDeskSearchTerm', searchTerm); } catch {} }, [searchTerm]);
  useEffect(() => { try { localStorage.setItem('dealDeskSortBy', sortBy); } catch {} }, [sortBy]);
  useEffect(() => { try { localStorage.setItem('dealDeskSortDir', sortDir); } catch {} }, [sortDir]);

  const SortHeader = ({ id, label, align = 'left' }) => {
    const active = sortBy === id;
    const arrow = active ? (sortDir === 'asc' ? '▲' : '▼') : '⇅';
    return (
      <th className={`px-3 py-2 font-medium ${align === 'right' ? 'text-right' : ''}`}>
        <button onClick={() => toggleSort(id)} className={`flex items-center gap-1 text-xs hover:text-teal-600 ${active ? 'text-teal-600' : ''} ${align === 'right' ? 'ml-auto' : ''}`}>
          {label}
          <span className={`text-[10px] ${active ? '' : 'opacity-30'}`}>{arrow}</span>
        </button>
      </th>
    );
  };

  // Filter and sort helper
  const filterAndSort = (data) => {
    let filtered = data;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = data.filter(d => 
        (d.stock_number || '').toLowerCase().includes(search) ||
        (d.make || '').toLowerCase().includes(search) ||
        (d.model || '').toLowerCase().includes(search) ||
        (d.buyer_name || '').toLowerCase().includes(search) ||
        (d.sale_type || '').toLowerCase().includes(search)
      );
    }
    return [...filtered].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      switch (sortBy) {
        case 'stock': return (a.stock_number || '').localeCompare(b.stock_number || '') * dir;
        case 'vehicle': return (`${a.year} ${a.make} ${a.model}`).localeCompare(`${b.year} ${b.make} ${b.model}`) * dir;
        case 'buyer': return (a.buyer_name || '').localeCompare(b.buyer_name || '') * dir;
        case 'type': return (a.sale_type || '').localeCompare(b.sale_type || '') * dir;
        case 'price': return (parseFloat(a.sale_price || 0) - parseFloat(b.sale_price || 0)) * dir;
        case 'cost': return (parseFloat(a.total_cost || 0) - parseFloat(b.total_cost || 0)) * dir;
        case 'profit': return (parseFloat(a.profit || 0) - parseFloat(b.profit || 0)) * dir;
        case 'date': return ((a.sale_date || '') > (b.sale_date || '') ? 1 : -1) * dir;
        case 'days': 
          const daysA = a.sale_date ? Math.floor((new Date() - new Date(a.sale_date)) / (1000 * 60 * 60 * 24)) : 0;
          const daysB = b.sale_date ? Math.floor((new Date() - new Date(b.sale_date)) / (1000 * 60 * 60 * 24)) : 0;
          return (daysA - daysB) * dir;
        default: return 0;
      }
    });
  };

  useEffect(() => {
    const loadDeals = async () => {
      setLoading(true);
      try {
        const [dealList, arData] = await Promise.all([
          api.getDeals(),
          api.getAccountsReceivable()
        ]);
        setDeals(dealList || []);
        setAR(arData || { deals: [], totalAR: 0, count: 0 });
      } catch (e) {
        console.error('Failed to load deals:', e);
      } finally {
        setLoading(false);
        restoreScroll();
      }
    };
    loadDeals();
  }, [refreshTrigger]);

  const handleChecklistUpdate = async (dealId, field, value) => {
    try {
      saveScroll();
      await api.updateDealChecklist(dealId, field, value);
      const [dealList, arData] = await Promise.all([api.getDeals(), api.getAccountsReceivable()]);
      setDeals(dealList || []);
      if (field === 'payment_received') setAR(arData || { deals: [], totalAR: 0, count: 0 });
      // Refresh parent vehicles so Titles module reflects title_status changes immediately
      if (field === 'title_sent') onRefresh();
      restoreScroll();
    } catch (e) {
      alert('Failed to update: ' + e.message);
    }
  };

  const handleCreateDeal = async (data) => {
    try {
      saveScroll();
      await api.createDeal(data);
      setShowSellModal(null);
      const [dealList, arData] = await Promise.all([
        api.getDeals(),
        api.getAccountsReceivable()
      ]);
      setDeals(dealList || []);
      setAR(arData || { deals: [], totalAR: 0, count: 0 });
      onRefresh();
      restoreScroll();
    } catch (e) {
      alert('Failed to create deal: ' + e.message);
    }
  };

  const handleUnwind = async (dealId) => {
    const reason = window.prompt('Reason for unwinding this deal:');
    if (!reason) return;
    try {
      saveScroll();
      await api.deleteDeal(dealId, reason);
      const [dealList, arData] = await Promise.all([
        api.getDeals(),
        api.getAccountsReceivable()
      ]);
      setDeals(dealList || []);
      setAR(arData || { deals: [], totalAR: 0, count: 0 });
      onRefresh();
      restoreScroll();
    } catch (e) {
      alert('Failed to unwind: ' + e.message);
    }
  };

  const handleAddCostToDealVehicle = async ({ deal, costType, amount, note }) => {
    const parsedAmount = parseFloat(amount || 0);
    if (!deal?.vehicle_id || !costType || !Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      alert('Enter a valid cost amount greater than 0.');
      return;
    }

    try {
      saveScroll();
      await api.addVehicleCostAdjustment({
        vehicleId: deal.vehicle_id,
        dealId: deal.id,
        costType,
        amount: parsedAmount,
        note: (note || '').trim() || null
      });
      const [dealList, arData] = await Promise.all([
        api.getDeals(),
        api.getAccountsReceivable()
      ]);
      setDeals(dealList || []);
      setAR(arData || { deals: [], totalAR: 0, count: 0 });
      onRefresh();
      restoreScroll();
      setDealCostModal({ isOpen: false, deal: null });
      alert('Additional cost added successfully.');
    } catch (e) {
      alert('Failed to add additional cost: ' + e.message);
    }
  };

  // Show all inventory except sold vehicles
  // IMPORTANT: Vehicles with pending deals should still be visible here
  const availableVehicles = vehicles.filter(v => {
    const isVisible = v.status !== 'sold' && v.status !== 'wholesale' && v.status !== 'copart';
    return isVisible;
  });
  
  // A deal is only complete when ALL required checkboxes are checked
  const isDealComplete = (deal) => {
    // Check if this deal needs a sale sheet (wholesale/auction sales except Byrider)
    const isByrider = (deal.buyer_name || '').toLowerCase().includes('byrider');
    const needsSaleSheet = (deal.sale_type === 'wholesale_auction' || deal.sale_type === 'wholesale_direct') && !isByrider;
    
    // Required fields for all deals
    const baseComplete = deal.all_costs_added && deal.title_sent && deal.placed_in_pickup && deal.payment_received;
    
    // If needs sale sheet, that must also be checked
    if (needsSaleSheet) {
      return baseComplete && deal.sale_sheet_printed;
    }
    return baseComplete;
  };
  
  const unpaidDeals = deals.filter(d => !isDealComplete(d));
  const paidDeals = deals.filter(d => isDealComplete(d));
  const soldNoDeal = vehicles.filter(v => (v.status === 'sold' || v.status === 'wholesale') && !deals.some(d => d.vehicle_id === v.id));

  const saleTypeLabels = {
    retail: 'Retail',
    wholesale_direct: 'Wholesale Direct',
    wholesale_auction: 'Wholesale Auction',
    copart: 'Copart'
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold">Deal Desk</h1>
        <div className="flex items-center gap-3">
          <input
            type="text"
            placeholder="Search stock, vehicle, buyer..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg w-56"
          />
          <button 
            onClick={async () => {
              const vin = window.prompt('Enter VIN to find lost vehicle:');
              if (!vin) return;
              try {
                const [vehicle, deal] = await Promise.all([
                  api.findVehicleByVin(vin).catch(() => null),
                  api.findDealByVin(vin).catch(() => null)
                ]);
                
                if (!vehicle && !deal) {
                  alert(`No vehicle or deal found for VIN: ${vin}`);
                  return;
                }
                
                let message = `Found:\n`;
                if (vehicle) {
                  message += `Vehicle: ${vehicle.stockNumber} - ${vehicle.year} ${vehicle.make} ${vehicle.model}\n`;
                  message += `Status: ${vehicle.status}\n`;
                }
                if (deal) {
                  message += `\nDeal #${deal.id} - ${deal.sale_type}\n`;
                  message += `Buyer: ${deal.buyer_name}\n`;
                  message += `Status: ${deal.status || 'active'}\n`;
                  if (deal.status !== 'completed') {
                    const unwind = window.confirm(message + '\n\nWould you like to unwind this deal?');
                    if (unwind) {
                      const reason = window.prompt('Reason for unwinding:');
                      if (reason) {
                        await api.deleteDeal(deal.id, reason);
                        alert('Deal unwound! Vehicle restored to inventory.');
                        const [dealList, arData] = await Promise.all([
                          api.getDeals(),
                          api.getAccountsReceivable()
                        ]);
                        setDeals(dealList || []);
                        setAR(arData || { deals: [], totalAR: 0, count: 0 });
                        onRefresh();
                      }
                    }
                  } else {
                    alert(message);
                  }
                } else {
                  alert(message);
                }
              } catch (e) {
                alert('Error: ' + e.message);
              }
            }}
            className="px-3 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 text-sm"
          >
            🔍 Find Lost Vehicle
          </button>
          <button onClick={() => setShowVindex(true)} className="px-3 py-2 border border-green-300 text-green-700 rounded-lg hover:bg-green-50 text-sm">💰 Get Buy Number</button>
          <button onClick={() => setShowFormTemplates(true)} className="px-3 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 text-sm">Form Templates</button>
          <button onClick={async () => {
            try {
              const debug = await api.getAllDealsDebug();
              alert(`Total deals: ${debug.total}\nUnpaid: ${debug.unpaid}`);
            } catch (e) {
              console.error('Debug error:', e);
              alert('Debug failed: ' + e.message);
            }
          }} className="px-3 py-2 border border-teal-300 text-teal-700 rounded-lg hover:bg-teal-50 text-sm">🔍 Debug All Deals</button>
          <button onClick={() => setShowPreSaleCalc(true)} className="px-3 py-2 border border-green-400 text-green-700 rounded-lg hover:bg-green-50 text-sm font-medium">🧮 Pre-Sale Calculator</button>
          <button onClick={() => setShowSaleTypeChoice({})} className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-500">+ New Sale</button>
        </div>
      </div>

      {/* Pre-Sale Calculator */}
      <PreSaleCalculator isOpen={showPreSaleCalc} onClose={() => setShowPreSaleCalc(false)} />

      {/* Form Templates Modal */}
      {showFormTemplates && <FormTemplateManager onClose={() => setShowFormTemplates(false)} />}

      {/* Vindex — Get Buy Number (any VIN) */}
      {showVindex && <VindexPanel onClose={() => setShowVindex(false)} />}

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 shadow-sm rounded-lg p-4">
          <div className="text-xs text-teal-700 font-medium">Available Inventory</div>
          <div className="text-2xl font-bold text-slate-800">{availableVehicles.length}</div>
        </div>
        <div className="bg-white border border-slate-200 shadow-sm rounded-lg p-4">
          <div className="text-xs text-teal-700 font-medium">Active Deals</div>
          <div className="text-2xl font-bold text-slate-800">{unpaidDeals.length}</div>
        </div>
        <div 
          className="bg-amber-50 border border-amber-200 rounded-lg p-4 cursor-pointer hover:bg-amber-100 transition-colors"
          onClick={() => setShowARBreakdown(true)}
          title="Click to see AR breakdown"
        >
          <div className="text-xs text-amber-700">Accounts Receivable</div>
          <div className="text-2xl font-bold text-amber-800">${ar.totalAR?.toLocaleString() || 0}</div>
          <div className="text-xs text-amber-600 mt-1">{ar.count || 0} deal{ar.count !== 1 ? 's' : ''}</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="text-xs text-green-700">Completed This Month</div>
          <div className="text-2xl font-bold text-green-800">{paidDeals.length}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        {[
          { id: 'active', label: 'Active Deals', count: unpaidDeals.length },
          { id: 'sold', label: 'All Sold Units', count: deals.length },
          { id: 'ar', label: 'Accounts Receivable', count: ar.count },
          { id: 'completed', label: 'Completed', count: paidDeals.length },
          ...(soldNoDeal.length > 0 ? [{ id: 'no_deal', label: 'Sold — No Deal', count: soldNoDeal.length }] : []),
          { id: 'inventory', label: 'All Inventory', count: availableVehicles.length },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px ${activeTab === tab.id ? 'border-teal-600 text-teal-700 font-medium' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            {tab.label} {tab.count > 0 && <span className="ml-1 text-xs bg-slate-200 px-1.5 py-0.5 rounded">{tab.count}</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-8 text-neutral-500">Loading...</div>
      ) : (
        <>
          {/* Active Deals */}
          {activeTab === 'active' && (
            <>
              {/* Checklist Filter Buttons */}
              <div className="flex flex-wrap gap-2 mb-3">
                {[
                  { id: 'all', label: 'All' },
                  { id: 'needs_costs', label: 'Needs Costs' },
                  { id: 'needs_title', label: 'Needs Title' },
                  { id: 'needs_sheet', label: 'Needs Sheet' },
                  { id: 'not_picked_up', label: 'Not Picked Up' },
                  { id: 'unpaid', label: 'Unpaid' },
                ].map(f => {
                  // Count deals matching each filter
                  let count = 0;
                  if (f.id === 'all') count = unpaidDeals.length;
                  else if (f.id === 'needs_costs') count = unpaidDeals.filter(d => !d.all_costs_added).length;
                  else if (f.id === 'needs_title') count = unpaidDeals.filter(d => !d.title_sent).length;
                  else if (f.id === 'needs_sheet') count = unpaidDeals.filter(d => {
                    const isByrider = (d.buyer_name || '').toLowerCase().includes('byrider');
                    const needsSheet = (d.sale_type === 'wholesale_direct' || d.sale_type === 'wholesale_auction' || d.is_dealer_direct) && !isByrider;
                    return needsSheet && !d.sale_sheet_printed;
                  }).length;
                  else if (f.id === 'not_picked_up') count = unpaidDeals.filter(d => !d.placed_in_pickup).length;
                  else if (f.id === 'unpaid') count = unpaidDeals.filter(d => !d.payment_received).length;
                  
                  return (
                    <button
                      key={f.id}
                      onClick={() => setChecklistFilter(f.id)}
                      className={`px-3 py-1.5 text-xs rounded-lg border ${
                        checklistFilter === f.id
                          ? 'bg-teal-600 text-white border-teal-600'
                          : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {f.label} {count > 0 && <span className="ml-1 opacity-75">({count})</span>}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs text-slate-500">
                  {selectedBatchDealIds.length > 0
                    ? `${selectedBatchDealIds.length} deal${selectedBatchDealIds.length === 1 ? '' : 's'} selected for batch deposit`
                    : 'Select multiple deals and use Batch Deposit to post one combined bank transaction.'}
                </div>
                {selectedBatchDealIds.length >= 2 && (
                  <button
                    onClick={() => setShowBatchDepositModal(true)}
                    className="px-3 py-1.5 text-xs bg-teal-600 text-white rounded-lg hover:bg-teal-500"
                  >
                    Batch Deposit Selected ({selectedBatchDealIds.length})
                  </button>
                )}
              </div>
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-400 border-b border-slate-200 bg-slate-50">
                    <th className="px-2 py-2 text-center w-8">
                      <input
                        type="checkbox"
                        checked={filterAndSort(unpaidDeals).length > 0 && filterAndSort(unpaidDeals).every(d => selectedBatchDealIds.includes(d.id))}
                        onChange={e => {
                          if (e.target.checked) {
                            setSelectedBatchDealIds(filterAndSort(unpaidDeals).map(d => d.id));
                          } else {
                            setSelectedBatchDealIds([]);
                          }
                        }}
                      />
                    </th>
                    <SortHeader id="vehicle" label="Vehicle" />
                    <SortHeader id="type" label="Type" />
                    <SortHeader id="buyer" label="Buyer" />
                    <SortHeader id="price" label="Price" align="right" />
                    <th className="px-3 py-2 font-medium text-center">QR</th>
                    <th className="px-3 py-2 font-medium text-center" title="All costs entered">Costs</th>
                    <th className="px-3 py-2 font-medium text-center" title="Title sent out">Title</th>
                    <th className="px-3 py-2 font-medium text-center" title="Sale sheet printed (wholesale/auction only)">Sheet</th>
                    <th className="px-3 py-2 font-medium text-center" title="Placed in pickup">Pickup</th>
                    <th className="px-3 py-2 font-medium text-center" title="Payment posted">Paid</th>
                    <th className="px-3 py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {filterAndSort(unpaidDeals).filter(deal => {
                    // Apply checklist filter
                    if (checklistFilter === 'all') return true;
                    if (checklistFilter === 'needs_costs') return !deal.all_costs_added;
                    if (checklistFilter === 'needs_title') return !deal.title_sent;
                    if (checklistFilter === 'needs_sheet') {
                      const isByrider = (deal.buyer_name || '').toLowerCase().includes('byrider');
                      const needsSheet = (deal.sale_type === 'wholesale_direct' || deal.sale_type === 'wholesale_auction' || deal.is_dealer_direct) && !isByrider;
                      return needsSheet && !deal.sale_sheet_printed;
                    }
                    if (checklistFilter === 'not_picked_up') return !deal.placed_in_pickup;
                    if (checklistFilter === 'unpaid') return !deal.payment_received;
                    return true;
                  }).map(deal => {
                    // Detect if this is a Byrider sale (don't need sale sheet printed)
                    const isByrider = (deal.buyer_name || '').toLowerCase().includes('byrider');
                    // Wholesale/auction sales need sale sheet printed (unless Byrider)
                    const needsSaleSheet = (deal.sale_type === 'wholesale_direct' || deal.sale_type === 'wholesale_auction' || deal.is_dealer_direct) && !isByrider;
                    
                    return (
                      <tr key={deal.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setViewDeal(deal)}>
                      <td className="px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={selectedBatchDealIds.includes(deal.id)}
                          onClick={e => e.stopPropagation()}
                          onChange={e => {
                            if (e.target.checked) {
                              setSelectedBatchDealIds(prev => [...prev, deal.id]);
                            } else {
                              setSelectedBatchDealIds(prev => prev.filter(id => id !== deal.id));
                            }
                          }}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-teal-600 hover:text-teal-800 hover:underline">{deal.stock_number}</div>
                        <div className="text-xs text-slate-500">{deal.year} {deal.make} {deal.model}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {saleTypeLabels[deal.sale_type]}
                        {deal.is_dealer_direct && <span className="ml-1 text-[10px] bg-teal-100 text-teal-700 px-1 rounded">DD</span>}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {deal.buyer_name || '-'}
                        {isByrider && <span className="ml-1 text-[10px] bg-purple-100 text-purple-700 px-1 rounded">Byrider</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">${parseFloat(deal.sale_price || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-center">
                        <QRCodePopup vehicle={{ stockNumber: deal.stock_number, year: deal.year, make: deal.make, model: deal.model, vin: deal.vin }} className="text-xs" />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <input type="checkbox" checked={deal.all_costs_added} onClick={e => e.stopPropagation()} onChange={e => handleChecklistUpdate(deal.id, 'all_costs_added', e.target.checked)} />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <input type="checkbox" checked={deal.title_sent} onClick={e => e.stopPropagation()} onChange={e => handleChecklistUpdate(deal.id, 'title_sent', e.target.checked)} />
                      </td>
                      <td className="px-3 py-2 text-center">
                        {needsSaleSheet ? (
                          <input 
                            type="checkbox" 
                            checked={deal.sale_sheet_printed} 
                            onClick={e => e.stopPropagation()}
                            onChange={async e => {
                              await api.updateDealChecklistExtended(deal.id, 'sale_sheet_printed', e.target.checked);
                              const dealList = await api.getDeals();
                              setDeals(dealList || []);
                            }} 
                          />
                        ) : (
                          <span className="text-neutral-300">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <input 
                          type="checkbox" 
                          checked={deal.placed_in_pickup} 
                          onClick={e => e.stopPropagation()}
                          onChange={async e => {
                            await api.updateDealChecklistExtended(deal.id, 'placed_in_pickup', e.target.checked);
                            const dealList = await api.getDeals();
                            setDeals(dealList || []);
                          }} 
                        />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={deal.payment_received}
                          onClick={e => e.stopPropagation()}
                          onChange={e => {
                            if (e.target.checked) {
                              setMarkPaidPaymentMethod('Check');
                              setMarkPaidBatchDeposit(false);
                              setMarkPaidBankAccount('');
                              api.getBankAccounts().then(accts => setMarkPaidBankAccounts(accts || [])).catch(() => {});
                              setPendingPaidDeal(deal);
                            } else {
                              handleChecklistUpdate(deal.id, 'payment_received', false);
                            }
                          }}
                        />
                      </td>
                      <td className="px-3 py-2 text-right flex gap-2 justify-end">
                        <button onClick={(e) => { e.stopPropagation(); setDealCostModal({ isOpen: true, deal }); }} className="text-xs text-amber-600 hover:text-amber-800">add recon/addtl</button>
                        <button onClick={(e) => { e.stopPropagation(); setEditDeal(deal); }} className="text-xs text-blue-500 hover:text-blue-700">edit</button>
                        {onEditVehicleFromDeal && canEditVehicle && (
                          <button onClick={(e) => { e.stopPropagation(); onEditVehicleFromDeal(deal.vehicle_id); setViewDeal(null); }} className="text-xs text-green-600 hover:text-green-800">vehicle</button>
                        )}
                        <button onClick={() => setPrintPacketDeal(deal)} className="text-xs text-green-500 hover:text-green-700">forms</button>
                        <a href="https://www.ohioautodealers.com" target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-xs text-amber-600 hover:text-amber-800">tag</a>
                        <button onClick={() => handleUnwind(deal.id)} className="text-xs text-red-500 hover:text-red-700">unwind</button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              {unpaidDeals.length === 0 && <div className="text-center py-8 text-neutral-500">No active deals</div>}
            </div>
            </>
          )}

          {/* All Sold Units Tab - all deals with checklist (linked to same data) */}
          {activeTab === 'sold' && (
            <>
              <div className="flex flex-wrap gap-2 mb-3">
                {[
                  { id: 'all', label: 'All' },
                  { id: 'needs_costs', label: 'Needs Costs' },
                  { id: 'needs_title', label: 'Needs Title' },
                  { id: 'needs_sheet', label: 'Needs Sheet' },
                  { id: 'not_picked_up', label: 'Not Picked Up' },
                  { id: 'unpaid', label: 'Unpaid' },
                ].map(f => {
                  let count = 0;
                  if (f.id === 'all') count = deals.length;
                  else if (f.id === 'needs_costs') count = deals.filter(d => !d.all_costs_added).length;
                  else if (f.id === 'needs_title') count = deals.filter(d => !d.title_sent).length;
                  else if (f.id === 'needs_sheet') count = deals.filter(d => {
                    const isByrider = (d.buyer_name || '').toLowerCase().includes('byrider');
                    const needsSheet = (d.sale_type === 'wholesale_direct' || d.sale_type === 'wholesale_auction' || d.is_dealer_direct) && !isByrider;
                    return needsSheet && !d.sale_sheet_printed;
                  }).length;
                  else if (f.id === 'not_picked_up') count = deals.filter(d => !d.placed_in_pickup).length;
                  else if (f.id === 'unpaid') count = deals.filter(d => !d.payment_received).length;
                  return (
                    <button key={f.id} onClick={() => setChecklistFilter(f.id)}
                      className={`px-3 py-1.5 text-xs rounded-lg border ${checklistFilter === f.id ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}>
                      {f.label} {count > 0 && <span className="ml-1 opacity-75">({count})</span>}
                    </button>
                  );
                })}
                <button
                  onClick={() => { setReportStartDate(''); setReportEndDate(''); setShowAllSalesReport(true); }}
                  className="px-3 py-1.5 text-xs rounded-lg border bg-slate-800 text-white border-slate-800 hover:bg-slate-700 ml-auto"
                >
                  Print Report
                </button>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-400 border-b border-slate-200 bg-slate-50">
                    <SortHeader id="vehicle" label="Vehicle" />
                    <SortHeader id="type" label="Type" />
                    <SortHeader id="buyer" label="Buyer" />
                    <th className="px-3 py-2 font-medium">Our Buyer</th>
                    <SortHeader id="price" label="Price" align="right" />
                    <th className="px-3 py-2 font-medium text-center">QR</th>
                    <th className="px-3 py-2 font-medium text-center" title="All costs entered">Costs</th>
                    <th className="px-3 py-2 font-medium text-center" title="Title sent out">Title</th>
                    <th className="px-3 py-2 font-medium text-center" title="Sale sheet printed">Sheet</th>
                    <th className="px-3 py-2 font-medium text-center" title="Placed in pickup">Pickup</th>
                    <th className="px-3 py-2 font-medium text-center" title="Payment posted">Paid</th>
                    <th className="px-3 py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {filterAndSort(deals).filter(deal => {
                    if (checklistFilter === 'all') return true;
                    if (checklistFilter === 'needs_costs') return !deal.all_costs_added;
                    if (checklistFilter === 'needs_title') return !deal.title_sent;
                    if (checklistFilter === 'needs_sheet') {
                      const isByrider = (deal.buyer_name || '').toLowerCase().includes('byrider');
                      const needsSheet = (deal.sale_type === 'wholesale_direct' || deal.sale_type === 'wholesale_auction' || deal.is_dealer_direct) && !isByrider;
                      return needsSheet && !deal.sale_sheet_printed;
                    }
                    if (checklistFilter === 'not_picked_up') return !deal.placed_in_pickup;
                    if (checklistFilter === 'unpaid') return !deal.payment_received;
                    return true;
                  }).map(deal => {
                    const isByrider = (deal.buyer_name || '').toLowerCase().includes('byrider');
                    const needsSaleSheet = (deal.sale_type === 'wholesale_direct' || deal.sale_type === 'wholesale_auction' || deal.is_dealer_direct) && !isByrider;
                    return (
                      <tr key={deal.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setViewDeal(deal)}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-teal-600 hover:text-teal-800 hover:underline">{deal.stock_number}</div>
                          <div className="text-xs text-slate-500">{deal.year} {deal.make} {deal.model}</div>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {saleTypeLabels[deal.sale_type]}
                          {deal.is_dealer_direct && <span className="ml-1 text-[10px] bg-teal-100 text-teal-700 px-1 rounded">DD</span>}
                        </td>
                        <td className="px-3 py-2 text-xs">{deal.buyer_name || '-'}</td>
                        <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                          <select
                            value={deal.vehicle_buyer || ''}
                            onChange={async (e) => {
                              await api.updateVehicleBuyer(deal.id, e.target.value);
                              const dealList = await api.getDeals();
                              setDeals(dealList || []);
                            }}
                            className="text-xs border border-slate-200 rounded px-1.5 py-0.5 bg-white"
                          >
                            <option value="">-</option>
                            <option value="Chris">Chris</option>
                            <option value="Alex">Alex</option>
                          </select>
                        </td>
                        <td className="px-3 py-2 text-right font-mono">${parseFloat(deal.sale_price || 0).toLocaleString()}</td>
                        <td className="px-3 py-2 text-center">
                          <QRCodePopup vehicle={{ stockNumber: deal.stock_number, year: deal.year, make: deal.make, model: deal.model, vin: deal.vin }} className="text-xs" />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <input type="checkbox" checked={deal.all_costs_added} onClick={e => e.stopPropagation()} onChange={e => handleChecklistUpdate(deal.id, 'all_costs_added', e.target.checked)} />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <input type="checkbox" checked={deal.title_sent} onClick={e => e.stopPropagation()} onChange={e => handleChecklistUpdate(deal.id, 'title_sent', e.target.checked)} />
                        </td>
                        <td className="px-3 py-2 text-center">
                          {needsSaleSheet ? (
                            <input type="checkbox" checked={deal.sale_sheet_printed} onClick={e => e.stopPropagation()} onChange={async e => {
                              await api.updateDealChecklistExtended(deal.id, 'sale_sheet_printed', e.target.checked);
                              const dealList = await api.getDeals();
                              setDeals(dealList || []);
                            }} />
                          ) : (
                            <span className="text-neutral-300">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <input type="checkbox" checked={deal.placed_in_pickup} onClick={e => e.stopPropagation()} onChange={async e => {
                            await api.updateDealChecklistExtended(deal.id, 'placed_in_pickup', e.target.checked);
                            const dealList = await api.getDeals();
                            setDeals(dealList || []);
                          }} />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <input type="checkbox" checked={deal.payment_received} onClick={e => e.stopPropagation()} onChange={e => {
                            if (e.target.checked) {
                              setMarkPaidPaymentMethod('Check');
                              setMarkPaidBatchDeposit(false);
                              setMarkPaidBankAccount('');
                              api.getBankAccounts().then(accts => setMarkPaidBankAccounts(accts || [])).catch(() => {});
                              setPendingPaidDeal(deal);
                            } else {
                              handleChecklistUpdate(deal.id, 'payment_received', false);
                            }
                          }} />
                        </td>
                        <td className="px-3 py-2 text-right flex gap-2 justify-end">
                          <button onClick={(e) => { e.stopPropagation(); setDealCostModal({ isOpen: true, deal }); }} className="text-xs text-amber-600 hover:text-amber-800">add recon/addtl</button>
                          <button onClick={(e) => { e.stopPropagation(); setEditDeal(deal); }} className="text-xs text-blue-500 hover:text-blue-700">edit</button>
                          {onEditVehicleFromDeal && canEditVehicle && (
                            <button onClick={(e) => { e.stopPropagation(); onEditVehicleFromDeal(deal.vehicle_id); setViewDeal(null); }} className="text-xs text-green-600 hover:text-green-800">vehicle</button>
                          )}
                          <button onClick={(e) => { e.stopPropagation(); setPrintPacketDeal(deal); }} className="text-xs text-green-500 hover:text-green-700">forms</button>
                          <a href="https://www.ohioautodealers.com" target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-xs text-amber-600 hover:text-amber-800">tag</a>
                          <button onClick={(e) => { e.stopPropagation(); handleUnwind(deal.id); }} className="text-xs text-red-500 hover:text-red-700">unwind</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {deals.length === 0 && <div className="text-center py-8 text-neutral-500">No sold units</div>}
            </div>
            </>
          )}

          {/* AR Tab */}
          {activeTab === 'ar' && (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-400 border-b border-slate-200 bg-slate-50">
                    <SortHeader id="stock" label="Vehicle" />
                    <SortHeader id="buyer" label="Buyer" />
                    <SortHeader id="date" label="Sale Date" />
                    <SortHeader id="price" label="Amount Owed" align="right" />
                    <SortHeader id="days" label="Days Outstanding" />
                  </tr>
                </thead>
                <tbody>
                  {filterAndSort(ar.deals || []).map(deal => {
                    const daysOut = deal.sale_date ? Math.floor((new Date() - new Date(deal.sale_date)) / (1000 * 60 * 60 * 24)) : 0;
                    const fullDeal = deals.find(d => d.id === deal.id) || deal;
                    const vehicleLabel = [fullDeal.year, fullDeal.make, fullDeal.model].filter(Boolean).join(' ') || fullDeal.stock_number || '-';
                    return (
                      <tr key={deal.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setViewDeal(fullDeal)}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-teal-600 hover:text-teal-800 hover:underline">{fullDeal.stock_number || '-'}</div>
                          <div className="text-xs text-slate-500">{vehicleLabel}</div>
                        </td>
                        <td className="px-3 py-2">{deal.buyer_name || '-'}</td>
                        <td className="px-3 py-2 text-xs">{deal.sale_date ? new Date(deal.sale_date).toLocaleDateString() : '-'}</td>
                        <td className="px-3 py-2 text-right font-mono font-medium">${parseFloat(deal.amount_owed || deal.balance_due || deal.total_due || deal.sale_price || 0).toLocaleString()}</td>
                        <td className="px-3 py-2">
                          <span className={`text-xs px-2 py-0.5 rounded ${daysOut > 30 ? 'bg-red-100 text-red-700' : daysOut > 14 ? 'bg-amber-100 text-amber-700' : 'bg-neutral-100'}`}>
                            {daysOut} days
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {ar.deals?.length === 0 && <div className="text-center py-8 text-neutral-500">No outstanding receivables</div>}
            </div>
          )}

          {/* Completed Tab */}
          {activeTab === 'completed' && (
            <>
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-slate-500">{paidDeals.length} completed deal{paidDeals.length !== 1 ? 's' : ''}</div>
                <button
                  onClick={() => setShowPrintReport(true)}
                  className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 text-sm font-medium"
                >
                  Print Report
                </button>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-400 border-b border-slate-200 bg-slate-50">
                      <SortHeader id="vehicle" label="Vehicle" />
                      <SortHeader id="type" label="Sale Type" />
                      <SortHeader id="buyer" label="Buyer" />
                      <SortHeader id="price" label="Sale Price" align="right" />
                      <SortHeader id="cost" label="Total Cost" align="right" />
                      <SortHeader id="profit" label="Profit" align="right" />
                      <th className="px-3 py-2 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterAndSort(paidDeals).map(deal => (
                      <tr key={deal.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setViewDeal(deal)}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-teal-600 hover:text-teal-800 hover:underline">{deal.stock_number}</div>
                          <div className="text-xs text-slate-500">{deal.year} {deal.make} {deal.model}</div>
                        </td>
                        <td className="px-3 py-2 text-xs">{saleTypeLabels[deal.sale_type]}</td>
                        <td className="px-3 py-2 text-xs">{deal.buyer_name || '-'}</td>
                        <td className="px-3 py-2 text-right font-mono">${parseFloat(deal.sale_price || 0).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-mono">${parseFloat(deal.total_cost || 0).toLocaleString()}</td>
                        <td className={`px-3 py-2 text-right font-mono font-medium ${(deal.profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          ${parseFloat(deal.profit || 0).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right flex gap-2 justify-end">
                          {onEditVehicleFromDeal && canEditVehicle && (
                            <button onClick={(e) => { e.stopPropagation(); onEditVehicleFromDeal(deal.vehicle_id); setViewDeal(null); }} className="text-xs text-green-600 hover:text-green-800">vehicle</button>
                          )}
                          <button onClick={(e) => { e.stopPropagation(); handleUnwind(deal.id); }} className="text-xs text-red-500 hover:text-red-700">unwind</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {paidDeals.length === 0 && <div className="text-center py-8 text-neutral-500">No completed deals</div>}
              </div>
            </>
          )}

          {/* Sold — No Deal Record Tab */}
          {activeTab === 'no_deal' && (
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                These vehicles have a sold/wholesale status in inventory but no deal record was created. Create a deal to track the sale properly.
              </div>
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-400 border-b border-slate-200 bg-slate-50">
                      <th className="px-3 py-2 font-medium">Stock #</th>
                      <th className="px-3 py-2 font-medium">Vehicle</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2 font-medium">VIN</th>
                      <th className="px-3 py-2 font-medium text-right">Purchase Price</th>
                      <th className="px-3 py-2 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {soldNoDeal.map(v => (
                      <tr key={v.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-3 py-2 font-mono font-medium">{v.stockNumber}</td>
                        <td className="px-3 py-2">{v.year} {v.make} {v.model}</td>
                        <td className="px-3 py-2"><span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700">{v.status}</span></td>
                        <td className="px-3 py-2 text-xs font-mono text-slate-500">{v.vin || '-'}</td>
                        <td className="px-3 py-2 text-right font-mono">${parseFloat(v.purchasePrice || 0).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => setShowSaleTypeChoice(v)}
                            className="text-xs bg-teal-600 text-white px-3 py-1 rounded hover:bg-teal-500 font-medium"
                          >
                            Create Deal
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {soldNoDeal.length === 0 && <div className="text-center py-8 text-neutral-500">All sold vehicles have deal records</div>}
              </div>
            </div>
          )}

          {/* All Inventory Tab */}
          {activeTab === 'inventory' && (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-400 border-b border-slate-200 bg-slate-50">
                    <th className="px-3 py-2 font-medium">Stock #</th>
                    <th className="px-3 py-2 font-medium">Vehicle</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium text-right">Est. Sale</th>
                    <th className="px-3 py-2 font-medium text-right">Total Cost</th>
                    <th className="px-3 py-2 font-medium text-right">Est. Profit</th>
                    <th className="px-3 py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {availableVehicles.map(v => {
                    const packFee = parseFloat(v.packFee) || ((v.endUse === 'copart' || v.endUse === 'salvage') ? 75 : 325);
                    const totalCost = parseFloat(v.purchasePrice || 0) +
                      parseFloat(v.buyerFee || 0) +
                      parseFloat(v.inputFee || 0) +
                      parseFloat(v.reconCost || 0) +
                      parseFloat(v.transportCost || 0) +
                      packFee;
                    const estProfit = parseFloat(v.estimatedSale || 0) - totalCost;
                    return (
                      <tr key={v.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-3 py-2 font-mono">{v.stockNumber}</td>
                        <td className="px-3 py-2">{v.year} {v.make} {v.model}</td>
                        <td className="px-3 py-2"><span className="text-xs px-2 py-0.5 rounded bg-slate-100">{v.status}</span></td>
                        <td className="px-3 py-2 text-right font-mono">${parseFloat(v.estimatedSale || 0).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-mono">${totalCost.toLocaleString()}</td>
                        <td className={`px-3 py-2 text-right font-mono font-medium ${estProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          ${estProfit.toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => setShowSaleTypeChoice(v)} className="text-xs bg-neutral-800 text-white px-2 py-1 rounded hover:bg-neutral-700">Sell</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {availableVehicles.length === 0 && <div className="text-center py-8 text-neutral-500">No vehicles in inventory</div>}
            </div>
          )}
        </>
      )}

      {/* Batch Deposit Modal for multiple deals */}
      {showBatchDepositModal && (
        <BatchDealDepositModal
          deals={deals.filter(d => selectedBatchDealIds.includes(d.id))}
          bankAccounts={markPaidBankAccounts}
          onLoadBankAccounts={async () => {
            try {
              const accts = await api.getBankAccounts();
              setMarkPaidBankAccounts(accts || []);
            } catch (e) {
              console.error('Failed to load bank accounts for batch deposit:', e);
            }
          }}
          onClose={() => setShowBatchDepositModal(false)}
          onSave={async ({ bankAccountId, paymentMethod, batchReference, notes, extraDeposits }) => {
            try {
              saveScroll();
              await api.batchMarkDealsPaid({
                dealIds: selectedBatchDealIds,
                paymentMethod,
                bankAccountId,
                batchReference,
                notes,
                extraDeposits,
              });
              setShowBatchDepositModal(false);
              setSelectedBatchDealIds([]);
              const [dealList, arData] = await Promise.all([
                api.getDeals(),
                api.getAccountsReceivable()
              ]);
              setDeals(dealList || []);
              setAR(arData || { deals: [], totalAR: 0, count: 0 });
              onRefresh?.();
              restoreScroll();
            } catch (e) {
              let msg = e.message || 'Batch deposit failed.';
              if (e.skippedDeals && e.skippedDeals.length > 0) {
                const details = e.skippedDeals
                  .map(s => `• ${s.label || `Deal #${s.id}`}: ${s.reason}`)
                  .join('\n');
                msg += `\n\nSkipped deals:\n${details}`;
              }
              alert(msg);
            }
          }}
        />
      )}

      {/* Sale Type Choice Modal with Vehicle Search */}
      {showSaleTypeChoice && (
        <SaleTypeChoiceModal
          selectedVehicle={showSaleTypeChoice}
          availableVehicles={availableVehicles}
          onSelectVehicle={setShowSaleTypeChoice}
          onRetail={(v) => { setShowRetailModal(v); setShowSaleTypeChoice(null); }}
          onWholesale={(v) => { setShowWholesaleModal(v); setShowSaleTypeChoice(null); }}
          onClose={() => setShowSaleTypeChoice(null)}
        />
      )}

      {/* Retail Sale Modal */}
      {showRetailModal && (
        <RetailSaleModal
          vehicle={showRetailModal}
          onClose={() => setShowRetailModal(null)}
          onComplete={() => {
            setShowRetailModal(null);
            onRefresh();
            // Reload deals
            api.getDeals().then(setDeals);
          }}
          onSaveAndNew={() => {
            setShowRetailModal(null);
            reopenNewSaleChooser();
            // Refresh Deal Desk data in background without forcing global module rerenders.
            Promise.all([api.getDeals(), api.getAccountsReceivable()])
              .then(([dealList, arData]) => {
                setDeals(dealList || []);
                setAR(arData || { deals: [], totalAR: 0, count: 0 });
              })
              .catch((e) => console.error('[Deal Desk] Save & New refresh failed:', e));
          }}
        />
      )}

      {/* Wholesale Sale Modal */}
      {showWholesaleModal && (
        <WholesaleSaleModal
          vehicle={showWholesaleModal}
          onClose={() => setShowWholesaleModal(null)}
          onDealCreated={() => {
            // Intentionally defer parent refresh until complete to avoid
            // wholesale modal remount race that can hide complete actions.
          }}
          onComplete={() => {
            setShowWholesaleModal(null);
            saveScroll();
            onRefresh();
            api.getDeals().then((data) => {
              setDeals(data || []);
              restoreScroll();
            });
          }}
          onSaveAndNew={() => {
            setShowWholesaleModal(null);
            saveScroll();
            reopenNewSaleChooser();
            // Refresh Deal Desk data in background without forcing global module rerenders.
            Promise.all([api.getDeals(), api.getAccountsReceivable()])
              .then(([dealList, arData]) => {
                setDeals(dealList || []);
                setAR(arData || { deals: [], totalAR: 0, count: 0 });
                restoreScroll();
              })
              .catch((e) => console.error('[Deal Desk] Save & New refresh failed:', e));
          }}
        />
      )}

      {/* Legacy Sell Vehicle Modal (for quick wholesale from old flow) */}
      {showSellModal && (
        <SellVehicleModal
          vehicle={showSellModal.id ? showSellModal : null}
          vehicles={availableVehicles}
          onClose={() => setShowSellModal(null)}
          onSave={handleCreateDeal}
        />
      )}

      {/* Deal Detail View Modal */}
      {viewDeal && (
        <DealDetailModal
          deal={viewDeal}
          onClose={() => setViewDeal(null)}
          onEdit={(deal) => setEditDeal(deal)}
          onEditVehicle={canEditVehicle && onEditVehicleFromDeal ? () => onEditVehicleFromDeal(viewDeal.vehicle_id) : null}
          onSendToBhph={onSendToBhph}
          canAccessBhph={canAccessBhph}
          onRefresh={async () => {
            saveScroll();
            const [dealsData, arData] = await Promise.all([api.getDeals(), api.getAccountsReceivable()]);
            setDeals(dealsData || []);
            setAR(arData || { deals: [], totalAR: 0, count: 0 });
            restoreScroll();
          }}
        />
      )}

      {/* Pre-Print screen for the row-level "forms" links */}
      {printPacketDeal && (
        <PrintPacketModal mode="deal" deal={printPacketDeal} onClose={() => setPrintPacketDeal(null)} />
      )}

      {/* Mark Paid Modal - when checking Paid in Active Deals */}
      {pendingPaidDeal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setPendingPaidDeal(null)}>
          <div className="bg-white w-full max-w-sm rounded-lg shadow-xl p-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-lg mb-3">{t('dealDesk.markAsPaid')}</h3>
            <p className="text-sm text-neutral-600 mb-4">
              {pendingPaidDeal.stock_number} — {pendingPaidDeal.year} {pendingPaidDeal.make} {pendingPaidDeal.model}
            </p>
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">{t('dealDesk.paymentMethod')} *</label>
                <select
                  value={markPaidPaymentMethod}
                  onChange={e => setMarkPaidPaymentMethod(e.target.value)}
                  className="w-full h-9 px-2 text-sm border border-neutral-200 rounded bg-white"
                >
                  <option value="Check">Check</option>
                  <option value="ACH">ACH</option>
                  <option value="Wire">Wire</option>
                  <option value="Cash">Cash</option>
                  <option value="Cashier's Check">Cashier's Check</option>
                  <option value="Money Order">Money Order</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">{t('dealDesk.depositTo')} *</label>
                <select
                  value={markPaidBankAccount}
                  onChange={e => setMarkPaidBankAccount(e.target.value)}
                  className="w-full h-9 px-2 text-sm border border-neutral-200 rounded bg-white"
                >
                  <option value="">{t('dealDesk.primaryAccount')}</option>
                  {markPaidBankAccounts.map(a => (
                    <option key={a.id} value={a.id}>{a.name}{a.accountType ? ` (${a.accountType})` : ''}</option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={markPaidBatchDeposit} onChange={e => setMarkPaidBatchDeposit(e.target.checked)} />
                <span className="text-sm">{t('dealDesk.batchDeposit')}</span>
              </label>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setPendingPaidDeal(null)} className="px-3 py-1.5 text-sm text-neutral-600 hover:bg-neutral-100 rounded">{t('actions.cancel')}</button>
              <button
                onClick={async () => {
                  try {
                    const payload = {
                      payment_method: markPaidPaymentMethod,
                      is_batch_deposit: markPaidBatchDeposit,
                      bank_account_id: markPaidBankAccount || undefined
                    };
                    await api.markDealPaidChecklist(pendingPaidDeal.id, payload);
                    const [dealsData, arData] = await Promise.all([api.getDeals(), api.getAccountsReceivable()]);
                    setDeals(dealsData || []);
                    setAR(arData || { deals: [], totalAR: 0, count: 0 });
                    setPendingPaidDeal(null);
                    onRefresh?.();
                  } catch (e) {
                    alert('Failed to mark paid: ' + e.message);
                  }
                }}
                className="px-4 py-1.5 text-sm bg-neutral-800 text-white rounded hover:bg-neutral-700"
              >
                {t('dealDesk.confirmPaid')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AR Breakdown Modal */}
      {showARBreakdown && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowARBreakdown(false)}>
          <div className="bg-white w-full max-w-4xl rounded-lg shadow-xl max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-neutral-200 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">Accounts Receivable Breakdown</h3>
                <div className="text-sm text-neutral-500">
                  Total AR: <span className="font-bold text-amber-800">${ar.totalAR?.toLocaleString() || 0}</span>
                  <span className="ml-4">Deals in AR: {ar.count || 0}</span>
                  <span className="ml-4">Total unpaid deals: {deals.filter(d => !d.payment_received).length}</span>
                </div>
              </div>
              <button onClick={() => setShowARBreakdown(false)} className="text-neutral-400 hover:text-neutral-600 text-xl">×</button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              <div className="mb-4">
                <h4 className="font-medium text-sm mb-2">Deals in AR ({ar.deals?.length || 0}):</h4>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200">
                      <th className="px-3 py-2 font-medium">Vehicle</th>
                      <th className="px-3 py-2 font-medium">Buyer</th>
                      <th className="px-3 py-2 font-medium text-right">Sale Price</th>
                      <th className="px-3 py-2 font-medium text-right">Balance Due</th>
                      <th className="px-3 py-2 font-medium text-right">Total Due</th>
                      <th className="px-3 py-2 font-medium text-right">Amount Owed</th>
                      <th className="px-3 py-2 font-medium">Payment Received</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(ar.deals || []).map(deal => {
                      const amountOwed = parseFloat(deal.amount_owed || deal.balance_due || deal.total_due || deal.sale_price || 0);
                      return (
                        <tr key={deal.id} className="border-b border-neutral-100">
                          <td className="px-3 py-2 font-medium">{deal.stock_number}</td>
                          <td className="px-3 py-2">{deal.buyer_name || '-'}</td>
                          <td className="px-3 py-2 text-right font-mono">${parseFloat(deal.sale_price || 0).toLocaleString()}</td>
                          <td className="px-3 py-2 text-right font-mono">{deal.balance_due !== null && deal.balance_due !== undefined ? `$${parseFloat(deal.balance_due || 0).toLocaleString()}` : 'NULL'}</td>
                          <td className="px-3 py-2 text-right font-mono">{deal.total_due !== null && deal.total_due !== undefined ? `$${parseFloat(deal.total_due || 0).toLocaleString()}` : 'NULL'}</td>
                          <td className="px-3 py-2 text-right font-mono font-bold">${amountOwed.toLocaleString()}</td>
                          <td className="px-3 py-2 text-center">{deal.payment_received ? '✓' : '✗'}</td>
                        </tr>
                      );
                    })}
                    {(!ar.deals || ar.deals.length === 0) && (
                      <tr>
                        <td colSpan={7} className="px-3 py-8 text-center text-neutral-500">No deals in AR</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              <div className="mt-6 border-t pt-4">
                <h4 className="font-medium text-sm mb-2 text-red-600">Missing from AR (unpaid but not showing):</h4>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200">
                      <th className="px-3 py-2 font-medium">Vehicle</th>
                      <th className="px-3 py-2 font-medium">Buyer</th>
                      <th className="px-3 py-2 font-medium text-right">Sale Price</th>
                      <th className="px-3 py-2 font-medium text-right">Balance Due</th>
                      <th className="px-3 py-2 font-medium text-right">Total Due</th>
                      <th className="px-3 py-2 font-medium">Payment Received</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deals.filter(d => !d.payment_received && !ar.deals?.some(ar => ar.id === d.id)).map(deal => (
                      <tr key={deal.id} className="border-b border-neutral-100 bg-red-50">
                        <td className="px-3 py-2 font-medium">{deal.stock_number}</td>
                        <td className="px-3 py-2">{deal.buyer_name || '-'}</td>
                        <td className="px-3 py-2 text-right font-mono">${parseFloat(deal.sale_price || 0).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-mono">{deal.balance_due !== null && deal.balance_due !== undefined ? `$${parseFloat(deal.balance_due || 0).toLocaleString()}` : 'NULL'}</td>
                        <td className="px-3 py-2 text-right font-mono">{deal.total_due !== null && deal.total_due !== undefined ? `$${parseFloat(deal.total_due || 0).toLocaleString()}` : 'NULL'}</td>
                        <td className="px-3 py-2 text-center">{deal.payment_received ? '✓' : '✗'}</td>
                        <td className="px-3 py-2 text-xs">{deal.status || '-'}</td>
                      </tr>
                    ))}
                    {deals.filter(d => !d.payment_received && !ar.deals?.some(ar => ar.id === d.id)).length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-3 py-4 text-center text-neutral-500">All unpaid deals are in AR</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              <div className="mt-4 border-t pt-4">
                <div className="text-xs text-neutral-500">
                  <div className="font-medium mb-1">Calculation Logic:</div>
                  <div>Amount Owed = balance_due (if > 0) OR total_due (if > 0) OR sale_price</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Print Report Modal - Completed Deals */}
      {showPrintReport && (
        <CompletedDealsReport
          onClose={() => setShowPrintReport(false)}
          startDate={reportStartDate}
          endDate={reportEndDate}
          onStartDateChange={setReportStartDate}
          onEndDateChange={setReportEndDate}
          all={true}
          title="All Deals Report"
        />
      )}

      {/* Print Report Modal - All Sales */}
      {showAllSalesReport && (
        <CompletedDealsReport
          onClose={() => setShowAllSalesReport(false)}
          startDate={reportStartDate}
          endDate={reportEndDate}
          onStartDateChange={setReportStartDate}
          onEndDateChange={setReportEndDate}
          all={true}
          title="All Sales Report"
        />
      )}

      {dealCostModal.isOpen && dealCostModal.deal && (
        <AddDealCostModal
          deal={dealCostModal.deal}
          onClose={() => setDealCostModal({ isOpen: false, deal: null })}
          onSave={handleAddCostToDealVehicle}
        />
      )}

      {/* Edit Deal Modal */}
      {editDeal && (
        <EditDealModal
          deal={editDeal}
          onClose={() => setEditDeal(null)}
          onSendToBhph={onSendToBhph}
          canAccessBhph={canAccessBhph}
          onSave={async (updatedDeal) => {
            setEditDeal(null);
            // Reload deals
            const [dealsData, arData] = await Promise.all([api.getDeals(), api.getAccountsReceivable()]);
            setDeals(dealsData || []);
            setAR(arData || { deals: [], totalAR: 0, count: 0 });
          }}
        />
      )}
    </div>
  );
};

// Sell Vehicle Modal
const SellVehicleModal = ({ vehicle, vehicles, onClose, onSave }) => {
  const [form, setForm] = useState({
    vehicleId: vehicle?.id || '',
    saleType: 'retail',
    buyerFirstName: '',
    buyerMiddleName: '',
    buyerLastName: '',
    buyerContact: '',
    buyerType: 'person',
    salePrice: vehicle?.estimatedSale || '',
    saleDate: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [savedCustomers, setSavedCustomers] = useState([]);
  const [savingCustomer, setSavingCustomer] = useState(false);

  useEffect(() => {
    api.getDealCustomers().then(setSavedCustomers).catch(() => setSavedCustomers([]));
  }, []);

  const handleSelectCustomer = (cust) => {
    if (!cust) return;
    const parts = (cust.name || '').trim().split(/\s+/).filter(Boolean);
    const first = parts[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1] : '';
    const middle = parts.length > 2 ? parts.slice(1, -1).join(' ') : '';
    setForm(f => ({ ...f, buyerFirstName: first, buyerMiddleName: middle, buyerLastName: last, buyerContact: cust.phone || cust.email || '' }));
  };

  const handleSaveAsCustomer = async () => {
    const buyerName = [form.buyerFirstName, form.buyerMiddleName, form.buyerLastName].filter(Boolean).join(' ').trim();
    if (!buyerName) {
      alert('Enter buyer name first');
      return;
    }
    setSavingCustomer(true);
    try {
      await api.saveDealCustomer({ name: buyerName, phone: form.buyerContact || null });
      const updated = await api.getDealCustomers();
      setSavedCustomers(updated || []);
      alert('Customer saved for future use');
    } catch (e) {
      alert('Failed to save: ' + e.message);
    } finally {
      setSavingCustomer(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.vehicleId || !form.salePrice) {
      return alert('Vehicle and sale price are required');
    }
    setSaving(true);
    try {
      const buyerName = [form.buyerFirstName, form.buyerMiddleName, form.buyerLastName].filter(Boolean).join(' ').trim();
      await onSave({ ...form, buyerName });
    } finally {
      setSaving(false);
    }
  };

  const packAmounts = { retail: 0, wholesale_direct: 325, wholesale_auction: 325, copart: 75 };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white w-full max-w-lg rounded-lg shadow-lg" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-neutral-200 flex justify-between items-center">
          <span className="font-medium">Record Sale</span>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600">x</button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {!vehicle && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Vehicle</label>
              <select value={form.vehicleId} onChange={e => setForm(f => ({ ...f, vehicleId: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" required>
                <option value="">Select vehicle...</option>
                {vehicles.map(v => (
                  <option key={v.id} value={v.id}>{v.stockNumber} - {v.year} {v.make} {v.model}</option>
                ))}
              </select>
            </div>
          )}
          {vehicle && (
            <div className="bg-neutral-50 p-3 rounded-lg">
              <div className="font-medium">{vehicle.stockNumber}</div>
              <div className="text-sm text-neutral-600">{vehicle.year} {vehicle.make} {vehicle.model}</div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Sale Type</label>
              <select value={form.saleType} onChange={e => setForm(f => ({ ...f, saleType: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg">
                <option value="retail">Retail</option>
                <option value="wholesale_direct">Wholesale Direct</option>
                <option value="wholesale_auction">Wholesale Auction</option>
                <option value="copart">Copart</option>
              </select>
              <div className="text-xs text-neutral-500 mt-1">Pack: ${packAmounts[form.saleType]}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Sale Price</label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-neutral-500">$</span>
                <input type="number" value={form.salePrice} onChange={e => setForm(f => ({ ...f, salePrice: e.target.value }))} className="w-full pl-7 pr-3 py-2 border border-neutral-200 rounded-lg" required />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-neutral-700 mb-1">Buyer Name</label>
              <div className="flex gap-2 mb-2">
                <select
                  value=""
                  onChange={e => { const v = e.target.value; if (v) { const c = savedCustomers.find(x => x.id === parseInt(v)); if (c) handleSelectCustomer(c); } }}
                  className="flex-shrink-0 w-32 px-2 py-2 border border-neutral-200 rounded-lg text-sm bg-neutral-50"
                >
                  <option value="">Saved...</option>
                  {savedCustomers.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <button type="button" onClick={handleSaveAsCustomer} disabled={savingCustomer || !form.buyerFirstName?.trim()} className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded hover:bg-green-200 disabled:opacity-50" title="Save for future deals">Save</button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">First *</label>
                  <input type="text" tabIndex={1} value={form.buyerFirstName} onChange={e => setForm(f => ({ ...f, buyerFirstName: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" placeholder="First" />
                </div>
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">Middle</label>
                  <input type="text" tabIndex={2} value={form.buyerMiddleName} onChange={e => setForm(f => ({ ...f, buyerMiddleName: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" placeholder="Middle" />
                </div>
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">Last *</label>
                  <input type="text" tabIndex={3} value={form.buyerLastName} onChange={e => setForm(f => ({ ...f, buyerLastName: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" placeholder="Last" />
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Buyer Type</label>
              <select value={form.buyerType} onChange={e => setForm(f => ({ ...f, buyerType: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg">
                <option value="person">Person</option>
                <option value="dealer">Dealer</option>
                <option value="auction">Auction</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Sale Date</label>
            <input type="date" value={form.saleDate} onChange={e => setForm(f => ({ ...f, saleDate: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" rows={2} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-neutral-600 hover:text-neutral-800">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 bg-neutral-800 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50">{saving ? 'Saving...' : 'Record Sale'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Retail Sale Modal - Full form pack for retail sales
const RetailSaleModal = ({ vehicle, onClose, onComplete, onSaveAndNew, onRefresh }) => {
  const [step, setStep] = useState(1); // 1: buyer info, 2: pricing, 3: forms, 4: complete
  const [loading, setLoading] = useState(false);
  const [taxRates, setTaxRates] = useState({});
  const [dealerInfo, setDealerInfo] = useState({});
  const [calculation, setCalculation] = useState(null);
  const [deal, setDeal] = useState(null);
  const [savedCustomers, setSavedCustomers] = useState([]);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [odometer, setOdometer] = useState(vehicle?.mileage || '');
  
  const [buyer, setBuyer] = useState({
    firstName: '', middleName: '', lastName: '',
    address: '', city: '', state: 'OH', zip: '',
    phone: '', email: '', dlNumber: '', dlState: 'OH', dlExpiration: ''
  });
  
  const [pricing, setPricing] = useState({
    purchasePrice: vehicle?.estimatedSale || '',
    tradeInValue: 0,
    county: 'Lucas',
    isOutOfState: false,
    outOfStateTaxRate: 0, // buyer's home-state rate, typed manually (e.g. Michigan 6)
    docFee: 300
  });

  // Editable fees with defaults
  const [fees, setFees] = useState([
    { id: 1, name: 'Temp Tag', amount: 20, isDefault: true },
    { id: 2, name: 'Title Fee', amount: 25, isDefault: true },
    { id: 3, name: 'Lien Fee', amount: 50, isDefault: true }
  ]);

  // Dealer Direct / Balance Due options
  const [isDealerDirect, setIsDealerDirect] = useState(false);
  const [hasBalanceDue, setHasBalanceDue] = useState(false);
  const [depositAmount, setDepositAmount] = useState(0);

  // Pre-Print screen (unified print flow)
  const [showPrintPacket, setShowPrintPacket] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [rates, dealer, customers] = await Promise.all([
          api.getTaxRates(),
          api.getDealerInfo(),
          api.getDealCustomers().catch(() => [])
        ]);
        setTaxRates(rates);
        setDealerInfo(dealer);
        setSavedCustomers(customers || []);
      } catch (e) {
        console.error('Failed to load data:', e);
      }
    };
    loadData();
  }, []);

  const handleSelectCustomer = (c) => {
    if (!c) return;
    const parts = (c.name || '').trim().split(/\s+/).filter(Boolean);
    const first = parts[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1] : '';
    const middle = parts.length > 2 ? parts.slice(1, -1).join(' ') : '';
    setBuyer(b => ({
      ...b,
      firstName: first,
      middleName: middle,
      lastName: last,
      address: c.address || '',
      city: c.city || '',
      state: c.state || b.state || 'OH',
      zip: c.zip || '',
      phone: c.phone || '',
      email: c.email || '',
      dlNumber: c.dl_number || c.dlNumber || '',
      dlState: c.dl_state || c.dlState || b.dlState || 'OH'
    }));
  };

  const handleSaveAsCustomer = async () => {
    const buyerName = [buyer.firstName, buyer.middleName, buyer.lastName].filter(Boolean).join(' ').trim();
    if (!buyerName) {
      alert('Enter buyer name first');
      return;
    }
    setSavingCustomer(true);
    try {
      await api.saveDealCustomer({
        name: buyerName,
        address: buyer.address || null,
        city: buyer.city || null,
        state: buyer.state || null,
        zip: buyer.zip || null,
        phone: buyer.phone || null,
        email: buyer.email || null,
        dlNumber: buyer.dlNumber || null,
        dlState: buyer.dlState || null
      });
      const updated = await api.getDealCustomers();
      setSavedCustomers(updated || []);
      alert('Customer saved for future use');
    } catch (e) {
      alert('Failed to save: ' + e.message);
    } finally {
      setSavingCustomer(false);
    }
  };

  const calculateTotals = async () => {
    if (!pricing.purchasePrice) return;
    setLoading(true);
    try {
      const result = await api.calculateSale({
        vehicleId: vehicle.id,
        saleType: 'retail',
        ...pricing
      });
      setCalculation(result);
    } catch (e) {
      alert('Calculation error: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (step === 2 && pricing.purchasePrice) {
      calculateTotals();
    }
  }, [step, pricing.purchasePrice, pricing.county, pricing.isOutOfState, pricing.outOfStateTaxRate, pricing.tradeInValue, pricing.docFee]);

  const handleCreateDeal = async () => {
    setLoading(true);
    try {
      // Update vehicle mileage if odometer was changed
      if (odometer && parseInt(odometer) !== parseInt(vehicle?.mileage || 0)) {
        await api.updateVehicle(vehicle.id, { mileage: parseInt(odometer) }).catch(e => console.warn('Failed to update odometer:', e));
      }
      const buyerName = [buyer.firstName, buyer.middleName, buyer.lastName].filter(Boolean).join(' ').trim();
      const result = await api.createRetailSale({
        vehicleId: vehicle.id,
        buyer: { ...buyer, name: buyerName },
        ...pricing,
        fees: fees.filter(f => f.name && f.amount > 0).map(f => ({ name: f.name, amount: f.amount })),
        isDealerDirect,
        depositAmount: hasBalanceDue ? depositAmount : 0,
        odometer: parseInt(odometer) || 0
      });
      setDeal(result.deal);
      setStep(3);
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteSale = async () => {
    if (!window.confirm('Confirm payment received and complete this sale?')) return;
    setLoading(true);
    try {
      await api.completeRetailSale(deal.id);
      alert('Sale completed! Vehicle marked as sold.');
      onComplete?.();
      onClose?.();
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const printSingleForm = async (formType) => {
    // For single forms, still use the old endpoint for now (or could be updated to use specific template)
    try {
      const response = await fetch(`${api.baseUrl}/sales/forms/single/${formType}/${deal.id}`, {
        headers: { 'Authorization': `Bearer ${api.accessToken || localStorage.getItem('lotsense_access_token')}` }
      });
      if (!response.ok) throw new Error('Failed to download');
      const blob = await response.blob();
      window.open(window.URL.createObjectURL(blob), '_blank');
    } catch (err) { alert('Failed: ' + err.message); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-lg shadow-xl overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-4 border-b border-neutral-200 flex justify-between items-center bg-neutral-50">
          <div>
            <div className="font-semibold text-lg">Retail Sale</div>
            <div className="text-sm text-neutral-500">{vehicle?.stockNumber} - {vehicle?.year} {vehicle?.make} {vehicle?.model}</div>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-2xl">&times;</button>
        </div>

        {/* Progress Steps */}
        <div className="flex border-b border-neutral-200">
          {['Buyer Info', 'Pricing', 'Forms', 'Complete'].map((label, i) => (
            <div key={i} className={`flex-1 py-3 text-center text-sm ${step === i + 1 ? 'bg-blue-50 text-blue-700 font-medium border-b-2 border-blue-600' : step > i + 1 ? 'text-green-600' : 'text-neutral-400'}`}>
              {step > i + 1 ? '✓ ' : ''}{label}
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Buyer Info */}
          {step === 1 && (
            <div className="space-y-4 max-w-2xl">
              {/* Odometer Verification */}
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">⚠️</span>
                  <span className="font-semibold text-amber-800">Verify Odometer Before Selling</span>
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-sm text-amber-700 font-medium whitespace-nowrap">Current Reading:</label>
                  <input
                    type="number"
                    value={odometer}
                    onChange={e => setOdometer(e.target.value)}
                    className="px-3 py-2 border-2 border-amber-400 rounded-lg text-lg font-mono font-bold w-40 bg-white focus:border-amber-600 focus:outline-none"
                    placeholder="Miles"
                  />
                  <span className="text-sm text-amber-700">miles</span>
                  {odometer && vehicle?.mileage && parseInt(odometer) !== parseInt(vehicle.mileage) && (
                    <span className="text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded">Changed from {Number(vehicle.mileage).toLocaleString()}</span>
                  )}
                </div>
              </div>

              <h3 className="font-medium text-lg">Buyer Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Full Name *</label>
                  <div className="flex gap-2 mb-2">
                    <select
                      value=""
                      onChange={e => { const v = e.target.value; if (v) { const c = savedCustomers.find(x => x.id === parseInt(v)); if (c) handleSelectCustomer(c); } }}
                      className="flex-shrink-0 w-32 px-2 py-2 border border-neutral-200 rounded-lg text-sm bg-neutral-50"
                    >
                      <option value="">Saved...</option>
                      {savedCustomers.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    <button type="button" onClick={handleSaveAsCustomer} disabled={savingCustomer || !buyer.firstName?.trim()} className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded hover:bg-green-200 disabled:opacity-50" title="Save for future deals">Save</button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs text-neutral-500 mb-1">First *</label>
                      <input type="text" tabIndex={1} value={buyer.firstName} onChange={e => setBuyer(b => ({ ...b, firstName: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" placeholder="First" required />
                    </div>
                    <div>
                      <label className="block text-xs text-neutral-500 mb-1">Middle</label>
                      <input type="text" tabIndex={2} value={buyer.middleName} onChange={e => setBuyer(b => ({ ...b, middleName: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" placeholder="Middle" />
                    </div>
                    <div>
                      <label className="block text-xs text-neutral-500 mb-1">Last *</label>
                      <input type="text" tabIndex={3} value={buyer.lastName} onChange={e => setBuyer(b => ({ ...b, lastName: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" placeholder="Last" required />
                    </div>
                  </div>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Street Address *</label>
                  <input type="text" value={buyer.address} onChange={e => setBuyer(b => ({ ...b, address: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">City *</label>
                  <input type="text" value={buyer.city} onChange={e => setBuyer(b => ({ ...b, city: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" required />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium mb-1">State *</label>
                    <input type="text" value={buyer.state} onChange={e => setBuyer(b => ({ ...b, state: e.target.value.toUpperCase() }))} maxLength={2} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">ZIP *</label>
                    <input type="text" value={buyer.zip} onChange={e => setBuyer(b => ({ ...b, zip: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" required />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <input type="tel" value={buyer.phone} onChange={e => setBuyer(b => ({ ...b, phone: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input type="email" value={buyer.email} onChange={e => setBuyer(b => ({ ...b, email: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" />
                </div>
              </div>
              <h4 className="font-medium mt-6">Driver's License</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">DL Number</label>
                  <input type="text" value={buyer.dlNumber} onChange={e => setBuyer(b => ({ ...b, dlNumber: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">DL State</label>
                  <input type="text" value={buyer.dlState} onChange={e => setBuyer(b => ({ ...b, dlState: e.target.value.toUpperCase() }))} maxLength={2} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Expiration</label>
                  <input type="date" value={buyer.dlExpiration} onChange={e => setBuyer(b => ({ ...b, dlExpiration: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Pricing */}
          {step === 2 && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-medium text-lg">Sale Details</h3>
                  <div>
                    <label className="block text-sm font-medium mb-1">Purchase Price *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-neutral-500">$</span>
                      <input type="number" value={pricing.purchasePrice} onChange={e => setPricing(p => ({ ...p, purchasePrice: parseFloat(e.target.value) || 0 }))} className="w-full pl-7 pr-3 py-2 border border-neutral-200 rounded-lg" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Trade-In Value</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-neutral-500">$</span>
                      <input type="number" value={pricing.tradeInValue} onChange={e => setPricing(p => ({ ...p, tradeInValue: parseFloat(e.target.value) || 0 }))} className="w-full pl-7 pr-3 py-2 border border-neutral-200 rounded-lg" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Doc Fee</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-neutral-500">$</span>
                      <input type="number" value={pricing.docFee} onChange={e => setPricing(p => ({ ...p, docFee: parseFloat(e.target.value) || 0 }))} className="w-full pl-7 pr-3 py-2 border border-neutral-200 rounded-lg" />
                    </div>
                  </div>
                  
                  {/* Editable Fees Section */}
                  <div className="border-t border-neutral-200 pt-4">
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium">Additional Fees</label>
                      <button 
                        type="button"
                        onClick={() => setFees(f => [...f, { id: Date.now(), name: '', amount: 0, isDefault: false }])}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        + Add Fee
                      </button>
                    </div>
                    <div className="space-y-2">
                      {fees.map((fee, idx) => (
                        <div key={fee.id} className="flex gap-2 items-center">
                          <input 
                            type="text" 
                            value={fee.name} 
                            onChange={e => setFees(f => f.map(x => x.id === fee.id ? {...x, name: e.target.value} : x))}
                            placeholder="Fee name"
                            className="flex-1 px-2 py-1 text-sm border border-neutral-200 rounded"
                          />
                          <div className="relative w-24">
                            <span className="absolute left-2 top-1 text-neutral-500 text-sm">$</span>
                            <input 
                              type="number" 
                              value={fee.amount} 
                              onChange={e => setFees(f => f.map(x => x.id === fee.id ? {...x, amount: parseFloat(e.target.value) || 0} : x))}
                              className="w-full pl-5 pr-2 py-1 text-sm border border-neutral-200 rounded"
                            />
                          </div>
                          <button 
                            type="button"
                            onClick={() => setFees(f => f.filter(x => x.id !== fee.id))}
                            className="text-red-500 hover:text-red-700 text-sm px-1"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={pricing.isOutOfState} onChange={e => setPricing(p => ({ ...p, isOutOfState: e.target.checked }))} />
                      <span className="text-sm">Out of State Buyer</span>
                    </label>
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={isDealerDirect} onChange={e => setIsDealerDirect(e.target.checked)} />
                      <span className="text-sm">Dealer Direct / Auction Sale</span>
                    </label>
                  </div>
                  {pricing.isOutOfState ? (
                    <div>
                      <label className="block text-sm font-medium mb-1">Tax Rate % <span className="text-neutral-400 font-normal">(type it — Michigan = 6, tax-exempt = 0)</span></label>
                      <input
                        type="number" step="0.25" min="0" max="15"
                        value={pricing.outOfStateTaxRate}
                        onChange={e => setPricing(p => ({ ...p, outOfStateTaxRate: e.target.value === '' ? '' : parseFloat(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border border-neutral-200 rounded-lg font-mono"
                        placeholder="0"
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium mb-1">Tax County</label>
                      <select value={pricing.county} onChange={e => setPricing(p => ({ ...p, county: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg">
                        {taxRates.counties?.map(c => (
                          <option key={c} value={c}>{c} ({taxRates.rates?.[c]}%)</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Calculation Summary */}
                <div className="bg-neutral-50 rounded-lg p-4">
                  <h3 className="font-medium text-lg mb-4">Sale Summary</h3>
                  {loading ? (
                    <div className="text-center py-8 text-neutral-500">Calculating...</div>
                  ) : calculation ? (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span>Purchase Price:</span><span className="font-mono">${calculation.sale.purchasePrice.toLocaleString()}</span></div>
                      {calculation.sale.tradeInValue > 0 && <div className="flex justify-between text-green-600"><span>Trade-In:</span><span className="font-mono">-${calculation.sale.tradeInValue.toLocaleString()}</span></div>}
                      <div className="flex justify-between"><span>Taxable Amount:</span><span className="font-mono">${calculation.sale.taxableAmount.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span>Sales Tax ({calculation.sale.taxRate}%):</span><span className="font-mono">${calculation.sale.salesTax.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span>Doc Fee:</span><span className="font-mono">${calculation.sale.docFee.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span>Title Fee:</span><span className="font-mono">${calculation.sale.titleFee.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span>Temp Tag:</span><span className="font-mono">${calculation.sale.tempTagFee.toLocaleString()}</span></div>
                      {/* Custom Fees */}
                      {fees.filter(f => f.name && f.amount > 0).map(fee => (
                        <div key={fee.id} className="flex justify-between text-neutral-600"><span>{fee.name}:</span><span className="font-mono">${fee.amount.toLocaleString()}</span></div>
                      ))}
                      <div className="border-t border-neutral-300 my-2"></div>
                      <div className="flex justify-between font-bold text-lg">
                        <span>Total Due:</span>
                        <span className="font-mono">${(calculation.sale.totalDue + fees.reduce((sum, f) => sum + (f.amount || 0), 0)).toLocaleString()}</span>
                      </div>
                      
                      {/* Balance Due Option */}
                      <div className="border-t border-neutral-300 my-2 pt-2">
                        <label className="flex items-center gap-2 mb-2">
                          <input type="checkbox" checked={hasBalanceDue} onChange={e => setHasBalanceDue(e.target.checked)} />
                          <span className="text-sm font-medium">Partial Payment / Balance Due</span>
                        </label>
                        {hasBalanceDue && (
                          <div className="ml-6 space-y-2">
                            <div>
                              <label className="block text-xs text-neutral-500 mb-1">Deposit Amount</label>
                              <div className="relative">
                                <span className="absolute left-2 top-1 text-neutral-500 text-sm">$</span>
                                <input 
                                  type="number" 
                                  value={depositAmount} 
                                  onChange={e => setDepositAmount(parseFloat(e.target.value) || 0)}
                                  className="w-full pl-5 pr-2 py-1 text-sm border border-neutral-200 rounded"
                                />
                              </div>
                            </div>
                            <div className="text-sm font-medium text-amber-600">
                              Balance Due: ${((calculation.sale.totalDue + fees.reduce((sum, f) => sum + (f.amount || 0), 0)) - depositAmount).toLocaleString()}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="border-t border-neutral-300 my-2"></div>
                      <div className="flex justify-between text-neutral-500"><span>Vehicle Cost:</span><span className="font-mono">${calculation.costs.totalVehicleCost.toLocaleString()}</span></div>
                      <div className={`flex justify-between font-medium ${calculation.profit.grossProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        <span>Gross Profit:</span><span className="font-mono">${calculation.profit.grossProfit.toLocaleString()}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-neutral-400">Enter purchase price to calculate</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Forms */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-lg">Print Form Pack</h3>
                <button onClick={() => setShowPrintPacket(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
                  Print Forms…
                </button>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="font-medium text-green-800">Deal #{deal?.id} Created Successfully</div>
                <div className="text-sm text-green-600 mt-1">Download the form pack PDF and have the buyer sign all documents.</div>
              </div>
              <details className="mt-4 border border-neutral-200 rounded-lg">
                <summary className="px-3 py-2 text-sm font-medium text-neutral-500 cursor-pointer hover:text-neutral-700 select-none">
                  Built-in forms (drawn by code — these are NOT in your Form Templates) — click to expand
                </summary>
                <div className="grid grid-cols-2 gap-3 p-3 pt-1">
                  {[
                    { name: 'Retail Buyer\'s Order', type: 'buyers-order' },
                    { name: 'AS-IS Warranty Disclaimer', type: 'as-is' },
                    { name: 'FTC Buyer\'s Guide', type: 'buyers-guide' },
                    { name: 'Due Bill', type: 'due-bill' },
                    { name: 'Odometer Disclosure (BMV 3724)', type: 'bmv-3724' },
                    { name: 'Power of Attorney (BMV 3771)', type: 'bmv-3771' },
                    { name: 'Dealer Assignment (BMV 3772)', type: 'bmv-3772' },
                  ].map((form, i) => (
                    <button 
                      key={i} 
                      onClick={() => printSingleForm(form.type)}
                      className="flex items-center gap-2 p-3 bg-white border border-neutral-200 rounded hover:bg-neutral-50 hover:border-blue-300 transition-colors text-left"
                    >
                      <span className="text-blue-500"></span>
                      <span className="text-sm">{form.name}</span>
                    </button>
                  ))}
                </div>
              </details>
            </div>
          )}

          {/* Step 4: Complete */}
          {step === 4 && (
            <div className="text-center py-8 space-y-4">
              <div className="text-6xl"></div>
              <h3 className="text-xl font-bold">Ready to Complete Sale</h3>
              <p className="text-neutral-600">Confirm that payment has been received to finalize this sale.</p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-md mx-auto">
                <div className="font-medium text-amber-800">Total Due: ${calculation?.sale.totalDue.toLocaleString()}</div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-neutral-200 flex justify-between">
          <button onClick={() => step > 1 ? setStep(step - 1) : onClose()} className="px-4 py-2 text-neutral-600 hover:text-neutral-800">
            {step === 1 ? 'Cancel' : '← Back'}
          </button>
          <div className="flex gap-2">
            {step === 1 && (
              <button onClick={() => setStep(2)} disabled={!buyer.firstName || !buyer.address || !buyer.city || !buyer.state || !buyer.zip} className="px-4 py-2 bg-neutral-800 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50">
                Next: Pricing →
              </button>
            )}
            {step === 2 && (
              <button onClick={handleCreateDeal} disabled={loading || !calculation} className="px-4 py-2 bg-neutral-800 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50">
                {loading ? 'Creating...' : 'Create Deal & Generate Forms →'}
              </button>
            )}
            {step === 3 && (
              <button onClick={() => setStep(4)} className="px-4 py-2 bg-neutral-800 text-white rounded-lg hover:bg-neutral-700">
                Next: Complete Sale →
              </button>
            )}
            {step === 4 && (
              <>
                <button
                  onClick={() => {
                    // Keep deal pending/unpaid and move to next sale
                    onSaveAndNew?.();
                  }}
                  disabled={loading}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
                >
                  ✓ Save & New
                </button>
                <button
                  onClick={() => {
                    // Keep deal pending/unpaid and close modal
                    onComplete?.();
                    onClose?.();
                  }}
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  ✓ Save & Close
                </button>
                <button
                  onClick={handleCompleteSale}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Completing...' : '✓ Mark Paid & Complete'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Unified Pre-Print screen */}
      {showPrintPacket && deal && (
        <PrintPacketModal mode="deal" deal={deal} onClose={() => setShowPrintPacket(false)} />
      )}
    </div>
  );
};

// Generate printable HTML for all retail forms
const generateRetailFormsPrintHTML = (vehicle, buyer, calculation, dealer, deal) => {
  const today = new Date().toLocaleDateString();
  const saleDate = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Retail Sale Forms - ${vehicle?.stockNumber}</title>
      <style>
        @media print { .page-break { page-break-after: always; } }
        body { font-family: Arial, sans-serif; font-size: 11px; line-height: 1.4; margin: 0; padding: 20px; }
        .form-title { font-size: 16px; font-weight: bold; text-align: center; margin-bottom: 15px; border-bottom: 2px solid #000; padding-bottom: 5px; }
        .section { margin-bottom: 15px; }
        .section-title { font-weight: bold; background: #f0f0f0; padding: 5px; margin-bottom: 5px; }
        .row { display: flex; margin-bottom: 5px; }
        .label { width: 150px; font-weight: bold; }
        .value { flex: 1; border-bottom: 1px solid #ccc; padding-left: 5px; }
        .signature-line { border-bottom: 1px solid #000; height: 30px; margin-top: 20px; }
        .signature-label { font-size: 9px; margin-top: 2px; }
        table { width: 100%; border-collapse: collapse; margin: 10px 0; }
        th, td { border: 1px solid #ccc; padding: 5px; text-align: left; }
        th { background: #f0f0f0; }
        .text-right { text-align: right; }
        .bold { font-weight: bold; }
        .dealer-header { text-align: center; margin-bottom: 20px; }
        .dealer-header h2 { margin: 0; }
        .checkbox { display: inline-block; width: 12px; height: 12px; border: 1px solid #000; margin-right: 5px; }
        .checkbox.checked::after { content: "✓"; }
      </style>
    </head>
    <body>
      <!-- RETAIL BUYER'S ORDER -->
      <div class="dealer-header">
        <h2>${dealer.name || 'Alexis Motorcar'}</h2>
        <div>${dealer.address || '4001 W Alexis Rd'}, ${dealer.city || 'Toledo'}, ${dealer.state || 'OH'} ${dealer.zip || '43623-1201'}</div>
        <div>Phone: ${dealer.phone || '(419) 270-2092'}</div>
      </div>
      
      <div class="form-title">RETAIL BUYER'S ORDER</div>
      
      <div class="section">
        <table>
          <tr><th colspan="2">Buyer Information</th><th colspan="2">Seller Information</th></tr>
          <tr>
            <td class="label">Name:</td><td>${[buyer.firstName, buyer.middleName, buyer.lastName].filter(Boolean).join(' ')}</td>
            <td class="label">Dealer:</td><td>${dealer.name || 'Alexis Motorcar'}</td>
          </tr>
          <tr>
            <td class="label">Address:</td><td>${buyer.address}</td>
            <td class="label">Address:</td><td>${dealer.address || '4001 W Alexis Rd'}</td>
          </tr>
          <tr>
            <td class="label">City/State/ZIP:</td><td>${buyer.city}, ${buyer.state} ${buyer.zip}</td>
            <td class="label">City/State/ZIP:</td><td>${dealer.city || 'Toledo'}, ${dealer.state || 'OH'} ${dealer.zip || '43623-1201'}</td>
          </tr>
          <tr>
            <td class="label">Phone:</td><td>${buyer.phone || ''}</td>
            <td class="label">Phone:</td><td>${dealer.phone || '(419) 270-2092'}</td>
          </tr>
        </table>
      </div>

      <div class="section">
        <table>
          <tr><th colspan="4">Vehicle Information</th></tr>
          <tr>
            <td class="label">Year:</td><td>${vehicle?.year}</td>
            <td class="label">Make:</td><td>${vehicle?.make}</td>
          </tr>
          <tr>
            <td class="label">Model:</td><td>${vehicle?.model}</td>
            <td class="label">Color:</td><td>${vehicle?.color || ''}</td>
          </tr>
          <tr>
            <td class="label">VIN:</td><td colspan="3">${vehicle?.vin}</td>
          </tr>
          <tr>
            <td class="label">Odometer:</td><td>${vehicle?.odometer?.toLocaleString() || ''}</td>
            <td class="label">Stock #:</td><td>${vehicle?.stockNumber}</td>
          </tr>
        </table>
      </div>

      <div class="section">
        <table>
          <tr><th colspan="2">Sale Details</th></tr>
          <tr><td>Purchase Price:</td><td class="text-right">$${calculation?.sale.purchasePrice?.toLocaleString() || '0'}</td></tr>
          ${calculation?.sale.tradeInValue > 0 ? `<tr><td>Trade-In Allowance:</td><td class="text-right">-$${calculation.sale.tradeInValue.toLocaleString()}</td></tr>` : ''}
          <tr><td>Taxable Amount:</td><td class="text-right">$${calculation?.sale.taxableAmount?.toLocaleString() || '0'}</td></tr>
          <tr><td>Sales Tax (${calculation?.sale.taxRate || 0}%):</td><td class="text-right">$${calculation?.sale.salesTax?.toLocaleString() || '0'}</td></tr>
          <tr><td>Documentation Fee:</td><td class="text-right">$${calculation?.sale.docFee?.toLocaleString() || '0'}</td></tr>
          <tr><td>Title Fee:</td><td class="text-right">$${calculation?.sale.titleFee?.toLocaleString() || '0'}</td></tr>
          <tr><td>Temp Tag Fee:</td><td class="text-right">$${calculation?.sale.tempTagFee?.toLocaleString() || '0'}</td></tr>
          <tr class="bold"><td>TOTAL DUE:</td><td class="text-right">$${calculation?.sale.totalDue?.toLocaleString() || '0'}</td></tr>
        </table>
      </div>

      <div class="section">
        <p><strong>THIS VEHICLE IS SOLD "AS-IS" WITHOUT WARRANTY.</strong> The dealer does not provide any warranty for any repairs after sale.</p>
      </div>

      <div class="row">
        <div style="flex:1">
          <div class="signature-line"></div>
          <div class="signature-label">Buyer Signature / Date</div>
        </div>
        <div style="width:30px"></div>
        <div style="flex:1">
          <div class="signature-line"></div>
          <div class="signature-label">Seller Signature / Date</div>
        </div>
      </div>

      <div class="page-break"></div>

      <!-- AS-IS WARRANTY DISCLAIMER -->
      <div class="dealer-header">
        <h2>${dealer.name || 'Alexis Motorcar'}</h2>
      </div>
      
      <div class="form-title">AS-IS - SOLD WITHOUT WARRANTY</div>
      
      <div class="section">
        <div class="row"><span class="label">Date:</span><span class="value">${saleDate}</span></div>
        <div class="row"><span class="label">Buyer's Name:</span><span class="value">${[buyer.firstName, buyer.middleName, buyer.lastName].filter(Boolean).join(' ')}</span></div>
        <div class="row"><span class="label">Address:</span><span class="value">${buyer.address}, ${buyer.city}, ${buyer.state} ${buyer.zip}</span></div>
      </div>

      <div class="section">
        <table>
          <tr><th>Year</th><th>Make</th><th>Model</th><th>VIN</th></tr>
          <tr><td>${vehicle?.year}</td><td>${vehicle?.make}</td><td>${vehicle?.model}</td><td>${vehicle?.vin}</td></tr>
          <tr><td colspan="2">Mileage: ${vehicle?.odometer?.toLocaleString() || ''}</td><td colspan="2">Stock #: ${vehicle?.stockNumber}</td></tr>
        </table>
      </div>

      <div class="section">
        <p><strong>SELLER HEREBY EXPRESSLY DISCLAIMS ALL WARRANTIES, ENTIRE EXPRESSED OR IMPLIED INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY OR FITNESS FOR A PARTICULAR PURPOSE AND SELLER NEITHER ASSUMES NOR AUTHORIZES ANY OTHER PERSON TO ASSUME FOR IT ANY LIABILITY IN CONNECTION WITH THE SALE OF THE VEHICLE.</strong></p>
        <p style="margin-top:15px"><span class="checkbox checked"></span> "NO RETURNS" &nbsp;&nbsp; <span class="checkbox checked"></span> "NOTICE OF VEHICLE SOLD WITHOUT ANY WARRANTY" &nbsp;&nbsp; <span class="checkbox checked"></span> "NO SUE / LITIGATION"</p>
        <p style="margin-top:15px">THIS VEHICLE IS SOLD <strong>WITHOUT ANY WARRANTY</strong>. THE BUYER WILL BEAR THE ENTIRE EXPENSE OF REPAIRING OR CORRECTING ANY DEFECTS THAT PRESENTLY EXIST AND/OR MAY OCCUR IN THE VEHICLE UNLESS THE SALESPERSON PROMISES IN WRITING AT THE TIME OF THE SALE TO CORRECT SUCH DEFECTS.</p>
        <p style="margin-top:15px"><strong>BUYER HEREBY ACKNOWLEDGES HE HAS READ, UNDERSTANDS AND ACCEPTS THE PROVISIONS OF THIS WARRANTY STATEMENT FOR THE ABOVE IDENTIFIED VEHICLE.</strong></p>
      </div>

      <div class="row" style="margin-top:30px">
        <div style="flex:1">
          <div class="signature-line"></div>
          <div class="signature-label">Buyer Signature / Date</div>
        </div>
        <div style="width:30px"></div>
        <div style="flex:1">
          <div class="signature-line"></div>
          <div class="signature-label">Seller Signature / Date</div>
        </div>
      </div>

      <div style="text-align:center; margin-top:30px; font-weight:bold">
        WARRANTY DISCLAIMER - SOLD AS-IS - NO RETURNS - NO SUE / LITIGATION
      </div>

      <div class="page-break"></div>

      <!-- DUE BILL -->
      <div class="form-title">DUE BILL</div>
      <div style="text-align:right; margin-bottom:10px">Date: ${saleDate}</div>
      
      <div class="section">
        <table>
          <tr>
            <th>Buyer</th><th>Seller</th>
          </tr>
          <tr>
            <td>${[buyer.firstName, buyer.middleName, buyer.lastName].filter(Boolean).join(' ')}<br>${buyer.address}<br>${buyer.city}, ${buyer.state} ${buyer.zip}<br>${buyer.phone || ''}</td>
            <td>${dealer.name || 'Alexis Motorcar'}<br>${dealer.address || '4001 W Alexis Rd'}<br>${dealer.city || 'Toledo'}, ${dealer.state || 'OH'} ${dealer.zip || '43623-1201'}<br>${dealer.phone || '(419) 270-2092'}</td>
          </tr>
        </table>
      </div>

      <div class="section">
        <div class="row"><span class="label">Vehicle:</span><span class="value">${vehicle?.year} ${vehicle?.make} ${vehicle?.model}</span></div>
        <div class="row"><span class="label">VIN:</span><span class="value">${vehicle?.vin}</span></div>
        <div class="row"><span class="label">Odometer:</span><span class="value">${vehicle?.odometer?.toLocaleString() || ''}</span><span class="label" style="margin-left:20px">Stock #:</span><span class="value">${vehicle?.stockNumber}</span></div>
      </div>

      <div class="section">
        <div class="section-title">SELLER OWES</div>
        <div style="min-height:60px; border:1px solid #ccc; padding:10px">Nothing else promised or owed.</div>
      </div>

      <div class="section">
        <div class="section-title">BUYER OWES</div>
        <div style="min-height:60px; border:1px solid #ccc; padding:10px">Nothing else promised or owed.</div>
      </div>

      <div class="row" style="margin-top:30px">
        <div style="flex:1">
          <div class="signature-line"></div>
          <div class="signature-label">Buyer Signature / Date</div>
        </div>
        <div style="width:30px"></div>
        <div style="flex:1">
          <div class="signature-line"></div>
          <div class="signature-label">Seller Signature / Date</div>
        </div>
      </div>

      <div class="page-break"></div>

      <!-- ODOMETER DISCLOSURE -->
      <div style="text-align:center; margin-bottom:20px">
        <div style="font-size:10px">OHIO DEPARTMENT OF PUBLIC SAFETY</div>
        <div style="font-size:10px">BUREAU OF MOTOR VEHICLES</div>
        <div class="form-title">ODOMETER DISCLOSURE STATEMENT</div>
      </div>

      <p>Federal law (and State law, if applicable) requires that you state the mileage upon transfer of ownership. Failure to complete or providing a false statement may result in fines and/or imprisonment.</p>

      <div class="section">
        <p>I, <strong>${dealer.name || 'Alexis Motorcar'}</strong>, state that the odometer of the vehicle described below now reads <strong>${vehicle?.odometer?.toLocaleString() || '______'}</strong> (no tenths) miles and to the best of my knowledge that it reflects the actual mileage of the vehicle described below, unless one of the following statements is checked.</p>
        
        <p><span class="checkbox"></span> (1) I hereby certify that to the best of my knowledge the odometer reading reflects the amount of mileage in excess of its mechanical limits.</p>
        <p><span class="checkbox"></span> (2) I hereby certify that the odometer reading is NOT the actual mileage. WARNING - ODOMETER DISCREPANCY.</p>
      </div>

      <div class="section">
        <table>
          <tr><th colspan="4">Vehicle Information</th></tr>
          <tr>
            <td class="label">Make:</td><td>${vehicle?.make}</td>
            <td class="label">Body Type:</td><td>${vehicle?.bodyType || 'Sedan'}</td>
          </tr>
          <tr>
            <td class="label">Model:</td><td>${vehicle?.model}</td>
            <td class="label">Year:</td><td>${vehicle?.year}</td>
          </tr>
          <tr>
            <td class="label">VIN:</td><td colspan="3">${vehicle?.vin}</td>
          </tr>
        </table>
      </div>

      <div class="section">
        <div class="section-title">TRANSFEROR'S (SELLER'S) INFORMATION</div>
        <div class="row"><span class="label">Printed Name:</span><span class="value">${dealer.name || 'Alexis Motorcar'}</span></div>
        <div class="row"><span class="label">Address:</span><span class="value">${dealer.address || '4001 W Alexis Rd'}</span></div>
        <div class="row"><span class="label">City/State/ZIP:</span><span class="value">${dealer.city || 'Toledo'}, ${dealer.state || 'OH'} ${dealer.zip || '43623-1201'}</span></div>
        <div class="signature-line"></div>
        <div class="signature-label">Transferor's Signature / Date</div>
      </div>

      <div class="section">
        <div class="section-title">TRANSFEREE'S (BUYER'S) INFORMATION</div>
        <div class="row"><span class="label">Printed Name:</span><span class="value">${[buyer.firstName, buyer.middleName, buyer.lastName].filter(Boolean).join(' ')}</span></div>
        <div class="row"><span class="label">Address:</span><span class="value">${buyer.address}</span></div>
        <div class="row"><span class="label">City/State/ZIP:</span><span class="value">${buyer.city}, ${buyer.state} ${buyer.zip}</span></div>
        <div class="signature-line"></div>
        <div class="signature-label">Transferee's Signature / Date - RECEIPT OF COPY ACKNOWLEDGED</div>
      </div>

      <div style="text-align:center; margin-top:20px; font-size:9px">BMV 3724 4/17</div>

    </body>
    </html>
  `;
};

// Wholesale Sale Modal - Simple bill of sale
const WholesaleSaleModal = ({ vehicle, onClose, onComplete, onSaveAndNew, onDealCreated }) => {
  const [loading, setLoading] = useState(false);
  const [savedCustomers, setSavedCustomers] = useState([]);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [odometer, setOdometer] = useState(vehicle?.mileage || '');
  const [buyer, setBuyer] = useState({
    dealerName: '', address: '', city: '', state: 'OH', zip: '',
    phone: '', dealerLicense: ''
  });
  const [salePrice, setSalePrice] = useState(vehicle?.estimatedSale || '');
  const [notes, setNotes] = useState('');
  const [deal, setDeal] = useState(null);

  useEffect(() => {
    api.getDealCustomers().then(setSavedCustomers).catch(() => setSavedCustomers([]));
  }, []);

  const handleSelectCustomer = (c) => {
    if (!c) return;
    setBuyer(b => ({
      ...b,
      dealerName: c.name || '',
      address: c.address || '',
      city: c.city || '',
      state: c.state || b.state || 'OH',
      zip: c.zip || '',
      phone: c.phone || '',
      dealerLicense: c.dealer_license || c.dealerLicense || ''
    }));
  };

  const handleSaveAsCustomer = async () => {
    if (!buyer.dealerName?.trim()) {
      alert('Enter dealer name first');
      return;
    }
    setSavingCustomer(true);
    try {
      await api.saveDealCustomer({
        name: buyer.dealerName.trim(),
        address: buyer.address || null,
        city: buyer.city || null,
        state: buyer.state || null,
        zip: buyer.zip || null,
        phone: buyer.phone || null,
        dealerLicense: buyer.dealerLicense || null
      });
      const updated = await api.getDealCustomers();
      setSavedCustomers(updated || []);
      alert('Customer saved for future use');
    } catch (e) {
      alert('Failed to save: ' + e.message);
    } finally {
      setSavingCustomer(false);
    }
  };

  const handleCreate = async (mode = 'create') => {
    if (!buyer.dealerName || !salePrice) {
      alert('Dealer name and sale price required');
      return;
    }
    setLoading(true);
    try {
      // Update vehicle mileage if odometer was changed
      if (odometer && parseInt(odometer) !== parseInt(vehicle?.mileage || 0)) {
        await api.updateVehicle(vehicle.id, { mileage: parseInt(odometer) }).catch(e => console.warn('Failed to update odometer:', e));
      }
      const result = await api.createWholesaleSale({
        vehicleId: vehicle.id,
        buyer,
        purchasePrice: parseFloat(salePrice),
        odometer: parseInt(odometer) || 0,
        notes: notes.trim() || undefined
      });

      // Save & New from create step: keep unpaid/pending and immediately open next sale chooser.
      if (mode === 'new') {
        onSaveAndNew?.();
        onClose?.();
        return;
      }

      setDeal(result.deal);
      onDealCreated?.();
    } catch (e) {
      console.error('[Wholesale Sale] Failed to create deal:', e);
      alert('Error: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!window.confirm('Confirm payment received and complete this wholesale sale?')) return;
    setLoading(true);
    try {
      await api.completeWholesaleSale(deal.id);
      alert('Wholesale sale completed!');
      onComplete?.();
      onClose?.();
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const printBillOfSale = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Bill of Sale - ${vehicle?.stockNumber}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          h1 { text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; }
          .section { margin: 20px 0; }
          .row { display: flex; margin: 8px 0; }
          .label { width: 150px; font-weight: bold; }
          .value { flex: 1; border-bottom: 1px solid #ccc; }
          .signature { margin-top: 50px; }
          .sig-line { border-bottom: 1px solid #000; height: 40px; }
          .sig-label { font-size: 11px; margin-top: 5px; }
          .as-is { background: #f5f5f5; padding: 15px; margin: 20px 0; border: 1px solid #ccc; }
        </style>
      </head>
      <body>
        <h1>BILL OF SALE</h1>
        <p style="text-align:center">Wholesale Vehicle Transaction</p>
        
        <div class="section">
          <h3>Seller</h3>
          <div class="row"><span class="label">Dealer Name:</span><span class="value">Alexis Motorcar</span></div>
          <div class="row"><span class="label">Address:</span><span class="value">4001 W Alexis Rd, Toledo, OH 43623-1201</span></div>
          <div class="row"><span class="label">Phone:</span><span class="value">(419) 270-2092</span></div>
          <div class="row"><span class="label">Dealer License:</span><span class="value">UD030949</span></div>
        </div>

        <div class="section">
          <h3>Buyer</h3>
          <div class="row"><span class="label">Dealer Name:</span><span class="value">${buyer.dealerName}</span></div>
          <div class="row"><span class="label">Address:</span><span class="value">${buyer.address} ${buyer.city}, ${buyer.state} ${buyer.zip}</span></div>
          <div class="row"><span class="label">Phone:</span><span class="value">${buyer.phone || ''}</span></div>
          <div class="row"><span class="label">Dealer License:</span><span class="value">${buyer.dealerLicense || ''}</span></div>
        </div>

        <div class="section">
          <h3>Vehicle</h3>
          <div class="row"><span class="label">Year/Make/Model:</span><span class="value">${vehicle?.year} ${vehicle?.make} ${vehicle?.model}</span></div>
          <div class="row"><span class="label">VIN:</span><span class="value">${vehicle?.vin}</span></div>
          <div class="row"><span class="label">Odometer:</span><span class="value">${vehicle?.odometer?.toLocaleString() || ''}</span></div>
          <div class="row"><span class="label">Stock Number:</span><span class="value">${vehicle?.stockNumber}</span></div>
          <div class="row"><span class="label">Color:</span><span class="value">${vehicle?.color || ''}</span></div>
        </div>

        <div class="section">
          <h3>Sale</h3>
          <div class="row"><span class="label">Sale Price:</span><span class="value" style="font-weight:bold;font-size:18px">$${parseFloat(salePrice).toLocaleString()}</span></div>
          <div class="row"><span class="label">Date:</span><span class="value">${new Date().toLocaleDateString()}</span></div>
        </div>

        ${(deal?.notes || notes) ? `
        <div class="section">
          <h3>Notes</h3>
          <div class="value" style="padding:8px 0; white-space:pre-wrap">${(deal?.notes || notes || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>
        </div>
        ` : ''}

        <div class="as-is">
          <strong>AS-IS DISCLOSURE:</strong> This vehicle is sold "AS-IS" without any warranty, expressed or implied. The seller makes no representations or warranties regarding the condition, fitness, or merchantability of this vehicle. The buyer accepts the vehicle in its current condition and assumes all risk.
        </div>

        <p>The undersigned Seller hereby sells, transfers, and conveys to the Buyer the above-described vehicle. Seller warrants that they have good and marketable title to said vehicle, free and clear of all liens and encumbrances.</p>

        <div class="signature">
          <div style="display:flex; gap:40px">
            <div style="flex:1">
              <div class="sig-line"></div>
              <div class="sig-label">Seller Signature / Date</div>
              <div style="margin-top:5px">Alexis Motorcar</div>
            </div>
            <div style="flex:1">
              <div class="sig-line"></div>
              <div class="sig-label">Buyer Signature / Date</div>
              <div style="margin-top:5px">${buyer.dealerName}</div>
            </div>
          </div>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-lg rounded-lg shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-neutral-200">
          <div className="font-semibold">Wholesale Sale</div>
          <div className="text-sm text-neutral-500">{vehicle?.stockNumber} - {vehicle?.year} {vehicle?.make} {vehicle?.model}</div>
        </div>

        <div className="p-4 space-y-4">
          {!deal ? (
            <>
              {/* Odometer Verification */}
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span>⚠️</span>
                  <span className="font-semibold text-amber-800 text-sm">Verify Odometer Before Selling</span>
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-sm text-amber-700 font-medium whitespace-nowrap">Reading:</label>
                  <input
                    type="number"
                    value={odometer}
                    onChange={e => setOdometer(e.target.value)}
                    className="px-3 py-1.5 border-2 border-amber-400 rounded-lg text-lg font-mono font-bold w-36 bg-white focus:border-amber-600 focus:outline-none"
                    placeholder="Miles"
                  />
                  <span className="text-sm text-amber-700">miles</span>
                  {odometer && vehicle?.mileage && parseInt(odometer) !== parseInt(vehicle.mileage) && (
                    <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded">Changed from {Number(vehicle.mileage).toLocaleString()}</span>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Buying Dealer Name *</label>
                <div className="flex gap-2">
                  <select
                    value=""
                    onChange={e => { const v = e.target.value; if (v) { const c = savedCustomers.find(x => x.id === parseInt(v)); if (c) handleSelectCustomer(c); } }}
                    className="flex-shrink-0 w-32 px-2 py-2 border border-neutral-200 rounded-lg text-sm bg-neutral-50"
                  >
                    <option value="">Saved...</option>
                    {savedCustomers.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <input type="text" value={buyer.dealerName} onChange={e => setBuyer(b => ({ ...b, dealerName: e.target.value }))} className="flex-1 px-3 py-2 border border-neutral-200 rounded-lg" placeholder="Dealer name" />
                  <button type="button" onClick={handleSaveAsCustomer} disabled={savingCustomer || !buyer.dealerName?.trim()} className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded hover:bg-green-200 disabled:opacity-50" title="Save for future deals">Save</button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Address</label>
                <input type="text" value={buyer.address} onChange={e => setBuyer(b => ({ ...b, address: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <input type="text" placeholder="City" value={buyer.city} onChange={e => setBuyer(b => ({ ...b, city: e.target.value }))} className="px-3 py-2 border border-neutral-200 rounded-lg" />
                <input type="text" placeholder="State" value={buyer.state} onChange={e => setBuyer(b => ({ ...b, state: e.target.value }))} maxLength={2} className="px-3 py-2 border border-neutral-200 rounded-lg" />
                <input type="text" placeholder="ZIP" value={buyer.zip} onChange={e => setBuyer(b => ({ ...b, zip: e.target.value }))} className="px-3 py-2 border border-neutral-200 rounded-lg" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <input type="tel" value={buyer.phone} onChange={e => setBuyer(b => ({ ...b, phone: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Dealer License #</label>
                  <input type="text" value={buyer.dealerLicense} onChange={e => setBuyer(b => ({ ...b, dealerLicense: e.target.value }))} className="w-full px-3 py-2 border border-neutral-200 rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Sale Price *</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-neutral-500">$</span>
                  <input type="number" value={salePrice} onChange={e => setSalePrice(e.target.value)} className="w-full pl-7 pr-3 py-2 border border-neutral-200 rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes (appears on Bill of Sale)</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes for the Bill of Sale..." className="w-full px-3 py-2 border border-neutral-200 rounded-lg resize-none" rows={3} />
              </div>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="text-4xl mb-2"></div>
              <div className="font-medium">Deal Created</div>
              <button onClick={printBillOfSale} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                🖨️ Print Bill of Sale
              </button>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-neutral-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-neutral-600">Cancel</button>
          {!deal ? (
            <>
              <button onClick={() => handleCreate('new')} disabled={loading} className="px-4 py-2 bg-teal-600 text-white rounded-lg disabled:opacity-50">
                {loading ? 'Creating...' : 'Save & New'}
              </button>
              <button onClick={() => handleCreate('create')} disabled={loading} className="px-4 py-2 bg-neutral-800 text-white rounded-lg disabled:opacity-50">
                {loading ? 'Creating...' : 'Create Deal'}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => {
                  // Keep deal pending/unpaid and move to next sale
                  onSaveAndNew?.();
                }}
                disabled={loading}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg disabled:opacity-50"
              >
                ✓ Save & New
              </button>
              <button
                onClick={() => {
                  // Keep deal pending/unpaid and close modal
                  onComplete?.();
                  onClose?.();
                }}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg disabled:opacity-50"
              >
                ✓ Save & Close
              </button>
              <button
                onClick={handleComplete}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50"
              >
                {loading ? 'Completing...' : '✓ Mark Paid & Complete'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Sales Sub-Tab for Accounting Module
const SalesSubTab = () => {
  const [sales, setSales] = useState([]);
  const [summary, setSummary] = useState({ count: 0, totalSales: 0, totalProfit: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    return {
      start: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0],
      end: now.toISOString().split('T')[0]
    };
  });

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const SortHeader = ({ id, label, align = 'left' }) => {
    const active = sortBy === id;
    const arrow = active ? (sortDir === 'asc' ? '▲' : '▼') : '⇅';
    return (
      <th className={`px-3 py-2 font-medium ${align === 'right' ? 'text-right' : ''}`}>
        <button onClick={() => toggleSort(id)} className={`flex items-center gap-1 text-xs hover:text-blue-600 ${active ? 'text-blue-600' : ''} ${align === 'right' ? 'ml-auto' : ''}`}>
          {label}
          <span className={`text-[10px] ${active ? '' : 'opacity-30'}`}>{arrow}</span>
        </button>
      </th>
    );
  };

  const filterAndSort = (data) => {
    let filtered = data;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = data.filter(s => 
        (s.stock_number || '').toLowerCase().includes(search) ||
        (s.make || '').toLowerCase().includes(search) ||
        (s.model || '').toLowerCase().includes(search) ||
        (s.buyer_name || '').toLowerCase().includes(search)
      );
    }
    return [...filtered].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      switch (sortBy) {
        case 'date': return ((a.sale_date || '') > (b.sale_date || '') ? 1 : -1) * dir;
        case 'stock': return (a.stock_number || '').localeCompare(b.stock_number || '') * dir;
        case 'vehicle': return (`${a.year} ${a.make} ${a.model}`).localeCompare(`${b.year} ${b.make} ${b.model}`) * dir;
        case 'buyer': return (a.buyer_name || '').localeCompare(b.buyer_name || '') * dir;
        case 'type': return (a.sale_type || '').localeCompare(b.sale_type || '') * dir;
        case 'price': return (parseFloat(a.sale_price || 0) - parseFloat(b.sale_price || 0)) * dir;
        case 'cost': return (parseFloat(a.total_cost || 0) - parseFloat(b.total_cost || 0)) * dir;
        case 'profit': return (parseFloat(a.profit || 0) - parseFloat(b.profit || 0)) * dir;
        default: return 0;
      }
    });
  };

  useEffect(() => {
    const loadSales = async () => {
      setLoading(true);
      try {
        const data = await api.getSales(dateRange.start, dateRange.end);
        setSales(data.sales || []);
        setSummary(data.summary || { count: 0, totalSales: 0, totalProfit: 0 });
      } catch (e) {
        console.error('Failed to load sales:', e);
      } finally {
        setLoading(false);
      }
    };
    loadSales();
  }, [dateRange.start, dateRange.end]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <input type="date" value={dateRange.start} onChange={e => setDateRange(d => ({ ...d, start: e.target.value }))} className="px-2 py-1 text-sm border border-neutral-200 rounded" />
          <span className="text-neutral-400">to</span>
          <input type="date" value={dateRange.end} onChange={e => setDateRange(d => ({ ...d, end: e.target.value }))} className="px-2 py-1 text-sm border border-neutral-200 rounded" />
        </div>
        <input
          type="text"
          placeholder="Search stock, vehicle, buyer..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="px-3 py-1.5 text-sm border border-neutral-200 rounded-lg w-56"
        />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="bg-white border border-neutral-200 rounded-lg p-3">
          <div className="text-xs text-neutral-500">Vehicles Sold</div>
          <div className="text-xl font-bold">{summary.count}</div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-3">
          <div className="text-xs text-neutral-500">Total Sales</div>
          <div className="text-xl font-bold text-green-700">${summary.totalSales?.toLocaleString()}</div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-3">
          <div className="text-xs text-neutral-500">Total Profit</div>
          <div className={`text-xl font-bold ${summary.totalProfit >= 0 ? 'text-green-700' : 'text-red-600'}`}>
            ${summary.totalProfit?.toLocaleString()}
          </div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-3">
          <div className="text-xs text-neutral-500">Avg Profit/Unit</div>
          <div className={`text-xl font-bold ${summary.averageProfit >= 0 ? 'text-green-700' : 'text-red-600'}`}>
            ${summary.averageProfit?.toLocaleString() || 0}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-neutral-500">Loading sales...</div>
      ) : sales.length === 0 ? (
        <Empty text="No completed sales in this period" />
      ) : (
        <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                <SortHeader id="date" label="Date" />
                <SortHeader id="stock" label="Stock #" />
                <SortHeader id="vehicle" label="Vehicle" />
                <SortHeader id="buyer" label="Buyer" />
                <SortHeader id="type" label="Type" />
                <SortHeader id="price" label="Sale Price" align="right" />
                <SortHeader id="cost" label="Total Cost" align="right" />
                <SortHeader id="profit" label="Profit" align="right" />
              </tr>
            </thead>
            <tbody>
              {filterAndSort(sales.map(s => ({ ...s, stock_number: s.stockNumber, sale_date: s.paymentReceivedDate, sale_price: s.salePrice, total_cost: s.totalCost, buyer_name: s.buyerName, sale_type: s.saleType }))).map(s => (
                <tr key={s.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                  <td className="px-3 py-2 text-xs">{s.paymentReceivedDate?.split('T')[0]}</td>
                  <td className="px-3 py-2 font-mono text-xs">{s.stockNumber}</td>
                  <td className="px-3 py-2">{s.year} {s.make} {s.model}</td>
                  <td className="px-3 py-2">{s.buyerName || '-'}</td>
                  <td className="px-3 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      s.saleType === 'retail' ? 'bg-green-100 text-green-700' :
                      s.saleType?.includes('wholesale') ? 'bg-blue-100 text-blue-700' :
                      'bg-neutral-100 text-neutral-700'
                    }`}>
                      {s.saleType?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono">${parseFloat(s.salePrice || 0).toLocaleString()}</td>
                  <td className="px-3 py-2 text-right font-mono text-neutral-500">${s.totalCost?.toLocaleString()}</td>
                  <td className={`px-3 py-2 text-right font-mono font-medium ${s.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ${s.profit?.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const AccountingDashboard = ({ onOpenBankAccount, onAddExpense, onInputSale, onNewInvoice }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailModal, setDetailModal] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [expandedTxn, setExpandedTxn] = useState(null);
  const [revealedCards, setRevealedCards] = useState(new Set());
  const isRevealed = (id) => revealedCards.has(id);
  const toggleCard = (id, e) => { if (e) e.stopPropagation(); setRevealedCards(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const toggleAll = () => { const bankIds = (summary?.bankAccounts || []).filter(a => a.name !== 'Business Savings').map(a => 'bank-' + a.id); const allIds = ['pending','rawcash','profit',...bankIds,'floorplan','ar','networth']; setRevealedCards(s => s.size > 0 ? new Set() : new Set(allIds)); };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await api.getDashboardSummary();
        setSummary(data);
      } catch (error) {
        console.error('Error fetching accounting summary:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const openDetail = async (type, accountId) => {
    setDetailModal(type === 'bank' ? { type, accountId } : type);
    setDetailLoading(true);
    setDetailData(null);
    try {
      if (type === 'pending') {
        const data = await api.getPendingPayments();
        setDetailData({ type: 'pending', items: data });
      } else if (type === 'ar') {
        const data = await api.getAccountsReceivable();
        setDetailData({ type: 'ar', items: data.deals || [], totalAR: data.totalAR, count: data.count });
      } else if (type === 'bank') {
        const endDate = new Date().toISOString().split('T')[0];
        const startDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        const data = await api.getReconciliation(accountId, startDate, endDate);
        const account = summary?.bankAccounts?.find(a => a.id === accountId);
        setDetailData({ type: 'bank', transactions: (data.transactions || []).slice(0, 20), account: account || data.account });
      }
    } catch (e) {
      console.error('Failed to load detail:', e);
      setDetailData({ type, error: e.message });
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => { setDetailModal(null); setDetailData(null); };

  const daysSince = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    const now = new Date();
    return Math.floor((now - d) / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return <div className="text-center p-8">Loading dashboard...</div>;
  }

  if (!summary) {
    return <div className="text-center p-8 text-red-500">Failed to load dashboard summary.</div>;
  }

  const { bankAccounts: allBankAccounts, paymentsNeedCleared, rawCash, accountsReceivable, arCount, pendingCount, monthGrossProfit, monthSalesCount, yearGrossProfit, yearSalesCount } = summary;
  const bankAccounts = allBankAccounts.filter(a => a.name !== 'Business Savings');
  const hv = '$\u2022\u2022\u2022\u2022\u2022';

  return (
    <>
    <div className="flex items-center justify-between mb-1.5">
      <div className="text-[11px] text-teal-600 uppercase tracking-wider font-semibold">Account Overview</div>
      <button onClick={toggleAll} className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] rounded-md border border-slate-300 text-slate-500 hover:text-teal-600 hover:bg-slate-50 transition-colors select-none">
        {revealedCards.size > 0 ? '[hide all]' : '[show all]'}
      </button>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 mb-3">
      <div className="bg-white border border-slate-200 shadow-sm p-2.5 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors relative" onClick={() => openDetail('pending')}>
        <div className="text-[11px] text-teal-700 font-medium">Payments Need Cleared</div>
        <div className="text-sm font-bold font-mono tracking-tight text-red-600">{isRevealed('pending') ? '-$' + paymentsNeedCleared.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2}) : hv}</div>
        <div className="flex items-center justify-between mt-0.5">
          <span className="text-[10px] text-slate-400">{pendingCount || 0} pending</span>
          <button onClick={(e) => toggleCard('pending', e)} className="text-[10px] text-slate-400 hover:text-teal-600 font-mono">{isRevealed('pending') ? '[hide]' : '[show]'}</button>
        </div>
      </div>
      <div className="bg-white border border-slate-200 shadow-sm p-2.5 rounded-lg relative">
        <div className="text-[11px] text-teal-700 font-medium">Raw Cash</div>
        <div className="text-sm font-bold font-mono tracking-tight text-slate-800">{isRevealed('rawcash') ? '$' + rawCash.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2}) : hv}</div>
        <div className="flex justify-end mt-0.5">
          <button onClick={() => toggleCard('rawcash')} className="text-[10px] text-slate-400 hover:text-teal-600 font-mono">{isRevealed('rawcash') ? '[hide]' : '[show]'}</button>
        </div>
      </div>
      {bankAccounts.map(account => (
        <div key={account.id} className="bg-white border border-slate-200 shadow-sm p-2.5 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors relative" onClick={() => openDetail('bank', account.id)}>
          <div className="text-[11px] text-teal-700 font-medium truncate">{account.name}</div>
          <div className="text-sm font-bold font-mono tracking-tight text-slate-800">{isRevealed('bank-' + account.id) ? '$' + parseFloat(account.balance).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2}) : hv}</div>
          <div className="flex items-center justify-between mt-0.5">
            <span className="text-[10px] text-slate-400">Click for transactions</span>
            <button onClick={(e) => toggleCard('bank-' + account.id, e)} className="text-[10px] text-slate-400 hover:text-teal-600 font-mono">{isRevealed('bank-' + account.id) ? '[hide]' : '[show]'}</button>
          </div>
        </div>
      ))}
      {/* Profit Summary Card */}
      <div className="bg-white border border-slate-200 shadow-sm p-2.5 rounded-lg relative">
        <div className="text-[11px] text-teal-700 font-medium">Profit Summary</div>
        {isRevealed('profit') ? (
          <div>
            <div className="flex justify-between items-baseline">
              <span className="text-[10px] text-slate-400">This Month:</span>
              <span className="text-sm font-bold font-mono tracking-tight text-slate-800">{'$' + (monthGrossProfit || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-baseline">
              <span className="text-[10px] text-slate-400">This Year:</span>
              <span className="text-sm font-bold font-mono tracking-tight text-slate-800">{'$' + (yearGrossProfit || 0).toLocaleString()}</span>
            </div>
          </div>
        ) : (
          <div className="text-sm font-bold font-mono tracking-tight text-slate-800">{hv}</div>
        )}
        <div className="flex items-center justify-between mt-0.5">
          <span className="text-[10px] text-slate-400">{(monthSalesCount || 0)} sold this mo</span>
          <button onClick={() => toggleCard('profit')} className="text-[10px] text-slate-400 hover:text-teal-600 font-mono">{isRevealed('profit') ? '[hide]' : '[show]'}</button>
        </div>
      </div>
      <div className="border-2 border-dashed border-teal-300 p-2.5 rounded-lg cursor-pointer hover:bg-teal-50 hover:border-teal-400 transition-colors flex flex-col items-center justify-center gap-0.5" onClick={onAddExpense}>
        <span className="text-sm font-mono text-teal-500">+</span>
        <span className="text-[11px] font-semibold text-slate-600">Add Expense</span>
      </div>
      <div className="border-2 border-dashed border-teal-300 p-2.5 rounded-lg cursor-pointer hover:bg-teal-50 hover:border-teal-400 transition-colors flex flex-col items-center justify-center gap-0.5" onClick={onInputSale}>
        <span className="text-sm font-mono text-teal-500">$</span>
        <span className="text-[11px] font-semibold text-slate-600">Input a Sale</span>
      </div>
      <div className="border-2 border-dashed border-teal-300 p-2.5 rounded-lg cursor-pointer hover:bg-teal-50 hover:border-teal-400 transition-colors flex flex-col items-center justify-center gap-0.5" onClick={onNewInvoice}>
        <span className="text-sm font-mono text-teal-500">#</span>
        <span className="text-[11px] font-semibold text-slate-600">New Invoice</span>
      </div>
    </div>

    {/* Detail Modal */}
    {detailModal && (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]" onClick={closeDetail}>
        <div className="bg-white w-full max-w-4xl rounded-lg shadow-xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
          <div className="p-4 border-b border-neutral-200 flex justify-between items-center">
            <h3 className="font-semibold text-lg">
              {detailData?.type === 'pending' && 'Payments Need Cleared'}
              {detailData?.type === 'ar' && 'Accounts Receivable'}
              {detailData?.type === 'bank' && `${detailData?.account?.name || 'Bank Account'} — Recent Transactions`}
              {detailLoading && 'Loading...'}
            </h3>
            <div className="flex items-center gap-2">
              {detailData?.type === 'bank' && onOpenBankAccount && (
                <button
                  onClick={() => { closeDetail(); onOpenBankAccount(detailData.account); }}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Adjust / Transfer
                </button>
              )}
              <button onClick={closeDetail} className="text-neutral-400 hover:text-neutral-600 text-xl">×</button>
            </div>
          </div>
          <div className="p-4 overflow-y-auto flex-1">
            {detailLoading && <div className="text-center py-8 text-neutral-500">Loading...</div>}
            {detailData?.error && <div className="text-center py-8 text-red-500">Failed to load: {detailData.error}</div>}

            {/* Pending Payments Detail */}
            {detailData?.type === 'pending' && !detailLoading && (
              <div>
                <div className="text-sm text-neutral-500 mb-3">{detailData.items.length} pending payment{detailData.items.length !== 1 ? 's' : ''}</div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                      <th className="px-3 py-2 font-medium">Vehicle</th>
                      <th className="px-3 py-2 font-medium">Amount</th>
                      <th className="px-3 py-2 font-medium">Type</th>
                      <th className="px-3 py-2 font-medium">Days Pending</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailData.items.map(pp => (
                      <tr key={pp.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                        <td className="px-3 py-2">{pp.stockNumber || pp.stock_number} - {pp.year} {pp.make} {pp.model}</td>
                        <td className="px-3 py-2 font-mono font-medium">${parseFloat(pp.purchasePrice || pp.purchase_price || pp.amount).toLocaleString()}</td>
                        <td className="px-3 py-2">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            pp.paymentType === 'ACH' || pp.payment_type === 'ACH' ? 'bg-blue-100 text-blue-700' :
                            pp.paymentType === 'Check' || pp.payment_type === 'Check' ? 'bg-purple-100 text-purple-700' :
                            pp.paymentType === 'Cash' || pp.payment_type === 'Cash' ? 'bg-amber-100 text-amber-700' :
                            pp.paymentType === 'Title Pending' || pp.payment_type === 'Title Pending' ? 'bg-orange-100 text-orange-700' :
                            'bg-neutral-100 text-neutral-700'
                          }`}>
                            {pp.paymentType || pp.payment_type}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs">{daysSince(pp.createdAt || pp.created_at)} days</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Bank Account Transactions Detail */}
            {detailData?.type === 'bank' && !detailLoading && (() => {
              // Compute running balance (start from current balance, work backwards)
              const currentBalance = parseFloat(detailData.account?.balance || 0);
              const txns = detailData.transactions || [];
              const balances = [];
              let runBal = currentBalance;
              // txns are sorted newest first — first row = current balance, then subtract/add going backward
              for (let i = 0; i < txns.length; i++) {
                balances[i] = runBal;
                const t = txns[i];
                const isDebit = t.entry_type === 'debit' || t.entryType === 'debit' || t.type === 'expense' || t.type === 'bill' || t.type === 'part';
                const amt = parseFloat(t.amount || 0);
                runBal = isDebit ? runBal + amt : runBal - amt; // reverse to get balance before this txn
              }
              return (
              <div>
                <div className="text-sm text-neutral-500 mb-3">
                  Current Balance: <span className={`font-mono font-bold ${currentBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ${currentBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                  <span className="ml-3">Last 90 days • {txns.length} transactions</span>
                </div>
                {txns.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                        <th className="px-2 py-2 font-medium">Date</th>
                        <th className="px-2 py-2 font-medium">Description</th>
                        <th className="px-2 py-2 font-medium text-right">Amount</th>
                        <th className="px-2 py-2 font-medium text-right">Balance</th>
                        <th className="px-2 py-2 font-medium">Type</th>
                        <th className="px-2 py-2 font-medium text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {txns.map((t, i) => {
                        const isDebit = t.entry_type === 'debit' || t.entryType === 'debit' || t.type === 'expense' || t.type === 'bill' || t.type === 'part';
                        const txnKey = `${t.type}-${t.id || i}`;
                        const isExpanded = expandedTxn === txnKey;
                        return (
                          <React.Fragment key={txnKey}>
                          <tr className="border-b border-neutral-100 hover:bg-neutral-50 group cursor-pointer" onClick={() => setExpandedTxn(isExpanded ? null : txnKey)}>
                            <td className="px-2 py-2 text-xs">{t.date ? new Date(t.date).toLocaleDateString() : '—'}</td>
                            <td className="px-2 py-2 text-xs">
                              <div>{t.description || t.vendor_name || '—'}</div>
                              {t.category && <div className="text-neutral-400 text-[10px]">{t.category}</div>}
                              {t.vehicle_name && t.vehicle_name.trim() && <div className="text-neutral-400 text-[10px]">{t.stock_number} - {t.vehicle_name}</div>}
                            </td>
                            <td className={`px-2 py-2 font-mono font-medium text-right ${isDebit ? 'text-red-600' : 'text-green-600'}`}>
                              {isDebit ? '-' : '+'}${parseFloat(t.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </td>
                            <td className="px-2 py-2 font-mono text-xs text-right text-neutral-500">
                              ${balances[i]?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </td>
                            <td className="px-2 py-2">
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                t.type === 'expense' ? 'bg-red-50 text-red-600' :
                                t.type === 'bill' ? 'bg-purple-50 text-purple-600' :
                                t.type === 'part' ? 'bg-blue-50 text-blue-600' :
                                'bg-neutral-100 text-neutral-600'
                              }`}>
                                {t.type || t.entry_type || t.entryType || 'entry'}
                              </span>
                            </td>
                            <td className="px-2 py-2 text-right">
                              <button
                                onClick={async (ev) => {
                                  ev.stopPropagation();
                                  if (!window.confirm(`Delete this ${t.type || 'entry'} for $${parseFloat(t.amount || 0).toFixed(2)}? This will reverse the bank balance.`)) return;
                                  try {
                                    await api.deleteTransaction(t.type || 'entry', t.id);
                                    const accountId = detailModal?.accountId;
                                    if (accountId) {
                                      const endDate = new Date().toISOString().split('T')[0];
                                      const startDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
                                      const freshData = await api.getReconciliation(accountId, startDate, endDate);
                                      const freshSummary = await api.getDashboardSummary();
                                      setSummary(freshSummary);
                                      const account = freshSummary?.bankAccounts?.find(a => a.id === accountId);
                                      setDetailData({ type: 'bank', transactions: (freshData.transactions || []).slice(0, 50), account: account || freshData.account });
                                    }
                                  } catch (err) {
                                    alert('Delete failed: ' + err.message);
                                  }
                                }}
                                className="text-xs text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Delete transaction"
                              >
                                del
                              </button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="bg-neutral-50">
                              <td colSpan={6} className="px-4 py-3">
                                <div className="grid grid-cols-3 gap-3 text-xs">
                                  {t.vendor_name && <div><span className="text-neutral-500">Vendor:</span> <span className="font-medium">{t.vendor_name}</span></div>}
                                  {t.category && <div><span className="text-neutral-500">Category:</span> <span className="font-medium">{t.category}</span></div>}
                                  {t.payment_method && <div><span className="text-neutral-500">Method:</span> <span className="font-medium">{t.payment_method}{t.check_number ? ` #${t.check_number}` : ''}</span></div>}
                                  {(t.stock_number || t.vehicle_name) && <div><span className="text-neutral-500">Vehicle:</span> <span className="font-medium">{t.stock_number ? `#${t.stock_number}` : ''} {t.vehicle_name || ''}</span></div>}
                                  {t.memo && <div className="col-span-3"><span className="text-neutral-500">Memo:</span> <span>{t.memo}</span></div>}
                                </div>
                                {t.type === 'expense' && (
                                  <div className="mt-2 flex gap-2">
                                    <button
                                      onClick={(ev) => {
                                        ev.stopPropagation();
                                        const receiptHtml = `<!DOCTYPE html><html><head><title>Expense Receipt</title><style>body{font-family:Arial,sans-serif;padding:40px;max-width:450px;margin:0 auto}.header{text-align:center;border-bottom:2px solid #333;padding-bottom:20px;margin-bottom:20px}.company{font-size:24px;font-weight:bold}.title{font-size:18px;color:#666;margin-top:5px}.row{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #eee}.label{color:#666}.value{font-weight:bold}.total{font-size:20px;margin-top:20px;padding-top:20px;border-top:2px solid #333}.footer{margin-top:40px;text-align:center;color:#999;font-size:12px}</style></head><body>
                                          <div class="header"><div class="company">THE WHOLESALE CARS</div><div class="title">EXPENSE RECEIPT</div></div>
                                          <div class="details">
                                            <div class="row"><span class="label">Receipt #:</span><span class="value">EXP${t.id}</span></div>
                                            <div class="row"><span class="label">Date:</span><span class="value">${t.date ? new Date(t.date).toLocaleDateString() : ''}</span></div>
                                            ${t.category ? `<div class="row"><span class="label">Category:</span><span class="value">${t.category}</span></div>` : ''}
                                            ${t.description ? `<div class="row"><span class="label">Description:</span><span class="value">${t.description}</span></div>` : ''}
                                            ${t.vendor_name ? `<div class="row"><span class="label">Vendor:</span><span class="value">${t.vendor_name}</span></div>` : ''}
                                            <div class="row"><span class="label">Paid From:</span><span class="value">${detailData.account?.name || ''}</span></div>
                                          </div>
                                          <div class="row total"><span>AMOUNT:</span><span>$${parseFloat(t.amount || 0).toFixed(2)}</span></div>
                                          <div class="footer"><p>4001 W Alexis Rd, Toledo, OH 43623</p></div>
                                        </body></html>`;
                                        const pw = window.open('', '_blank');
                                        pw.document.write(receiptHtml);
                                        pw.document.close();
                                        pw.focus();
                                        setTimeout(() => pw.print(), 250);
                                      }}
                                      className="text-xs px-2 py-1 bg-neutral-800 text-white rounded hover:bg-neutral-700"
                                    >
                                      Print Receipt
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-8 text-neutral-400">No recent transactions</div>
                )}
              </div>
              );
            })()}

            {/* AR Detail */}
            {detailData?.type === 'ar' && !detailLoading && (
              <div>
                <div className="text-sm text-neutral-500 mb-3">
                  Total AR: <span className="font-mono font-bold text-amber-800">${(detailData.totalAR || 0).toLocaleString()}</span>
                  <span className="ml-3">{detailData.count || detailData.items.length} unpaid deal{(detailData.count || detailData.items.length) !== 1 ? 's' : ''}</span>
                </div>
                {detailData.items.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                        <th className="px-3 py-2 font-medium">Customer</th>
                        <th className="px-3 py-2 font-medium">Vehicle</th>
                        <th className="px-3 py-2 font-medium text-right">Amount Owed</th>
                        <th className="px-3 py-2 font-medium">Days Outstanding</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailData.items.map(deal => (
                        <tr key={deal.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                          <td className="px-3 py-2 font-medium">{deal.buyer_name || '—'}</td>
                          <td className="px-3 py-2">{deal.stock_number} - {deal.year} {deal.make} {deal.model}</td>
                          <td className="px-3 py-2 font-mono font-medium text-right text-amber-700">
                            ${parseFloat(deal.amount_owed || deal.balance_due || deal.sale_price || 0).toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-xs">{daysSince(deal.sale_date || deal.created_at)} days</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-8 text-neutral-400">No unpaid deals</div>
                )}
              </div>
            )}
          </div>
          <div className="p-4 border-t border-neutral-200">
            <button onClick={closeDetail} className="w-full px-4 py-2 bg-neutral-100 hover:bg-neutral-200 text-neutral-700 rounded-lg">Close</button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};


// Compact Calculator Widget
const CalculatorWidget = ({ onClose }) => {
  const [display, setDisplay] = useState('0');
  const [prev, setPrev] = useState(null);
  const [op, setOp] = useState(null);
  const [fresh, setFresh] = useState(true);

  const input = (d) => {
    if (fresh) { setDisplay(d); setFresh(false); }
    else setDisplay(display === '0' && d !== '.' ? d : display + d);
  };
  const clear = () => { setDisplay('0'); setPrev(null); setOp(null); setFresh(true); };
  const doOp = (nextOp) => {
    if (prev !== null && op && !fresh) {
      const a = parseFloat(prev), b = parseFloat(display);
      let r = 0;
      if (op === '+') r = a + b;
      else if (op === '-') r = a - b;
      else if (op === '*') r = a * b;
      else if (op === '/') r = b !== 0 ? a / b : 0;
      const rs = parseFloat(r.toFixed(10)).toString();
      setDisplay(rs); setPrev(rs);
    } else {
      setPrev(display);
    }
    setOp(nextOp); setFresh(true);
  };
  const equals = () => { doOp(null); };
  const pct = () => setDisplay((parseFloat(display) / 100).toString());
  const negate = () => setDisplay((parseFloat(display) * -1).toString());

  const Btn = ({ label, onClick, className = '' }) => (
    <button onClick={onClick} className={`h-9 rounded-md text-sm font-medium transition-colors ${className}`}>{label}</button>
  );

  return (
    <div className="fixed bottom-20 right-6 z-[70] w-56 bg-white rounded-xl shadow-2xl border border-neutral-200 overflow-hidden select-none">
      <div className="flex items-center justify-between px-3 py-1.5 bg-neutral-800 text-white">
        <span className="text-xs font-medium">Calculator</span>
        <button onClick={onClose} className="text-neutral-400 hover:text-white text-sm">×</button>
      </div>
      <div className="px-3 py-2 bg-neutral-50 text-right font-mono text-lg font-bold text-neutral-800 truncate border-b border-neutral-200">
        {display}
      </div>
      <div className="grid grid-cols-4 gap-1 p-2">
        <Btn label="C" onClick={clear} className="bg-neutral-200 hover:bg-neutral-300 text-neutral-700" />
        <Btn label="+/-" onClick={negate} className="bg-neutral-200 hover:bg-neutral-300 text-neutral-700" />
        <Btn label="%" onClick={pct} className="bg-neutral-200 hover:bg-neutral-300 text-neutral-700" />
        <Btn label="÷" onClick={() => doOp('/')} className="bg-blue-500 hover:bg-blue-600 text-white" />
        <Btn label="7" onClick={() => input('7')} className="bg-white hover:bg-neutral-100 border border-neutral-200" />
        <Btn label="8" onClick={() => input('8')} className="bg-white hover:bg-neutral-100 border border-neutral-200" />
        <Btn label="9" onClick={() => input('9')} className="bg-white hover:bg-neutral-100 border border-neutral-200" />
        <Btn label="×" onClick={() => doOp('*')} className="bg-blue-500 hover:bg-blue-600 text-white" />
        <Btn label="4" onClick={() => input('4')} className="bg-white hover:bg-neutral-100 border border-neutral-200" />
        <Btn label="5" onClick={() => input('5')} className="bg-white hover:bg-neutral-100 border border-neutral-200" />
        <Btn label="6" onClick={() => input('6')} className="bg-white hover:bg-neutral-100 border border-neutral-200" />
        <Btn label="-" onClick={() => doOp('-')} className="bg-blue-500 hover:bg-blue-600 text-white" />
        <Btn label="1" onClick={() => input('1')} className="bg-white hover:bg-neutral-100 border border-neutral-200" />
        <Btn label="2" onClick={() => input('2')} className="bg-white hover:bg-neutral-100 border border-neutral-200" />
        <Btn label="3" onClick={() => input('3')} className="bg-white hover:bg-neutral-100 border border-neutral-200" />
        <Btn label="+" onClick={() => doOp('+')} className="bg-blue-500 hover:bg-blue-600 text-white" />
        <Btn label="0" onClick={() => input('0')} className="col-span-2 bg-white hover:bg-neutral-100 border border-neutral-200" />
        <Btn label="." onClick={() => input('.')} className="bg-white hover:bg-neutral-100 border border-neutral-200" />
        <Btn label="=" onClick={equals} className="bg-neutral-800 hover:bg-neutral-700 text-white" />
      </div>
    </div>
  );
};

// Error Boundary for Accounting Module - prevents white screen crashes
class AccountingErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error('AccountingModule render error:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 text-center">
          <div className="text-red-600 mb-4 text-lg font-semibold">Something went wrong in Accounting</div>
          <p className="text-neutral-500 mb-4 text-sm">{this.state.error?.message || 'Unknown render error'}</p>
          <button
            onClick={() => { this.setState({ hasError: false, error: null }); if (this.props.onRefresh) this.props.onRefresh(); }}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Reload Module
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Safe child helper: guarantees no object/array is ever rendered as a React child (prevents React #300)
const sc = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return v;
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

// Accounting Module - Full CRUD with bank accounts, payment verification, and ledger
const AccountingModule = ({ currentUser, expenses, vehicles, vendors, onRefresh, onSilentRefresh, showFinancials, onOpenBankAccount, initialTab, refreshTrigger, onQuickEdit, autoOpenExpenseTrigger = 0 }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [accountingEntries, setAccountingEntries] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(null);
  
  // Persist active tab to localStorage so it survives refresh
  const getPersistedTab = () => {
    try {
      return localStorage.getItem('accountingActiveTab') || 'expenses';
    } catch { return 'expenses'; }
  };
  const [activeTab, setActiveTabState] = useState(initialTab || getPersistedTab());
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    try { localStorage.setItem('accountingActiveTab', tab); } catch {}
  };
  const [floorplanSummary, setFloorplanSummary] = useState({ totalFloored: 0, totalFlooredAmount: 0 });
  const [buyingPowerData, setBuyingPowerData] = useState(null);
  const [showNetWorth, setShowNetWorth] = useState(false);
  const [invoiceFormTrigger, setInvoiceFormTrigger] = useState(0);
  const [revealedInfo, setRevealedInfo] = useState(new Set());
  const toggleInfo = (id, e) => { if (e) e.stopPropagation(); setRevealedInfo(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const infoHv = '$•••••';

  // Auto-open new expense form when triggered from home screen quick action
  useEffect(() => {
    if (autoOpenExpenseTrigger > 0) {
      setActiveTab('expenses');
      window.setTimeout(() => { setEditingExpense(null); setShowForm(true); }, 100);
    }
  }, [autoOpenExpenseTrigger]);
  const [showCalculator, setShowCalculator] = useState(false);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [pendingPaymentsSummary, setPendingPaymentsSummary] = useState(null);
  const [showClearPaymentModal, setShowClearPaymentModal] = useState(null);
  const [selectedBatchIds, setSelectedBatchIds] = useState([]);
  const [showBatchClearModal, setShowBatchClearModal] = useState(null);
  const [viewingExpense, setViewingExpense] = useState(null);
  const [viewingTransaction, setViewingTransaction] = useState(null);
  const [fixFloorplanPendingLoading, setFixFloorplanPendingLoading] = useState(false);
  const [bulkMarkClearedLoading, setBulkMarkClearedLoading] = useState(false);
  const [backfillDealPaymentsLoading, setBackfillDealPaymentsLoading] = useState(false);
  const [recoverTransportLoading, setRecoverTransportLoading] = useState(false);
  const [transportRecoveryPreview, setTransportRecoveryPreview] = useState(null);

  const handleRecoverTransportCosts = async () => {
    setRecoverTransportLoading(true);
    try {
      const preview = await api.recoverTransportCosts(true);
      if (!preview.vehiclesToRecover || preview.vehiclesToRecover === 0) {
        alert('No vehicles need transport cost recovery. All transport costs are already populated.');
        return;
      }
      setTransportRecoveryPreview(preview.vehicles);
    } catch (e) {
      alert('Failed: ' + (e.message || e));
    } finally {
      setRecoverTransportLoading(false);
    }
  };

  const handleApplyTransportRecovery = async () => {
    setRecoverTransportLoading(true);
    try {
      const result = await api.recoverTransportCosts(false);
      alert(`Done! Recovered transport costs for ${result.recovered} vehicles.`);
      setTransportRecoveryPreview(null);
      if (onRefresh) onRefresh();
    } catch (e) {
      alert('Failed: ' + (e.message || e));
    } finally {
      setRecoverTransportLoading(false);
    }
  };

  const handleFixFloorplanPending = async () => {
    if (!confirm('Set 3 vehicles as floorplan and keep 5 as pending? This will update the database.')) return;
    setFixFloorplanPendingLoading(true);
    try {
      const r = await api.fixFloorplanPending();
      const created = r.pending?.created || 0;
      alert(`Done. Floorplan: ${r.floorplan?.updated || 0} updated. Pending: ${r.pending?.updated || 0} kept.${created ? ` ${created} pending payment(s) created.` : ''}`);
      refreshAccountingData();
      if (onRefresh) onRefresh();
    } catch (e) {
      alert('Failed: ' + (e.message || e));
    } finally {
      setFixFloorplanPendingLoading(false);
    }
  };

  const handleBulkMarkCleared = async () => {
    if (!confirm('Mark ALL vehicles and pending payments as cleared EXCEPT the 5 pending VINs? No bank debit. OK?')) return;
    setBulkMarkClearedLoading(true);
    try {
      const r = await api.bulkMarkCleared();
      alert(`Done. ${r.vehiclesUpdated || 0} vehicles updated, ${r.pendingPaymentsCleared || 0} pending payments cleared.`);
      refreshAccountingData();
      if (onRefresh) onRefresh();
    } catch (e) {
      alert('Failed: ' + (e.message || e));
    } finally {
      setBulkMarkClearedLoading(false);
    }
  };

  const handleBackfillDealPayments = async () => {
    if (!confirm('Backfill: Credit bank for deals marked paid but never got bank entries. Also fix deal status (revert to pending if costs not finalized). OK?')) return;
    setBackfillDealPaymentsLoading(true);
    try {
      const r = await api.backfillDealPayments();
      alert(`Done. ${r.backfilled || 0} deal(s) credited to bank. ${r.statusFixed || 0} deal(s) status fixed (pending until costs finalized).`);
      refreshAccountingData();
      if (onRefresh) onRefresh();
    } catch (e) {
      alert('Failed: ' + (e.message || e));
    } finally {
      setBackfillDealPaymentsLoading(false);
    }
  };

  const [recalcBalancesLoading, setRecalcBalancesLoading] = useState(false);
  const handleRecalculateBalances = async () => {
    if (!confirm('Recalculate ALL bank account balances from actual transactions? This will also clean up orphaned entries. Current balances will be overwritten.')) return;
    setRecalcBalancesLoading(true);
    try {
      const r = await api.recalculateBalances();
      const lines = (r.accounts || []).map(a => 
        `${a.name}: $${a.oldBalance.toLocaleString()} → $${a.newBalance.toLocaleString()} (${a.difference >= 0 ? '+' : ''}$${a.difference.toLocaleString()})`
      );
      alert(`Recalculation complete!\n\nOrphaned entries cleaned: ${r.orphansCleaned || 0}\n\n${lines.join('\n')}`);
      refreshAccountingData();
      if (onRefresh) onRefresh();
    } catch (e) {
      alert('Failed: ' + (e.message || e));
    } finally {
      setRecalcBalancesLoading(false);
    }
  };

  const ExpenseDetailModal = ({ expense, onClose, vendors, bankAccounts }) => {
  if (!expense) return null;

  const vendor = vendors.find(v => v.id === expense.vendor_id);
  const bankAccount = bankAccounts.find(ba => ba.id === expense.bank_account_id);

  // Use line_items if available, otherwise fall back to legacy fields
  const lineItems = expense.line_items && expense.line_items.length > 0 
    ? expense.line_items 
    : [{ category: expense.category, description: expense.description, amount: expense.amount }];

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]" onClick={(e) => { e.preventDefault(); onClose(); }}>
      <div className="bg-white w-full max-w-lg rounded-lg shadow-lg max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-neutral-200 flex justify-between items-center">
          <span className="font-medium">Expense Detail</span>
          <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }} className="text-neutral-400 hover:text-neutral-600 text-xl">×</button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <label className="block text-xs text-neutral-500">Vendor</label>
              <p>{sc(vendor?.name) || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-xs text-neutral-500">Date</label>
              <p>{formatDate(expense.date)}</p>
            </div>
            <div>
              <label className="block text-xs text-neutral-500">Total Amount</label>
              <p className="font-mono font-medium">${parseFloat(expense.amount).toLocaleString()}</p>
            </div>
            <div>
              <label className="block text-xs text-neutral-500">Paid From</label>
              <p>{sc(bankAccount?.name) || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-xs text-neutral-500">Payment Method</label>
              <p>{sc(expense.payment_method)}{expense.check_number ? ` #${sc(expense.check_number)}` : ''}</p>
            </div>
             {expense.memo && (
              <div className="col-span-2">
                <label className="block text-xs text-neutral-500">Memo</label>
                <p>{sc(expense.memo)}</p>
              </div>
            )}
          </div>

          <div className="border-t pt-4">
            <h3 className="text-sm font-medium mb-2">Line Items</h3>
            <table className="w-full text-sm">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-neutral-600">Category</th>
                  <th className="px-3 py-2 text-left font-medium text-neutral-600">Description</th>
                  <th className="px-3 py-2 text-right font-medium text-neutral-600">Amount</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item, index) => (
                  <tr key={index} className="border-b border-neutral-100">
                    <td className="px-3 py-2">{sc(item.category)}</td>
                    <td className="px-3 py-2">{sc(item.description)}</td>
                    <td className="px-3 py-2 text-right font-mono">${parseFloat(item.amount).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="p-4 border-t border-neutral-200">
          <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }} className="w-full px-4 py-2 bg-neutral-100 hover:bg-neutral-200 text-neutral-700 rounded-lg">Close</button>
        </div>
      </div>
    </div>
  );
};

  // Update tab when initialTab prop changes
  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);
  const [showBillChecklist, setShowBillChecklist] = useState(false);
  const [showBackupManager, setShowBackupManager] = useState(false);
  
  // Sorting and filtering state
  const [ledgerSort, setLedgerSort] = useState({ key: 'date', dir: 'desc' });
  const [expenseSort, setExpenseSort] = useState({ key: 'date', dir: 'desc' });
  const [pendingSort, setPendingSort] = useState({ key: 'date', dir: 'desc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingSearchTerm, setPendingSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [accountFilter, setAccountFilter] = useState('all');
  const [expenseAccountFilter, setExpenseAccountFilter] = useState('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [ledgerReconciliation, setLedgerReconciliation] = useState({ transactions: [], account: null });
  const [ledgerReconciliationLoading, setLedgerReconciliationLoading] = useState(false);
  const [ledgerDeleteTrigger, setLedgerDeleteTrigger] = useState(0);

  const handleDeleteLedgerTransaction = async (entry) => {
    const type = entry.type || 'entry';
    const id = entry.id;
    if (!confirm(`Delete this ${type} transaction? This will reverse the bank balance.`)) return;
    try {
      await api.deleteTransaction(type, id);
      refreshAccountingData();
      setLedgerDeleteTrigger(t => t + 1);
      onRefresh();
    } catch (err) {
      alert('Failed to delete: ' + (err.message || err));
    }
  };

  useEffect(() => {
    const loadAccountingData = async () => {
      try {
        // Backfill any orphaned expense categories before loading
        api.backfillCategories().catch(e => console.warn('Category backfill skipped:', e));
        const [banks, entries, cats, allTxns] = await Promise.all([
          api.getBankAccounts(),
          api.getAccountingEntries({ limit: 500 }),
          api.getAccountingCategories(),
          api.getAllTransactions()
        ]);
        setBankAccounts(banks);
        setAccountingEntries(entries);
        setCategories(cats);
        setAllTransactions(allTxns.transactions || []);
      } catch (e) {
        console.error('Failed to load accounting data:', e);
      }
      // Load pending payments
      try {
        const [pending, summary] = await Promise.all([
          api.getPendingPayments(),
          api.getPendingPaymentsSummary()
        ]);
        setPendingPayments(pending);
        setPendingPaymentsSummary(summary);
      } catch (e) {
        console.error('Failed to load pending payments:', e);
      }
      // Load floorplan separately so it doesn't break other data if it fails
      try {
        const floorplan = await api.getFloorplan();
        if (floorplan?.summary) setFloorplanSummary(floorplan.summary);
      } catch (e) {
        console.error('Failed to load floorplan data:', e);
      }
      // Load buying power data for floorplan usage and net worth
      try {
        const bpData = await api.getBuyingPower();
        setBuyingPowerData(bpData);
      } catch (e) {
        console.error('Failed to load buying power data:', e);
      }
    };
    if (showFinancials) loadAccountingData();
  }, [showFinancials, refreshTrigger]);

  // When Ledger tab shows a specific account, load reconciliation (same data as bank modal)
  useEffect(() => {
    if (!showFinancials || accountFilter === 'all' || !bankAccounts?.length) {
      setLedgerReconciliation({ transactions: [], account: null });
      return;
    }
    const accId = parseInt(accountFilter);
    if (isNaN(accId)) return;
    const loadReconciliation = async () => {
      setLedgerReconciliationLoading(true);
      try {
        const endDate = new Date().toISOString().split('T')[0];
        const startDate = new Date(Date.now() - 3 * 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        const rec = await api.getReconciliation(accId, startDate, endDate);
        setLedgerReconciliation({ transactions: rec.transactions || [], account: rec.account });
      } catch (e) {
        console.error('Failed to load ledger reconciliation:', e);
        setLedgerReconciliation({ transactions: [], account: null });
      } finally {
        setLedgerReconciliationLoading(false);
      }
    };
    loadReconciliation();
  }, [showFinancials, accountFilter, bankAccounts?.length, ledgerDeleteTrigger]);

  if (!showFinancials) return <Empty text="No access to financial data" />;

  // Diagnostic: find any object fields in expenses that would crash React rendering
  if (expenses?.length > 0) {
    const sample = expenses[0];
    const objFields = Object.entries(sample).filter(([k, v]) => v !== null && typeof v === 'object').map(([k]) => k);
    if (objFields.length > 0) console.warn('[AccountingModule] Object fields in expenses:', objFields, 'Sample:', JSON.stringify(sample));
  }

  // Sanitize expenses: ensure no object/array fields leak into JSX rendering
  const safeExpenses = (expenses || []).map(e => {
    const safe = { ...e };
    Object.keys(safe).forEach(k => {
      if (safe[k] !== null && typeof safe[k] === 'object' && !(safe[k] instanceof Date)) {
        // Preserve line_items as-is for the detail modal
        if (k !== 'line_items') safe[k] = String(safe[k]);
      }
    });
    return safe;
  });

  // Separate pending payment verifications from other expenses
  const pendingVerifications = safeExpenses.filter(e => e.pending_action === 'verify_payment' && !e.action_completed);
  const completedExpenses = safeExpenses.filter(e => e.pending_action !== 'verify_payment' || e.action_completed);
  const totalExpenses = completedExpenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
  
  // Sorting helper
  const sortData = (data, sortConfig, type = 'ledger') => {
    return [...data].sort((a, b) => {
      const dir = sortConfig.dir === 'asc' ? 1 : -1;
      switch (sortConfig.key) {
        case 'date': return ((a.date || '') > (b.date || '') ? 1 : -1) * dir;
        case 'amount': return (parseFloat(a.amount || 0) - parseFloat(b.amount || 0)) * dir;
        case 'description': return (a.description || '').localeCompare(b.description || '') * dir;
        case 'category': return (a.category || '').localeCompare(b.category || '') * dir;
        case 'account': return (a.bankAccountName || '').localeCompare(b.bankAccountName || '') * dir;
        case 'vehicle': return (a.stockNumber || '').localeCompare(b.stockNumber || '') * dir;
        case 'method': return (a.paymentMethod || '').localeCompare(b.paymentMethod || '') * dir;
        default: return 0;
      }
    });
  };
  
  // Filter and sort ledger entries - use unified all-transactions endpoint for "All Accounts" view
  // This includes expenses, bill payments, parts purchases, and manual entries from ALL bank accounts
  const allLedgerEntries = allTransactions.map(t => {
    // Sanitize: convert any object fields to strings to prevent React error #300
    const safe = { ...t };
    Object.keys(safe).forEach(k => {
      if (safe[k] !== null && typeof safe[k] === 'object' && !(safe[k] instanceof Date)) safe[k] = String(safe[k]);
    });
    return {
      ...safe,
      // Normalize field names from snake_case backend to what the UI expects
      bankAccountId: t.bank_account_id,
      bankAccountName: String(t.bank_account_name || ''),
      stockNumber: String(t.stock_number || ''),
      paymentMethod: String(t.payment_method || ''),
      vendor_name: String(t.vendor_name || ''),
      entryType: t.entryType || t.entry_type || 'debit',
      expenseId: t.type === 'expense' ? t.id : undefined
    };
  });
  const filteredLedger = allLedgerEntries.filter(e => {
    if (accountFilter !== 'all' && e.bankAccountId !== parseInt(accountFilter)) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (e.description || '').toLowerCase().includes(search) ||
             (e.stockNumber || '').toLowerCase().includes(search) ||
             (e.paymentMethod || '').toLowerCase().includes(search) ||
             (e.vendor_name || '').toLowerCase().includes(search) ||
             (e.bankAccountName || '').toLowerCase().includes(search) ||
             (e.category || '').toLowerCase().includes(search) ||
             String(e.amount || '').includes(searchTerm);
    }
    return true;
  });

  // When viewing a specific account, use reconciliation (same as bank modal: expenses + bills + parts + entries)
  const ledgerAccount = accountFilter !== 'all' ? bankAccounts?.find(a => a.id === parseInt(accountFilter)) : null;
  const ledgerReconRaw = (ledgerReconciliation.transactions || []).map(t => ({
    ...t,
    stockNumber: t.stock_number || t.stockNumber,
    bankAccountName: ledgerAccount?.name
  }));
  const ledgerReconFiltered = ledgerReconRaw.filter(t => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (t.description || '').toLowerCase().includes(s) ||
           (t.vendor_name || '').toLowerCase().includes(s) ||
           (t.stock_number || '').toLowerCase().includes(s) ||
           (t.category || '').toLowerCase().includes(s) ||
           String(t.amount || '').includes(searchTerm);
  });
  const ledgerReconSorted = sortData(ledgerReconFiltered, ledgerSort);

  // Compute balance before/after for ledger when viewing single account
  const ledgerWithBalance = accountFilter !== 'all' && ledgerAccount && ledgerReconciliation.transactions?.length
    ? (() => {
        const currentBalance = parseFloat(ledgerAccount?.balance || 0);
        let runningBalance = currentBalance;
        return ledgerReconSorted.map(t => {
          const isDebit = t.entryType === 'debit' || t.type === 'expense' || t.type === 'bill' || t.type === 'part';
          const amt = parseFloat(t.amount || 0);
          const balanceAfter = runningBalance;
          const balanceBefore = isDebit ? balanceAfter + amt : balanceAfter - amt;
          runningBalance = balanceBefore;
          return { ...t, balanceBefore, balanceAfter };
        });
      })()
    : ledgerReconSorted;

  const sortedLedger = accountFilter !== 'all' && ledgerReconciliation.transactions?.length
    ? ledgerWithBalance
    : sortData(filteredLedger, ledgerSort);
  
  // Filter and sort expenses
  const filteredExpenses = completedExpenses.filter(e => {
    if (categoryFilter !== 'all' && e.category !== categoryFilter) return false;
    if (vendorFilter && e.vendor_id?.toString() !== vendorFilter) return false;
    if (vehicleFilter && e.vehicle_id?.toString() !== vehicleFilter) return false;
    if (expenseAccountFilter && e.bank_account_id?.toString() !== expenseAccountFilter) return false;
    if (paymentMethodFilter && e.payment_method !== paymentMethodFilter) return false;
    if (dateFromFilter && e.date?.split('T')[0] < dateFromFilter) return false;
    if (dateToFilter && e.date?.split('T')[0] > dateToFilter) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const vendorName = vendors.find(v => v.id === e.vendor_id)?.name || '';
      const accountName = bankAccounts.find(ba => ba.id === e.bank_account_id)?.name || '';
      // Search all line item categories and descriptions
      const lineItemsText = (e.line_items || []).map(li => `${li.category || ''} ${li.description || ''}`).join(' ');
      return (e.description || '').toLowerCase().includes(search) ||
             (e.check_number || '').toLowerCase().includes(search) ||
             (e.category || '').toLowerCase().includes(search) ||
             (e.memo || '').toLowerCase().includes(search) ||
             vendorName.toLowerCase().includes(search) ||
             accountName.toLowerCase().includes(search) ||
             lineItemsText.toLowerCase().includes(search) ||
             (e.amount?.toString() || '').includes(search);
    }
    return true;
  });
  const sortedExpenses = sortData(filteredExpenses, expenseSort);
  
  // Toggle sort helper
  const toggleLedgerSort = (key) => {
    setLedgerSort(prev => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };
  
  const toggleExpenseSort = (key) => {
    setExpenseSort(prev => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  const togglePendingSort = (key) => {
    setPendingSort(prev => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Filter and sort pending verifications
  const filteredPending = pendingVerifications.filter(e => {
    if (!pendingSearchTerm) return true;
    const search = pendingSearchTerm.toLowerCase();
    const v = vehicles.find(veh => veh.id === e.vehicle_id);
    return (e.description || '').toLowerCase().includes(search) ||
           (v?.stockNumber || '').toLowerCase().includes(search) ||
           (v?.make || '').toLowerCase().includes(search) ||
           (v?.model || '').toLowerCase().includes(search) ||
           (e.payment_method || '').toLowerCase().includes(search);
  });
  const sortedPending = sortData(filteredPending, pendingSort);

  const PendingSortHeader = ({ id, label }) => {
    const active = pendingSort.key === id;
    const arrow = active ? (pendingSort.dir === 'asc' ? '▲' : '▼') : '⇅';
    return (
      <th className="px-3 py-2 font-medium">
        <button onClick={() => togglePendingSort(id)} className={`flex items-center gap-1 text-xs hover:text-blue-600 ${active ? 'text-blue-600' : ''}`}>
          {label}
          <span className={`text-[10px] ${active ? '' : 'opacity-30'}`}>{arrow}</span>
        </button>
      </th>
    );
  };
  
  const LedgerSortHeader = ({ id, label }) => {
    const active = ledgerSort.key === id;
    const arrow = active ? (ledgerSort.dir === 'asc' ? '▲' : '▼') : '⇅';
    return (
      <button onClick={() => toggleLedgerSort(id)} className={`flex items-center gap-1 text-xs font-medium hover:text-blue-600 ${active ? 'text-blue-600' : ''}`}>
        {label}
        <span className={`text-[10px] ${active ? '' : 'opacity-30'}`}>{arrow}</span>
      </button>
    );
  };
  
  const ExpenseSortHeader = ({ id, label }) => {
    const active = expenseSort.key === id;
    const arrow = active ? (expenseSort.dir === 'asc' ? '▲' : '▼') : '⇅';
    return (
      <button onClick={() => toggleExpenseSort(id)} className={`flex items-center gap-1 text-xs font-medium hover:text-blue-600 ${active ? 'text-blue-600' : ''}`}>
        {label}
        <span className={`text-[10px] ${active ? '' : 'opacity-30'}`}>{arrow}</span>
      </button>
    );
  };

  const handleDelete = async (expense) => {
    const vendor = vendors.find(v => v.id === expense.vendor_id);
    const bankAccount = bankAccounts.find(ba => ba.id === expense.bank_account_id);
    const details = [
      `Amount: $${parseFloat(expense.amount).toFixed(2)}`,
      `Date: ${formatDate(expense.date)}`,
      vendor ? `Vendor: ${vendor.name}` : null,
      expense.description ? `Description: ${expense.description}` : null,
      bankAccount ? `Account: ${bankAccount.name}` : null,
      expense.check_number ? `Check #${expense.check_number}` : null
    ].filter(Boolean).join('\n');
    
    if (!window.confirm(`Delete this expense?\n\n${details}\n\nThis will add $${parseFloat(expense.amount).toFixed(2)} back to ${bankAccount?.name || 'the account'}.`)) return;
    try {
      await api.deleteExpense(expense.id);
      refreshAccountingData();
      onRefresh();
    } catch (e) {
      alert('Delete failed: ' + e.message);
    }
  };

  const refreshAccountingData = async () => {
    try {
      const [banks, entries, allTxns] = await Promise.all([
        api.getBankAccounts(),
        api.getAccountingEntries({ limit: 500 }),
        api.getAllTransactions()
      ]);
      setBankAccounts(banks);
      setAccountingEntries(entries);
      setAllTransactions(allTxns.transactions || []);
    } catch (e) {
      console.error('Failed to refresh accounting data:', e);
    }
  };

  return (
    <div className="space-y-3">
      <AccountingDashboard onOpenBankAccount={onOpenBankAccount} onAddExpense={() => { setEditingExpense(null); setShowForm(true); setActiveTab('expenses'); }} onInputSale={() => { window.location.hash = 'dealdesk'; }} onNewInvoice={() => { setActiveTab('invoices'); setInvoiceFormTrigger(t => t + 1); }} />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
        {/* Floorplan Summary Card */}
        <div 
          className="p-2.5 bg-white border border-slate-200 shadow-sm rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => setActiveTab('floorplan')}
        >
          <div className="text-[11px] text-teal-700 font-medium">Floorplan (1st Choice)</div>
          <div className="text-base font-bold font-mono tracking-tight text-slate-800">
            {revealedInfo.has('floorplan') ? '$' + (buyingPowerData?.buyingPower?.floorplanUsed || 0).toLocaleString() + ' / $' + (buyingPowerData?.buyingPower?.floorplanLimit || 200000).toLocaleString() : infoHv}
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2 mt-1">
            <div 
              className={`h-2 rounded-full ${((buyingPowerData?.buyingPower?.floorplanUsed || 0) / (buyingPowerData?.buyingPower?.floorplanLimit || 200000)) > 0.8 ? 'bg-red-500' : 'bg-teal-500'}`}
              style={{ width: `${Math.min(100, ((buyingPowerData?.buyingPower?.floorplanUsed || 0) / (buyingPowerData?.buyingPower?.floorplanLimit || 200000)) * 100)}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-xs text-slate-400">{Math.round(((buyingPowerData?.buyingPower?.floorplanUsed || 0) / (buyingPowerData?.buyingPower?.floorplanLimit || 200000)) * 100)}% • {buyingPowerData?.buyingPower?.floorplanCount || 0} vehicles</span>
            <button onClick={(e) => toggleInfo('floorplan', e)} className="text-[10px] text-slate-400 hover:text-teal-600 font-mono">{revealedInfo.has('floorplan') ? '[hide]' : '[show]'}</button>
          </div>
        </div>
        {/* Accounts Receivable Card */}
        <div 
          className="p-2.5 bg-white border border-slate-200 shadow-sm rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => window.location.hash = 'dealdesk'}
        >
          <div className="text-[11px] text-teal-700 font-medium">Accounts Receivable</div>
          <div className="text-base font-bold font-mono tracking-tight text-slate-800">
            {revealedInfo.has('ar') ? '$' + (buyingPowerData?.buyingPower?.auctionReceivables || 0).toLocaleString() : infoHv}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">{buyingPowerData?.buyingPower?.auctionReceivablesCount || 0} unpaid deals</span>
            <button onClick={(e) => toggleInfo('ar', e)} className="text-[10px] text-slate-400 hover:text-teal-600 font-mono">{revealedInfo.has('ar') ? '[hide]' : '[show]'}</button>
          </div>
        </div>
        {/* Bill Payment Checklist Card */}
        <div 
          className="p-2.5 bg-white border border-slate-200 shadow-sm rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => setShowBillChecklist(true)}
        >
          <div className="text-[11px] text-teal-700 font-medium">Monthly Bills</div>
          <div className="text-base font-bold text-slate-800">Bill Checklist</div>
          <div className="text-xs text-slate-400">Track utility payments</div>
        </div>
        {/* Backup & Reports Card */}
        <div 
          className="p-2.5 bg-white border border-slate-200 shadow-sm rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => setShowBackupManager(true)}
        >
          <div className="text-[11px] text-teal-700 font-medium">Weekly Backups</div>
          <div className="text-base font-bold text-slate-800">Backup & Reports</div>
          <div className="text-xs text-slate-400">PDF + CSV exports</div>
        </div>
        {/* Dealer Net Worth Card */}
        <div 
          className="p-2.5 bg-white border border-slate-200 shadow-sm rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => setShowNetWorth(!showNetWorth)}
        >
          <div className="flex items-center justify-between">
            <div className="text-[11px] text-teal-700 font-medium">Dealer Net Worth</div>
            <button onClick={(e) => toggleInfo('networth', e)} className="text-[10px] text-slate-400 hover:text-teal-600 font-mono">{revealedInfo.has('networth') ? '[hide]' : '[show]'}</button>
          </div>
          <div className="text-base font-bold font-mono tracking-tight text-slate-800">
            {revealedInfo.has('networth') ? '$' + ((buyingPowerData?.buyingPower?.bankBalance || 0) + (buyingPowerData?.inventory?.totalValue || 0) + (buyingPowerData?.buyingPower?.auctionReceivables || 0) - (buyingPowerData?.buyingPower?.floorplanUsed || 0) - (buyingPowerData?.buyingPower?.titlePendingOwed || 0) - (buyingPowerData?.buyingPower?.creditCardDebt || 0) - (buyingPowerData?.buyingPower?.floorplanPayoffsDue || 0) - (buyingPowerData?.buyingPower?.pendingPaymentsTotal || 0)).toLocaleString() : infoHv}
          </div>
          <div className="text-xs text-slate-400">{showNetWorth ? '▲ Hide details' : '▼ Show details'}</div>
        </div>
      </div>

      {/* Net Worth Breakdown (Expandable) */}
      {showNetWorth && buyingPowerData && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <h4 className="font-medium text-teal-800 mb-3">Net Worth Breakdown</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Assets */}
            <div className="space-y-2">
              <div className="text-xs font-medium text-teal-700 uppercase">Assets</div>
              <div className="bg-white rounded p-2 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Bank Balance:</span>
                  <span className="font-mono text-slate-800">
                    ${(buyingPowerData?.buyingPower?.bankBalance || 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Inventory Value:</span>
                  <span className="font-mono text-slate-800">${(buyingPowerData?.inventory?.totalValue || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Accounts Receivable:</span>
                  <span className="font-mono text-slate-800">${(buyingPowerData?.buyingPower?.auctionReceivables || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm pt-1 border-t border-slate-200 font-medium">
                  <span>Total Assets:</span>
                  <span className="font-mono text-slate-900 font-bold">
                    ${((buyingPowerData?.buyingPower?.bankBalance || 0) + (buyingPowerData?.inventory?.totalValue || 0) + (buyingPowerData?.buyingPower?.auctionReceivables || 0)).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Liabilities */}
            <div className="space-y-2">
              <div className="text-xs font-medium text-teal-700 uppercase">Liabilities</div>
              <div className="bg-white rounded p-2 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Floorplan Owed:</span>
                  <span className="font-mono text-red-600">-${(buyingPowerData?.buyingPower?.floorplanUsed || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Credit Card Debt:</span>
                  <span className="font-mono text-red-600">-${(buyingPowerData?.buyingPower?.creditCardDebt || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Titles Pending:</span>
                  <span className="font-mono text-red-600">-${(buyingPowerData?.buyingPower?.titlePendingOwed || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Floorplan Payoffs Due:</span>
                  <span className="font-mono text-red-600">-${(buyingPowerData?.buyingPower?.floorplanPayoffsDue || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Pending Drafts:</span>
                  <span className="font-mono text-red-600">-${(buyingPowerData?.buyingPower?.pendingPaymentsTotal || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm pt-1 border-t border-slate-200 font-medium">
                  <span>Total Liabilities:</span>
                  <span className="font-mono text-red-600 font-bold">
                    -${(buyingPowerData?.buyingPower?.totalLiabilities || 0).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Inventory Stats */}
            <div className="space-y-2">
              <div className="text-xs font-medium text-teal-700 uppercase">Inventory</div>
              <div className="bg-white rounded p-2 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Total Vehicles:</span>
                  <span className="font-mono">{buyingPowerData?.inventory?.count || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Total Cost:</span>
                  <span className="font-mono">${(buyingPowerData?.inventory?.totalCost || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Est. Sale Value:</span>
                  <span className="font-mono">${(buyingPowerData?.inventory?.totalValue || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm pt-1 border-t border-slate-200 font-medium">
                  <span>Potential Profit:</span>
                  <span className="font-mono text-slate-900 font-bold">
                    ${((buyingPowerData?.inventory?.totalValue || 0) - (buyingPowerData?.inventory?.totalCost || 0)).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Summary */}
            <div className="space-y-2">
              <div className="text-xs font-medium text-teal-700 uppercase">Summary</div>
              <div className="bg-white rounded p-2 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Buying Power:</span>
                  <span className="font-mono text-slate-800">
                    ${(buyingPowerData?.buyingPower?.total || 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Floorplan Available:</span>
                  <span className="font-mono text-slate-800">${(buyingPowerData?.buyingPower?.floorplanAvailable || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm pt-1 border-t border-slate-200">
                  <span className="font-medium">Net Worth:</span>
                  <span className="font-mono font-bold text-teal-700">
                    ${(
                      (buyingPowerData?.buyingPower?.bankBalance || 0) + 
                      (buyingPowerData?.inventory?.totalValue || 0) + 
                      (buyingPowerData?.buyingPower?.auctionReceivables || 0) - 
                      (buyingPowerData?.buyingPower?.floorplanUsed || 0) - 
                      (buyingPowerData?.buyingPower?.titlePendingOwed || 0) - 
                      (buyingPowerData?.buyingPower?.creditCardDebt || 0) - 
                      (buyingPowerData?.buyingPower?.floorplanPayoffsDue || 0) - 
                      (buyingPowerData?.buyingPower?.pendingPaymentsTotal || 0)
                    ).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Tools */}
      {currentUser?.role === 'admin' && (
        <div className="flex items-center gap-2 mb-2">
          <button
            onClick={handleRecoverTransportCosts}
            disabled={recoverTransportLoading}
            className="text-xs bg-teal-600 text-white px-3 py-1.5 rounded hover:bg-teal-700 disabled:opacity-50"
            title="Restore transport costs from estimates for vehicles where cost was zeroed"
          >
            {recoverTransportLoading ? 'Loading...' : 'Recover Transport Costs'}
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-neutral-200">
        {[
          { id: 'expenses', label: 'Expenses', count: completedExpenses.length },
          { id: 'buying_power', label: 'Buying Power', count: 0 },
          { id: 'pending', label: 'Pending Payments', count: pendingVerifications.length },
          { id: 'customers', label: 'Customers', count: 0 },
          { id: 'buyer_transport', label: 'Buyer/Transport Fees', count: 0 },
          { id: 'floorplan', label: 'Floorplan', count: 0 },
          { id: 'sales', label: 'Sales', count: 0 },
          { id: 'ledger', label: 'Ledger', count: accountingEntries.length },
          { id: 'vendors', label: 'Vendors', count: vendors.length },
          { id: 'payroll', label: 'Payroll', count: 0 },
          { id: 'categories', label: 'Categories' },
          { id: 'bank-accounts', label: 'Bank Accounts' },
          { id: 'buyer-balances', label: 'Buyer Balances' },
          { id: 'deposits', label: 'Deposits' },
          { id: 'invoices', label: 'Invoices' },
          { id: 'sales-tax', label: 'Ohio Sales Tax' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              // Refresh ledger data when switching to ledger tab
              if (tab.id === 'ledger') refreshAccountingData();
            }}
            className={`px-3 py-2 text-sm border-b-2 -mb-px ${activeTab === tab.id ? 'border-neutral-800 text-neutral-800' : 'border-transparent text-neutral-500 hover:text-neutral-700'}`}
          >
            {tab.label} {tab.count > 0 && <span className="ml-1 text-xs bg-neutral-200 px-1.5 py-0.5 rounded">{tab.count}</span>}
          </button>
        ))}
      </div>

      {/* Buying Power Tab */}
            {activeTab === 'buying_power' && (
        <BuyingPowerDashboard currentUser={currentUser} onRefresh={onRefresh} bankAccounts={bankAccounts} onOpenBankAccount={onOpenBankAccount} refreshTrigger={refreshTrigger} />
      )}

      {/* Customers Tab - Outside Service + Vehicle Sales */}
      {activeTab === 'customers' && (
        <CustomersTab />
      )}

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <InvoicesTab bankAccounts={bankAccounts} onRefresh={() => { refreshAccountingData(); onRefresh(); }} autoOpenTrigger={invoiceFormTrigger} />
      )}

      {/* Pending Payments Tab - NEW: Uses pending_payments table */}
      {activeTab === 'pending' && (
        <div>
          {/* Summary Cards */}
          {pendingPaymentsSummary && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="text-xs text-blue-600 font-medium">ACH Pending</div>
                <div className="text-lg font-bold text-blue-800">${(pendingPaymentsSummary.ach?.total || 0).toLocaleString()}</div>
                <div className="text-xs text-blue-500">{pendingPaymentsSummary.ach?.count || 0} vehicles</div>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <div className="text-xs text-purple-600 font-medium">Checks Pending</div>
                <div className="text-lg font-bold text-purple-800">${(pendingPaymentsSummary.check?.total || 0).toLocaleString()}</div>
                <div className="text-xs text-purple-500">{pendingPaymentsSummary.check?.count || 0} vehicles</div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="text-xs text-amber-600 font-medium">Cash Pending</div>
                <div className="text-lg font-bold text-amber-800">${(pendingPaymentsSummary.cash?.total || 0).toLocaleString()}</div>
                <div className="text-xs text-amber-500">{pendingPaymentsSummary.cash?.count || 0} vehicles</div>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <div className="text-xs text-orange-600 font-medium">Title Pending</div>
                <div className="text-lg font-bold text-orange-800">${(pendingPaymentsSummary.titlePending?.total || 0).toLocaleString()}</div>
                <div className="text-xs text-orange-500">{pendingPaymentsSummary.titlePending?.count || 0} vehicles</div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="text-xs text-red-600 font-medium">Total Pending</div>
                <div className="text-lg font-bold text-red-800">${(pendingPaymentsSummary.totalPending || 0).toLocaleString()}</div>
                <div className="text-xs text-red-500">{pendingPayments.length} total</div>
              </div>
            </div>
          )}

          {(() => {
            const filteredPendingForTable = pendingPayments.filter(pp => {
              if (!pendingSearchTerm.trim()) return true;
              const search = pendingSearchTerm.trim().toLowerCase();
              const searchNumeric = search.replace(/[^0-9.]/g, '');
              const haystack = [pp.stockNumber, pp.vin, pp.year, pp.make, pp.model, pp.auction, pp.paymentType, pp.checkNumber, pp.notes, pp.amount, pp.purchasePrice, pp.purchaseDate, pp.createdAt].filter(v => v != null).join(' ').toLowerCase();
              if (haystack.includes(search)) return true;
              if (searchNumeric) {
                const amountNorm = String(pp.amount ?? '').replace(/[^0-9.]/g, '');
                const purchaseNorm = String(pp.purchasePrice ?? '').replace(/[^0-9.]/g, '');
                return amountNorm.includes(searchNumeric) || purchaseNorm.includes(searchNumeric);
              }
              return false;
            });
            return (
          <>
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <input
              type="text"
              placeholder="Search anything (auction, stock#, year/make/model, amount, type, notes...)"
              value={pendingSearchTerm}
              onChange={e => setPendingSearchTerm(e.target.value)}
              className="px-3 py-1.5 text-xs border border-neutral-200 rounded-lg w-96 max-w-full"
            />
            <span className="text-xs text-neutral-500">{pendingPayments.length} pending payments</span>
            {selectedBatchIds.length >= 2 && (
              <button
                onClick={() => {
                  const selected = pendingPayments.filter(pp => selectedBatchIds.includes(pp.id));
                  setShowBatchClearModal(selected);
                }}
                className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700"
              >
                Batch Clear Selected ({selectedBatchIds.length})
              </button>
            )}
            {currentUser?.role === 'admin' && (
              <>
                <button
                  onClick={handleFixFloorplanPending}
                  disabled={fixFloorplanPendingLoading}
                  className="text-xs bg-purple-600 text-white px-3 py-1.5 rounded hover:bg-purple-700 disabled:opacity-50"
                  title="Set 3 floorplan VINs, keep 5 pending"
                >
                  {fixFloorplanPendingLoading ? 'Running...' : 'Fix Floorplan & Pending'}
                </button>
                <button
                  onClick={handleBulkMarkCleared}
                  disabled={bulkMarkClearedLoading}
                  className="text-xs bg-amber-600 text-white px-3 py-1.5 rounded hover:bg-amber-700 disabled:opacity-50"
                  title="Mark all as cleared except 5 pending VINs (no bank debit)"
                >
                  {bulkMarkClearedLoading ? 'Running...' : 'Bulk Mark Cleared'}
                </button>
                <button
                  onClick={handleBackfillDealPayments}
                  disabled={backfillDealPaymentsLoading}
                  className="text-xs bg-emerald-600 text-white px-3 py-1.5 rounded hover:bg-emerald-700 disabled:opacity-50"
                  title="Credit bank for deals marked paid; fix status for deals completed without costs finalized"
                >
                  {backfillDealPaymentsLoading ? 'Running...' : 'Backfill Deal Payments'}
                </button>
                <button
                  onClick={handleRecalculateBalances}
                  disabled={recalcBalancesLoading}
                  className="text-xs bg-red-600 text-white px-3 py-1.5 rounded hover:bg-red-700 disabled:opacity-50"
                  title="Recalculate all bank balances from actual transactions and clean orphaned entries"
                >
                  {recalcBalancesLoading ? 'Running...' : 'Recalculate Balances'}
                </button>
              </>
            )}
          </div>
          {pendingPayments.length > 0 ? (
            <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                    <th className="px-3 py-2 w-10" title="Select for batch clear">
                      <input
                        type="checkbox"
                        className="w-4 h-4"
                        checked={filteredPendingForTable.length > 0 && filteredPendingForTable.every(pp => selectedBatchIds.includes(pp.id))}
                        onChange={e => {
                          if (e.target.checked) {
                            setSelectedBatchIds(filteredPendingForTable.map(pp => pp.id));
                          } else {
                            setSelectedBatchIds([]);
                          }
                        }}
                      />
                    </th>
                    <th className="px-3 py-2 font-medium">Date</th>
                    <th className="px-3 py-2 font-medium">Vehicle</th>
                    <th className="px-3 py-2 font-medium">Amount</th>
                    <th className="px-3 py-2 font-medium">Type</th>
                    <th className="px-3 py-2 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPendingForTable.map(pp => (
                      <tr key={pp.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                        <td className="px-3 py-2 w-10" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="w-4 h-4 cursor-pointer"
                            checked={selectedBatchIds.includes(pp.id)}
                            onChange={e => {
                              if (e.target.checked) {
                                setSelectedBatchIds(prev => [...prev, pp.id]);
                              } else {
                                setSelectedBatchIds(prev => prev.filter(id => id !== pp.id));
                              }
                            }}
                          />
                        </td>
                        <td className="px-3 py-2 text-xs">{formatDate(pp.createdAt)}</td>
                        <td className="px-3 py-2">{sc(pp.stockNumber)} - {sc(pp.year)} {sc(pp.make)} {sc(pp.model)}</td>
                        <td className="px-3 py-2 font-mono font-medium">${parseFloat(pp.purchasePrice || pp.amount).toLocaleString()}</td>
                        <td className="px-3 py-2">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            pp.paymentType === 'ACH' ? 'bg-blue-100 text-blue-700' :
                            pp.paymentType === 'Check' ? 'bg-purple-100 text-purple-700' :
                            pp.paymentType === 'Cash' ? 'bg-amber-100 text-amber-700' :
                            pp.paymentType === 'Title Pending' ? 'bg-orange-100 text-orange-700' :
                            'bg-neutral-100 text-neutral-700'
                          }`}>
                            {sc(pp.paymentType)}
                          </span>
                          {pp.checkNumber && <span className="ml-1 text-xs text-neutral-500">#{sc(pp.checkNumber)}</span>}
                        </td>
                        <td className="px-3 py-2 flex gap-2">
                          {onQuickEdit && (
                            <button 
                              onClick={async () => {
                                try {
                                  let vehicle = vehicles.find(v => v.id == pp.vehicleId);
                                  if (!vehicle && pp.vehicleId) {
                                    vehicle = await api.getVehicle(pp.vehicleId);
                                  }
                                  if (!vehicle) {
                                    alert('Could not load vehicle for editing');
                                    return;
                                  }
                                  onQuickEdit(vehicle);
                                } catch (e) {
                                  alert('Could not load vehicle: ' + (e.message || 'Unknown error'));
                                }
                              }}
                              className="text-xs bg-orange-500 text-white px-3 py-1 rounded hover:bg-orange-600"
                            >
                              Edit
                            </button>
                          )}
                          <button 
                            onClick={() => setShowClearPaymentModal(pp)}
                            className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                          >
                            Mark Cleared
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Empty text="No pending payments - all vehicle purchases are cleared!" />
          )}
          </>
            );
          })()}
        </div>
      )}

      {/* Clear Payment Modal */}
      {showClearPaymentModal && (
        <ClearPaymentModal 
          payment={showClearPaymentModal} 
          bankAccounts={bankAccounts}
          onClose={() => setShowClearPaymentModal(null)}
          onSave={async (data) => {
            try {
              await api.clearPendingPayment(showClearPaymentModal.id, data);
              setShowClearPaymentModal(null);
              // Reload data
              const [banks, pending, summary, bpData] = await Promise.all([
                api.getBankAccounts(),
                api.getPendingPayments(),
                api.getPendingPaymentsSummary(),
                api.getBuyingPower()
              ]);
              setBankAccounts(banks);
              setPendingPayments(pending);
              setPendingPaymentsSummary(summary);
              setBuyingPowerData(bpData);
              onRefresh();
            } catch (e) {
              alert('Failed to clear payment: ' + e.message);
            }
          }}
        />
      )}

      {/* Batch Clear Payment Modal */}
      {showBatchClearModal && (
        <BatchClearPaymentModal
          payments={showBatchClearModal}
          bankAccounts={bankAccounts}
          onClose={() => { setShowBatchClearModal(null); setSelectedBatchIds([]); }}
          onSave={async (data) => {
            try {
              await api.batchClearPendingPayments({
                ids: showBatchClearModal.map(p => p.id),
                bankAccountId: data.bankAccountId,
                clearedDate: data.clearedDate,
                notes: data.notes
              });
              setShowBatchClearModal(null);
              setSelectedBatchIds([]);
              const [banks, pending, summary, bpData, entries] = await Promise.all([
                api.getBankAccounts(),
                api.getPendingPayments(),
                api.getPendingPaymentsSummary(),
                api.getBuyingPower(),
                api.getAccountingEntries({ limit: 50 })
              ]);
              setBankAccounts(banks);
              setPendingPayments(pending);
              setPendingPaymentsSummary(summary);
              setBuyingPowerData(bpData);
              setAccountingEntries(entries);
              onRefresh();
            } catch (e) {
              alert('Failed to batch clear: ' + e.message);
            }
          }}
        />
      )}

      {/* Buyer/Transport Fees Tab */}
      {activeTab === 'buyer_transport' && (
        <BuyerTransportFeesSubTab onRefresh={onRefresh} bankAccounts={bankAccounts} isAdmin={currentUser?.role === 'admin'} />
      )}

      {/* Floorplan Tab */}
      {activeTab === 'floorplan' && (
        <FloorplanSubTab onRefresh={onRefresh} />
      )}

      {/* Sales Tab */}
      {activeTab === 'sales' && (
        <SalesSubTab />
      )}

      {/* Ledger Tab */}
      {activeTab === 'ledger' && (
        <div>
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <input
              type="text"
              placeholder="Search description, vehicle, method..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="px-3 py-1.5 text-xs border border-neutral-200 rounded-lg w-56"
            />
            <select 
              value={accountFilter} 
              onChange={e => setAccountFilter(e.target.value)}
              className="text-xs border border-neutral-200 rounded px-2 py-1.5 bg-white"
            >
              <option value="all">All Accounts</option>
              {bankAccounts.map(acc => (
                <option key={acc.id} value={acc.id}>{acc.name}</option>
              ))}
            </select>
            <span className="text-xs text-neutral-500">{sortedLedger.length} entries</span>
            {accountFilter !== 'all' && ledgerAccount && (
              <span className="text-xs font-medium text-neutral-700">
                Balance: <span className={parseFloat(ledgerAccount.balance) < 0 ? 'text-red-600' : 'text-green-700'}>${parseFloat(ledgerAccount.balance || 0).toLocaleString()}</span>
              </span>
            )}
          </div>
          <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
            {ledgerReconciliationLoading && accountFilter !== 'all' ? (
              <div className="p-8 text-center text-neutral-500">Loading ledger...</div>
            ) : sortedLedger.length > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                    <th className="px-3 py-2"><LedgerSortHeader id="date" label="Date" /></th>
                    {accountFilter === 'all' && <th className="px-3 py-2"><LedgerSortHeader id="account" label="Account" /></th>}
                    <th className="px-3 py-2"><LedgerSortHeader id="description" label="Type" /></th>
                    <th className="px-3 py-2"><LedgerSortHeader id="description" label="Description" /></th>
                    <th className="px-3 py-2">Details</th>
                    <th className="px-3 py-2 text-right"><LedgerSortHeader id="amount" label="Amount" /></th>
                    {accountFilter !== 'all' && ledgerAccount && (
                      <>
                        <th className="px-3 py-2 text-right">Balance Before</th>
                        <th className="px-3 py-2 text-right">Balance After</th>
                      </>
                    )}
                    <th className="px-3 py-2 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedLedger.map((entry, idx) => {
                    const isCreditType = ['credit', 'income'].includes(entry.entryType);
                    const typeLabel = entry.type === 'expense' ? 'Expense' : entry.type === 'bill' ? 'Bill' : entry.type === 'part' ? 'Part' : isCreditType ? 'Credit' : 'Debit';
                    const typeClass = entry.type === 'expense' ? 'bg-blue-100 text-blue-700' : entry.type === 'bill' ? 'bg-orange-100 text-orange-700' : entry.type === 'part' ? 'bg-purple-100 text-purple-700' : isCreditType ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700';
                    const handleRowClick = () => {
                      if (entry.type === 'expense' || entry.expenseId) {
                        const expObj = safeExpenses.find(e => e.id === (entry.expenseId || entry.id));
                        if (expObj) { setViewingExpense(expObj); return; }
                      }
                      // For bills, parts, and manual entries — show generic transaction detail
                      setViewingTransaction(entry);
                    };
                    const isClickable = true;
                    return (
                      <tr key={`${entry.type}-${entry.id}-${idx}`} className={`border-b border-neutral-100 hover:bg-neutral-50 ${isClickable ? 'cursor-pointer' : ''}`} onClick={handleRowClick}>
                        <td className="px-3 py-2 text-xs">{formatDate(entry.date)}</td>
                        {accountFilter === 'all' && <td className="px-3 py-2 text-xs">{sc(entry.bankAccountName) || '-'}</td>}
                        <td className="px-3 py-2"><span className={`text-xs px-1.5 py-0.5 rounded ${typeClass}`}>{typeLabel}</span></td>
                        <td className="px-3 py-2 max-w-[200px] truncate">{sc(entry.description)}</td>
                        <td className="px-3 py-2 text-xs text-neutral-500">
                          {(entry.vendor_name || entry.vendorName) && <span className="mr-2">{sc(entry.vendor_name || entry.vendorName)}</span>}
                          {(entry.stock_number || entry.stockNumber) && <span className="font-mono">#{sc(entry.stock_number || entry.stockNumber)}</span>}
                          {entry.category && <span className="ml-2 bg-neutral-100 px-1 rounded">{sc(entry.category)}</span>}
                          {entry.paymentMethod && <span>{sc(entry.paymentMethod)}{entry.checkNumber ? ` #${sc(entry.checkNumber)}` : ''}</span>}
                        </td>
                        <td className={`px-3 py-2 text-right font-mono font-medium ${isCreditType ? 'text-green-600' : 'text-red-600'}`}>
                          {isCreditType ? '+' : '-'}${Math.abs(parseFloat(entry.amount || 0)).toLocaleString()}
                        </td>
                        {accountFilter !== 'all' && ledgerAccount && (
                          <>
                            <td className="px-3 py-2 text-right font-mono text-xs text-neutral-600">
                              {entry.balanceBefore !== undefined ? `$${entry.balanceBefore.toLocaleString()}` : '-'}
                            </td>
                            <td className="px-3 py-2 text-right font-mono text-xs font-medium">
                              {entry.balanceAfter !== undefined ? `$${entry.balanceAfter.toLocaleString()}` : '-'}
                            </td>
                          </>
                        )}
                        <td className="px-3 py-2 flex gap-1">
                          {(entry.type === 'expense' || entry.expenseId) && (
                            <button
                              onClick={(ev) => { ev.stopPropagation(); const expObj = safeExpenses.find(e => e.id === (entry.expenseId || entry.id)); if (expObj) { setEditingExpense(expObj); setShowForm(true); } }}
                              className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-1.5 py-0.5 rounded text-xs font-medium"
                              title="Edit expense"
                            >
                              Edit
                            </button>
                          )}
                          <button
                            onClick={(ev) => { ev.stopPropagation(); handleDeleteLedgerTransaction(entry); }}
                            className="text-red-600 hover:text-red-800 hover:bg-red-50 px-1.5 py-0.5 rounded text-xs font-medium"
                            title="Delete transaction"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <Empty text={searchTerm || accountFilter !== 'all' ? 'No matching entries' : 'No accounting entries'} />
            )}
          </div>
        </div>
      )}

      {/* Expenses Tab */}
      {activeTab === 'expenses' && (
        <div>
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <button onClick={() => { setEditingExpense(null); setShowForm(true); }} className="px-3 py-1.5 text-xs bg-neutral-800 text-white rounded-lg hover:bg-neutral-700">+ Add Expense</button>
            <input
              type="text"
              placeholder="Search expenses..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="px-3 py-1.5 text-xs border border-neutral-200 rounded-lg w-48"
            />
            <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="px-3 py-1.5 text-xs border border-neutral-200 rounded-lg">
                <option value="all">All Categories</option>
                {categories.filter(c => c.categoryType === 'expense').map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
            <select value={vendorFilter} onChange={e => setVendorFilter(e.target.value)} className="px-3 py-1.5 text-xs border border-neutral-200 rounded-lg">
                <option value="">All Vendors</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
            <select value={vehicleFilter} onChange={e => setVehicleFilter(e.target.value)} className="px-3 py-1.5 text-xs border border-neutral-200 rounded-lg">
                <option value="">All Vehicles</option>
                {vehicles.map(v => <option key={v.id} value={v.id}>{v.stockNumber} - {v.year} {v.make} {v.model}</option>)}
            </select>
            <select value={expenseAccountFilter} onChange={e => setExpenseAccountFilter(e.target.value)} className="px-3 py-1.5 text-xs border border-neutral-200 rounded-lg">
                <option value="">All Accounts</option>
                {bankAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <select value={paymentMethodFilter} onChange={e => setPaymentMethodFilter(e.target.value)} className="px-3 py-1.5 text-xs border border-neutral-200 rounded-lg">
                <option value="">All Methods</option>
                <option value="ACH">ACH</option>
                <option value="Check">Check</option>
                <option value="Wire">Wire</option>
                <option value="Cash">Cash</option>
                <option value="Credit Card">Credit Card</option>
            </select>
            <input type="date" value={dateFromFilter} onChange={e => setDateFromFilter(e.target.value)} className="px-3 py-1.5 text-xs border border-neutral-200 rounded-lg" title="From date" />
            <span className="text-xs text-neutral-400">to</span>
            <input type="date" value={dateToFilter} onChange={e => setDateToFilter(e.target.value)} className="px-3 py-1.5 text-xs border border-neutral-200 rounded-lg" title="To date" />
            {(categoryFilter !== 'all' || vendorFilter || vehicleFilter || expenseAccountFilter || paymentMethodFilter || dateFromFilter || dateToFilter) && (
              <button onClick={() => { setCategoryFilter('all'); setVendorFilter(''); setVehicleFilter(''); setExpenseAccountFilter(''); setPaymentMethodFilter(''); setDateFromFilter(''); setDateToFilter(''); }} className="text-xs text-blue-600 hover:underline">Clear filters</button>
            )}
            {sortedExpenses.length > 0 && (
              <button onClick={() => {
                const filterDesc = [];
                if (categoryFilter !== 'all') filterDesc.push(`Category: ${categoryFilter}`);
                if (vendorFilter) filterDesc.push(`Vendor: ${vendors.find(v => v.id.toString() === vendorFilter)?.name || vendorFilter}`);
                if (expenseAccountFilter) filterDesc.push(`Account: ${bankAccounts.find(a => a.id.toString() === expenseAccountFilter)?.name || expenseAccountFilter}`);
                if (paymentMethodFilter) filterDesc.push(`Method: ${paymentMethodFilter}`);
                if (dateFromFilter) filterDesc.push(`From: ${formatDate(dateFromFilter)}`);
                if (dateToFilter) filterDesc.push(`To: ${formatDate(dateToFilter)}`);
                const total = sortedExpenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
                const printWindow = window.open('', '_blank');
                printWindow.document.write(`
                  <html><head><title>Expense Report</title>
                  <style>
                    body { font-family: Arial, sans-serif; padding: 20px; }
                    h1 { font-size: 18px; margin-bottom: 5px; }
                    .filters { font-size: 12px; color: #666; margin-bottom: 15px; }
                    table { width: 100%; border-collapse: collapse; font-size: 12px; }
                    th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
                    th { background: #f5f5f5; }
                    .amount { text-align: right; font-family: monospace; }
                    .total { font-weight: bold; background: #f9f9f9; }
                    @media print { body { padding: 0; } }
                  </style></head><body>
                  <h1>Expense Report</h1>
                  <div class="filters">${filterDesc.length ? filterDesc.join(' | ') : 'All Expenses'} | Generated: ${new Date().toLocaleDateString()}</div>
                  <table>
                    <thead><tr><th>Date</th><th>Vendor</th><th>Category</th><th>Description</th><th class="amount">Amount</th></tr></thead>
                    <tbody>
                      ${sortedExpenses.map(e => {
                        const vendor = vendors.find(v => v.id === e.vendor_id);
                        return `<tr><td>${formatDate(e.date)}</td><td>${vendor?.name || '-'}</td><td>${e.category || '-'}</td><td>${e.description || '-'}</td><td class="amount">$${parseFloat(e.amount).toLocaleString()}</td></tr>`;
                      }).join('')}
                      <tr class="total"><td colspan="4">Total (${sortedExpenses.length} expenses)</td><td class="amount">$${total.toLocaleString()}</td></tr>
                    </tbody>
                  </table>
                  </body></html>
                `);
                printWindow.document.close();
                printWindow.print();
              }} className="px-3 py-1.5 text-xs bg-neutral-100 hover:bg-neutral-200 border border-neutral-200 rounded-lg">Print Report</button>
            )}
          </div>
          {sortedExpenses.length > 0 ? (
            <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                    <th className="px-3 py-2"><ExpenseSortHeader id="date" label="Date" /></th>
                    <th className="px-3 py-2"><ExpenseSortHeader id="category" label="Category" /></th>
                    <th className="px-3 py-2"><ExpenseSortHeader id="description" label="Description" /></th>
                    <th className="px-3 py-2 font-medium">Vehicle</th>
                    <th className="px-3 py-2 font-medium">Payment</th>
                    <th className="px-3 py-2 text-right"><ExpenseSortHeader id="amount" label="Amount" /></th>
                    <th className="px-3 py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedExpenses.map(e => {
                    const v = vehicles.find(veh => veh.id === e.vehicle_id);
                    return (
                      <tr key={e.id} className="border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer" onClick={() => setViewingExpense(e)}>
                        <td className="px-3 py-2 text-xs">{formatDate(e.date)}</td>
                        <td className="px-3 py-2 text-xs">{sc(e.category)}</td>
                        <td className="px-3 py-2">{sc(e.description)}</td>
                        <td className="px-3 py-2 text-xs font-mono">{sc(v?.stock_number || v?.stockNumber) || '-'}</td>
                        <td className="px-3 py-2 text-xs">{sc(bankAccounts.find(ba => ba.id === e.bank_account_id)?.name) || '-'}{e.payment_method ? ` - ${sc(e.payment_method)}` : ''}{e.check_number ? ` #${sc(e.check_number)}` : ''}</td>
                        <td className="px-3 py-2 text-right font-mono">${parseFloat(e.amount).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right text-xs">
                          <button onClick={(ev) => { ev.stopPropagation(); setEditingExpense(e); setShowForm(true); }} className="text-neutral-500 hover:text-neutral-800 mr-2">edit</button>
                          <button onClick={(ev) => { ev.stopPropagation(); handleDelete(e); }} className="text-red-500 hover:text-red-700">del</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <Empty text={searchTerm || categoryFilter !== 'all' || vendorFilter || vehicleFilter || expenseAccountFilter || paymentMethodFilter || dateFromFilter || dateToFilter ? 'No matching expenses' : 'No expenses recorded'} />
          )}
        </div>
      )}

      {/* Vendors Tab */}
      {activeTab === 'vendors' && (
        <VendorsSubTab vendors={vendors} onRefresh={onRefresh} />
      )}

      {/* Payroll Tab */}
      {activeTab === 'payroll' && (
        <PayrollSubTab onRefresh={() => { refreshAccountingData(); onRefresh(); }} />
      )}

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <CategoriesManager />
      )}

      {/* Bank Accounts Tab */}
      {activeTab === 'bank-accounts' && (
        <BankAccountManager />
      )}

      {/* Ohio Sales Tax Report Tab */}
      {activeTab === 'sales-tax' && (
        <SalesTaxReportTab />
      )}

      {showForm && <ExpenseForm expense={editingExpense} vehicles={vehicles} vendors={vendors} categories={categories} bankAccounts={bankAccounts} onClose={() => setShowForm(false)} onSave={() => { setShowForm(false); refreshAccountingData(); if (onSilentRefresh) onSilentRefresh(); else onRefresh(); }} />}
      
      {showPaymentModal && (
        <PaymentClearedModal 
          expense={showPaymentModal.expense}
          vehicle={showPaymentModal.vehicle}
          vehicles={vehicles}
          bankAccounts={bankAccounts}
          onClose={() => setShowPaymentModal(null)}
          onSave={() => {
            setShowPaymentModal(null);
            onRefresh();
            refreshAccountingData();
          }}
        />
      )}


      {/* Floating Calculator */}
      <button
        onClick={() => setShowCalculator(c => !c)}
        className="fixed bottom-6 right-6 z-50 w-12 h-12 bg-neutral-800 text-white rounded-full shadow-lg hover:bg-neutral-700 transition-colors flex items-center justify-center text-xl"
        title="Calculator"
      >
        🖩
      </button>
      {showCalculator && <CalculatorWidget onClose={() => setShowCalculator(false)} />}

      {showBillChecklist && (
        <BillPaymentChecklist onClose={() => setShowBillChecklist(false)} />
      )}

      {showBackupManager && <BackupManager isOpen={showBackupManager} onClose={() => setShowBackupManager(false)} />}
      {viewingExpense && (() => {
        const expense = viewingExpense;
        const vendor = vendors.find(v => v.id === expense.vendor_id);
        const bankAccount = bankAccounts.find(ba => ba.id === expense.bank_account_id);
        const lineItems = expense.line_items && expense.line_items.length > 0 
          ? expense.line_items 
          : [{ category: expense.category, description: expense.description, amount: expense.amount }];
        return (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]" onClick={() => setViewingExpense(null)}>
            <div className="bg-white w-full max-w-lg rounded-lg shadow-lg max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="p-4 border-b border-neutral-200 flex justify-between items-center">
                <span className="font-medium">Expense Detail</span>
                <button type="button" onClick={() => setViewingExpense(null)} className="text-neutral-400 hover:text-neutral-600 text-xl leading-none">&times;</button>
              </div>
              <div className="p-4 overflow-y-auto flex-1 space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="block text-xs text-neutral-500">Vendor</label>
                    <p>{sc(vendor?.name) || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-xs text-neutral-500">Date</label>
                    <p>{formatDate(expense.date)}</p>
                  </div>
                  <div>
                    <label className="block text-xs text-neutral-500">Total Amount</label>
                    <p className="font-mono font-medium">${parseFloat(expense.amount).toLocaleString()}</p>
                  </div>
                  <div>
                    <label className="block text-xs text-neutral-500">Paid From</label>
                    <p>{sc(bankAccount?.name) || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-xs text-neutral-500">Payment Method</label>
                    <p>{sc(expense.payment_method)}{expense.check_number ? ` #${sc(expense.check_number)}` : ''}</p>
                  </div>
                  {expense.memo && (
                    <div className="col-span-2">
                      <label className="block text-xs text-neutral-500">Memo</label>
                      <p>{sc(expense.memo)}</p>
                    </div>
                  )}
                </div>
                <div className="border-t pt-4">
                  <h3 className="text-sm font-medium mb-2">Line Items</h3>
                  <table className="w-full text-sm">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-neutral-600">Category</th>
                        <th className="px-3 py-2 text-left font-medium text-neutral-600">Description</th>
                        <th className="px-3 py-2 text-right font-medium text-neutral-600">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((item, index) => (
                        <tr key={index} className="border-b border-neutral-100">
                          <td className="px-3 py-2">{sc(item.category)}</td>
                          <td className="px-3 py-2">{sc(item.description)}</td>
                          <td className="px-3 py-2 text-right font-mono">${parseFloat(item.amount).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="p-4 border-t border-neutral-200 flex gap-2">
                <button type="button" onClick={() => {
                  const items = lineItems;
                  const lineItemsHtml = items.length > 1 ? `
                    <div style="margin: 15px 0;">
                      <div style="font-weight: bold; margin-bottom: 8px; font-size: 14px;">Line Items:</div>
                      <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
                        <thead><tr style="border-bottom: 1px solid #333;"><th style="text-align: left; padding: 4px 0;">Category</th><th style="text-align: left; padding: 4px 0;">Description</th><th style="text-align: right; padding: 4px 0;">Amount</th></tr></thead>
                        <tbody>${items.map(li => `<tr style="border-bottom: 1px solid #eee;"><td style="padding: 4px 0;">${li.category || ''}</td><td style="padding: 4px 0;">${li.description || ''}</td><td style="text-align: right; padding: 4px 0;">$${parseFloat(li.amount || 0).toFixed(2)}</td></tr>`).join('')}</tbody>
                      </table>
                    </div>` : `
                    <div class="row"><span class="label">Category:</span><span class="value">${expense.category || ''}</span></div>
                    <div class="row"><span class="label">Description:</span><span class="value">${expense.description || ''}</span></div>`;
                  const receiptHtml = `<!DOCTYPE html><html><head><title>Expense Receipt</title><style>body{font-family:Arial,sans-serif;padding:40px;max-width:450px;margin:0 auto}.header{text-align:center;border-bottom:2px solid #333;padding-bottom:20px;margin-bottom:20px}.company{font-size:24px;font-weight:bold}.title{font-size:18px;color:#666;margin-top:5px}.row{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #eee}.label{color:#666}.value{font-weight:bold}.total{font-size:20px;margin-top:20px;padding-top:20px;border-top:2px solid #333}.footer{margin-top:40px;text-align:center;color:#999;font-size:12px}</style></head><body>
                    <div class="header"><div class="company">THE WHOLESALE CARS</div><div class="title">EXPENSE RECEIPT</div></div>
                    <div class="details">
                      <div class="row"><span class="label">Receipt #:</span><span class="value">EXP${expense.id}</span></div>
                      <div class="row"><span class="label">Date:</span><span class="value">${new Date(expense.date).toLocaleDateString()}</span></div>
                      ${lineItemsHtml}
                      ${vendor ? `<div class="row"><span class="label">Vendor:</span><span class="value">${vendor.name}</span></div>` : ''}
                      <div class="row"><span class="label">Paid From:</span><span class="value">${bankAccount?.name || ''}</span></div>
                    </div>
                    <div class="row total"><span>AMOUNT:</span><span>$${parseFloat(expense.amount || 0).toFixed(2)}</span></div>
                    <div class="footer"><p>4001 W Alexis Rd, Toledo, OH 43623</p></div>
                  </body></html>`;
                  const pw = window.open('', '_blank');
                  pw.document.write(receiptHtml);
                  pw.document.close();
                  pw.focus();
                  setTimeout(() => pw.print(), 250);
                }} className="flex-1 px-4 py-2 bg-neutral-800 text-white rounded-lg font-medium text-sm">Print Receipt</button>
                <button type="button" onClick={() => { setViewingExpense(null); setEditingExpense(expense); setShowForm(true); }} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm">Edit</button>
                <button type="button" onClick={() => setViewingExpense(null)} className="px-4 py-2 text-neutral-600 hover:bg-neutral-100 rounded-lg text-sm">Close</button>
              </div>
            </div>
          </div>
        );
      })()}
      {/* Transaction Detail Modal - for bills, parts, and manual entries */}
      {viewingTransaction && (() => {
        const t = viewingTransaction;
        const isCreditType = ['credit', 'income'].includes(t.entryType);
        const typeLabel = t.type === 'bill' ? 'Bill Payment' : t.type === 'part' ? 'Part Purchase' : isCreditType ? 'Funds Added' : 'Funds Removed';
        const typeColor = t.type === 'bill' ? 'text-orange-700 bg-orange-50' : t.type === 'part' ? 'text-purple-700 bg-purple-50' : isCreditType ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50';
        return (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]" onClick={() => setViewingTransaction(null)}>
            <div className="bg-white w-full max-w-md rounded-lg shadow-lg max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="p-4 border-b border-neutral-200 flex justify-between items-center">
                <span className="font-medium">Transaction Detail</span>
                <button type="button" onClick={() => setViewingTransaction(null)} className="text-neutral-400 hover:text-neutral-600 text-xl leading-none">&times;</button>
              </div>
              <div className="p-4 overflow-y-auto flex-1 space-y-4">
                <div className={`inline-block text-xs font-medium px-2 py-1 rounded ${typeColor}`}>{typeLabel}</div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="block text-xs text-neutral-500">Date</label>
                    <p>{formatDate(t.date)}</p>
                  </div>
                  <div>
                    <label className="block text-xs text-neutral-500">Amount</label>
                    <p className={`font-mono font-medium ${isCreditType ? 'text-green-600' : 'text-red-600'}`}>
                      {isCreditType ? '+' : '-'}${Math.abs(parseFloat(t.amount || 0)).toLocaleString()}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-neutral-500">Description</label>
                    <p>{sc(t.description) || 'N/A'}</p>
                  </div>
                  {(t.vendor_name || t.vendorName) && (
                    <div>
                      <label className="block text-xs text-neutral-500">Vendor</label>
                      <p>{sc(t.vendor_name || t.vendorName)}</p>
                    </div>
                  )}
                  {(t.stock_number || t.stockNumber) && (
                    <div>
                      <label className="block text-xs text-neutral-500">Vehicle</label>
                      <p className="font-mono">#{sc(t.stock_number || t.stockNumber)}</p>
                    </div>
                  )}
                  {t.category && (
                    <div>
                      <label className="block text-xs text-neutral-500">Category</label>
                      <p>{sc(t.category)}</p>
                    </div>
                  )}
                  {(t.payment_method || t.paymentMethod) && (
                    <div>
                      <label className="block text-xs text-neutral-500">Payment Method</label>
                      <p>{sc(t.payment_method || t.paymentMethod)}{(t.check_number || t.checkNumber) ? ` #${sc(t.check_number || t.checkNumber)}` : ''}</p>
                    </div>
                  )}
                  {t.bankAccountName && (
                    <div>
                      <label className="block text-xs text-neutral-500">Account</label>
                      <p>{sc(t.bankAccountName)}</p>
                    </div>
                  )}
                  {t.balanceBefore !== undefined && (
                    <>
                      <div>
                        <label className="block text-xs text-neutral-500">Balance Before</label>
                        <p className="font-mono">${t.balanceBefore.toLocaleString()}</p>
                      </div>
                      <div>
                        <label className="block text-xs text-neutral-500">Balance After</label>
                        <p className="font-mono font-medium">${t.balanceAfter.toLocaleString()}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="p-4 border-t border-neutral-200 flex gap-2">
                {(t.type === 'expense' || t.expenseId) && (
                  <button type="button" onClick={() => {
                    const expObj = safeExpenses.find(e => e.id === (t.expenseId || t.id));
                    if (expObj) { setViewingTransaction(null); setEditingExpense(expObj); setShowForm(true); }
                  }} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm">Edit Expense</button>
                )}
                <button type="button" onClick={() => setViewingTransaction(null)} className={`${(t.type === 'expense' || t.expenseId) ? 'flex-1' : 'w-full'} px-4 py-2 bg-neutral-100 hover:bg-neutral-200 text-neutral-700 rounded-lg text-sm`}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Transport Cost Recovery Preview Modal */}
      {transportRecoveryPreview && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]" onClick={() => setTransportRecoveryPreview(null)}>
          <div className="bg-white w-full max-w-lg rounded-lg shadow-lg max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-neutral-200 flex justify-between items-center">
              <div>
                <span className="font-medium">Recover Transport Costs</span>
                <p className="text-xs text-neutral-500 mt-0.5">{transportRecoveryPreview.length} vehicles with zeroed transport costs</p>
              </div>
              <button type="button" onClick={() => setTransportRecoveryPreview(null)} className="text-neutral-400 hover:text-neutral-600 text-xl leading-none">&times;</button>
            </div>
            <div className="overflow-y-auto flex-1">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-neutral-50">
                  <tr className="text-left text-xs text-neutral-500 border-b">
                    <th className="px-3 py-2">Stock #</th>
                    <th className="px-3 py-2">Vehicle</th>
                    <th className="px-3 py-2 text-right">Transport Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {transportRecoveryPreview.map(v => (
                    <tr key={v.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                      <td className="px-3 py-2 font-mono text-xs">{v.stockNumber}</td>
                      <td className="px-3 py-2">{v.name}</td>
                      <td className="px-3 py-2 text-right font-mono text-green-700 font-medium">${parseFloat(v.estimatedCost).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-neutral-50 border-t-2">
                  <tr>
                    <td colSpan={2} className="px-3 py-2 font-medium text-sm">Total</td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-green-700">
                      ${transportRecoveryPreview.reduce((sum, v) => sum + parseFloat(v.estimatedCost || 0), 0).toLocaleString()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="p-4 border-t border-neutral-200 flex gap-2">
              <button
                onClick={handleApplyTransportRecovery}
                disabled={recoverTransportLoading}
                className="flex-1 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm disabled:opacity-50"
              >
                {recoverTransportLoading ? 'Applying...' : `Apply to ${transportRecoveryPreview.length} Vehicles`}
              </button>
              <button onClick={() => setTransportRecoveryPreview(null)} className="flex-1 px-4 py-2 bg-neutral-100 hover:bg-neutral-200 text-neutral-700 rounded-lg text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Clear Payment Modal - For new pending payments system
const ClearPaymentModal = ({ payment, bankAccounts, onClose, onSave }) => {
  const [form, setForm] = useState({
    bankAccountId: bankAccounts.find(a => a.name?.toLowerCase().includes('checking'))?.id?.toString() || bankAccounts[0]?.id?.toString() || '',
    clearedDate: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.bankAccountId) {
      alert('Please select a bank account');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        bankAccountId: parseInt(form.bankAccountId),
        clearedDate: form.clearedDate,
        notes: form.notes
      });
    } catch (err) {
      alert('Failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-neutral-200">
          <h3 className="font-semibold">Mark Payment Cleared</h3>
          <p className="text-sm text-neutral-500 mt-1">
            {sc(payment.stockNumber)} - {sc(payment.year)} {sc(payment.make)} {sc(payment.model)}
          </p>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="bg-neutral-50 rounded-lg p-3">
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600">Amount:</span>
              <span className="font-mono font-bold text-lg">${parseFloat(payment.purchasePrice || payment.amount).toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span className="text-neutral-600">Payment Type:</span>
              <span className={`px-2 py-0.5 rounded text-xs ${
                payment.paymentType === 'ACH' ? 'bg-blue-100 text-blue-700' :
                payment.paymentType === 'Check' ? 'bg-purple-100 text-purple-700' :
                payment.paymentType === 'Cash' ? 'bg-amber-100 text-amber-700' :
                payment.paymentType === 'Title Pending' ? 'bg-orange-100 text-orange-700' :
                'bg-neutral-100 text-neutral-700'
              }`}>{sc(payment.paymentType)}</span>
            </div>
            {payment.checkNumber && (
              <div className="flex justify-between text-sm mt-1">
                <span className="text-neutral-600">Check #:</span>
                <span>{sc(payment.checkNumber)}</span>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs text-neutral-500 mb-1">Paid From Account *</label>
            <select
              value={form.bankAccountId}
              onChange={e => setForm(f => ({ ...f, bankAccountId: e.target.value }))}
              className="w-full h-9 px-3 border border-neutral-200 rounded-lg"
              required
            >
              <option value="">Select account...</option>
              {bankAccounts.map(a => (
                <option key={a.id} value={a.id}>
                  {a.name} (${parseFloat(a.balance || 0).toLocaleString()})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-neutral-500 mb-1">Cleared Date</label>
            <input
              type="date"
              value={form.clearedDate}
              onChange={e => setForm(f => ({ ...f, clearedDate: e.target.value }))}
              className="w-full h-9 px-3 border border-neutral-200 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-xs text-neutral-500 mb-1">Notes (optional)</label>
            <input
              type="text"
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="e.g., Confirmation #, reference..."
              className="w-full h-9 px-3 border border-neutral-200 rounded-lg"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-neutral-200 rounded-lg hover:bg-neutral-50">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
              {saving ? 'Processing...' : 'Mark Cleared'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Batch Clear Payment Modal - Clear multiple vehicles in one ACH/batch transaction
const BatchClearPaymentModal = ({ payments, bankAccounts, onClose, onSave }) => {
  const [form, setForm] = useState({
    bankAccountId: bankAccounts.find(a => a.name?.toLowerCase().includes('checking'))?.id?.toString() || bankAccounts[0]?.id?.toString() || '',
    clearedDate: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  const totalAmount = payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.bankAccountId) {
      alert('Please select a bank account');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        bankAccountId: parseInt(form.bankAccountId),
        clearedDate: form.clearedDate,
        notes: form.notes
      });
    } catch (err) {
      alert('Failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-neutral-200">
          <h3 className="font-semibold">Batch Clear – Same Payment</h3>
          <p className="text-sm text-neutral-500 mt-1">
            {payments.length} vehicles · One transaction
          </p>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="bg-neutral-50 rounded-lg p-3 max-h-32 overflow-y-auto">
            {payments.map(p => (
              <div key={p.id} className="flex justify-between text-sm py-1">
                <span>{sc(p.stockNumber)} - {sc(p.year)} {sc(p.make)} {sc(p.model)}</span>
                <span className="font-mono font-medium">${parseFloat(p.amount).toLocaleString()}</span>
              </div>
            ))}
            <div className="flex justify-between text-sm font-bold pt-2 border-t border-neutral-200 mt-2">
              <span>Total</span>
              <span className="font-mono text-lg">${totalAmount.toLocaleString()}</span>
            </div>
          </div>

          <div>
            <label className="block text-xs text-neutral-500 mb-1">Paid From Account *</label>
            <select
              value={form.bankAccountId}
              onChange={e => setForm(f => ({ ...f, bankAccountId: e.target.value }))}
              className="w-full h-9 px-3 border border-neutral-200 rounded-lg"
              required
            >
              <option value="">Select account...</option>
              {bankAccounts.map(a => (
                <option key={a.id} value={a.id}>
                  {a.name} (${parseFloat(a.balance || 0).toLocaleString()})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-neutral-500 mb-1">Cleared Date</label>
            <input
              type="date"
              value={form.clearedDate}
              onChange={e => setForm(f => ({ ...f, clearedDate: e.target.value }))}
              className="w-full h-9 px-3 border border-neutral-200 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-xs text-neutral-500 mb-1">Notes (optional)</label>
            <input
              type="text"
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="e.g., ACH batch ref, confirmation #..."
              className="w-full h-9 px-3 border border-neutral-200 rounded-lg"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-neutral-200 rounded-lg hover:bg-neutral-50">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Processing...' : `Batch Clear ($${totalAmount.toLocaleString()})`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Payment Cleared Modal (Legacy - for old expense-based system)
const PaymentClearedModal = ({ expense, vehicle, vehicles, bankAccounts, onClose, onSave }) => {
  // Get vehicle ID - check multiple possible sources
  // expense.vehicle_id comes from backend (snake_case)
  // vehicle.id comes from the matched vehicle object
  const vehicleId = expense?.vehicle_id || expense?.vehicleId || vehicle?.id;
  
  // Auto-select first bank account, or the one with highest balance
  const defaultBankAccount = bankAccounts.length === 1 
    ? bankAccounts[0] 
    : bankAccounts.find(a => a.name?.toLowerCase().includes('checking')) || bankAccounts[0];
  
  const [form, setForm] = useState({
    bankAccountId: defaultBankAccount?.id?.toString() || '',
    paymentMethod: expense?.payment_method || vehicle?.paymentMethod || 'ACH',
    checkNumber: vehicle?.checkNumber || '',
    clearedDate: new Date().toISOString().split('T')[0]
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate vehicle ID - try multiple fallback methods
    let finalVehicleId = vehicleId;
    
    if (!finalVehicleId && expense?.stockNumber && vehicles) {
      // Try to find vehicle by stock number from the expense
      const matchedVehicle = vehicles.find(v => v.stockNumber === expense.stockNumber);
      finalVehicleId = matchedVehicle?.id;
    }
    
    if (!finalVehicleId && expense?.description && vehicles) {
      // Try to extract stock number from description - more flexible patterns
      const stockPatterns = [
        /(\d{4,6})-[A-Z]{3}/,    // "8628-ACV" pattern (stock-auction format)
        /\b(\d{4,6})\b/,         // 4-6 digits with word boundaries
        /stock[:\s#]*(\d+)/i,    // "stock: 12345" or "stock #12345"
        /vehicle[:\s#]*(\d+)/i,  // "vehicle: 12345"
        /#(\d{4,6})/,            // "#12345"
        /(\d{4,6})/              // any 4-6 digit number (fallback)
      ];
      
      for (const pattern of stockPatterns) {
        const stockMatch = expense.description.match(pattern);
        if (stockMatch) {
          const stockFromDesc = stockMatch[1] || stockMatch[0];
          const matchedVehicle = vehicles.find(v => v.stockNumber === stockFromDesc || v.stockNumber === stockFromDesc.replace(/\D/g, ''));
          if (matchedVehicle) {
            finalVehicleId = matchedVehicle.id;
            break;
          }
        }
      }
    }
    
    // Try by vehicle object if passed directly
    if (!finalVehicleId && vehicle?.id) {
      finalVehicleId = vehicle.id;
    }
    
    // Last resort: try to match by any expense fields
    if (!finalVehicleId && vehicles && expense) {
      const expenseText = JSON.stringify(expense).toLowerCase();
      const possibleStockNumbers = expenseText.match(/\d{4,6}/g) || [];
      
      for (const stockNum of possibleStockNumbers) {
        const matchedVehicle = vehicles.find(v => v.stockNumber === stockNum);
        if (matchedVehicle) {
          finalVehicleId = matchedVehicle.id;
          break;
        }
      }
    }
    
    if (!finalVehicleId) {
      alert(`Error: Vehicle ID not found. 

Debugging info:
- Initial ID: ${vehicleId || 'none'}
- Expense stock: ${expense?.stockNumber || 'none'}
- Expense desc: ${expense?.description || 'none'}
- Vehicles available: ${vehicles?.length || 0}

Please ensure the expense is properly linked to a vehicle with a valid stock number.`);
      return;
    }
    
    // Validate bank account
    if (!form.bankAccountId) {
      alert('Please select a bank account');
      return;
    }
    
    if (form.paymentMethod === 'Check' && !form.checkNumber) {
      alert('Check number is required for check payments');
      return;
    }
    
    setSaving(true);
    try {
      // First, mark payment as cleared
      const paymentResult = await api.markPaymentCleared({
        vehicleId: parseInt(finalVehicleId),
        bankAccountId: parseInt(form.bankAccountId),
        paymentMethod: form.paymentMethod,
        checkNumber: form.checkNumber || null,
        clearedDate: form.clearedDate,
        expenseId: expense?.id
      });
      
      // Then, create a transaction record for the bank account
      try {
        const transactionData = {
          bankAccountId: parseInt(form.bankAccountId),
          type: 'debit', // Payment is a debit from the account
          amount: expense?.amount || vehicle?.purchasePrice || 0,
          description: `Vehicle Purchase Payment - ${vehicle?.stockNumber || finalVehicleId} - ${expense?.description || 'Payment Cleared'}`,
          date: form.clearedDate,
          category: 'vehicle_purchase',
          vehicleId: parseInt(finalVehicleId),
          expenseId: expense?.id,
          paymentMethod: form.paymentMethod,
          checkNumber: form.checkNumber || null,
          reference: `Payment cleared for vehicle ${vehicle?.stockNumber || finalVehicleId}`
        };
        
        // Create the transaction record
        await api.createTransaction(transactionData);
      } catch (transactionErr) {
        console.warn('Failed to create transaction record:', transactionErr);
        // Don't fail the whole operation if transaction creation fails
      }
      
      alert(' Payment cleared and transaction recorded in bank account!');
      onSave();
    } catch (err) {
      console.error('Payment cleared error:', err);
      alert('Failed to mark payment cleared: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-lg shadow-lg" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-neutral-200">
          <div className="font-medium">Mark Payment Cleared</div>
          {vehicle && <div className="text-sm text-neutral-500">{vehicle.stockNumber} - {vehicle.year} {vehicle.make} {vehicle.model}</div>}
          <div className="text-lg font-mono font-medium mt-1">${parseFloat(expense.amount).toLocaleString()}</div>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <div>
            <label className="block text-xs text-neutral-500 mb-1">Debit From Account *</label>
            <select
              value={form.bankAccountId}
              onChange={e => setForm(f => ({ ...f, bankAccountId: e.target.value }))}
              className="w-full h-8 px-2 text-sm border border-neutral-200 rounded bg-white"
              required
            >
              {bankAccounts.map(acc => (
                <option key={acc.id} value={acc.id}>{acc.name} (${parseFloat(acc.balance).toLocaleString()})</option>
              ))}
            </select>
          </div>
          
                    
          <Input 
            label="Cleared Date" 
            type="date" 
            value={form.clearedDate} 
            onChange={v => setForm(f => ({ ...f, clearedDate: v }))} 
          />
          
          <div className="flex justify-end gap-2 pt-2 border-t border-neutral-200">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-neutral-600">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">
              {saving ? 'Processing...' : 'Confirm Payment Cleared'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Autocomplete Input Component for QuickBooks-style data entry
const AutocompleteInput = ({ 
  value, 
  onChange, 
  onSelect, 
  options, 
  placeholder, 
  displayKey = 'name',
  valueKey = 'id',
  renderOption,
  className = '',
  inputRef,
  onKeyDown,
  tabIndex
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState(value || '');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const dropdownRef = useRef(null);
  const localInputRef = useRef(null);
  const actualRef = inputRef || localInputRef;

  useEffect(() => {
    setSearch(value || '');
  }, [value]);

  const filtered = useMemo(() => {
    // Require at least 1 character before showing results
    if (!search || search.length < 1) return [];
    const lower = search.toLowerCase();
    return options.filter(opt => 
      (opt[displayKey] || '').toLowerCase().includes(lower)
    ).slice(0, 15);
  }, [search, options, displayKey]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    onChange?.(val);
    setIsOpen(true);
    setHighlightIndex(0);
  };

  const handleSelect = (option) => {
    setSearch(option[displayKey]);
    onSelect?.(option);
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(i => Math.min(i + 1, filtered.length - 1));
      setIsOpen(true);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && isOpen && filtered.length > 0) {
      e.preventDefault();
      handleSelect(filtered[highlightIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'Tab' && isOpen && filtered.length > 0 && search.length >= 1) {
      // Auto-select on tab if there's a match
      const exactMatch = filtered.find(f => f[displayKey].toLowerCase() === search.toLowerCase());
      if (exactMatch) {
        handleSelect(exactMatch);
      } else if (filtered.length === 1) {
        handleSelect(filtered[0]);
      }
    }
    onKeyDown?.(e);
  };

  return (
    <div className="relative">
      <input
        ref={actualRef}
        type="text"
        value={search}
        onChange={handleInputChange}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 150)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={`w-full h-9 px-2 text-sm border border-neutral-200 rounded ${className}`}
        tabIndex={tabIndex}
        autoComplete="off"
      />
      {isOpen && filtered.length > 0 && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-64 overflow-y-auto"
        >
          {filtered.map((option, idx) => (
            <div
              key={option[valueKey] || idx}
              onClick={() => handleSelect(option)}
              className={`px-3 py-2 cursor-pointer text-sm ${
                idx === highlightIndex ? 'bg-blue-50 text-blue-700' : 'hover:bg-neutral-50'
              }`}
            >
              {renderOption ? renderOption(option) : option[displayKey]}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Expense Form - with vendor, category, and bank account dropdowns (QuickBooks-style)
const ExpenseForm = ({ expense, vehicles, vendors = [], categories = [], bankAccounts = [], onClose, onSave, autoPrint = false }) => {
  const isNew = !expense?.id;
  
  // Load last used values from localStorage
  const getLastUsed = () => {
    try {
      return JSON.parse(localStorage.getItem('expenseFormLastUsed') || '{}');
    } catch { return {}; }
  };
  const lastUsed = getLastUsed();
  
  // Load vendor memory (last category/account used per vendor)
  const getVendorMemory = () => {
    try {
      return JSON.parse(localStorage.getItem('expenseVendorMemory') || '{}');
    } catch { return {}; }
  };
  
  // Load recently used categories and accounts
  const getRecentlyUsed = (key) => {
    try {
      return JSON.parse(localStorage.getItem(`expense_recent_${key}`) || '[]');
    } catch { return []; }
  };

  // Initialize line items from expense or with one empty line
  const initialLineItems = expense?.line_items?.length > 0 
    ? expense.line_items 
    : [{ category: expense?.category || lastUsed.category || '', description: expense?.description || '', amount: expense?.amount || '' }];
  
  // Performance tag only — does not change accounting. New entries start unset.
  // Edits map legacy tags (vehicle/operating/non_essential/excluded) onto the 5 boxes.
  const mapExpenseCostType = (raw, hasVehicle, isEdit) => {
    if (raw === 'overhead' || raw === 'into_cars' || raw === 'labor' || raw === 'consumables' || raw === 'extra') return raw;
    if (raw === 'vehicle') return 'into_cars';
    if (raw === 'operating') return 'overhead';
    if (raw === 'non_essential' || raw === 'excluded') return 'extra';
    if (hasVehicle) return 'into_cars';
    if (isEdit) return 'overhead';
    return null;
  };
  const [costType, setCostType] = useState(
    mapExpenseCostType(expense?.cost_type, !!expense?.vehicle_id, !!expense?.id)
  );

  const [form, setForm] = useState({
    vehicleId: expense?.vehicle_id || '',
    vendorId: expense?.vendor_id || '',
    vendorName: vendors.find(v => v.id === expense?.vendor_id)?.name || '',
    bankAccountId: (expense?.bank_account_id?.toString()) || lastUsed.bankAccountId || '',
    date: expense?.date?.split('T')[0] || new Date().toISOString().split('T')[0],
    paymentMethod: expense?.payment_method || (() => {
      const initAccId = (expense?.bank_account_id?.toString()) || lastUsed.bankAccountId || '';
      const initAcc = bankAccounts.find(a => a.id.toString() === initAccId);
      if (initAcc?.accountType === 'cash' || initAcc?.name?.toLowerCase().includes('cash')) return 'Cash';
      if (initAcc?.accountType === 'checking') return 'ACH';
      return 'ACH';
    })(),
    checkNumber: expense?.check_number || '',
    memo: expense?.memo || '',
  });
  
  // Line items state (each has category, description, amount)
  const [lineItems, setLineItems] = useState(initialLineItems);
  
  // Calculate total from line items
  const lineItemsTotal = useMemo(() => {
    return lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  }, [lineItems]);
  
  // Add a new line item
  const addLineItem = () => {
    setLineItems(prev => [...prev, { category: '', description: '', amount: '' }]);
  };
  
  // Update a line item
  const updateLineItem = (index, field, value) => {
    setLineItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };
  
  // Remove a line item
  const removeLineItem = (index) => {
    if (lineItems.length === 1) return; // Keep at least one line
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };
  
  // Clear all line items
  const clearAllLineItems = () => {
    setLineItems([{ category: '', description: '', amount: '' }]);
  };
  const [saving, setSaving] = useState(false);
  const [showQuickAddVendor, setShowQuickAddVendor] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');
  const [showQuickAddCategory, setShowQuickAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [localCategories, setLocalCategories] = useState(categories);
  const [localVendors, setLocalVendors] = useState(vendors);
  const [savedExpense, setSavedExpense] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  
  // Management modals
  const [editingVendor, setEditingVendor] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showManageCategories, setShowManageCategories] = useState(false);
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSelection, setMergeSelection] = useState([]);
  
  // Refs for keyboard navigation
  const vendorRef = useRef(null);
  const amountRef = useRef(null);
  const categoryRef = useRef(null);
  const accountRef = useRef(null);
  const dateRef = useRef(null);
  const descriptionRef = useRef(null);
  const submitRef = useRef(null);
  
  // Auto-focus vendor field when form opens
  useEffect(() => {
    setTimeout(() => vendorRef.current?.focus(), 100);
  }, []);
  
  // Recently used lists with most recent first
  const recentCategories = getRecentlyUsed('categories');
  const recentAccounts = getRecentlyUsed('accounts');
  
  // All accounting categories (except income). Performance box does not filter this list.
  const sortedCategories = useMemo(() => {
    const allCats = (localCategories.length > 0 ? localCategories : categories);
    const expenseCats = allCats.filter(c => c.categoryType !== 'income');
    
    // Sort: recently used first, then alphabetically
    return [...expenseCats].sort((a, b) => {
      const aRecent = recentCategories.indexOf(a.name);
      const bRecent = recentCategories.indexOf(b.name);
      if (aRecent !== -1 && bRecent === -1) return -1;
      if (bRecent !== -1 && aRecent === -1) return 1;
      if (aRecent !== -1 && bRecent !== -1) return aRecent - bRecent;
      return a.name.localeCompare(b.name);
    });
  }, [localCategories, categories, recentCategories]);
  
  // Sort bank accounts with recently used at top
  const sortedAccounts = useMemo(() => {
    return [...bankAccounts].sort((a, b) => {
      const aRecent = recentAccounts.indexOf(a.id.toString());
      const bRecent = recentAccounts.indexOf(b.id.toString());
      if (aRecent !== -1 && bRecent === -1) return -1;
      if (bRecent !== -1 && aRecent === -1) return 1;
      if (aRecent !== -1 && bRecent !== -1) return aRecent - bRecent;
      return a.name.localeCompare(b.name);
    });
  }, [bankAccounts, recentAccounts]);
  
  // Handle vendor selection - prefill from memory (including description)
  const handleVendorSelect = (vendor) => {
    const vendorMemory = getVendorMemory();
    const memory = vendorMemory[vendor.id];
    
    setForm(f => ({
      ...f,
      vendorId: vendor.id.toString(),
      vendorName: vendor.name,
      bankAccountId: memory?.bankAccountId || f.bankAccountId,
    }));
    
    // Prefill first line item from vendor memory if available
    if (memory) {
      setLineItems(prev => {
        const updated = [...prev];
        updated[0] = {
          ...updated[0],
          category: memory.category || updated[0].category,
          description: memory.description || updated[0].description,
          amount: memory.amount || updated[0].amount,
        };
        return updated;
      });
    }
    
    // Move to amount field
    setTimeout(() => amountRef.current?.focus(), 50);
  };
  
  // Save to localStorage on successful save
  const saveToMemory = () => {
    const firstLineItem = lineItems[0] || {};
    
    // Save last used values (use first line item's category)
    localStorage.setItem('expenseFormLastUsed', JSON.stringify({
      category: firstLineItem.category,
      bankAccountId: form.bankAccountId,
    }));
    
    // Save vendor memory if vendor was selected (use first line item)
    if (form.vendorId) {
      const vendorMemory = getVendorMemory();
      vendorMemory[form.vendorId] = {
        category: firstLineItem.category,
        bankAccountId: form.bankAccountId,
        description: firstLineItem.description,
        amount: firstLineItem.amount,
      };
      localStorage.setItem('expenseVendorMemory', JSON.stringify(vendorMemory));
    }
    
    // Update recently used categories (add all used categories)
    const recentCats = getRecentlyUsed('categories');
    const usedCategories = lineItems.map(li => li.category).filter(Boolean);
    let newRecentCats = [...recentCats];
    usedCategories.forEach(cat => {
      newRecentCats = [cat, ...newRecentCats.filter(c => c !== cat)];
    });
    localStorage.setItem('expense_recent_categories', JSON.stringify(newRecentCats.slice(0, 10)));
    
    // Update recently used accounts
    const recentAccs = getRecentlyUsed('accounts');
    const newRecentAccs = [form.bankAccountId.toString(), ...recentAccs.filter(a => a !== form.bankAccountId.toString())].slice(0, 10);
    localStorage.setItem('expense_recent_accounts', JSON.stringify(newRecentAccs));
  };
  
  // Reset form for new entry (keeps last used category/account)
  const resetFormForNew = () => {
    const lastUsed = getLastUsed();
    setCostType(null); // each new expense requires a fresh Performance box
    setForm({
      vehicleId: '',
      vendorId: '',
      vendorName: '',
      bankAccountId: lastUsed.bankAccountId || '',
      date: new Date().toISOString().split('T')[0],
      paymentMethod: 'ACH',
      checkNumber: '',
      memo: '',
    });
    setLineItems([{ category: lastUsed.category || '', description: '', amount: '' }]);
    setSavedExpense(null);
    setDuplicateWarning(null);
    setLocalDuplicateWarning(null);
    // Focus vendor field
    setTimeout(() => vendorRef.current?.focus(), 100);
  };
  
  // Check if selected account is a checking account (needs payment method)
  const selectedAccount = bankAccounts.find(a => a.id.toString() === form.bankAccountId);
  const isCheckingAccount = selectedAccount?.accountType === 'checking';
  
  // Check for duplicate before saving
  const checkForDuplicate = async () => {
    if (lineItemsTotal === 0 || !form.date) return null;
    try {
      const expenses = (await api.getExpenses() || []).map(sanitizeExpense);
      // Check for same amount + same date (regardless of vendor)
      const potential = expenses.find(e => 
        Math.abs(parseFloat(e.amount) - lineItemsTotal) < 0.01 &&
        e.date?.split('T')[0] === form.date
      );
      return potential;
    } catch {
      return null;
    }
  };

  // State for save mode and local duplicate warning
  const [saveMode, setSaveMode] = useState('close'); // 'close' or 'new'
  const [localDuplicateWarning, setLocalDuplicateWarning] = useState(null);
  
  const handleSubmit = async (e, skipDuplicateCheck = false, mode = saveMode) => {
    e?.preventDefault();
    if (saving) return; // Prevent double-submit
    
    if (!costType) return alert('Choose where this money went (top of the form)');
    
    // Validate line items
    const validLineItems = lineItems.filter(li => li.category && li.amount);
    if (validLineItems.length === 0) return alert('At least one line item with category and amount is required');
    if (!form.bankAccountId) return alert('Bank account is required');
    
    // Check for local duplicate (same amount + date) before sending to server
    if (!skipDuplicateCheck) {
      setSaving(true);
      const duplicate = await checkForDuplicate();
      if (duplicate) {
        setSaving(false);
        setLocalDuplicateWarning({
          duplicate,
          mode,
        });
        return;
      }
    } else {
      setSaving(true);
    }
    
    try {
      // Auto-create any new categories that don't exist in accounting_categories
      const existingCatNames = new Set((localCategories.length > 0 ? localCategories : categories).map(c => c.name.toLowerCase()));
      for (const li of validLineItems) {
        if (li.category && !existingCatNames.has(li.category.toLowerCase())) {
          try {
            const newCat = await api.createAccountingCategory({ name: li.category, categoryType: 'expense' });
            setLocalCategories(prev => [...prev, newCat]);
            existingCatNames.add(li.category.toLowerCase());
          } catch (e) {
            console.warn('Auto-create category failed (may already exist):', li.category, e);
          }
        }
      }

      // For single line item, use legacy format for backward compatibility
      // For multi-line, include line_items array
      const firstLine = validLineItems[0];
      const data = { 
        ...form, 
        vehicleId: form.vehicleId || null,
        costType,
        vendorId: form.vendorId || null,
        bankAccountId: parseInt(form.bankAccountId),
        // Legacy fields for backward compatibility
        category: firstLine.category,
        description: firstLine.description || '',
        amount: lineItemsTotal,
        // Include line items for multi-line support
        lineItems: validLineItems,
        skipDuplicateCheck: true // We already checked locally
      };
      let result;
      if (isNew) {
        result = await api.createExpense(data);
      } else {
        result = await api.updateExpense(expense.id, data);
      }
      
      // Save to memory for future autocomplete
      saveToMemory();
      
      // Handle based on save mode
      if (mode === 'new') {
        // Save & New - reset form and stay open
        resetFormForNew();
      } else {
        // Save & Close - show success/print option
        setSavedExpense({
          ...form,
          id: result?.id || expense?.id,
          amount: lineItemsTotal,
          category: validLineItems[0].category,
          description: validLineItems[0].description || form.memo || '',
          lineItems: validLineItems,
          receiptNumber: `EXP${Date.now().toString().slice(-8)}`,
          vendorName: localVendors.find(v => v.id === parseInt(form.vendorId))?.name,
          bankAccountName: bankAccounts.find(a => a.id === parseInt(form.bankAccountId))?.name
        });
      }
      setDuplicateWarning(null);
      setLocalDuplicateWarning(null);
    } catch (e) {
      // Check if it's a duplicate warning (409 status) from server
      if (e.status === 409 && e.duplicates) {
        setDuplicateWarning({
          duplicates: e.duplicates,
          message: e.message || 'Potential duplicate expense found'
        });
      } else {
        alert('Save failed: ' + e.message);
      }
    } finally {
      setSaving(false);
    }
  };
  
  // Handle keyboard shortcuts (Cmd+Enter for Save & New)
  const handleFormKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(e, false, 'new');
    }
  };

  // Local Duplicate Warning Modal (same vendor + amount + date)
  if (localDuplicateWarning) {
    const dup = localDuplicateWarning.duplicate;
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
        <div className="bg-white w-full max-w-md rounded-lg shadow-lg" onClick={e => e.stopPropagation()}>
          <div className="p-4 border-b border-amber-200 bg-amber-50 rounded-t-lg">
            <div className="flex items-center gap-2 text-amber-800">
              <span className="text-2xl">⚠️</span>
              <h3 className="text-lg font-bold">Possible Duplicate</h3>
            </div>
            <p className="text-amber-700 text-sm mt-1">
              This may be a duplicate of a transaction from {new Date(dup.date).toLocaleDateString()}.
            </p>
          </div>
          
          <div className="p-4">
            <div className="text-sm font-medium text-neutral-600 mb-2">Existing transaction:</div>
            <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-neutral-500">Vendor:</span>
                <span className="font-medium">{localVendors.find(v => v.id === dup.vendor_id)?.name || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-500">Amount:</span>
                <span className="font-bold text-red-600">${parseFloat(dup.amount).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-500">Date:</span>
                <span className="font-medium">{new Date(dup.date).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-500">Description:</span>
                <span className="font-medium">{dup.description || 'N/A'}</span>
              </div>
            </div>
          </div>
          
          <div className="p-4 border-t border-neutral-200 flex gap-2">
            <button 
              onClick={() => setLocalDuplicateWarning(null)} 
              className="flex-1 px-4 py-2 bg-neutral-100 text-neutral-700 rounded-lg font-medium hover:bg-neutral-200"
            >
              Cancel
            </button>
            <button 
              onClick={() => {
                const mode = localDuplicateWarning.mode;
                setLocalDuplicateWarning(null);
                handleSubmit(null, true, mode);
              }} 
              disabled={saving}
              className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Anyway'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Server Duplicate Warning Modal
  if (duplicateWarning) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
        <div className="bg-white w-full max-w-lg rounded-lg shadow-lg" onClick={e => e.stopPropagation()}>
          <div className="p-4 border-b border-amber-200 bg-amber-50 rounded-t-lg">
            <div className="flex items-center gap-2 text-amber-800">
              <span className="text-2xl">⚠️</span>
              <h3 className="text-lg font-bold">Potential Duplicate Expense</h3>
            </div>
            <p className="text-amber-700 text-sm mt-1">An expense with the same date and amount already exists. Please verify this isn't a duplicate.</p>
          </div>
          
          <div className="p-4 max-h-80 overflow-y-auto">
            <div className="text-sm font-medium text-neutral-600 mb-2">Existing expense(s) found:</div>
            {duplicateWarning.duplicates.map((dup, idx) => (
              <div key={idx} className="bg-neutral-50 border border-neutral-200 rounded-lg p-3 mb-2">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-neutral-500">Date:</span> <span className="font-medium">{new Date(dup.date).toLocaleDateString()}</span></div>
                  <div><span className="text-neutral-500">Amount:</span> <span className="font-bold text-red-600">${parseFloat(dup.amount).toFixed(2)}</span></div>
                  <div><span className="text-neutral-500">Vendor:</span> <span className="font-medium">{dup.vendor_name || 'N/A'}</span></div>
                  <div><span className="text-neutral-500">Category:</span> <span className="font-medium">{dup.category || 'N/A'}</span></div>
                  <div className="col-span-2"><span className="text-neutral-500">Description:</span> <span className="font-medium">{dup.description || 'N/A'}</span></div>
                  {dup.stock_number && (
                    <div className="col-span-2"><span className="text-neutral-500">Vehicle:</span> <span className="font-medium">{dup.stock_number} - {dup.year} {dup.make} {dup.model}</span></div>
                  )}
                </div>
              </div>
            ))}
            
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="text-sm font-medium text-blue-800 mb-1">You're trying to add:</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-blue-600">Date:</span> <span className="font-medium">{new Date(form.date).toLocaleDateString()}</span></div>
                <div><span className="text-blue-600">Amount:</span> <span className="font-bold">${parseFloat(form.amount).toFixed(2)}</span></div>
                <div><span className="text-blue-600">Vendor:</span> <span className="font-medium">{localVendors.find(v => v.id === parseInt(form.vendorId))?.name || 'N/A'}</span></div>
                <div><span className="text-blue-600">Category:</span> <span className="font-medium">{form.category}</span></div>
                <div className="col-span-2"><span className="text-blue-600">Description:</span> <span className="font-medium">{form.description}</span></div>
              </div>
            </div>
          </div>
          
          <div className="p-4 border-t border-neutral-200 flex gap-2">
            <button 
              onClick={() => setDuplicateWarning(null)} 
              className="flex-1 px-4 py-2 bg-neutral-100 text-neutral-700 rounded-lg font-medium hover:bg-neutral-200"
            >
              ← Go Back & Edit
            </button>
            <button 
              onClick={() => handleSubmit(null, true)} 
              disabled={saving}
              className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Add Anyway (Not a Duplicate)'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handlePrintReceipt = () => {
    const items = savedExpense.lineItems || [{ category: savedExpense.category, description: savedExpense.description, amount: savedExpense.amount }];
    const lineItemsHtml = items.length > 1 ? `
      <div style="margin: 15px 0;">
        <div style="font-weight: bold; margin-bottom: 8px; font-size: 14px;">Line Items:</div>
        <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
          <thead>
            <tr style="border-bottom: 1px solid #333;">
              <th style="text-align: left; padding: 4px 0;">Category</th>
              <th style="text-align: left; padding: 4px 0;">Description</th>
              <th style="text-align: right; padding: 4px 0;">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${items.map(li => `
              <tr style="border-bottom: 1px solid #eee;">
                <td style="padding: 4px 0;">${li.category || ''}</td>
                <td style="padding: 4px 0;">${li.description || ''}</td>
                <td style="text-align: right; padding: 4px 0;">$${parseFloat(li.amount || 0).toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    ` : `
      <div class="row"><span class="label">Category:</span><span class="value">${savedExpense.category || ''}</span></div>
      <div class="row"><span class="label">Description:</span><span class="value">${savedExpense.description || ''}</span></div>
    `;

    const receiptHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Expense Receipt - ${savedExpense.receiptNumber}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 450px; margin: 0 auto; }
          .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 20px; }
          .company { font-size: 24px; font-weight: bold; }
          .title { font-size: 18px; color: #666; margin-top: 5px; }
          .details { margin: 20px 0; }
          .row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #eee; }
          .label { color: #666; }
          .value { font-weight: bold; }
          .total { font-size: 20px; margin-top: 20px; padding-top: 20px; border-top: 2px solid #333; }
          .footer { margin-top: 40px; text-align: center; color: #999; font-size: 12px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company">THE WHOLESALE CARS</div>
          <div class="title">EXPENSE RECEIPT</div>
        </div>
        <div class="details">
          <div class="row"><span class="label">Receipt #:</span><span class="value">${savedExpense.receiptNumber}</span></div>
          <div class="row"><span class="label">Date:</span><span class="value">${new Date(savedExpense.date).toLocaleDateString()}</span></div>
          ${lineItemsHtml}
          ${savedExpense.vendorName ? `<div class="row"><span class="label">Vendor:</span><span class="value">${savedExpense.vendorName}</span></div>` : ''}
          <div class="row"><span class="label">Paid From:</span><span class="value">${savedExpense.bankAccountName || ''}</span></div>
        </div>
        <div class="row total"><span>AMOUNT:</span><span>$${parseFloat(savedExpense.amount || 0).toFixed(2)}</span></div>
        <div class="footer">
          <p>4001 W Alexis Rd, Toledo, OH 43623</p>
        </div>
      </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(receiptHtml);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  // Auto-print receipt when saved from Weekly Tasks
  const autoPrintTriggered = useRef(false);
  useEffect(() => {
    if (autoPrint && savedExpense && !autoPrintTriggered.current) {
      autoPrintTriggered.current = true;
      setTimeout(() => handlePrintReceipt(), 300);
    }
  }, [savedExpense, autoPrint]);

  // Show success with print option
  if (savedExpense) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => { onSave(); }}>
        <div className="bg-white w-full max-w-md rounded-lg shadow-lg" onClick={e => e.stopPropagation()}>
          <div className="p-6 text-center">
            <div className="text-green-600 text-5xl mb-4">✓</div>
            <h3 className="text-xl font-bold mb-2">Expense Saved!</h3>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
              <div className="text-green-700 font-medium">Entered into Accounting</div>
              <div className="text-green-600 text-sm">Deducted from {savedExpense.bankAccountName}</div>
            </div>
            <div className="bg-neutral-50 rounded-lg p-4 text-left mb-4 space-y-2">
              <div className="flex justify-between"><span className="text-neutral-600">Receipt #:</span><span className="font-mono text-sm">{savedExpense.receiptNumber}</span></div>
              <div className="flex justify-between"><span className="text-neutral-600">Date:</span><span>{new Date(savedExpense.date).toLocaleDateString()}</span></div>
              <div className="flex justify-between"><span className="text-neutral-600">Category:</span><span className="font-medium">{savedExpense.category}</span></div>
              <div className="flex justify-between"><span className="text-neutral-600">Description:</span><span>{savedExpense.description}</span></div>
              <div className="flex justify-between"><span className="text-neutral-600">Amount:</span><span className="font-bold text-lg">${parseFloat(savedExpense.amount).toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-neutral-600">Pulled From:</span><span className="font-medium">{savedExpense.bankAccountName}</span></div>
            </div>
            <div className="flex gap-2">
              <button onClick={handlePrintReceipt} className="flex-1 px-4 py-2 bg-neutral-800 text-white rounded-lg font-medium">🖨️ Print Receipt</button>
              <button onClick={() => onSave()} className="px-4 py-2 text-neutral-600 hover:bg-neutral-100 rounded-lg">Done</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleQuickAddVendor = async () => {
    if (!newVendorName.trim()) return;
    try {
      const newVendor = await api.createVendor({ name: newVendorName.trim(), type: 'other' });
      // Add to local vendors list so it shows in dropdown
      setLocalVendors(prev => [...prev, newVendor]);
      // Select the new vendor
      setForm(f => ({ ...f, vendorId: newVendor.id.toString() }));
      setShowQuickAddVendor(false);
      setNewVendorName('');
    } catch (e) {
      console.error('[ExpenseForm] Failed to add vendor:', e);
      alert('Failed to add vendor: ' + e.message);
    }
  };

  const handleQuickAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      const newCat = await api.createAccountingCategory({
        name: newCategoryName,
        categoryType: 'expense',
        isVehicleCost: false,
      });
      setLocalCategories(prev => [...prev, newCat]);
      setForm(f => ({ ...f, category: newCat.name }));
      setShowQuickAddCategory(false);
      setNewCategoryName('');
    } catch (e) {
      alert('Failed to add category: ' + e.message);
    }
  };

  // Handle vendor rename
  const handleSaveVendor = async (vendorId, newName) => {
    try {
      await api.updateVendor(vendorId, { name: newName });
      setLocalVendors(prev => prev.map(v => v.id === vendorId ? { ...v, name: newName } : v));
      if (form.vendorId === vendorId.toString()) {
        setForm(f => ({ ...f, vendorName: newName }));
      }
      setEditingVendor(null);
    } catch (e) {
      alert('Failed to update vendor: ' + e.message);
    }
  };

  // Handle category rename
  const handleSaveCategory = async (categoryId, newName) => {
    try {
      await api.updateAccountingCategory(categoryId, { name: newName });
      setLocalCategories(prev => prev.map(c => c.id === categoryId ? { ...c, name: newName } : c));
      if (form.category === localCategories.find(c => c.id === categoryId)?.name) {
        setForm(f => ({ ...f, category: newName }));
      }
      setEditingCategory(null);
    } catch (e) {
      alert('Failed to update category: ' + e.message);
    }
  };

  // Handle category deactivate (soft delete)
  const handleDeactivateCategory = async (categoryId) => {
    if (!confirm('Deactivate this category? It will be hidden from future entries but historical data will be preserved.')) return;
    try {
      await api.deleteAccountingCategory(categoryId);
      setLocalCategories(prev => prev.filter(c => c.id !== categoryId));
      setEditingCategory(null);
    } catch (e) {
      alert('Failed to deactivate category: ' + e.message);
    }
  };

  // Handle category merge
  const handleMergeCategories = async () => {
    if (mergeSelection.length !== 2) return alert('Select exactly 2 categories to merge');
    const [sourceId, targetId] = mergeSelection;
    const sourceCat = sortedCategories.find(c => c.id === sourceId);
    const targetCat = sortedCategories.find(c => c.id === targetId);
    if (!confirm(`Merge "${sourceCat?.name}" into "${targetCat?.name}"? All transactions will be reassigned to "${targetCat?.name}".`)) return;
    try {
      await api.mergeCategories(sourceId, targetId);
      setLocalCategories(prev => prev.filter(c => c.id !== sourceId));
      setMergeMode(false);
      setMergeSelection([]);
    } catch (e) {
      alert('Failed to merge categories: ' + e.message);
    }
  };

  // Toggle category selection for merge
  const toggleMergeSelection = (catId) => {
    setMergeSelection(prev => {
      if (prev.includes(catId)) return prev.filter(id => id !== catId);
      if (prev.length >= 2) return [prev[1], catId];
      return [...prev, catId];
    });
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white w-full max-w-lg rounded-lg shadow-lg" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-neutral-200 flex justify-between items-center">
          <div>
            <span className="font-medium">{isNew ? 'Add Expense' : 'Edit Expense'}</span>
            <div className="text-xs text-neutral-400 mt-0.5">Tab through fields • ⌘+Enter = Save & New</div>
          </div>
          <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }} className="text-neutral-400 hover:text-neutral-600 text-xl">×</button>
        </div>
        <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} className="p-4 space-y-3">
          {/* Performance tag — required. Does not change accounting. */}
          <div>
            <label className="block text-xs text-neutral-500 mb-1">
              Where did this money go? <span className="text-red-500">*</span>
            </label>
            <p className="text-[10px] text-neutral-400 mb-1.5">This is for Performance only — the expense still hits accounting.</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'overhead', label: 'Overhead', hint: 'rent, utilities, insurance, taxes', on: 'bg-emerald-600 text-white border-emerald-600', hintOn: 'text-emerald-100', off: 'hover:border-emerald-400' },
                { id: 'into_cars', label: 'Into Cars', hint: 'recon, parts, transport, detail', on: 'bg-blue-600 text-white border-blue-600', hintOn: 'text-blue-100', off: 'hover:border-blue-400' },
                { id: 'labor', label: 'Labor', hint: 'what we pay people', on: 'bg-violet-600 text-white border-violet-600', hintOn: 'text-violet-100', off: 'hover:border-violet-400' },
                { id: 'consumables', label: 'Consumables', hint: 'supplies, tools, extra shop stuff', on: 'bg-orange-500 text-white border-orange-500', hintOn: 'text-orange-100', off: 'hover:border-orange-400' },
                { id: 'extra', label: 'Extra', hint: 'not required to keep the doors open', on: 'bg-amber-500 text-white border-amber-500', hintOn: 'text-amber-100', off: 'hover:border-amber-400' },
              ].map(box => (
                <button
                  key={box.id}
                  type="button"
                  onClick={() => setCostType(box.id)}
                  className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    costType === box.id
                      ? box.on
                      : `bg-white text-neutral-700 border-neutral-300 ${box.off}`
                  } ${box.id === 'extra' ? 'col-span-2' : ''}`}
                >
                  {box.label}
                  <span className={`block text-[10px] font-normal ${costType === box.id ? box.hintOn : 'text-neutral-400'}`}>
                    {box.hint}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Row 1: Vendor (with autocomplete) + Amount */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-neutral-500 mb-1">Vendor <span className="text-neutral-400">(type to search)</span></label>
              <div className="flex gap-1">
                <div className="flex-1">
                  <AutocompleteInput
                    inputRef={vendorRef}
                    value={form.vendorName}
                    onChange={(val) => setForm(f => ({ ...f, vendorName: val, vendorId: '' }))}
                    onSelect={handleVendorSelect}
                    options={localVendors}
                    placeholder="Start typing vendor..."
                    displayKey="name"
                    valueKey="id"
                    tabIndex={1}
                    renderOption={(v) => (
                      <div className="flex justify-between items-center">
                        <span>{v.name}</span>
                        {getVendorMemory()[v.id] && (
                          <span className="text-xs text-green-600">●</span>
                        )}
                      </div>
                    )}
                  />
                </div>
                <button 
                  type="button" 
                  onClick={() => setShowQuickAddVendor(!showQuickAddVendor)}
                  className="px-2 text-blue-600 hover:text-blue-800 text-lg"
                  title="Add new vendor"
                  tabIndex={-1}
                >
                  +
                </button>
              </div>
              {showQuickAddVendor && (
                <div className="flex gap-2 mt-2">
                  <input
                    type="text"
                    value={newVendorName}
                    onChange={e => setNewVendorName(e.target.value)}
                    placeholder="New vendor name"
                    className="flex-1 h-8 px-2 text-sm border border-neutral-200 rounded"
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleQuickAddVendor())}
                  />
                  <button type="button" onClick={handleQuickAddVendor} className="px-2 text-xs bg-blue-600 text-white rounded">Add</button>
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs text-neutral-500 mb-1">Date *</label>
              <input
                ref={dateRef}
                type="date"
                value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                className="w-full h-9 px-2 text-sm border border-neutral-200 rounded"
                tabIndex={2}
              />
            </div>
          </div>

          {/* Line Items Table */}
          <div className="border border-neutral-200 rounded-lg">
            <div className="flex justify-between items-center px-3 py-2 bg-neutral-50 border-b border-neutral-200">
              <span className="text-xs font-medium text-neutral-600">
                Line Items
                <button type="button" onClick={() => setShowManageCategories(true)} className="ml-2 text-blue-600 hover:underline" tabIndex={-1}>Manage Categories</button>
              </span>
              <div className="flex gap-2">
                <button type="button" onClick={addLineItem} className="text-xs text-blue-600 hover:text-blue-800">+ Add Line</button>
                {lineItems.length > 1 && (
                  <button type="button" onClick={clearAllLineItems} className="text-xs text-red-500 hover:text-red-700">Clear All</button>
                )}
              </div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-xs text-neutral-500">
                <tr>
                  <th className="px-2 py-1.5 text-left w-8">#</th>
                  <th className="px-2 py-1.5 text-left">Category *</th>
                  <th className="px-2 py-1.5 text-left">Description</th>
                  <th className="px-2 py-1.5 text-right w-24">Amount *</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item, idx) => (
                  <tr key={idx} className="border-t border-neutral-100">
                    <td className="px-2 py-1 text-neutral-400 text-xs">{idx + 1}</td>
                    <td className="px-1 py-1">
                      <AutocompleteInput
                        inputRef={idx === 0 ? categoryRef : null}
                        value={item.category}
                        onChange={(val) => updateLineItem(idx, 'category', val)}
                        onSelect={(cat) => updateLineItem(idx, 'category', cat.name)}
                        options={sortedCategories}
                        placeholder="Type category..."
                        displayKey="name"
                        valueKey="id"
                        tabIndex={10 + idx * 3}
                      />
                    </td>
                    <td className="px-1 py-1">
                      <input
                        type="text"
                        value={item.description}
                        onChange={e => updateLineItem(idx, 'description', e.target.value)}
                        className="w-full h-8 px-2 text-sm border border-neutral-200 rounded"
                        placeholder="Description..."
                        tabIndex={11 + idx * 3}
                      />
                    </td>
                    <td className="px-1 py-1">
                      <input
                        ref={idx === 0 ? amountRef : null}
                        type="number"
                        step="0.01"
                        value={item.amount}
                        onChange={e => updateLineItem(idx, 'amount', e.target.value)}
                        className="w-full h-8 px-2 text-sm border border-neutral-200 rounded font-mono text-right"
                        placeholder="0.00"
                        tabIndex={12 + idx * 3}
                      />
                    </td>
                    <td className="px-1 py-1 text-center">
                      {lineItems.length > 1 && (
                        <button type="button" onClick={() => removeLineItem(idx)} className="text-red-400 hover:text-red-600 text-xs">×</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-neutral-50 border-t border-neutral-200">
                <tr>
                  <td colSpan="3" className="px-2 py-2 text-right font-medium text-sm">Total:</td>
                  <td className="px-2 py-2 text-right font-mono font-bold text-sm">${lineItemsTotal.toFixed(2)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Paid From Account + Payment Method */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-neutral-500 mb-1">Paid From * <span className="text-neutral-400">(type to filter)</span></label>
              <AutocompleteInput
                inputRef={accountRef}
                value={sortedAccounts.find(a => a.id.toString() === form.bankAccountId)?.name || ''}
                onChange={() => {}}
                onSelect={(acc) => {
                  setForm(f => ({ ...f, bankAccountId: acc.id.toString() }));
                  if (acc.accountType === 'cash' || acc.name?.toLowerCase().includes('cash')) {
                    setForm(f => ({ ...f, paymentMethod: 'Cash', checkNumber: '' }));
                  } else if (acc.accountType === 'checking') {
                    setForm(f => ({ ...f, paymentMethod: 'ACH', checkNumber: '' }));
                  } else {
                    setForm(f => ({ ...f, paymentMethod: '', checkNumber: '' }));
                  }
                }}
                options={sortedAccounts}
                placeholder="Type account..."
                displayKey="name"
                valueKey="id"
                tabIndex={100}
                renderOption={(acc) => (
                  <div className="flex justify-between items-center">
                    <span>{acc.name}</span>
                    <span className={`text-xs font-mono ${parseFloat(acc.balance) < 0 ? 'text-red-500' : 'text-green-600'}`}>
                      ${parseFloat(acc.balance).toLocaleString()}
                    </span>
                  </div>
                )}
              />
            </div>
            {isCheckingAccount ? (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-neutral-500 mb-1">Payment Method</label>
                  <select
                    value={form.paymentMethod}
                    onChange={e => setForm(f => ({ ...f, paymentMethod: e.target.value, checkNumber: e.target.value !== 'Check' ? '' : f.checkNumber }))}
                    className="w-full h-9 px-2 text-sm border border-neutral-200 rounded bg-white"
                    tabIndex={101}
                  >
                    <option value="ACH">ACH</option>
                    <option value="Check">Check</option>
                    <option value="Card">Debit Card</option>
                  </select>
                </div>
                {form.paymentMethod === 'Check' && (
                  <div>
                    <label className="block text-xs text-neutral-500 mb-1">Check #</label>
                    <input
                      type="text"
                      value={form.checkNumber}
                      onChange={e => setForm(f => ({ ...f, checkNumber: e.target.value }))}
                      className="w-full h-9 px-2 text-sm border border-neutral-200 rounded"
                      placeholder="Check #"
                      tabIndex={102}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div></div>
            )}
          </div>

          {/* Memo field */}
          <div>
            <label className="block text-xs text-neutral-500 mb-1">Memo <span className="text-neutral-400">(optional notes)</span></label>
            <input
              ref={descriptionRef}
              type="text"
              value={form.memo}
              onChange={e => setForm(f => ({ ...f, memo: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), submitRef.current?.focus())}
              className="w-full h-9 px-2 text-sm border border-neutral-200 rounded"
              placeholder="Additional notes for this expense..."
              tabIndex={103}
            />
          </div>

          <div>
            <label className="block text-xs text-neutral-500 mb-1">Attach to car <span className="text-neutral-400">(optional — links this cost to a specific vehicle)</span></label>
            <select
              value={form.vehicleId}
              onChange={e => setForm(f => ({ ...f, vehicleId: e.target.value }))}
              className="w-full h-9 px-2 text-sm border border-neutral-200 rounded bg-white"
              tabIndex={104}
            >
              <option value="">No specific car</option>
              {vehicles.map(v => (
                <option key={v.id} value={v.id}>{v.stockNumber} - {v.year} {v.make} {v.model}</option>
              ))}
            </select>
          </div>

          {/* Vendor memory indicator */}
          {form.vendorId && getVendorMemory()[form.vendorId] && (
            <div className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded flex items-center gap-1">
              <span>✓</span> Prefilled from last {localVendors.find(v => v.id.toString() === form.vendorId)?.name} expense
            </div>
          )}

          <div className="flex justify-between items-center pt-3 border-t border-neutral-200">
            <button type="button" onClick={onClose} className="px-3 py-2 text-sm text-neutral-500 hover:text-neutral-700" tabIndex={10}>Cancel</button>
            <div className="flex gap-2">
              <button 
                type="button"
                onClick={(e) => handleSubmit(e, false, 'new')}
                disabled={saving} 
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 font-medium"
                tabIndex={8}
              >
                {saving ? 'Saving...' : 'Save & New'}
              </button>
              <button 
                ref={submitRef}
                type="submit" 
                disabled={saving} 
                className="px-4 py-2 text-sm bg-neutral-800 text-white rounded hover:bg-neutral-700 disabled:opacity-50 font-medium"
                tabIndex={9}
              >
                {saving ? 'Saving...' : 'Save & Close'}
              </button>
            </div>
          </div>
        </form>
      </div>
      {showManageCategories && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]" onClick={() => { setShowManageCategories(false); setMergeMode(false); setMergeSelection([]); }}>
          <div className="bg-white w-full max-w-md rounded-lg shadow-lg max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-neutral-200 flex justify-between items-center">
              <span className="font-medium">{mergeMode ? 'Merge Categories' : 'Manage Categories'}</span>
              <div className="flex gap-2">
                {!mergeMode && (
                  <button onClick={() => setMergeMode(true)} className="text-xs text-purple-600 hover:text-purple-800">Merge</button>
                )}
                <button onClick={() => { setShowManageCategories(false); setMergeMode(false); setMergeSelection([]); }} className="text-neutral-400 hover:text-neutral-600 text-xl">×</button>
              </div>
            </div>
            {mergeMode && (
              <div className="px-4 py-2 bg-purple-50 border-b border-purple-100 text-xs text-purple-700">
                Select 2 categories: first will be merged INTO second. 
                {mergeSelection.length === 2 && (
                  <button onClick={handleMergeCategories} className="ml-2 px-2 py-1 bg-purple-600 text-white rounded">Merge Now</button>
                )}
                <button onClick={() => { setMergeMode(false); setMergeSelection([]); }} className="ml-2 text-purple-600 hover:underline">Cancel</button>
              </div>
            )}
            <div className="p-4 overflow-y-auto flex-1">
              <div className="space-y-2">
                {sortedCategories.map(cat => (
                  <div 
                    key={cat.id} 
                    className={`flex items-center justify-between p-2 rounded ${mergeMode ? (mergeSelection.includes(cat.id) ? (mergeSelection[0] === cat.id ? 'bg-red-100 border border-red-300' : 'bg-green-100 border border-green-300') : 'bg-neutral-50 cursor-pointer hover:bg-neutral-100') : 'bg-neutral-50'}`}
                    onClick={mergeMode ? () => toggleMergeSelection(cat.id) : undefined}
                  >
                    {mergeMode ? (
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{cat.name}</span>
                        {mergeSelection[0] === cat.id && <span className="text-xs text-red-600">(source - will be removed)</span>}
                        {mergeSelection[1] === cat.id && <span className="text-xs text-green-600">(target - will keep)</span>}
                      </div>
                    ) : editingCategory?.id === cat.id ? (
                      <input
                        type="text"
                        value={editingCategory.name}
                        onChange={e => setEditingCategory({ ...editingCategory, name: e.target.value })}
                        onKeyDown={e => e.key === 'Enter' && handleSaveCategory(cat.id, editingCategory.name)}
                        className="flex-1 h-8 px-2 text-sm border border-neutral-200 rounded mr-2"
                        autoFocus
                      />
                    ) : (
                      <span className="text-sm">{cat.name}</span>
                    )}
                    {!mergeMode && (
                      <div className="flex gap-1">
                        {editingCategory?.id === cat.id ? (
                          <>
                            <button onClick={() => handleSaveCategory(cat.id, editingCategory.name)} className="px-2 py-1 text-xs bg-green-600 text-white rounded">Save</button>
                            <button onClick={() => setEditingCategory(null)} className="px-2 py-1 text-xs bg-neutral-200 rounded">Cancel</button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => setEditingCategory({ id: cat.id, name: cat.name })} className="px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded">Edit</button>
                            <button onClick={() => handleDeactivateCategory(cat.id)} className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded">Delete</button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {!mergeMode && (
                <div className="mt-4 pt-4 border-t">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newCategoryName}
                      onChange={e => setNewCategoryName(e.target.value)}
                      placeholder="New category name"
                      className="flex-1 h-8 px-2 text-sm border border-neutral-200 rounded"
                      onKeyDown={e => e.key === 'Enter' && handleQuickAddCategory()}
                    />
                    <button onClick={handleQuickAddCategory} className="px-3 py-1 text-sm bg-blue-600 text-white rounded">Add</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Vendors - Full CRUD
const VendorList = ({ vendors, onRefresh, canEdit }) => {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    try {
      await api.deleteVendor(id);
      onRefresh();
    } catch (e) {
      alert('Delete failed: ' + e.message);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <Header title="Vendors" count={vendors.length} />
        {canEdit && <button onClick={() => { setEditing(null); setShowForm(true); }} className="ml-auto text-xs bg-neutral-800 text-white px-3 py-1 rounded hover:bg-neutral-700">+ Add Vendor</button>}
      </div>
      <Table headers={['Name', 'Type', 'Contact', 'Phone', 'Email', ...(canEdit ? [''] : [])]}>
        {vendors.map(v => (
          <tr key={v.id} className="border-b border-neutral-100">
            <td className="py-1.5">{v.name}</td>
            <td className="py-1.5 text-xs">{v.vendor_type}</td>
            <td className="py-1.5 text-xs">{v.contact_name || '-'}</td>
            <td className="py-1.5 text-xs">{v.phone || '-'}</td>
            <td className="py-1.5 text-xs">{v.email || '-'}</td>
            {canEdit && (
              <td className="py-1.5 text-right text-xs">
                <button onClick={() => { setEditing(v); setShowForm(true); }} className="text-neutral-500 hover:text-neutral-800 mr-2">edit</button>
                <button onClick={() => handleDelete(v.id)} className="text-red-500 hover:text-red-700">del</button>
              </td>
            )}
          </tr>
        ))}
      </Table>
      {vendors.length === 0 && <Empty text="No vendors" />}
      {showForm && <VendorForm vendor={editing} onClose={() => setShowForm(false)} onSave={() => { setShowForm(false); onRefresh(); }} />}
    </div>
  );
};

// Vendor Form
const VendorForm = ({ vendor, onClose, onSave }) => {
  const isNew = !vendor?.id;
  const [form, setForm] = useState({
    name: vendor?.name || '',
    vendor_type: vendor?.vendor_type || 'auction',
    contact_name: vendor?.contact_name || '',
    phone: vendor?.phone || '',
    email: vendor?.email || '',
    address: vendor?.address || '',
    notes: vendor?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) return alert('Name required');
    setSaving(true);
    try {
      if (isNew) {
        await api.createVendor(form);
      } else {
        await api.updateVendor(vendor.id, form);
      }
      onSave();
    } catch (e) {
      alert('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded shadow-lg" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-neutral-200 flex justify-between">
          <span className="font-medium">{isNew ? 'Add Vendor' : 'Edit Vendor'}</span>
          <button onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <Input label="Name" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} required />
          <Select label="Type" value={form.vendor_type} onChange={v => setForm(f => ({ ...f, vendor_type: v }))} options={['auction', 'transport', 'mechanic', 'parts', 'detail', 'other']} />
          <Input label="Contact Name" value={form.contact_name} onChange={v => setForm(f => ({ ...f, contact_name: v }))} />
          <Input label="Phone" value={form.phone} onChange={v => setForm(f => ({ ...f, phone: v }))} />
          <Input label="Email" value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} />
          <Input label="Address" value={form.address} onChange={v => setForm(f => ({ ...f, address: v }))} />
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-neutral-600">Cancel</button>
            <button type="submit" disabled={saving} className="px-3 py-1.5 text-sm bg-neutral-800 text-white rounded disabled:opacity-50">{saving ? 'Saving...' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Vehicle Detail
const VehicleDetail = ({ vehicle, onClose, showFinancials, onEdit, canEdit, limitedView, onNavigateToRO, onNavigateToInspection }) => {
  const { currentUser } = useAuth();
  const [extraData, setExtraData] = useState({ loading: true, hasRo: false, roNumber: null, roId: null, isInspected: false, inspectionDate: null, inspectionId: null });
  const [costAdjustments, setCostAdjustments] = useState([]);
  const [reportText, setReportText] = useState(null);
  const [reportKind, setReportKind] = useState('inventory'); // 'inventory' | 'recon'
  const [reportLoading, setReportLoading] = useState(false);
  const [showVindex, setShowVindex] = useState(false);
  
  // Fetch RO and inspection status on mount
  useEffect(() => {
    const fetchExtraData = async () => {
      try {
        const [data, adjustmentRows] = await Promise.all([
          api.getVehicle(vehicle.id),
          api.getVehicleCostAdjustments(vehicle.id).catch(() => [])
        ]);
        const adjustments = Array.isArray(adjustmentRows) ? adjustmentRows : [];
        setCostAdjustments(adjustments);
        setExtraData({
          loading: false,
          hasRo: data.hasRo || false,
          roNumber: data.roNumber || null,
          roId: data.roId || null,
          roStatus: data.roStatus || null,
          isInspected: data.isInspected || false,
          inspectionDate: data.inspectionDate || null,
          inspectionId: data.inspectionId || null
        });
      } catch (err) {
        console.error('Failed to fetch vehicle extra data:', err);
        setCostAdjustments([]);
        setExtraData(prev => ({ ...prev, loading: false }));
      }
    };
    fetchExtraData();
  }, [vehicle.id]);
  
  const inspectionLabel = vehicle.inspectionType === 'dealer_direct' ? 'Dealer Direct (Full)' : 
                          vehicle.inspectionType === 'wholesale_platform' ? 'Wholesale (Basic)' : '-';
  const adjustmentTotal = costAdjustments.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
  const projectedProfit = (parseFloat(vehicle.estimatedSale) || 0) - (getTotalCost(vehicle) + adjustmentTotal);
  const adjustmentTypeLabels = {
    recon: 'Recon',
    additional: 'Additional',
    transport: 'Transport',
    buyer_fee: 'Buyer Fee',
    input_fee: 'Input Fee',
    pack_fee: 'Pack Fee'
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
  };

  // ===== Internal inventory / recon report (terminal-style, editable before printing) =====
  const generateVehicleReport = (kind = 'inventory') => {
    const HR2 = '='.repeat(64);
    const HR1 = '-'.repeat(64);
    const up = (s) => (s == null ? '' : String(s)).toUpperCase();
    const clean = (s) => up(s).replace(/_/g, ' ');
    const money = (n) => {
      const v = parseFloat(n) || 0;
      return '$ ' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).padStart(10);
    };
    const dotLine = (label, value) => `${(label + ' ').padEnd(20, '.')} ${value}`;
    const priceLine = (label, value) => {
      const lbl = up(label);
      const pad = Math.max(lbl.length + 1, 39);
      return `${(lbl + ' ').padEnd(pad, '.')} ${money(value)}`;
    };
    const wrap = (text, indent = '') => {
      const words = String(text).trim().split(/\s+/);
      const out = [];
      let line = '';
      words.forEach(w => {
        if ((line + ' ' + w).trim().length > 64) { out.push(indent + line.trim()); line = w; }
        else { line = (line + ' ' + w).trim(); }
      });
      if (line.trim()) out.push(indent + line.trim());
      return out.join('\n');
    };
    const printedBy = (() => {
      const name = (currentUser?.name || '').trim();
      if (!name) return '-';
      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) return up(parts.map(p => p[0]).join('') + '.');
      return up(name);
    })();

    const now = new Date();
    const dateKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    const stockKey = up(vehicle.stockNumber) || vehicle.id;
    const reportId = kind === 'recon'
      ? `VDX-R-${dateKey}-${stockKey}`
      : `VDX-${dateKey}-${stockKey}`;
    const printed = now.toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit' });

    const vhrLabels = { clean: 'CLEAN', issues: 'HAS ISSUES', not_pulled: 'NOT PULLED', caution: 'CAUTION' };
    const vhrBits = [];
    if (vehicle.vhrScore != null && vehicle.vhrScore !== '') vhrBits.push(`VHR SCORE ${vehicle.vhrScore}`);
    const vhrStatus = vhrLabels[vehicle.vhrStatus] || clean(vehicle.vhrStatus);
    if (vhrStatus) vhrBits.push(vhrStatus);
    if (vehicle.vhrNotes) vhrBits.push(up(vehicle.vhrNotes));
    if (vehicle.damageDescription) vhrBits.push(up(vehicle.damageDescription));
    const vhrLine = vhrBits.length ? vhrBits.join(' /') : 'NOT PULLED';

    const paymentParts = [];
    const payMethod = clean(vehicle.paymentMethod || vehicle.paymentType);
    if (payMethod) paymentParts.push(payMethod);
    if (vehicle.paymentClearedDate) paymentParts.push(formatDate(vehicle.paymentClearedDate));
    if (vehicle.paymentCleared) paymentParts.push('CLR');
    if (vehicle.checkNumber) paymentParts.push(up(vehicle.checkNumber));
    const paymentLine = paymentParts.join(' ') || '-';

    const laborParts = (parseFloat(vehicle.partsCost) || 0) + (parseFloat(vehicle.laborCost) || 0);
    const reconLaborLabel = vehicle.reconNotes
      ? `RECON - LABOR/PARTS ${up(vehicle.reconNotes)}`
      : 'RECON - LABOR/PARTS';
    const transportLabel = vehicle.transportNotes
      ? `RECON - TRANSPORT ${up(vehicle.transportNotes)}`
      : 'RECON - TRANSPORT';

    const timelineBits = [
      vehicle.notes,
      vehicle.whatItNeeds ? `VEH INSP:\n${vehicle.whatItNeeds}` : null,
      vehicle.inspectionNotes,
      vehicle.reconGamePlan || vehicle.recon_game_plan
    ].filter(Boolean);

    const lines = [];
    lines.push(kind === 'recon' ? 'RECON REPORT' : 'INVENTORY REPORT');
    lines.push(`REPORT ID: ${reportId} PRINTED: ${printed}`);
    lines.push(`PRINTED BY: ${printedBy} REPORT TYPE: INTERNAL`);
    lines.push(HR1);
    lines.push('VEHICLE');
    lines.push(`${vehicle.year} ${up(vehicle.make)} ${up(vehicle.model)}${vehicle.trim ? ' ' + up(vehicle.trim) : ''}`);
    lines.push(dotLine('VIN', up(vehicle.vin) || '-'));
    lines.push(dotLine('STOCK #', up(vehicle.stockNumber) || '-'));
    lines.push(dotLine('COLOR', (up(vehicle.color) || '-').padEnd(18) + `MILEAGE: ${vehicle.mileage ? Number(vehicle.mileage).toLocaleString() : '-'}`));
    lines.push(dotLine('STATUS', (clean(vehicle.status) || '-').padEnd(18) + `DAYS ON LOT: ${getDays(vehicle.purchaseDate)}`));
    lines.push(dotLine('TITLE', (clean(vehicle.titleStatus) || '-').padEnd(18) + `END USE: ${clean(vehicle.endUse) || '-'}`));
    lines.push('VEHICLE HISTORY');
    lines.push(dotLine('VHR SCORE/STATUS', vhrLine));
    lines.push('ACQUISITION');
    lines.push(dotLine('AUCTION', (up(vehicle.auction) || '-').padEnd(18) + `BUYER: ${up(vehicle.buyer) || '-'}`));
    lines.push(dotLine('PURCHASE DATE', (formatDate(vehicle.purchaseDate) || '-').padEnd(18) + `PAYMENT: ${paymentLine}`));
    lines.push('PRICE SUMMARY');
    lines.push(priceLine('PURCHASE PRICE', vehicle.purchasePrice));
    lines.push(priceLine('BUYER FEE', vehicle.buyerFee));
    lines.push(priceLine('INPUT FEE', vehicle.inputFee));
    lines.push(priceLine('PACK FEE', vehicle.packFee || getPackFee(vehicle)));
    lines.push(priceLine(reconLaborLabel, laborParts));
    lines.push(priceLine('RECON - OUTSIDE SERVICE', vehicle.outsideServiceCost));
    lines.push(priceLine(transportLabel, vehicle.transportCost));
    costAdjustments.forEach(adj => {
      const typeLabel = up(adjustmentTypeLabels[adj.cost_type] || adj.cost_type || 'Adjustment');
      const note = adj.note ? ` ${up(adj.note)}` : '';
      lines.push(priceLine(`${typeLabel}${note}`, adj.amount));
    });
    lines.push('-'.repeat(48));
    lines.push(priceLine('TOTAL INVESTED', getTotalCost(vehicle) + adjustmentTotal));
    lines.push(priceLine('EST. SALE PRICE PER VINDEX', vehicle.estimatedSale));
    lines.push(priceLine('PROJECTED PROFIT', projectedProfit));
    lines.push('NOTES/KEY TIMELINE');
    if (timelineBits.length) {
      timelineBits.forEach(bit => lines.push(wrap(bit)));
    } else {
      lines.push('');
    }
    lines.push(HR1);
    lines.push('LOTSENSE DEALER MANAGEMENT SYSTEM');
    lines.push('Vehicle data and market analytics provided by VINDEX');
    lines.push('Wholesale Market Analytics. 2025. All rights reserved.');
    lines.push('For internal dealership use only.');
    lines.push(HR2);
    return lines.join('\n');
  };

  const openVehicleReport = async (kind = 'inventory') => {
    setReportLoading(true);
    try {
      setReportKind(kind);
      setReportText(generateVehicleReport(kind));
    } finally {
      setReportLoading(false);
    }
  };

  const handlePrintReport = () => {
    const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const title = reportKind === 'recon' ? 'Recon Report' : 'Inventory Report';
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>${title} - ${vehicle.stockNumber || ''}</title>
      <style>body{margin:24px} pre{font-family:'Courier New',Courier,monospace;font-size:12px;line-height:1.4;white-space:pre-wrap}</style>
      </head><body><pre>${esc(reportText)}</pre></body></html>
    `);
    win.document.close();
    win.print();
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex justify-end z-50" onClick={onClose}>
      <div className="w-96 bg-white h-full overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-neutral-200 flex justify-between items-start">
          <div>
            <div className="font-medium">{vehicle.year} {vehicle.make} {vehicle.model}</div>
            <div className="text-xs text-neutral-500">{vehicle.stockNumber} · {vehicle.vin}</div>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600">×</button>
        </div>
        
        {/* Inspection & RO Status */}
        <div className="p-3 bg-neutral-50 border-b border-neutral-200">
          <div className="flex gap-4 text-sm">
            <div className="flex-1">
              <span className="text-neutral-500">Inspected: </span>
              {extraData.loading ? (
                <span className="text-neutral-400">...</span>
              ) : extraData.isInspected ? (
                onNavigateToInspection ? (
                  <button 
                    onClick={() => { onClose(); onNavigateToInspection(vehicle.id); }}
                    className="text-green-600 font-medium hover:underline cursor-pointer transition-colors hover:text-green-700"
                  >
                    ✅ Yes {extraData.inspectionDate && `(${formatDate(extraData.inspectionDate)})`}
                  </button>
                ) : (
                  <span className="text-green-600 font-medium">✅ Yes {extraData.inspectionDate && `(${formatDate(extraData.inspectionDate)})`}</span>
                )
              ) : (
                <span className="text-red-600 font-medium">❌ No</span>
              )}
            </div>
          </div>
          <div className="flex gap-4 text-sm mt-1">
            <div className="flex-1">
              <span className="text-neutral-500">Repair Order: </span>
              {extraData.loading ? (
                <span className="text-neutral-400">...</span>
              ) : extraData.hasRo ? (
                onNavigateToRO && extraData.roId ? (
                  <button 
                    onClick={() => { onClose(); onNavigateToRO(extraData.roId); }}
                    className="text-green-600 font-medium hover:underline cursor-pointer transition-colors hover:text-green-700"
                  >
                    ✅ {extraData.roNumber}
                  </button>
                ) : (
                  <span className="text-green-600 font-medium">✅ {extraData.roNumber}</span>
                )
              ) : (
                <span className="text-red-600 font-medium">❌ None</span>
              )}
            </div>
          </div>
        </div>
        
        {/* Price prominently displayed */}
        <div className="p-4 bg-green-50 border-b border-green-100">
          <div className="text-xs text-green-700 mb-1">Price</div>
          <div className="text-2xl font-bold text-green-800">
            {vehicle.estimatedSale ? `$${parseFloat(vehicle.estimatedSale).toLocaleString()}` : '-'}
          </div>
        </div>
        
        {/* Dealer Direct Alert */}
        {vehicle.endUse === 'dealer_direct' && (
          <div className="p-3 bg-red-100 border-b border-red-200">
            <div className="flex items-center gap-2 text-red-800 font-medium text-sm">
              <span className="text-lg">⚠️</span>
              DEALER DIRECT - THOROUGH INSPECTION REQUIRED
            </div>
            <div className="text-xs text-red-700 mt-1">
              20 min test drive • Hard accel • Lift inspection • Check for leaks
            </div>
          </div>
        )}

        {/* Auction Lights - HIDDEN from inventory, only shown in Recon/Shop/Admin views */}
        {/* This section intentionally removed from VehicleDetail - auction lights are visible in Recon module only */}
        
        {/* Files & QR Buttons */}
        <div className="p-4 border-b border-neutral-100 flex gap-2">
          <VehicleFilesButton vehicleId={vehicle.id} vehicle={vehicle} className="flex-1 justify-center" />
          <QRButton vehicle={vehicle} className="px-3 py-2 border border-neutral-200 rounded-lg hover:bg-neutral-50" />
          <button
            onClick={() => window.open(window.location.origin + window.location.pathname + '#partsroom', '_blank')}
            className="px-3 py-2 border border-neutral-200 rounded-lg hover:bg-neutral-50 text-sm"
            title="Open Parts Room"
          >
            📦 Parts
          </button>
          {showFinancials && !limitedView && (
            <>
              <button
                onClick={() => setShowVindex(true)}
                className="px-3 py-2 border border-green-300 text-green-700 rounded-lg hover:bg-green-50 text-sm"
                title="Vindex intelligence + live ACV buy number for this vehicle"
              >
                💰 Buy #
              </button>
              <button
                onClick={() => openVehicleReport('inventory')}
                disabled={reportLoading}
                className="px-3 py-2 border border-neutral-200 rounded-lg hover:bg-neutral-50 text-sm disabled:opacity-50"
                title="Print Inventory Report"
              >
                {reportLoading && reportKind === 'inventory' ? '...' : '🖨 Report'}
              </button>
              <button
                onClick={() => openVehicleReport('recon')}
                disabled={reportLoading}
                className="px-3 py-2 border border-neutral-200 rounded-lg hover:bg-neutral-50 text-sm disabled:opacity-50"
                title="Print Recon Report"
              >
                {reportLoading && reportKind === 'recon' ? '...' : '🖨 Recon'}
              </button>
            </>
          )}
        </div>
        
        <div className="p-4 space-y-2 text-sm">
          <Row label="Status" value={vehicle.status} />
          {!limitedView && <Row label="Title" value={vehicle.titleStatus} />}
          <Row label="Mileage" value={vehicle.mileage?.toLocaleString() || '-'} />
          <Row label="Color" value={vehicle.color || '-'} />
          <Row label="Days" value={getDays(vehicle.purchaseDate)} />
          <Row label="End Use" value={vehicle.endUse || '-'} />
          <Row label="Inspection Type" value={inspectionLabel} />
          
          {/* Financial details - admin/office only */}
          {showFinancials && !limitedView && (
            <>
              <div className="border-t border-neutral-200 pt-2 mt-2">
                <div className="text-xs text-neutral-500 mb-2 font-semibold">💰 Cost Breakdown</div>
                <Row label="Purchase Price" value={`$${(vehicle.purchasePrice || 0).toLocaleString()}`} />
                <Row label={`Buyer Fee ${vehicle.buyer === 'Alex' ? '(Alex)' : ''}`} value={`$${(vehicle.buyerFee || 0).toLocaleString()}`} />
                <Row label="Input Fee" value={`$${(vehicle.inputFee || 0).toLocaleString()}`} />
                <Row label={`Pack Fee ${(vehicle.endUse === 'copart' || vehicle.endUse === 'salvage') ? '(Copart)' : '(Wholesale)'}`} value={`$${(vehicle.packFee || getPackFee(vehicle)).toLocaleString()}`} />
                <Row label="Transport" value={`$${(vehicle.transportCost || 0).toLocaleString()}`} />
                {costAdjustments.length > 0 && (
                  <>
                    <div className="text-xs text-neutral-400 mt-2 mb-1">Added Recon/Addtl Entries:</div>
                    {costAdjustments.map((adj) => (
                      <Row
                        key={adj.id}
                        label={`  ${adjustmentTypeLabels[adj.cost_type] || adj.cost_type || 'Adjustment'}${adj.note ? ` - ${adj.note}` : ''}`}
                        value={`$${(parseFloat(adj.amount || 0)).toLocaleString()}`}
                      />
                    ))}
                  </>
                )}
                <div className="text-xs text-neutral-400 mt-2 mb-1">Recon Costs:</div>
                <Row label="  Parts" value={`$${(vehicle.partsCost || 0).toLocaleString()}`} />
                <Row label="  Labor" value={`$${(vehicle.laborCost || 0).toLocaleString()}`} />
                <Row label="  Outside Service" value={`$${(vehicle.outsideServiceCost || 0).toLocaleString()}`} />
                <Row label="  Total Recon" value={`$${(vehicle.reconCost || 0).toLocaleString()}`} />
                {costAdjustments.length > 0 && <Row label="  Added Recon/Addtl Total" value={`$${adjustmentTotal.toLocaleString()}`} />}
                <div className="border-t border-neutral-300 pt-2 mt-2">
                  <Row label="TOTAL INVESTED" value={`$${(getTotalCost(vehicle) + adjustmentTotal).toLocaleString()}`} bold />
                </div>
              </div>
              <div className="border-t border-neutral-200 pt-2 mt-2">
                <Row label="Est. Sale Price" value={`$${(vehicle.estimatedSale || 0).toLocaleString()}`} />
                <Row 
                  label="Projected Profit" 
                  value={`$${projectedProfit.toLocaleString()}`} 
                  bold 
                />
              </div>
            </>
          )}

          {/* Limited roles get ONE blended number (server-computed, display-only) — no breakdown */}
          {(!showFinancials || limitedView) && vehicle.limitedCost != null && (
            <div className="border-t border-neutral-200 pt-2 mt-2">
              <Row label="Cost in Vehicle" value={`$${Number(vehicle.limitedCost).toLocaleString()}`} bold />
            </div>
          )}
          
          {vehicle.whatItNeeds && <Row label="Needs" value={vehicle.whatItNeeds} />}
          
          {/* Notes Section */}
          {vehicle.notes && (
            <div className="border-t border-neutral-200 pt-2 mt-2">
              <div className="text-xs text-neutral-500 mb-2 font-semibold">📝 Notes</div>
              <div className="text-sm text-neutral-700 whitespace-pre-wrap bg-neutral-50 p-3 rounded-lg border border-neutral-200">
                {vehicle.notes}
              </div>
            </div>
          )}
        </div>
        {canEdit && !limitedView && (
          <div className="p-4 border-t border-neutral-200">
            <button onClick={onEdit} className="w-full py-2 text-sm bg-neutral-800 text-white rounded hover:bg-neutral-700">Edit Vehicle</button>
          </div>
        )}

        {/* Vindex intelligence + Get Buy Number — VIN & miles prefilled from this vehicle */}
        {showVindex && <VindexPanel vehicle={vehicle} onClose={() => setShowVindex(false)} />}

        {/* Inventory / Recon Report Preview (editable before printing) */}
        {reportText !== null && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl p-4 flex flex-col" style={{ maxHeight: '92vh' }}>
              <div className="mb-3">
                <h3 className="font-bold">{reportKind === 'recon' ? 'Recon Report Preview' : 'Inventory Report Preview'}</h3>
                <div className="text-xs text-neutral-500">
                  Add, edit, or remove any lines below — changes only affect this printout, not the vehicle record.
                </div>
              </div>
              <textarea
                value={reportText}
                onChange={e => setReportText(e.target.value)}
                spellCheck={false}
                className="flex-1 w-full border border-neutral-200 rounded-lg p-3 font-mono resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                style={{ fontSize: '12px', lineHeight: 1.4, minHeight: '55vh' }}
              />
              <div className="flex justify-between items-center mt-3">
                <button
                  onClick={() => openVehicleReport(reportKind)}
                  className="text-sm text-neutral-500 hover:text-neutral-700"
                  title="Discard edits and regenerate from vehicle data"
                >
                  Reset to original
                </button>
                <div className="flex gap-3">
                  <button
                    onClick={() => setReportText(null)}
                    className="px-4 py-2 text-neutral-600 hover:bg-neutral-100 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePrintReport}
                    className="px-6 py-2 bg-neutral-800 text-white rounded-lg font-medium hover:bg-neutral-700"
                  >
                    🖨 Print
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// UI Components
const Header = ({ title, count }) => (
  <div className="flex items-baseline gap-2">
    <span className="text-sm font-medium text-neutral-700">{title}</span>
    {count !== undefined && <span className="text-xs text-neutral-400">{count}</span>}
  </div>
);

const Empty = ({ text }) => <div className="py-8 text-center text-neutral-400 text-sm">{text}</div>;

const Table = ({ headers, children }) => (
  <table className="w-full text-sm">
    <thead>
      <tr className="text-left text-xs text-neutral-500 border-b border-neutral-200">
        {headers.map((h, i) => <th key={i} className="pb-2 font-medium cursor-pointer" style={{ pointerEvents: 'auto' }}>{h}</th>)}
      </tr>
    </thead>
    <tbody>{children}</tbody>
  </table>
);

const Row = ({ label, value, bold }) => (
  <div className="flex justify-between">
    <span className="text-neutral-500">{label}</span>
    <span className={bold ? 'font-medium' : ''}>{value}</span>
  </div>
);

const Input = ({ label, value, onChange, type = 'text', required, disabled }) => (
  <div>
    <label className="block text-xs text-neutral-500 mb-1">{label}{required && ' *'}</label>
    <input type={type} value={value} onChange={e => onChange(e.target.value)} className={`w-full h-8 px-2 text-sm border border-neutral-200 rounded ${disabled ? 'bg-neutral-100 text-neutral-400' : ''}`} required={required} disabled={disabled} />
  </div>
);

const Select = ({ label, value, onChange, options }) => (
  <div>
    <label className="block text-xs text-neutral-500 mb-1">{label}</label>
    <select value={value} onChange={e => onChange(e.target.value)} className="w-full h-8 px-2 text-sm border border-neutral-200 rounded bg-white">
      {options.map(o => typeof o === 'string' ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const getDays = (date) => date ? Math.floor((Date.now() - new Date(date)) / 86400000) : 0;

// Calculate pack fee based on endUse: Copart=$75, Wholesale/Retail=$325
const getPackFee = (v) => {
  if (v.endUse === 'copart' || v.endUse === 'salvage') return 75;
  return 325;
};

// Full cost breakdown
// Hard costs only (no recon) - for inventory display
const getHardCosts = (v) => {
  const purchasePrice = parseFloat(v.purchasePrice) || 0;
  const buyerFee = parseFloat(v.buyerFee) || 0;
  const inputFee = parseFloat(v.inputFee) || 0;
  const packFee = parseFloat(v.packFee) || getPackFee(v);
  const transportCost = parseFloat(v.transportCost) || 0;
  return purchasePrice + buyerFee + inputFee + packFee + transportCost;
};

// Full cost breakdown including recon
const getTotalCost = (v) => {
  return getHardCosts(v) + (parseFloat(v.reconCost) || 0);
};

export default LotSenseApp;
